// src/Account.jsx
import { useNavigate } from 'react-router-dom';
import { getToken } from "./tokenStorage";
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AuthCtx } from "./AuthProvider";
import AdminAccessPanel from "./AdminAccessPanel";
import TeacherDisciplineChart from "./TeacherDisciplineChart";
import { useTheme } from "./useTheme";
import { HALLOWEEN_MODE } from "./halloweenMode";
import { Lottie } from 'lottie-react';
import giftAnimData from './GiftAnim.json';
import diceRollAnimData from './DiceRollAnim.json';
import CaseOpeningAnim from './CaseOpeningAnim';
import CosmeticAsset from './CosmeticAsset';
import ItemEffect from './ItemEffect';

// iOS fires mouseenter/mouseleave on tap, triggering inline-style writes and jank.
// Gate hover handlers so touch devices skip them entirely.
const SUPPORTS_HOVER = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function getPushBlockedSteps() {
  const ua = navigator.userAgent;
  if (/YaApp_Android|YaSearchBrowser/i.test(ua)) {
    return {
      browser: 'Приложение Яндекс',
      steps: [
        'Откройте Настройки Android',
        'Приложения → Яндекс → Уведомления',
        'Разрешите уведомления от приложения',
        'Вернитесь и нажмите колокольчик снова',
      ],
    };
  }
  if (/YaBrowser/i.test(ua)) {
    return {
      browser: 'Яндекс Браузер',
      steps: [
        'Нажмите ⋮ → значок шестерёнки (Настройки)',
        'Перейдите в «Уведомления» → «Уведомления от сайтов»',
        'В списке «Заблокированы» найдите сайт и нажмите на него',
        'Включите «Разрешить», обновите страницу',
      ],
    };
  }
  const isMobile = /Android|iPhone|iPad|Mobile/i.test(ua);
  if (/Firefox/i.test(ua)) {
    return {
      browser: 'Firefox',
      steps: isMobile ? [
        'Нажмите ⋮ → «Настройки»',
        'Выберите «Разрешения сайтов» → «Уведомления»',
        'Найдите сайт и измените статус на «Разрешить»',
        'Обновите страницу и нажмите колокольчик снова',
      ] : [
        'Нажмите на 🔒 замок слева от адреса',
        'Выберите «Разрешения для этого сайта»',
        'В строке «Уведомления» выберите «Разрешить»',
        'Перезагрузите страницу и нажмите колокольчик снова',
      ],
    };
  }
  if (/Edg\//i.test(ua)) {
    return {
      browser: 'Microsoft Edge',
      steps: isMobile ? [
        'Нажмите ⋮ → «Настройки»',
        'Конфиденциальность и безопасность → «Разрешения сайта»',
        'Уведомления → найдите сайт → «Разрешить»',
        'Вернитесь и нажмите колокольчик снова',
      ] : [
        'Нажмите на 🔒 замок слева от адреса сайта',
        'Выберите «Разрешения для этого сайта»',
        'В строке «Уведомления» выберите «Разрешить»',
        'Вернитесь и нажмите колокольчик снова',
      ],
    };
  }
  return {
    browser: 'Chrome',
    steps: isMobile ? [
      'Нажмите ⋮ → «Настройки»',
      'Выберите «Настройки сайтов» → «Уведомления»',
      'Найдите сайт и переключите на «Разрешить»',
      'Вернитесь и нажмите колокольчик снова',
    ] : [
      'Нажмите на 🔒 замок слева от адреса сайта',
      'Выберите «Настройки сайта» → «Уведомления»',
      'Переключите на «Разрешить»',
      'Вернитесь и нажмите колокольчик снова',
    ],
  };
}

function AvatarLightbox({ src, onClose }) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const lastPt = useRef({ x: 0, y: 0 });
  const lastDist = useRef(null);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const zoom = delta => setScale(s => clamp(s * delta, 1, 5));

  const reset = () => { setScale(1); setPan({ x: 0, y: 0 }); };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center overflow-hidden select-none"
      onClick={() => scale <= 1 && onClose()}
      onWheel={e => { e.preventDefault(); zoom(e.deltaY < 0 ? 1.12 : 0.89); }}
    >
      {/* Закрыть */}
      <button type="button" onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors">
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
      {/* Сбросить zoom */}
      {scale > 1 && (
        <button type="button" onClick={reset}
          className="absolute top-4 left-4 z-10 px-3 h-9 bg-white/20 hover:bg-white/30 rounded-full text-white text-xs font-medium transition-colors">
          1:1
        </button>
      )}

      <img
        src={src} alt=""
        loading="lazy"
        className="max-w-[88vw] max-h-[88vh] rounded-3xl object-contain shadow-2xl"
        style={{
          transform: `scale(${scale}) translate(${pan.x / scale}px, ${pan.y / scale}px)`,
          cursor: scale > 1 ? (dragging.current ? 'grabbing' : 'grab') : 'zoom-in',
          transition: dragging.current ? 'none' : 'transform 0.15s',
        }}
        onDoubleClick={() => scale > 1 ? reset() : setScale(2.5)}
        onClick={e => scale > 1 && e.stopPropagation()}
        onPointerDown={e => {
          if (scale <= 1) return;
          dragging.current = true;
          lastPt.current = { x: e.clientX, y: e.clientY };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={e => {
          if (!dragging.current) return;
          const dx = e.clientX - lastPt.current.x;
          const dy = e.clientY - lastPt.current.y;
          lastPt.current = { x: e.clientX, y: e.clientY };
          setPan(p => ({ x: p.x + dx, y: p.y + dy }));
        }}
        onPointerUp={() => { dragging.current = false; }}
        onTouchStart={e => {
          if (e.touches.length === 2) {
            lastDist.current = Math.hypot(
              e.touches[0].clientX - e.touches[1].clientX,
              e.touches[0].clientY - e.touches[1].clientY
            );
          }
        }}
        onTouchMove={e => {
          if (e.touches.length === 2 && lastDist.current) {
            e.preventDefault();
            const dist = Math.hypot(
              e.touches[0].clientX - e.touches[1].clientX,
              e.touches[0].clientY - e.touches[1].clientY
            );
            zoom(dist / lastDist.current);
            lastDist.current = dist;
          }
        }}
        draggable={false}
      />
      {scale <= 1 && (
        <p className="absolute bottom-6 text-white/30 text-[11px]">
          Двойной клик · прокрутка · pinch для приближения
        </p>
      )}
    </div>,
    document.body
  );
}

const AvatarPositioner = React.memo(function AvatarPositioner({ src, pos, onChange, gradient, onTap, busy }) {
  const containerRef = useRef(null);
  const dragging = useRef(false);
  const startPt = useRef({ x: 0, y: 0 });
  const lastPt = useRef({ x: 0, y: 0 });
  const moved = useRef(false);

  const onPointerDown = e => {
    dragging.current = true;
    moved.current = false;
    startPt.current = { x: e.clientX, y: e.clientY };
    lastPt.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = e => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPt.current.x;
    const dy = e.clientY - lastPt.current.y;
    const totalDx = e.clientX - startPt.current.x;
    const totalDy = e.clientY - startPt.current.y;
    if (Math.abs(totalDx) > 4 || Math.abs(totalDy) > 4) moved.current = true;
    lastPt.current = { x: e.clientX, y: e.clientY };
    if (moved.current) onChange(prev => ({
      x: Math.max(0, Math.min(100, prev.x - dx * 0.6)),
      y: Math.max(0, Math.min(100, prev.y - dy * 0.6)),
    }));
  };

  const onPointerUp = () => {
    if (dragging.current && !moved.current && onTap) onTap();
    dragging.current = false;
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div style={{ position: 'relative' }}>
        <div
          ref={containerRef}
          className="w-[110px] h-[110px] rounded-full overflow-hidden ring-4 ring-white/30 touch-none"
          style={{ background: gradient, cursor: onTap ? 'pointer' : 'grab' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {src ? (
            <img
              src={src}
              alt=""
              className="w-full h-full object-cover pointer-events-none select-none"
              style={{ objectPosition: `${pos.x}% ${pos.y}%` }}
              draggable={false}
            />
          ) : (
            <div className="w-full h-full bg-white/20" />
          )}
        </div>
        {onTap && (
          <div className="pointer-events-none absolute inset-0 rounded-full flex items-end justify-center pb-2" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 55%)' }}>
            {busy
              ? <span className="w-4 h-4 rounded-full border-2 border-white/60 border-t-white animate-spin block" />
              : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
            }
          </div>
        )}
      </div>
    </div>
  );
});

/* ─── BannerPreviewCard ────────────────────────────────────────────── */
function BannerPreviewCard({ src, pos, onChange, onZoomChange, zoom = 1, fio, avatarSrc, avatarPos, scale = 1, label, rounded = true, readOnly = false, extended = false }) {
  const dragging = useRef(false);
  const lastPt = useRef({ x: 0, y: 0 });
  const activePointers = useRef(new Map());
  const lastPinchDist = useRef(null);
  const dragPosRef = useRef({ x: pos?.x ?? 50, y: pos?.y ?? 50 });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => { if (!dragging.current) dragPosRef.current = pos; }, [pos]);

  const onPointerDown = e => {
    if (readOnly) return;
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.current.size === 1) {
      dragging.current = true;
      dragPosRef.current = pos;
      lastPt.current = { x: e.clientX, y: e.clientY };
      lastPinchDist.current = null;
      setIsDragging(true);
    }
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = e => {
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...activePointers.current.values()];
    if (pts.length === 2 && onZoomChange) {
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (lastPinchDist.current !== null) {
        const delta = dist - lastPinchDist.current;
        onZoomChange(z => Math.max(0.5, Math.min(4, z + delta * 0.004)));
      }
      lastPinchDist.current = dist;
      dragging.current = false;
      return;
    }
    lastPinchDist.current = null;
    if (!dragging.current) return;
    const dx = (e.clientX - lastPt.current.x) / scale;
    const dy = (e.clientY - lastPt.current.y) / scale;
    lastPt.current = { x: e.clientX, y: e.clientY };
    const newX = Math.max(0, Math.min(100, dragPosRef.current.x + dx * 0.4));
    const newY = Math.max(0, Math.min(100, dragPosRef.current.y + dy * 0.4));
    dragPosRef.current = { x: newX, y: newY };
    onChange({ x: newX, y: newY });
  };

  const onPointerUp = e => {
    activePointers.current.delete(e.pointerId);
    if (activePointers.current.size === 0) {
      dragging.current = false;
      lastPinchDist.current = null;
      setIsDragging(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {label && <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider text-center">{label}</div>}
      <div
        className={`relative overflow-hidden shadow-xl touch-none select-none ${readOnly ? 'cursor-default' : isDragging ? 'cursor-grabbing' : 'cursor-grab'} ${rounded ? 'rounded-2xl' : 'rounded-none'}`}
        style={{
          backgroundColor: src ? undefined : '#2563eb',
          minHeight: extended ? HERO_MIN_HEIGHT + BANNER_EXTEND_PX * 2 : undefined,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {src && <div style={{ position: 'absolute', inset: 0, backgroundImage: `url('${src}')`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(22px) brightness(0.55) saturate(1.4)', transform: 'scale(1.08)', transformOrigin: 'center', zIndex: 0 }} />}
        {src && <div style={{ position: 'absolute', inset: 0, backgroundImage: `url('${src}')`, backgroundSize: zoom === 1 ? 'cover' : `${Math.round(zoom * 100)}%`, backgroundPosition: `${pos.x}% ${pos.y}%`, backgroundRepeat: 'no-repeat', zIndex: 1 }} />}
        {/* Crop zone overlays — top & bottom only (sides are full-width on both desktop and mobile) */}
        {extended && (
          <>
            <div className="absolute left-0 right-0 top-0 pointer-events-none z-20" style={{ height: BANNER_EXTEND_PX, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(8px)' }} />
            <div className="absolute left-0 right-0 bottom-0 pointer-events-none z-20" style={{ height: BANNER_EXTEND_PX, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(8px)' }} />
            <div className="absolute left-0 right-0 pointer-events-none z-21" style={{ top: BANNER_EXTEND_PX, height: '1px', background: 'rgba(255,255,255,0.22)' }} />
            <div className="absolute left-0 right-0 pointer-events-none z-21" style={{ bottom: BANNER_EXTEND_PX, height: '1px', background: 'rgba(255,255,255,0.22)' }} />
          </>
        )}

        {/* Drag hint */}
        {!readOnly && (
          <div className="absolute z-10 pointer-events-none" style={{
            top: extended ? BANNER_EXTEND_PX + 12 : 12,
            left: '50%',
            transform: 'translateX(-50%)',
            opacity: isDragging ? 0 : 1,
            transition: 'opacity 180ms ease',
          }}>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{
              background: 'rgba(0,0,0,0.48)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <svg className="w-3 h-3" style={{ color: 'rgba(255,255,255,0.75)' }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 9l-3 3 3 3M19 9l3 3-3 3M9 5l3-3 3 3M9 19l3 3 3-3"/>
              </svg>
              <span className="text-[10px] font-semibold tracking-[0.04em]" style={{ color: 'rgba(255,255,255,0.8)' }}>тяни</span>
            </div>
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute pointer-events-none" style={{
          top: extended ? BANNER_EXTEND_PX : 0,
          left: 0, right: 0, bottom: extended ? BANNER_EXTEND_PX : 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.04) 38%, rgba(0,0,0,0.14) 100%)',
          zIndex: 5,
        }} />

        {/* Content */}
        <div className="relative flex flex-col items-center pt-20 pb-8 px-5 text-center pointer-events-none" style={{
          zIndex: 6,
          minHeight: HERO_MIN_HEIGHT,
          marginTop: extended ? BANNER_EXTEND_PX : 0,
        }}>
          <div className="relative mb-4">
            <div className="w-[80px] h-[80px] rounded-full overflow-hidden ring-4 ring-white/30 bg-white/20"
              style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.32)' }}>
              {avatarSrc
                ? <img src={avatarSrc} alt="" className="w-full h-full object-cover"
                    style={{ objectPosition: `${avatarPos?.x ?? 50}% ${avatarPos?.y ?? 50}%` }} />
                : <div className="w-full h-full flex items-center justify-center">
                    <span className="text-3xl text-white font-medium">{fio?.charAt(0)?.toUpperCase() || '?'}</span>
                  </div>
              }
            </div>
            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.22)' }}>
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
              </svg>
            </div>
          </div>
          <h1 className="text-[20px] font-semibold text-white leading-tight truncate" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.42)', maxWidth: '88%' }}>{fio || 'Пользователь'}</h1>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" style={{ boxShadow: '0 0 6px rgba(52,211,153,0.65)' }} />
            <span className="text-[11px] text-white/70">В сети</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// max-w-5xl (1024px) - px-6*2 (48px) = 976px — реальная ширина hero-карточки на десктопе
const DESKTOP_CARD_W = 976;
// Shared hero section height — keeps banner crop identical between preview and real profile
const HERO_MIN_HEIGHT = 210;
// Extra px shown above/below crop zone in preview modal
const BANNER_EXTEND_PX = 72;

function BannerPreviewModal({ src, pos, onChange, onClose, fio, avatarSrc, avatarPos, onSave, zoom: zoomProp = 1, onZoomChange, posMobile, onChangeMobile, zoomMobile: zoomMobileProp = 1, onZoomChangeMobile }) {
  const { isDark } = useTheme();
  const [device, setDevice] = useState('desktop');
  const deviceRef = useRef('desktop');
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [zoom, setZoom] = useState(zoomProp);
  const [zoomMob, setZoomMob] = useState(zoomMobileProp);
  const [mounted, setMounted] = useState(false);
  const [deviceKey, setDeviceKey] = useState(0);
  const [isRealMobile, setIsRealMobile] = useState(() => window.matchMedia('(max-width: 639px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = e => setIsRealMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleZoom = (fn) => {
    setZoom(prev => {
      const next = typeof fn === 'function' ? fn(prev) : fn;
      const clamped = Math.max(0.5, Math.min(4, next));
      onZoomChange?.(clamped);
      return clamped;
    });
  };

  const handleZoomMob = (fn) => {
    setZoomMob(prev => {
      const next = typeof fn === 'function' ? fn(prev) : fn;
      const clamped = Math.max(0.5, Math.min(4, next));
      onZoomChangeMobile?.(clamped);
      return clamped;
    });
  };

  const currentZoom = device === 'desktop' ? zoom : zoomMob;
  const currentHandleZoom = device === 'desktop' ? handleZoom : handleZoomMob;
  const zoomFill = ((currentZoom - 0.5) / 3.5) * 100;

  const switchDevice = (d) => {
    if (d === device) return;
    setDevice(d);
    deviceRef.current = d;
    setDeviceKey(k => k + 1);
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setMounted(true)));
    return () => { cancelAnimationFrame(id); document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const handler = deviceRef.current === 'desktop' ? handleZoom : handleZoomMob;
      handler(z => Math.max(0.5, Math.min(4, z - e.deltaY * 0.001)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    if (device !== 'desktop') return;
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      setScale(Math.min(1, w / DESKTOP_CARD_W));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [device]);



  return createPortal(
    <div
      className="fixed inset-0 z-[600] flex flex-col"
      style={{
        background: mounted ? 'rgba(3,3,10,0.94)' : 'rgba(3,3,10,0)',
        backdropFilter: 'blur(18px)',
        transition: 'background 280ms ease',
      }}
    >
      <style>{`
        @keyframes bannerModalIn {
          from { opacity: 0; transform: translateY(22px) scale(0.975); filter: blur(5px); }
          to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes deviceIn {
          from { opacity: 0; transform: translateY(10px) scale(0.97); filter: blur(3px); }
          to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes fakeCardIn {
          from { opacity: 0; transform: translateY(7px); }
          to   { opacity: 0.45; transform: translateY(0); }
        }
        .bz-slider {
          -webkit-appearance: none; appearance: none;
          background: transparent; cursor: pointer;
          position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; padding: 0;
        }
        .bz-slider:focus { outline: none; }
        .bz-slider::-webkit-slider-runnable-track { background: transparent; height: 100%; }
        .bz-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px; height: 18px; border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 8px rgba(0,0,0,0.5), 0 0 0 2.5px rgba(59,130,246,0.55);
          cursor: grab;
          transition: transform 120ms cubic-bezier(0.34,1.56,0.64,1), box-shadow 120ms ease;
          margin-top: 0;
        }
        .bz-slider:active::-webkit-slider-thumb {
          cursor: grabbing;
          transform: scale(1.22);
          box-shadow: 0 2px 14px rgba(0,0,0,0.5), 0 0 0 3.5px rgba(59,130,246,0.7);
        }
        .bz-slider::-moz-range-thumb {
          width: 18px; height: 18px; border-radius: 50%;
          background: #fff; border: none; cursor: grab;
          box-shadow: 0 1px 8px rgba(0,0,0,0.5), 0 0 0 2.5px rgba(59,130,246,0.55);
        }
        .bz-slider::-moz-range-track { background: transparent; }
      `}</style>

      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0" style={{
        background: 'radial-gradient(ellipse 55% 35% at 50% 52%, rgba(59,130,246,0.055) 0%, transparent 70%)',
        opacity: mounted ? 1 : 0,
        transition: 'opacity 700ms ease',
      }} />

      {/* All content — spring entrance */}
      <div className="flex flex-col h-full" style={mounted ? {
        animation: 'bannerModalIn 420ms cubic-bezier(0.16,1,0.3,1) both',
      } : { opacity: 0 }}>

        {/* Top bar */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 shrink-0">
          {isRealMobile ? (
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
              style={{ background: '#e5373a', boxShadow: '0 4px 16px rgba(229,55,58,0.38)' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#cf2e31'; e.currentTarget.style.boxShadow = '0 6px 22px rgba(229,55,58,0.52)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#e5373a'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(229,55,58,0.38)'; }}
            >
              <svg className="w-4 h-4" fill="none" stroke="white" strokeWidth={2.2} strokeLinecap="round" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          ) : <div className="w-10" />}

          <span className="text-[14px] font-semibold tracking-[-0.015em]" style={{ color: 'rgba(255,255,255,0.88)' }}>
            Предпросмотр
          </span>

          {isRealMobile ? (
            <button
              onClick={onSave}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
              style={{ background: '#2AABEE', boxShadow: '0 4px 16px rgba(42,171,238,0.38)' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1d97d8'; e.currentTarget.style.boxShadow = '0 6px 22px rgba(42,171,238,0.52)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#2AABEE'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(42,171,238,0.38)'; }}
            >
              <svg className="w-5 h-5" fill="none" stroke="white" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M4 12.5l5.5 5.5L20 7"/>
              </svg>
            </button>
          ) : <div className="w-10" />}
        </div>

        {/* Device switcher */}
        <div className="flex justify-center pb-4 shrink-0">
          <div className="flex p-1 gap-0.5" style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: '14px',
          }}>
            {[
              { id: 'desktop', label: 'ПК', icon: (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <rect x="2" y="3" width="20" height="14" rx="2"/><path strokeLinecap="round" d="M8 21h8M12 17v4"/>
                </svg>
              )},
              { id: 'mobile', label: 'Телефон', icon: (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <rect x="5" y="2" width="14" height="20" rx="2"/><circle cx="12" cy="18" r="1" fill="currentColor"/>
                </svg>
              )},
            ].map(d => (
              <button key={d.id} onClick={() => switchDevice(d.id)}
                className="flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold transition-all duration-150 active:scale-95"
                style={{
                  borderRadius: '10px',
                  background: device === d.id ? 'rgba(255,255,255,0.92)' : 'transparent',
                  color: device === d.id ? '#0f172a' : 'rgba(255,255,255,0.42)',
                  boxShadow: device === d.id ? '0 1px 6px rgba(0,0,0,0.28)' : 'none',
                  transition: 'background 260ms cubic-bezier(0.16,1,0.3,1), color 200ms ease, box-shadow 260ms ease',
                }}
                onMouseEnter={e => { if (device !== d.id) e.currentTarget.style.color = 'rgba(255,255,255,0.78)'; }}
                onMouseLeave={e => { if (device !== d.id) e.currentTarget.style.color = 'rgba(255,255,255,0.42)'; }}
              >
                {d.icon}{d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Zoom controls */}
        <div className="shrink-0 px-5 pb-5 flex flex-col gap-2.5 items-center">
          <div className="flex items-center gap-3 w-full max-w-xs">
            <button
              onClick={() => currentHandleZoom(z => Math.max(0.5, z - 0.1))}
              className="w-9 h-9 rounded-full flex items-center justify-center text-[20px] font-light transition-all duration-150 active:scale-85 shrink-0"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.13)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
            >−</button>

            <div className="relative flex-1 h-6 flex items-center">
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[5px] rounded-full overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.1)' }}>
                <div className="h-full rounded-full" style={{
                  width: `${zoomFill}%`,
                  background: 'linear-gradient(90deg, #93c5fd, #3b82f6)',
                  transition: 'width 55ms ease',
                }} />
              </div>
              {[1, 2, 3, 4].map(v => (
                <div key={v} className="absolute top-1/2 w-1 h-1 rounded-full pointer-events-none" style={{
                  left: `${((v - 0.5) / 3.5) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  background: currentZoom >= v - 0.05 ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.22)',
                  transition: 'background 150ms ease',
                }} />
              ))}
              <input
                type="range" min={0.5} max={4} step={0.05}
                value={currentZoom}
                onChange={e => currentHandleZoom(parseFloat(e.target.value))}
                className="bz-slider"
              />
            </div>

            <button
              onClick={() => currentHandleZoom(z => Math.min(4, z + 0.1))}
              className="w-9 h-9 rounded-full flex items-center justify-center text-[20px] font-light transition-all duration-150 active:scale-85 shrink-0"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.13)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
            >+</button>

            <button
              onClick={() => { currentHandleZoom(1); (device === 'desktop' ? onChange : (onChangeMobile || onChange))({ x: 50, y: 50 }); }}
              className="px-3 h-9 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-all duration-200 active:scale-95 shrink-0"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.32)' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.78)'; e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.32)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >×1</button>
          </div>

        </div>

        {/* Preview area */}
        <div ref={containerRef} className="flex-1 overflow-auto px-4 pb-8">
          <div key={deviceKey} style={{ animation: 'deviceIn 340ms cubic-bezier(0.16,1,0.3,1) both' }}>

            {device === 'desktop' ? (
              <div style={{ height: `${Math.round(scale * (HERO_MIN_HEIGHT + BANNER_EXTEND_PX * 2))}px`, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
                <div style={{
                  width: `${DESKTOP_CARD_W}px`,
                  flexShrink: 0,
                  transformOrigin: 'top center',
                  transform: `scale(${scale})`,
                }}>
                  <BannerPreviewCard
                    src={src} pos={pos} onChange={onChange}
                    zoom={zoom} onZoomChange={handleZoom}
                    fio={fio} avatarSrc={avatarSrc} avatarPos={avatarPos}
                    label={null} scale={scale} rounded extended
                  />
                </div>
              </div>

            ) : (
              <div className="mx-auto" style={{ maxWidth: '410px' }}>
                <div style={{
                  background: 'linear-gradient(145deg, #2e2e31 0%, #1b1b1e 100%)',
                  borderRadius: '50px',
                  padding: '10px',
                  boxShadow: [
                    '0 0 0 1px rgba(255,255,255,0.08)',
                    '0 0 0 2px rgba(0,0,0,0.55)',
                    '0 36px 80px rgba(0,0,0,0.78)',
                    'inset 0 1px 0 rgba(255,255,255,0.06)',
                  ].join(', '),
                }}>
                  <div style={{ borderRadius: '40px', overflow: 'hidden', background: isDark ? '#080808' : '#fafafa' }}>
                    <div style={{
                      background: '#000', height: '38px',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0 22px', position: 'relative',
                    }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.88)', letterSpacing: '-0.02em', fontFeatureSettings: '"tnum"' }}>9:41</span>
                      <div style={{ width: '108px', height: '30px', background: '#000', borderRadius: '20px', position: 'absolute', left: '50%', transform: 'translateX(-50%)', zIndex: 2 }} />
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', zIndex: 3 }}>
                        <svg width="14" height="11" viewBox="0 0 24 16" fill="rgba(255,255,255,0.86)"><rect x="0" y="5" width="4" height="11" rx="1.5"/><rect x="6" y="3" width="4" height="13" rx="1.5"/><rect x="12" y="1" width="4" height="15" rx="1.5"/><rect x="18" y="0" width="4" height="16" rx="1.5"/></svg>
                        <svg width="13" height="11" viewBox="0 0 24 20" fill="rgba(255,255,255,0.86)"><path d="M12 2C6.48 2 1.74 4.57 0 8.5L12 20l12-11.5C22.26 4.57 17.52 2 12 2z"/></svg>
                        <svg width="24" height="11" viewBox="0 0 35 16" fill="none"><rect x="0.5" y="0.5" width="31" height="15" rx="4.5" stroke="rgba(255,255,255,0.28)"/><rect x="2" y="2" width="27" height="12" rx="3" fill="rgba(255,255,255,0.88)"/><rect x="32" y="5" width="3" height="6" rx="1.5" fill="rgba(255,255,255,0.28)"/></svg>
                      </div>
                    </div>

                    <div style={{ background: isDark ? '#1c1c1e' : '#f2f2f7' }}>
                      <BannerPreviewCard
                        src={src} pos={posMobile || pos} onChange={onChangeMobile || onChange}
                        zoom={zoomMob} onZoomChange={handleZoomMob}
                        fio={fio} avatarSrc={avatarSrc} avatarPos={avatarPos}
                        label={null} rounded={false} extended
                      />
                      <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px', pointerEvents: 'none' }}>
                        {[40, 60, 40, 40].map((h, i) => (
                          <div key={i} style={{
                            height: `${h}px`,
                            background: isDark ? '#2c2c2e' : '#fff',
                            borderRadius: '14px',
                            opacity: 0,
                            animation: `fakeCardIn 360ms ${i * 55 + 30}ms cubic-bezier(0.16,1,0.3,1) forwards`,
                          }} />
                        ))}
                      </div>
                    </div>

                    <div style={{ background: isDark ? '#1c1c1e' : '#f2f2f7', display: 'flex', justifyContent: 'center', padding: '10px 0 14px' }}>
                      <div style={{ width: '132px', height: '5px', borderRadius: '3px', background: isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.16)' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action buttons right below banner — desktop only */}
            {!isRealMobile && <div className="flex items-center justify-center gap-6 pt-4 pb-2">
              <button
                onClick={onClose}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
                style={{ background: '#e5373a', boxShadow: '0 4px 18px rgba(229,55,58,0.42)' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#cf2e31'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(229,55,58,0.56)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#e5373a'; e.currentTarget.style.boxShadow = '0 4px 18px rgba(229,55,58,0.42)'; }}
              >
                <svg className="w-5 h-5" fill="none" stroke="white" strokeWidth={2.2} strokeLinecap="round" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
              <button
                onClick={onSave}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
                style={{ background: '#2AABEE', boxShadow: '0 4px 18px rgba(42,171,238,0.42)' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#1d97d8'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(42,171,238,0.56)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#2AABEE'; e.currentTarget.style.boxShadow = '0 4px 18px rgba(42,171,238,0.42)'; }}
              >
                <svg className="w-5 h-5" fill="none" stroke="white" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M4 12.5l5.5 5.5L20 7"/>
                </svg>
              </button>
            </div>}
          </div>
        </div>

      </div>
    </div>,
    document.body
  );
}

/* ─── EquipPreviewModal ─────────────────────────────────────────────── */
function EquipPreviewModal({ item, user, avatarSrc, avatarPos, bannerPos, onConfirm, onClose }) {
  const { isDark } = useTheme();
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const RC = item.rarity === 'chromatic'  ? { accent: isDark ? '#d946ef' : '#a21caf', pill: isDark ? '#f0abfc' : '#701a75', pillBg: isDark ? 'rgba(217,70,239,0.18)' : 'rgba(217,70,239,0.10)', label: 'Хроматический' }
           : item.rarity === 'legendary' ? { accent: isDark ? '#f59e0b' : '#d97706', pill: isDark ? '#fde68a' : '#92400e', pillBg: isDark ? 'rgba(251,191,36,0.18)' : 'rgba(251,191,36,0.12)', label: 'Легендарный' }
           : item.rarity === 'epic'      ? { accent: isDark ? '#8b5cf6' : '#7c3aed', pill: isDark ? '#c4b5fd' : '#4c1d95', pillBg: isDark ? 'rgba(139,92,246,0.18)' : 'rgba(139,92,246,0.10)', label: 'Эпический' }
           :                              { accent: isDark ? '#94a3b8' : '#64748b', pill: isDark ? '#cbd5e1' : '#334155', pillBg: isDark ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.10)', label: 'Обычный' };

  return createPortal(
    <div className="fixed inset-0 z-[600] flex flex-col items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.80)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl p-5 pb-6"
        style={{ background: isDark ? '#1c1c1e' : '#ffffff' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        {item.item_type !== 'badge' && (
          <div className="text-center mb-4">
            <div className="text-[15px] font-semibold" style={{ color: isDark ? '#fff' : '#0f172a' }}>{item.name}</div>
            <span className="inline-block mt-1 text-[10px] font-black uppercase tracking-wider px-2 py-[2px] rounded-full"
              style={{ background: RC.pillBg, color: RC.pill, border: `1px solid ${RC.accent}66` }}>
              {RC.label}
            </span>
          </div>
        )}

        {/* Preview */}
        {item.item_type === 'banner' && (
          <div className="rounded-2xl overflow-hidden mb-5" style={{ height: 150 }}>
            <BannerPreviewCard
              src={item.asset} pos={bannerPos || { x: 50, y: 50 }} onChange={() => {}}
              fio={user?.fio} avatarSrc={avatarSrc} avatarPos={avatarPos}
              rounded={false}
            />
          </div>
        )}
        {item.item_type === 'frame' && (
          <div className="flex justify-center mb-5">
            <div className="relative w-28 h-28">
              <div className="w-full h-full rounded-full overflow-hidden bg-white/10 ring-2 ring-white/20">
                {avatarSrc
                  ? <img src={avatarSrc} alt="" className="w-full h-full object-cover"
                      style={{ objectPosition: `${avatarPos?.x ?? 50}% ${avatarPos?.y ?? 50}%` }} />
                  : <div className="w-full h-full flex items-center justify-center">
                      <span className="text-3xl text-white font-medium">{user?.fio?.charAt(0)?.toUpperCase() || '?'}</span>
                    </div>
                }
              </div>
              <img src={item.asset} alt="" className="absolute pointer-events-none select-none"
                style={{ inset: 0, width: '100%', height: '100%', transform: `translateX(${item.frame_offset_x ?? 0}%) translateY(${item.frame_offset_y ?? 0}%) scale(${(item.frame_scale ?? 100) / 100})`, transformOrigin: 'center' }} />
            </div>
          </div>
        )}
        {item.item_type === 'title' && (
          <div className="flex justify-center mb-5">
            <img src={item.asset} alt={item.name} className="w-24 h-24 object-contain"
              style={{ filter: `drop-shadow(0 2px 16px ${RC.accent}88)` }} />
          </div>
        )}
        {item.item_type === 'badge' && (
          <div className="mb-5 rounded-2xl overflow-hidden" style={{ height: 200, position: 'relative' }}>
            <div
              style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)', gap: 12, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'none' }}
              onClick={e => e.preventDefault()}
              onContextMenu={e => e.preventDefault()}
              onMouseMove={e => {
                if (navigator.maxTouchPoints > 0) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left, y = e.clientY - rect.top;
                const Z = 2.5, LS = 100;
                const lens = e.currentTarget.querySelector('[data-eql]');
                if (!lens) return;
                const inner = lens.querySelector('[data-eql-dsk]');
                if (inner) { inner.style.width = rect.width + 'px'; inner.style.height = rect.height + 'px'; inner.style.transform = `translate(${LS/2 - x*Z}px,${LS/2 - y*Z}px) scale(${Z})`; }
                lens.style.opacity = '1';
                lens.style.left = Math.min(Math.max(x + 14, 0), rect.width - LS - 4) + 'px';
                lens.style.top = Math.min(Math.max(y - LS - 14, 0), rect.height - LS - 4) + 'px';
              }}
              onMouseLeave={e => { const l = e.currentTarget.querySelector('[data-eql]'); if (l) l.style.opacity = '0'; }}
              onTouchStart={e => {
                const el = e.currentTarget; const t = e.touches[0]; const rect = el.getBoundingClientRect();
                const Z = 2.5, LS = 110;
                el._lensTx = t.clientX - rect.left; el._lensTy = t.clientY - rect.top; el._lensRect = rect;
                el._lensTimer = setTimeout(() => {
                  const tx = el._lensTx, ty = el._lensTy, r = el._lensRect;
                  const lens = el.querySelector('[data-eql-mob]'); if (!lens) return;
                  const inner = lens.querySelector('[data-eql-inner]');
                  if (inner) { inner.style.width = r.width + 'px'; inner.style.height = r.height + 'px'; inner.style.transform = `translate(${LS/2 - tx*Z}px,${LS/2 - ty*Z}px) scale(${Z})`; }
                  if (navigator.vibrate) navigator.vibrate(10);
                  lens.style.display = 'block';
                  lens.style.transform = 'scale(0.6)'; lens.style.opacity = '0';
                  requestAnimationFrame(() => { lens.style.transform = 'scale(1)'; lens.style.opacity = '1'; });
                  lens.style.left = Math.min(Math.max(tx - LS/2, 4), r.width - LS - 4) + 'px';
                  lens.style.top = Math.max(ty - LS - 24, 4) + 'px';
                }, 280);
              }}
              onTouchMove={e => {
                const el = e.currentTarget; const t = e.touches[0]; const rect = el.getBoundingClientRect();
                const Z = 2.5, LS = 110, tx = t.clientX - rect.left, ty = t.clientY - rect.top;
                el._lensTx = tx; el._lensTy = ty;
                const lens = el.querySelector('[data-eql-mob]');
                if (!lens || lens.style.display !== 'block') return;
                const inner = lens.querySelector('[data-eql-inner]');
                if (inner) inner.style.transform = `translate(${LS/2 - tx*Z}px,${LS/2 - ty*Z}px) scale(${Z})`;
                lens.style.left = Math.min(Math.max(tx - LS/2, 4), rect.width - LS - 4) + 'px';
                lens.style.top = Math.max(ty - LS - 24, 4) + 'px';
              }}
              onTouchEnd={e => {
                const el = e.currentTarget; clearTimeout(el._lensTimer);
                const l = el.querySelector('[data-eql-mob]');
                if (!l || l.style.display !== 'block') return;
                l.style.transform = 'scale(0.6)'; l.style.opacity = '0';
                setTimeout(() => { l.style.display = 'none'; }, 150);
              }}
            >
              <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', flexShrink: 0 }}>
                {avatarSrc ? <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${avatarPos?.x ?? 50}% ${avatarPos?.y ?? 50}%` }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{user?.fio || 'Имя Фамилия'}</span>
                <img src={item.asset} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
              </div>
              {/* Desktop loupe */}
              <div data-eql="" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.15s', width: 100, height: 100, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 4px 24px rgba(0,0,0,0.7)', overflow: 'hidden', zIndex: 20 }}>
                <div data-eql-dsk="" style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)', gap: 12 }}>
                  <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', flexShrink: 0 }}>
                    {avatarSrc ? <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${avatarPos?.x ?? 50}% ${avatarPos?.y ?? 50}%` }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{user?.fio || 'Имя Фамилия'}</span>
                    <img src={item.asset} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                  </div>
                </div>
              </div>
              {/* Mobile loupe */}
              <div data-eql-mob="" style={{ display: 'none', position: 'absolute', zIndex: 30, pointerEvents: 'none', width: 110, height: 110, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.4)', boxShadow: '0 8px 32px rgba(0,0,0,0.8)', overflow: 'hidden', transition: 'transform 0.18s cubic-bezier(0.34,1.56,0.64,1), opacity 0.15s' }}>
                <div data-eql-inner="" style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)', gap: 12 }}>
                  <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', flexShrink: 0 }}>
                    {avatarSrc ? <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${avatarPos?.x ?? 50}% ${avatarPos?.y ?? 50}%` }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{user?.fio || 'Имя Фамилия'}</span>
                    <img src={item.asset} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {item.item_type === 'badge' && (
          <div className="text-center mb-4">
            <div className="text-[15px] font-semibold" style={{ color: isDark ? '#fff' : '#0f172a' }}>{item.name}</div>
            {item.description && (
              <div className="mt-1 text-[12px]" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>{item.description}</div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3">
          <button type="button" onClick={onClose}
            className="flex-1 py-3 rounded-2xl font-semibold text-[14px]"
            style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>
            Отмена
          </button>
          <button type="button" onClick={onConfirm}
            className="flex-1 py-3 rounded-2xl text-white font-semibold text-[14px] active:scale-95 transition-transform"
            style={{ background: `linear-gradient(135deg, ${RC.accent}cc, ${RC.accent})` }}>
            Надеть
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─── RotationCountdown ─────────────────────────────────────────────── */
function RotationClock({ rotationUntil, isDark, onExpired }) {
  const [diff, setDiff] = useState(0);
  const [hover, setHover] = useState(false);
  const expiredFiredRef = useRef(false);

  useEffect(() => {
    if (!rotationUntil) return;
    expiredFiredRef.current = false;
    const tick = () => {
      const now = Date.now();
      const until = new Date(rotationUntil + 'Z').getTime();
      const d = Math.max(0, until - now);
      setDiff(d);
      if (d === 0 && !expiredFiredRef.current) {
        expiredFiredRef.current = true;
        setTimeout(() => onExpired?.(), 1500);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [rotationUntil]);

  if (!rotationUntil) return null;

  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const urgent = diff < 3600000;
  const critical = diff < 60000;

  const accent = critical
    ? (isDark ? '#f87171' : '#dc2626')
    : urgent
      ? (isDark ? '#fb923c' : '#ea580c')
      : (isDark ? '#a78bfa' : '#7c3aed');

  const pad = (n) => String(n).padStart(2, '0');
  const timeStr = diff <= 0
    ? '—'
    : hover
      ? h > 0
        ? `${h}:${pad(m)}:${pad(s)}`
        : m > 0
          ? `${m}:${pad(s)}`
          : `${s}с`
      : h > 0
        ? `${h}:${pad(m)}`
        : m > 0
          ? `${m}:${pad(s)}`
          : `${s}`;

  return (
    <span
      className="shrink-0 font-mono font-bold tabular-nums select-none inline-flex items-baseline"
      title="До следующей ротации"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        color: accent,
        fontSize: 14,
        letterSpacing: '0.02em',
        textShadow: urgent ? `0 0 10px ${accent}66` : undefined,
        animation: critical ? 'rotCountPulse 1s ease-in-out infinite' : undefined,
        cursor: 'default',
        transition: 'color 220ms, text-shadow 220ms',
      }}
    >
      <style>{`
        @keyframes rotCountPulse { 0%,100%{opacity:1} 50%{opacity:0.55} }
        @keyframes rotSecFade { from{opacity:0;transform:translateX(-2px)} to{opacity:1;transform:translateX(0)} }
      `}</style>
      {diff > 0 && h > 0 ? (
        <>
          <span>{`${h}:${pad(m)}`}</span>
          {hover && (
            <span style={{ animation: 'rotSecFade 180ms ease both', opacity: 0.75 }}>
              {`:${pad(s)}`}
            </span>
          )}
        </>
      ) : (
        timeStr
      )}
    </span>
  );
}

/* ─── ShopFilterMorph ──────────────────────────────────────────────── */
function ShopFilterMorph({
  isDark,
  search, onSearch,
  rarity, onRarity,
  type, onType,
  showType,
  rarityOptions,
  typeOptions,
  newOnly, onNewOnly,
  compact,
  align = 'right',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const activeCount =
    (search && search.trim() ? 1 : 0) +
    (rarity ? 1 : 0) +
    (showType && type && type !== 'all' ? 1 : 0) +
    (newOnly ? 1 : 0);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => inputRef.current?.focus(), 140);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);

  const clearAll = () => {
    onSearch('');
    onRarity(null);
    if (showType) onType('all');
    if (onNewOnly) onNewOnly(false);
  };

  const btnSize = compact ? 28 : 36;
  const iconSize = compact ? 14 : 16;

  return (
    <div ref={rootRef} className={`relative shrink-0 ${compact ? 'mb-1.5' : ''}`} style={{ zIndex: 50 }}>
      <style>{`
        @keyframes sfm-pop { from{opacity:0;transform:scale(0.82) translateY(-6px);filter:blur(6px)} to{opacity:1;transform:scale(1) translateY(0);filter:blur(0)} }
        @keyframes sfm-row { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-label="Поиск и фильтры"
        className="relative rounded-full inline-flex items-center justify-center active:scale-90"
        style={{
          width: btnSize, height: btnSize,
          background: activeCount > 0
            ? (isDark ? 'rgba(96,165,250,0.14)' : 'rgba(59,130,246,0.08)')
            : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
          border: `1px solid ${activeCount > 0
            ? (isDark ? 'rgba(96,165,250,0.4)' : 'rgba(59,130,246,0.3)')
            : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')}`,
          color: activeCount > 0
            ? (isDark ? '#93c5fd' : '#1d4ed8')
            : (isDark ? 'rgba(255,255,255,0.75)' : '#475569'),
          transition: 'background 220ms, color 220ms, border-color 220ms, transform 180ms',
          opacity: open ? 0 : 1,
          pointerEvents: open ? 'none' : 'auto',
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
          <circle cx="11" cy="11" r="7"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        {activeCount > 0 && (
          <span className="absolute rounded-full"
            style={{
              top: -2, right: -2,
              width: 10, height: 10,
              background: '#60a5fa',
              boxShadow: '0 0 8px rgba(96,165,250,0.9)',
              border: `2px solid ${isDark ? '#0f0f14' : '#fff'}`,
            }} />
        )}
      </button>

      {open && (
        <div
          className={`absolute top-0 ${align === 'left' ? 'left-0 origin-top-left' : 'right-0 origin-top-right'}`}
          style={{
            animation: 'sfm-pop 260ms cubic-bezier(0.34, 1.3, 0.64, 1) both',
            width: 300,
          }}
        >
          <div
            className="rounded-2xl p-2.5 flex flex-col gap-2.5"
            style={{
              background: isDark ? 'rgba(20,20,32,0.96)' : 'rgba(255,255,255,0.98)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              boxShadow: isDark
                ? '0 20px 48px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset'
                : '0 20px 48px -12px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.6) inset',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
            }}
          >
            <div className="relative">
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
                style={{ color: isDark ? 'rgba(147,197,253,0.9)' : '#1d4ed8' }}
                fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24" strokeLinecap="round"
              >
                <circle cx="11" cy="11" r="7"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                ref={inputRef}
                type="text"
                placeholder="Поиск…"
                value={search}
                onChange={e => onSearch(e.target.value)}
                className="w-full pl-8 pr-16 py-1.5 rounded-lg text-[12px] font-medium outline-none"
                style={{
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                  color: isDark ? '#fff' : '#111827',
                  caretColor: isDark ? '#fff' : '#111827',
                }}
              />
              <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                {search && (
                  <button
                    type="button" onClick={() => onSearch('')} aria-label="Очистить"
                    className="w-5 h-5 rounded-md inline-flex items-center justify-center"
                    style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)' }}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                      <path d="M6 6l12 12M18 6L6 18"/>
                    </svg>
                  </button>
                )}
                <button
                  type="button" onClick={() => setOpen(false)} aria-label="Закрыть"
                  className="w-5 h-5 rounded-md inline-flex items-center justify-center"
                  style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.35)' }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                    <path d="M6 6l12 12M18 6L6 18"/>
                  </svg>
                </button>
              </div>
            </div>

            <div style={{ animation: 'sfm-row 220ms ease 80ms both' }}>
              <div className="text-[9px] font-bold uppercase tracking-widest mb-1.5"
                style={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.4)' }}>
                Редкость
              </div>
              <div className="flex flex-wrap gap-1">
                {rarityOptions.map(opt => {
                  const active = rarity === opt.value;
                  const accent = opt.color;
                  return (
                    <button
                      key={opt.value || '_all'}
                      type="button"
                      onClick={() => onRarity(active ? null : opt.value)}
                      className="px-2 h-6 rounded-full text-[10.5px] font-semibold inline-flex items-center transition-all active:scale-95"
                      style={{
                        background: active
                          ? (accent ? `${accent}22` : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'))
                          : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.035)'),
                        border: `1px solid ${active
                          ? (accent ? `${accent}66` : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'))
                          : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)')}`,
                        color: active
                          ? (accent || (isDark ? '#fff' : '#111827'))
                          : (isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.6)'),
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {onNewOnly && (
              <div style={{ animation: 'sfm-row 220ms ease 110ms both' }}>
                <div className="text-[9px] font-bold uppercase tracking-widest mb-1.5"
                  style={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.4)' }}>
                  Ярлыки
                </div>
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => onNewOnly(!newOnly)}
                    className="px-2 h-6 rounded-full text-[10.5px] font-semibold inline-flex items-center gap-1.5 transition-all active:scale-95"
                    style={{
                      background: newOnly
                        ? 'rgba(16,185,129,0.18)'
                        : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.035)'),
                      border: `1px solid ${newOnly
                        ? 'rgba(16,185,129,0.5)'
                        : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)')}`,
                      color: newOnly
                        ? (isDark ? '#6ee7b7' : '#047857')
                        : (isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.6)'),
                    }}
                  >
                    <img src="/novinka.svg" alt="" style={{ width: 14, height: 14, filter: newOnly ? 'none' : 'grayscale(0.7) opacity(0.6)' }} />
                    Новинки
                  </button>
                </div>
              </div>
            )}

            {showType && (
              <div style={{ animation: 'sfm-row 220ms ease 140ms both' }}>
                <div className="text-[9px] font-bold uppercase tracking-widest mb-1.5"
                  style={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.4)' }}>
                  Тип
                </div>
                <div className="flex flex-wrap gap-1">
                  {typeOptions.map(opt => {
                    const active = type === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => onType(opt.value)}
                        className="px-2 h-6 rounded-full text-[10.5px] font-semibold inline-flex items-center transition-all active:scale-95"
                        style={{
                          background: active
                            ? (isDark ? '#fff' : '#0f172a')
                            : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.035)'),
                          color: active
                            ? (isDark ? '#0f172a' : '#fff')
                            : (isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.6)'),
                          border: active ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {activeCount > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="self-start text-[10.5px] font-semibold inline-flex items-center gap-1 opacity-85 hover:opacity-100 transition-opacity"
                style={{ color: isDark ? '#93c5fd' : '#1d4ed8' }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8"/>
                  <path d="M3 3v5h5"/>
                </svg>
                Сбросить
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── ShopItemPreviewModal ──────────────────────────────────────────── */
function ShopItemPreviewModal({ item, user, avatarSrc, avatarPos, balance, buying, onBuy, onLike, onClose, onGoToCollection }) {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const goToAuthor = (uid) => { onClose?.(); navigate(`/profile/${uid}`); };
  const [magnifyMode, setMagnifyMode] = useState(false);
  const [magnifyPos, setMagnifyPos] = useState({ x: 50, y: 50 });
  const [isHoveringImg, setIsHoveringImg] = useState(false);
  const [isHoveringZone, setIsHoveringZone] = useState(false);
  const [buyHovered, setBuyHovered] = useState(false);
  const [previewMode, setPreviewMode] = useState('phone');
  const [mobilePage, setMobilePage] = useState(0);
  // Shared banner heights — чтобы main-превью и loupe клоны использовали одну и ту же шкалу
  const [sharedPcBannerH, setSharedPcBannerH] = useState(140);
  const [sharedPhoneBannerH, setSharedPhoneBannerH] = useState(115);
  const sharedPcBannerHRef = useRef(140);
  const sharedPhoneBannerHRef = useRef(115);
  // Реальный aspect-ratio экрана юзера (portrait, w/h < 1).
  // Берём screen.width/height — не меняется от orientation на большинстве браузеров,
  // но страхуемся Math.min/max чтобы всегда получить портретный коэффициент.
  const realPhoneAspect = useMemo(() => {
    if (typeof window === 'undefined' || !window.screen) return 9 / 19.5;
    const w = window.screen.width, h = window.screen.height;
    if (!w || !h) return 9 / 19.5;
    return Math.min(w, h) / Math.max(w, h);
  }, []);
  const imgContainerRef = useRef(null);
  const isTouchDevice = useRef(false);
  const mobileSwipeX = useRef(null);
  const mobileSwipeY = useRef(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const wasBuying = useRef(false);

  useEffect(() => {
    if (wasBuying.current && !buying) {
      setShowSuccess(true);
      const t = setTimeout(onClose, 2400);
      return () => clearTimeout(t);
    }
    wasBuying.current = buying;
  }, [buying]);

  const getTouchPos = (e) => {
    const rect = imgContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const touch = e.touches[0] || e.changedTouches[0];
    setMagnifyPos({
      x: Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((touch.clientY - rect.top) / rect.height) * 100)),
    });
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const RC = item.rarity === 'chromatic'  ? { accent: isDark ? '#d946ef' : '#a21caf', pill: isDark ? '#f0abfc' : '#701a75', pillBg: isDark ? 'rgba(217,70,239,0.18)' : 'rgba(217,70,239,0.10)', label: 'Хроматический' }
           : item.rarity === 'legendary' ? { accent: isDark ? '#f59e0b' : '#d97706', pill: isDark ? '#fde68a' : '#92400e', pillBg: isDark ? 'rgba(251,191,36,0.18)' : 'rgba(251,191,36,0.12)', label: 'Легендарный' }
           : item.rarity === 'epic'      ? { accent: isDark ? '#8b5cf6' : '#7c3aed', pill: isDark ? '#c4b5fd' : '#4c1d95', pillBg: isDark ? 'rgba(139,92,246,0.18)' : 'rgba(139,92,246,0.10)', label: 'Эпический' }
           :                              { accent: isDark ? '#94a3b8' : '#64748b', pill: isDark ? '#cbd5e1' : '#334155', pillBg: isDark ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.10)', label: 'Обычный' };
  const TYPE_LABEL = { frame: 'Рамка', badge: 'Бейджик', title: 'Титул', banner: 'Баннер' };
  const canAfford = (balance ?? 0) >= item.price;
  const isImg = item.asset?.startsWith('/');

  const [downloading, setDownloading] = useState(false);
  const [downloadDone, setDownloadDone] = useState(false);
  const [ripple, setRipple] = useState(0);

  const handleDevDownload = async () => {
    if (!item?.asset || downloading) return;
    setRipple(k => k + 1);
    setDownloading(true);
    try {
      const r = await fetch(item.asset);
      if (!r.ok) throw new Error('fetch failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const extMatch = item.asset.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
      const ext = extMatch ? extMatch[1] : (blob.type.split('/')[1] || 'bin');
      const base = (item.name || `item-${item.id}`).replace(/[^\p{L}\p{N}._-]+/gu, '_').slice(0, 80);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${base}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      setDownloadDone(true);
      setTimeout(() => setDownloadDone(false), 1800);
    } catch {
      try { window.open(item.asset, '_blank', 'noopener'); } catch {}
    } finally {
      setDownloading(false);
    }
  };

  // envRef — route all closure vars to STABLE child components below.
  const envRef = useRef({});
  envRef.current = { isDark, RC, item, user, isImg, avatarSrc, avatarPos, sharedPcBannerH, sharedPhoneBannerH, sharedPcBannerHRef, sharedPhoneBannerHRef, setSharedPcBannerH, setSharedPhoneBannerH };

  const [AvatarPreview] = useState(() => function AvatarPreview({ size = 56, showOnline = true, ringW = 2 }) {
    const { item, user, avatarSrc, avatarPos, isDark } = envRef.current;
    const dotSize = Math.max(6, Math.round(size * 0.18));
    return (
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <div className="w-full h-full rounded-full overflow-hidden bg-white/10"
          style={{ outline: `${ringW}px solid rgba(255,255,255,0.22)`, outlineOffset: 1 }}>
          {avatarSrc
            ? <img src={avatarSrc} alt="" className="w-full h-full object-cover"
                style={{ objectPosition: `${avatarPos?.x ?? 50}% ${avatarPos?.y ?? 50}%` }} />
            : <div className="w-full h-full flex items-center justify-center text-white font-medium"
                style={{ fontSize: size * 0.35 }}>
                {user?.fio?.charAt(0)?.toUpperCase() || '?'}
              </div>
          }
        </div>
        {item.item_type === 'frame' && (
          <img src={item.asset} alt="" className="absolute inset-0 pointer-events-none select-none"
            style={{ width: '100%', height: '100%', transform: `translateX(${item.frame_offset_x ?? 0}%) translateY(${item.frame_offset_y ?? 0}%) scale(${(item.frame_scale ?? 100) / 100})`, transformOrigin: 'center' }} />
        )}
        {showOnline && (
          <span className="absolute rounded-full bg-emerald-400"
            style={{ width: dotSize, height: dotSize, right: 1, bottom: 1, boxShadow: `0 0 0 2px ${isDark ? '#18181c' : '#f1f5f9'}` }} />
        )}
      </div>
    );
  });

  const [ProfileRows] = useState(() => function ProfileRows({ compact = false, scale = 1 } = {}) {
    const { isDark } = envRef.current;
    const rows = [
      { label: 'Почта',       w: '85%', bg: 'rgba(16,185,129,0.18)',  ring: 'rgba(52,211,153,0.4)' },
      { label: 'Роль',        w: '55%', bg: 'rgba(139,92,246,0.18)',  ring: 'rgba(167,139,250,0.4)' },
      { label: 'Моя группа',  w: '70%', bg: 'rgba(251,146,60,0.18)',  ring: 'rgba(253,186,116,0.4)' },
      { label: 'Коллекция',   w: '48%', bg: 'rgba(59,130,246,0.18)',  ring: 'rgba(96,165,250,0.4)' },
      { label: 'Скрепки',     w: '38%', bg: 'rgba(236,72,153,0.18)',  ring: 'rgba(244,114,182,0.4)' },
    ];
    const s = Math.max(0.55, Math.min(scale, 1.3));
    const tileSize = compact ? 20 : Math.max(16, Math.round(26 * s));
    const labelFs  = compact ? 8  : Math.max(7,  Math.round(9  * s));
    const barH     = compact ? 7  : Math.max(6,  Math.round(9  * s));
    const padXnum  = compact ? 8  : Math.max(8,  Math.round(12 * s));
    const padYnum  = compact ? 6  : Math.max(6,  Math.round(8  * s));
    const gapNum   = Math.max(4, Math.round(6 * s));
    const outerPadBottom = Math.max(6, Math.round(8 * s));
    return (
      <div className="flex-1 flex flex-col justify-evenly min-h-0 overflow-hidden"
        style={{ padding: `0 ${padXnum}px ${outerPadBottom}px`, gap: gapNum }}>
        {rows.map(({ label, w, bg, ring }) => (
          <div key={label} className="flex items-center rounded-lg"
            style={{
              gap: Math.max(5, Math.round(8 * s)),
              padding: `${padYnum}px ${padXnum}px`,
              background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)',
            }}>
            <div className="rounded-md shrink-0" style={{ width: tileSize, height: tileSize, background: bg, border: `1px solid ${ring}` }} />
            <div className="flex flex-col flex-1 min-w-0" style={{ gap: Math.max(2, Math.round(4 * s)) }}>
              <span className="font-black uppercase tracking-wider leading-none" style={{ fontSize: labelFs, color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.4)' }}>{label}</span>
              <div className="rounded" style={{ height: barH, width: w, background: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.12)' }} />
            </div>
          </div>
        ))}
      </div>
    );
  });

  const [PcPreview] = useState(() => function PcPreview({ observe = true, bannerAspect = 2.8, topPadMul = 0.58, topPadMin = 28 } = {}) {
    const { isDark, RC, item, user, isImg, sharedPcBannerH, setSharedPcBannerH, sharedPcBannerHRef } = envRef.current;
    // Пропорции real-PC профиля: banner ~ 400 px при container ~ 960, avatar 96, fio 24.
    const bannerRef = useRef(null);
    const pcBannerH = sharedPcBannerH;
    useLayoutEffect(() => {
      if (!observe) return;
      if (!bannerRef.current) return;
      const rafId = requestAnimationFrame(() => {
        if (!bannerRef.current) return;
        const h = bannerRef.current.getBoundingClientRect().height;
        if (h > 20 && Math.abs(h - sharedPcBannerHRef.current) >= 2) {
          sharedPcBannerHRef.current = h;
          setSharedPcBannerH(h);
        }
      });
      return () => cancelAnimationFrame(rafId);
    }, [observe]);
    const avatarSize = Math.max(44, Math.round(pcBannerH * 0.42));
    const fioFs      = Math.max(12, Math.round(pcBannerH * 0.11));
    const badgePx    = Math.max(14, Math.round(pcBannerH * 0.15));
    const topPad     = Math.max(topPadMin, Math.round(pcBannerH * topPadMul));
    const isBanner = item.item_type === 'banner';
    return (
      <div className="relative w-full h-full flex flex-col items-center overflow-hidden pt-4 px-2" style={{
        background: isDark
          ? 'radial-gradient(circle at 50% 0%, #16161e 0%, #0b0b11 70%)'
          : 'radial-gradient(circle at 50% 0%, #e2e8f0 0%, #cbd5e1 70%)',
      }}>
        {/* Фоновые орнаменты (мимика главного градиентного pattern) */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: isDark
            ? 'radial-gradient(circle at 15% 20%, rgba(139,92,246,0.08) 0%, transparent 25%), radial-gradient(circle at 85% 70%, rgba(16,185,129,0.07) 0%, transparent 30%)'
            : 'radial-gradient(circle at 15% 20%, rgba(139,92,246,0.06) 0%, transparent 25%), radial-gradient(circle at 85% 70%, rgba(16,185,129,0.05) 0%, transparent 30%)',
        }} />
        {/* Центрированная колонка профиля — расширено для mod-превью */}
        <div className="relative w-full flex flex-col rounded-xl overflow-hidden" style={{ maxWidth: '92%', background: isDark ? '#18181c' : '#f8fafc', border: `1px solid ${RC.accent}22`, boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 24px rgba(15,23,42,0.12)' }}>
          <div ref={bannerRef} className="relative flex flex-col items-center pb-3 overflow-hidden" style={{ aspectRatio: `${bannerAspect} / 1` }}>
            <div className="absolute inset-0" style={isBanner && isImg
              ? { backgroundImage: `url('${item.asset}')`, backgroundSize: 'cover', backgroundPosition: '50% 50%' }
              : { background: isDark ? 'linear-gradient(135deg,#1a1a2e,#16213e,#0f3460)' : `linear-gradient(135deg,${RC.accent}18,${RC.accent}10,${RC.accent}06)` }} />
            {isBanner && (
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 60%)' }} />
            )}
            <div className="absolute inset-0 flex flex-col items-center z-10" style={{ paddingTop: topPad }}>
              <AvatarPreview size={avatarSize} ringW={3} />
              <div className="flex items-center gap-2 max-w-[85%]" style={{ marginTop: Math.round(pcBannerH * 0.05) }}>
                <p className="font-semibold drop-shadow truncate" style={{ color: isBanner ? '#fff' : (isDark ? '#f1f5f9' : '#0f172a'), letterSpacing: '-0.3px', fontSize: fioFs }}>{user?.fio || 'Пользователь'}</p>
                {(item.item_type === 'badge' || item.item_type === 'title') && isImg && (
                  <img src={item.asset} alt="" className="shrink-0 object-contain"
                    style={{ width: badgePx, height: badgePx, filter: `drop-shadow(0 0 5px ${RC.accent}aa)`, transform: `scale(${(item.frame_scale ?? 100) / 100})`, transformOrigin: 'center' }} />
                )}
              </div>
            </div>
          </div>
          <ProfileRows scale={pcBannerH / 140} />
        </div>
      </div>
    );
  });

  const [PhonePreview] = useState(() => function PhonePreview({ wrapStyle = {}, observe = true, topPadMul = 0.15, topPadMin = 6 } = {}) {
    const { isDark, RC, item, user, isImg, sharedPhoneBannerH, setSharedPhoneBannerH, sharedPhoneBannerHRef } = envRef.current;
    const bannerRef = useRef(null);
    const bannerH = sharedPhoneBannerH;
    useLayoutEffect(() => {
      if (!observe) return;
      if (!bannerRef.current) return;
      const rafId = requestAnimationFrame(() => {
        if (!bannerRef.current) return;
        const h = bannerRef.current.getBoundingClientRect().height;
        if (h > 20 && Math.abs(h - sharedPhoneBannerHRef.current) >= 2) {
          sharedPhoneBannerHRef.current = h;
          setSharedPhoneBannerH(h);
        }
      });
      return () => cancelAnimationFrame(rafId);
    }, [observe]);
    // Телефонный превью короче реального hero — ставим аватар выше (0.22),
    // чтобы под ним помещались FIO + ряды профиля без обрезки.
    const avatarSize = Math.max(22, Math.round(bannerH * 0.306));
    const fioFs      = Math.max(7,  Math.round(bannerH * 0.0808));
    const badgePx    = Math.max(10, Math.round(bannerH * 0.133));
    const topPad     = Math.max(topPadMin, Math.round(bannerH * topPadMul));
    const fioMarginT = Math.max(4,  Math.round(bannerH * 0.076));
    return (
    <div className="rounded-2xl overflow-hidden mx-auto flex flex-col" style={{ width: 160, background: isDark ? '#141418' : '#f1f5f9', border: `1px solid ${RC.accent}22`, ...wrapStyle }}>
      {/* Шапка */}
      <div ref={bannerRef} className="relative overflow-hidden flex flex-col items-center pb-3" style={{ minHeight: 115 }}>
        {item.item_type === 'banner' && isImg
          ? <div className="absolute inset-0" style={{ backgroundImage: `url('${item.asset}')`, backgroundSize: 'cover', backgroundPosition: '50% 50%' }} />
          : <div className="absolute inset-0" style={{ background: isDark ? 'linear-gradient(135deg,#1a1a2e,#16213e,#0f3460)' : `linear-gradient(135deg,${RC.accent}18,${RC.accent}10,${RC.accent}06)` }} />
        }
        <div className="absolute inset-0" style={{ background: isDark ? 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.05) 60%)' : 'none' }} />
        <div className="absolute inset-0 flex flex-col items-center z-10" style={{ paddingTop: topPad }}>
          <AvatarPreview size={avatarSize} ringW={2} />
          <div className="flex items-center gap-1 max-w-[90%]" style={{ marginTop: fioMarginT }}>
            <p className="font-semibold drop-shadow truncate" style={{ color: item.item_type === 'banner' ? '#fff' : (isDark ? '#f1f5f9' : '#0f172a'), letterSpacing: '-0.1px', fontSize: fioFs }}>{user?.fio || 'Пользователь'}</p>
            {(item.item_type === 'badge' || item.item_type === 'title') && isImg && (
              <img src={item.asset} alt="" className="shrink-0 object-contain"
                style={{ width: badgePx, height: badgePx, filter: `drop-shadow(0 0 3px ${RC.accent}aa)`, transform: `scale(${(item.frame_scale ?? 100) / 100})`, transformOrigin: 'center' }} />
            )}
          </div>
        </div>
      </div>

      {/* Строки */}
      <div className="flex flex-col px-1.5 py-1 gap-px">
        {[
          { w: '65%' }, { w: '40%' }, { w: '55%' }, { w: '50%' }, { w: '60%' },
        ].map(({ w }, i) => (
          <div key={i} className="flex items-center gap-1.5 px-1.5 py-1.5 rounded-lg" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
            <div className="w-4 h-4 rounded-md shrink-0" style={{ background: `${RC.accent}33` }} />
            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
              <div className="h-1.5 rounded" style={{ width: '35%', background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' }} />
              <div className="h-2 rounded" style={{ width: w, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)' }} />
            </div>
          </div>
        ))}
      </div>

    </div>
    );
  });

  return createPortal(
    <>
      {/* Backdrop: bottom-sheet на мобилке, центрировано на sm+ */}
      <div className="fixed inset-0 z-[600] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(16px)' }}
        onClick={onClose}>

        {/* Контейнер */}
        <div className="relative w-full rounded-3xl overflow-y-auto sm:overflow-hidden flex flex-col sm:flex-row"
          style={{ background: isDark ? '#18181c' : '#ffffff', maxWidth: 820, maxHeight: '92svh' }}
          onClick={e => e.stopPropagation()}>



          {/* Оверлей успешной покупки */}
          {showSuccess && (
            <>
              <style>{`
                @keyframes audBackdrop {
                  from { opacity: 0; backdrop-filter: blur(0px); }
                  to   { opacity: 1; backdrop-filter: blur(12px); }
                }
                @keyframes audCircle {
                  0%   { opacity: 0; transform: scale(0.3); filter: blur(8px); }
                  60%  { opacity: 1; transform: scale(1.15); filter: blur(0px); }
                  80%  { transform: scale(0.93); }
                  100% { transform: scale(1); }
                }
                @keyframes audCheck {
                  from { stroke-dashoffset: 30; opacity: 0; }
                  to   { stroke-dashoffset: 0;  opacity: 1; }
                }
                @keyframes audTitle {
                  from { opacity: 0; transform: translateY(14px); filter: blur(4px); }
                  to   { opacity: 1; transform: translateY(0);    filter: blur(0px); }
                }
                @keyframes audSub {
                  from { opacity: 0; transform: translateY(10px); }
                  to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes audGlow {
                  0%, 100% { box-shadow: 0 0 24px ${RC.accent}55, 0 0 8px ${RC.accent}33; }
                  50%      { box-shadow: 0 0 64px ${RC.accent}99, 0 0 28px ${RC.accent}66; }
                }
                @keyframes audRipple {
                  0%   { transform: scale(1);   opacity: 0.5; }
                  100% { transform: scale(2.4); opacity: 0; }
                }
              `}</style>
              <div className="absolute inset-0 z-[200] flex flex-col items-center justify-center rounded-3xl"
                style={{ background: 'rgba(0,0,0,0.82)', animation: 'audBackdrop 0.35s cubic-bezier(0.16,1,0.3,1) both' }}>
                <div className="flex flex-col items-center gap-4">
                  {/* Circle + ripple */}
                  <div className="relative flex items-center justify-center"
                    style={{ animation: 'audCircle 0.55s cubic-bezier(0.34,1.56,0.64,1) both' }}>
                    {/* ripple ring */}
                    <div className="absolute rounded-full"
                      style={{
                        width: 80, height: 80,
                        border: `2px solid ${RC.accent}`,
                        animation: 'audRipple 1s 0.5s cubic-bezier(0.16,1,0.3,1) both',
                      }} />
                    <div className="w-20 h-20 rounded-full flex items-center justify-center"
                      style={{
                        background: `${RC.accent}18`,
                        border: `2.5px solid ${RC.accent}`,
                        animation: 'audGlow 1.6s 0.6s ease-in-out infinite',
                      }}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={RC.accent} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 13l4 4L19 7"
                          strokeDasharray="30"
                          style={{ animation: 'audCheck 0.4s 0.2s cubic-bezier(0.16,1,0.3,1) both' }} />
                      </svg>
                    </div>
                  </div>
                  {/* Title */}
                  <p className="font-bold text-2xl text-white"
                    style={{ animation: 'audTitle 0.45s 0.3s cubic-bezier(0.16,1,0.3,1) both' }}>
                    Куплено!
                  </p>
                  {/* Item name */}
                  <p className="text-sm font-medium"
                    style={{ color: RC.pill, animation: 'audSub 0.4s 0.45s cubic-bezier(0.16,1,0.3,1) both' }}>
                    {item.name}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* ЛЕВАЯ КОЛОНКА — товар */}
          <div className="relative flex flex-col overflow-hidden sm:flex-1" style={{ background: isDark ? '#0f0f15' : '#e8edf3', height: 'clamp(230px, 62vw, 380px)' }}>

            {/* === МОБИЛЬНЫЙ КАРУСЕЛЬ === */}
            <div className="sm:hidden absolute inset-0 z-10 overflow-hidden rounded-3xl"
              onTouchStart={e => { mobileSwipeX.current = e.touches[0].clientX; mobileSwipeY.current = e.touches[0].clientY; }}
              onTouchEnd={e => {
                if (mobileSwipeX.current === null) return;
                const dx = e.changedTouches[0].clientX - mobileSwipeX.current;
                const dy = e.changedTouches[0].clientY - mobileSwipeY.current;
                if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
                  if (dx < 0 && mobilePage < 2) { setMobilePage(p => p + 1); setMagnifyMode(false); }
                  else if (dx > 0 && mobilePage > 0) setMobilePage(p => p - 1);
                }
                mobileSwipeX.current = null;
              }}>

              {/* 3 слайда рядом */}
              <div className="flex h-full" style={{ width: '300%', transform: `translateX(${-mobilePage * 100 / 3}%)`, transition: 'transform 0.32s cubic-bezier(0.22,1,0.36,1)' }}>

                {/* Слайд 0: изображение товара */}
                <div className="relative overflow-hidden" style={{ width: '33.333%', flexShrink: 0 }}>
                  {item.item_type === 'banner' && isImg
                    ? <div className="absolute inset-0" style={{ backgroundImage: `url('${item.asset}')`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                    : item.item_type === 'frame'
                      ? <div className="absolute inset-0 flex items-center justify-center" style={{ background: isDark ? 'radial-gradient(ellipse at center,#1e1e2e 0%,#0f0f15 70%)' : 'radial-gradient(ellipse at center,#dde3eb 0%,#e8edf3 70%)' }}>
                          <div style={{ filter: `drop-shadow(0 0 20px ${RC.accent}55)` }}><AvatarPreview size={80} /></div>
                        </div>
                      : isImg
                        ? <div className="absolute inset-0 flex items-center justify-center p-6">
                            <img src={item.asset} alt="" className="max-h-full max-w-full object-contain" />
                          </div>
                        : <div className="absolute inset-0 flex items-center justify-center text-6xl" style={{ color: RC.accent }}>
                            {({ badge: '🏅', title: '📛', banner: '🎨' })[item.item_type] || '🎨'}
                          </div>
                  }
                  <div className="absolute inset-0 pointer-events-none z-30 rounded-3xl" style={{ border: `2px solid ${RC.accent}` }} />
                </div>

                {/* Слайд 1: телефон */}
                <div className="relative flex flex-col items-center justify-start p-3" style={{ width: '33.333%', flexShrink: 0, background: isDark ? '#0f0f15' : '#e8edf3', paddingTop: '5%' }}>
                  {/* Рамка телефона */}
                  <div className="relative mx-auto" style={{ width: '55%', transform: 'scale(1.35)', transformOrigin: 'top center' }}>
                    {/* Боковые кнопки (левая сторона) */}
                    <div className="absolute -left-[3px] flex flex-col gap-[5px]" style={{ top: '18%' }}>
                      <div style={{ width: 3, height: 10, borderRadius: '2px 0 0 2px', background: isDark ? '#3a3a42' : '#c8cdd4' }} />
                      <div style={{ width: 3, height: 16, borderRadius: '2px 0 0 2px', background: isDark ? '#3a3a42' : '#c8cdd4' }} />
                      <div style={{ width: 3, height: 16, borderRadius: '2px 0 0 2px', background: isDark ? '#3a3a42' : '#c8cdd4' }} />
                    </div>
                    {/* Кнопка питания (правая сторона) */}
                    <div className="absolute -right-[3px]" style={{ top: '22%', width: 3, height: 22, borderRadius: '0 2px 2px 0', background: isDark ? '#3a3a42' : '#c8cdd4' }} />
                    {/* Корпус */}
                    <div style={{
                      borderRadius: 18,
                      padding: 4,
                      background: isDark ? 'linear-gradient(145deg,#2a2a32,#1a1a22)' : 'linear-gradient(145deg,#d0d5de,#b8bdc8)',
                      boxShadow: isDark
                        ? `0 0 0 1px #0a0a0f, 0 8px 32px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)`
                        : `0 0 0 1px #8890a0, 0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.6)`,
                    }}>
                      {/* Экран с Dynamic Island — пропорции реального экрана пользователя */}
                      <div style={{ borderRadius: 14, overflow: 'hidden', position: 'relative', aspectRatio: String(realPhoneAspect) }}>
                        {/* Dynamic Island */}
                        <div className="absolute left-0 right-0 flex justify-center z-10" style={{ top: 5 }}>
                          <div style={{ width: 36, height: 8, borderRadius: 6, background: '#000' }} />
                        </div>
                        <PhonePreview wrapStyle={{ width: '100%', height: '100%', maxWidth: 'none', borderRadius: 0, border: 'none' }} />
                        {/* Home indicator */}
                        <div className="absolute bottom-1 left-0 right-0 flex justify-center">
                          <div style={{ width: 36, height: 3, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 pointer-events-none z-30 rounded-3xl" style={{ border: `1px solid ${RC.accent}33` }} />
                </div>

                {/* Слайд 2: ПК */}
                <div className="relative flex flex-col items-center justify-center" style={{ width: '33.333%', flexShrink: 0, background: isDark ? '#0f0f15' : '#e8edf3' }}>
                  <div className="flex flex-col items-center w-full" style={{ maxWidth: '90%', zoom: 0.62 }}>
                    {/* ── Monitor body ── */}
                    <div className="w-full" style={{
                      borderRadius: 10,
                      background: isDark
                        ? 'linear-gradient(170deg,#35353f 0%,#22222a 50%,#1a1a22 100%)'
                        : 'linear-gradient(170deg,#d6dae3 0%,#bcc1cc 50%,#a8adb8 100%)',
                      boxShadow: isDark
                        ? '0 0 0 1.5px #0c0c10, 0 12px 40px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.3)'
                        : '0 0 0 1.5px #8890a0, 0 8px 28px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.1)',
                      padding: '8px 6px 0 6px',
                    }}>
                      {/* Top bar with camera */}
                      <div className="flex justify-center items-center" style={{ height: 16, marginBottom: 3 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: isDark ? '#2a2a38' : '#8a9099', boxShadow: isDark ? 'inset 0 1px 3px rgba(0,0,0,0.9), 0 0 0 1px rgba(0,0,0,0.4)' : 'inset 0 1px 2px rgba(0,0,0,0.35)' }} />
                      </div>
                      {/* Screen area — 16:10 монитор, баннер шире (aspect 2.6) чтобы аватар+ФИО были видны на мобиле */}
                      <div style={{ borderRadius: '5px 5px 0 0', overflow: 'hidden', background: '#000', aspectRatio: '16 / 10', width: '100%' }}>
                        <PcPreview bannerAspect={2.6} />
                      </div>
                    </div>
                    {/* ── Chin (bottom bezel) ── */}
                    <div className="w-full flex items-center justify-between px-3" style={{
                      height: 16,
                      borderRadius: '0 0 8px 8px',
                      background: isDark
                        ? 'linear-gradient(to bottom,#1e1e26,#16161e)'
                        : 'linear-gradient(to bottom,#b0b6c2,#a0a6b2)',
                      boxShadow: isDark ? '0 2px 0 #0a0a10' : '0 2px 0 #8890a0',
                    }}>
                      {/* Left: brand dots */}
                      <div className="flex gap-1">
                        <div style={{ width: 3, height: 3, borderRadius: '50%', background: isDark ? '#2e2e3a' : '#888fa0' }} />
                      </div>
                      {/* Right: power LED */}
                      <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e88' }} />
                    </div>
                    {/* ── Neck ── */}
                    <div style={{ position: 'relative', width: '100%', height: 20, display: 'flex', justifyContent: 'center' }}>
                      <svg width="60" height="20" viewBox="0 0 60 20" fill="none" style={{ overflow: 'visible' }}>
                        <path d="M30 0 C30 0, 22 8, 18 20 L42 20 C38 8, 30 0, 30 0Z"
                          fill={isDark ? '#1e1e26' : '#b0b6c2'} />
                        <path d="M30 0 C30 0, 24 8, 21 20"
                          stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.4)'} strokeWidth="1" fill="none" />
                      </svg>
                    </div>
                    {/* ── Base ── */}
                    <div style={{
                      width: '72%',
                      height: 8,
                      borderRadius: '50% 50% 4px 4px / 80% 80% 4px 4px',
                      background: isDark
                        ? 'linear-gradient(to bottom,#28282e,#1a1a20)'
                        : 'linear-gradient(to bottom,#bcc2cc,#a8aeb8)',
                      boxShadow: isDark
                        ? '0 3px 12px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)'
                        : '0 3px 10px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.5)',
                    }} />
                    {/* Shadow under base */}
                    <div style={{ width: '55%', height: 4, borderRadius: '50%', background: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.1)', marginTop: 1, filter: 'blur(3px)' }} />
                  </div>
                  <div className="absolute inset-0 pointer-events-none z-30 rounded-3xl" style={{ border: `1px solid ${RC.accent}33` }} />
                </div>
              </div>

              {/* Точки-индикаторы */}
              <div className="absolute bottom-2.5 left-0 right-0 flex justify-center gap-1.5 z-50 pointer-events-none">
                {[0,1,2].map(i => (
                  <div key={i} style={{ height: 4, width: mobilePage === i ? 20 : 4, borderRadius: 2, background: mobilePage === i ? RC.accent : 'rgba(255,255,255,0.35)', transition: 'all 0.2s ease' }} />
                ))}
              </div>
            </div>

            {/* === ДЕСКТОП: зона изображения === */}
            <div className="shop-cover-zone group/cover hidden sm:flex flex-1 relative overflow-hidden min-h-0 rounded-3xl"
              style={{ cursor: magnifyMode && item.item_type !== 'banner' ? 'zoom-in' : 'default' }}
              onMouseMove={e => {
                if (item.item_type === 'banner') return;
                if (!magnifyMode) return;
                const rect = imgContainerRef.current?.getBoundingClientRect();
                if (!rect) return;
                setMagnifyPos({
                  x: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
                  y: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)),
                });
              }}
              onMouseEnter={() => { if (magnifyMode && item.item_type !== 'banner') setIsHoveringImg(true); }}
              onMouseLeave={() => { if (isHoveringImg) setIsHoveringImg(false); }}
              onTouchStart={e => { if (item.item_type === 'banner') return; isTouchDevice.current = true; getTouchPos(e); if (!magnifyMode) setMagnifyMode(true); }}
              onTouchMove={e => { if (item.item_type === 'banner') return; e.preventDefault(); getTouchPos(e); }}
              onTouchEnd={() => {}}>

              {item.item_type === 'banner' && isImg ? (
                /* Баннер — full-bleed background */
                <div className="absolute inset-0">
                  <div className="absolute inset-0"
                    style={{ backgroundImage: `url('${item.asset}')`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
                  <div className="absolute inset-0 pointer-events-none"
                    style={{ background: `linear-gradient(to bottom, transparent 35%, rgba(15,15,21,0.6) 100%), radial-gradient(ellipse at 85% 10%, ${RC.accent}14 0%, transparent 55%)` }} />
                  <div />
                </div>
              ) : item.item_type === 'frame' ? (
                /* Рамка — показываем аватар с применённой рамкой */
                <div className="absolute inset-4 flex items-center justify-center"
                  style={{ background: isDark ? 'radial-gradient(ellipse at center, #1e1e2e 0%, #0f0f15 70%)' : 'radial-gradient(ellipse at center, #dde3eb 0%, #e8edf3 70%)', borderRadius: 12 }}>
                  <div style={{ filter: `drop-shadow(0 0 20px ${RC.accent}55)` }}>
                    <AvatarPreview size={80} />
                  </div>
                </div>
              ) : (
                /* Бейджи / прочее — центровано */
                <>
                  <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
                    <div ref={imgContainerRef} className="relative rounded-2xl overflow-hidden transition-shadow duration-300"
                      style={{ background: item.item_type === 'badge' ? 'transparent' : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.7)'),
                        border: item.item_type === 'badge' ? 'none' : `2px solid ${RC.accent}55`,
                        boxShadow: item.item_type === 'badge' ? `drop-shadow(0 2px 24px ${RC.accent}66)` : (magnifyMode ? `0 0 40px ${RC.accent}55, 0 0 0 1px ${RC.accent}22` : `0 0 18px ${RC.accent}30`) }}>
                      {isImg
                        ? <img src={item.asset} alt="" loading="lazy" className="block"
                            style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain', display: 'block',
                              transform: magnifyMode && (isHoveringImg || isTouchDevice.current) ? (isHoveringImg ? 'scale(3)' : 'scale(2.5)') : 'scale(1)',
                              transformOrigin: `${magnifyPos.x}% ${magnifyPos.y}%`,
                              transition: (magnifyMode && isHoveringImg)
                                ? 'transform 0.12s ease-out'
                                : 'transform 0.3s cubic-bezier(0.22,1,0.36,1)' }} />
                        : <div className="w-40 h-40 flex items-center justify-center text-6xl"
                            style={{ color: RC.accent }}>
                            {({ badge: '🏅', title: '📛', banner: '🎨' })[item.item_type] || '🎨'}
                          </div>
                      }
                      {isImg && item.item_type !== 'badge' && (
                        <button type="button"
                          onClick={e => { e.stopPropagation(); setMagnifyMode(m => !m); setIsHoveringImg(false); }}
                          className="absolute bottom-2 right-2 w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-90"
                          style={{ background: magnifyMode ? RC.accent : `${RC.accent}22`,
                            border: `1px solid ${RC.accent}55`,
                            color: magnifyMode ? '#000' : RC.accent,
                            boxShadow: magnifyMode ? `0 0 16px ${RC.accent}66` : 'none',
                            backdropFilter: 'blur(6px)' }}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="m21 21-4.35-4.35"/>
                            <path strokeLinecap="round" d="M11 8v6M8 11h6"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 pointer-events-none"
                    style={{ height: '35%', background: isDark ? 'linear-gradient(to top, #0f0f15, transparent)' : 'linear-gradient(to top, #e8edf3, transparent)' }} />
                </>
              )}
            <div className="absolute inset-0 pointer-events-none z-30 rounded-3xl"
              style={{ border: `2px solid ${RC.accent}` }} />
            <div className="absolute top-3 left-0 right-0 flex justify-center pointer-events-none z-40 transition-all duration-200 opacity-0 -translate-y-1.5 group-hover/cover:opacity-100 group-hover/cover:translate-y-0">
              <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full"
                style={{ background: RC.pillBg, color: RC.pill, border: `1px solid ${RC.accent}55`, backdropFilter: 'blur(10px)', boxShadow: `0 2px 10px ${RC.accent}33` }}>
                {TYPE_LABEL[item.item_type] || item.item_type}
              </span>
            </div>
            {item.author_user_id ? (
              <button type="button" onClick={e => { e.stopPropagation(); goToAuthor(item.author_user_id); }}
                className="absolute bottom-3 left-3 z-40 group flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full cursor-pointer transition-transform hover:scale-105"
                style={{ background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(8px)', border: '1px solid rgba(250,204,21,0.35)' }}>
                {item.author_user_avatar
                  ? <img src={resolveAvatarSrc(item.author_user_avatar)} alt="" style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover' }} />
                  : <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'linear-gradient(135deg,#facc15,#f59e0b)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 9 }}>{(item.author_user_fio || '?')[0].toUpperCase()}</div>}
                <span style={{ fontSize: 9, color: '#facc15', fontWeight: 600, lineHeight: 1 }}>{item.author_user_fio || 'автор'}</span>
                <div className="absolute bottom-full left-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap px-2.5 py-1.5 rounded-lg"
                  style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.12)', fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 400 }}>
                  Автор идеи: <span style={{ color: '#fff', fontWeight: 600 }}>{item.author_user_fio}</span>
                </div>
              </button>
            ) : item.author_name ? (
              <div className="absolute bottom-3 left-3 z-40 group flex items-center gap-1 px-2 py-1 rounded-full cursor-default"
                style={{ background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(8px)', border: '1px solid rgba(250,204,21,0.28)' }}>
                <svg className="w-2.5 h-2.5 shrink-0" fill="none" stroke="#facc15" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.74V17h8v-2.26A7 7 0 0012 2z"/></svg>
                <span style={{ fontSize: 9, color: '#facc15', fontWeight: 600, lineHeight: 1 }}>{item.author_name}</span>
                <div className="absolute bottom-full left-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap px-2.5 py-1.5 rounded-lg"
                  style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.12)', fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 400 }}>
                  Автор идеи: <span style={{ color: '#fff', fontWeight: 600 }}>{item.author_name}</span>
                </div>
              </div>
            ) : item.uploader_name && (
              <div className="absolute bottom-3 left-3 z-40 group flex items-center gap-1 px-2 py-1 rounded-full cursor-default"
                style={{ background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <svg className="w-2.5 h-2.5 shrink-0" fill="none" stroke="rgba(255,255,255,0.55)" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="8" r="4"/><path strokeLinecap="round" d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.55)', fontWeight: 500, lineHeight: 1 }}>{item.uploader_name}</span>
                <div className="absolute bottom-full left-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap px-2.5 py-1.5 rounded-lg"
                  style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.12)', fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 400 }}>
                  Загружен пользователем: <span style={{ color: '#fff', fontWeight: 600 }}>{item.uploader_name}</span>
                </div>
              </div>
            )}
            <div className="absolute bottom-3 right-3 z-40 flex items-center gap-2">
              <span className="flex items-center gap-1 px-2 py-1 rounded-full"
                style={{ background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.08)', fontSize: 9, color: 'rgba(255,255,255,0.55)', fontWeight: 500, lineHeight: 1 }}>
                <svg className="w-2.5 h-2.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                {item.views ?? 0}
              </span>
              <button type="button" onClick={onLike}
                className="flex items-center gap-1 px-2 py-1 rounded-full transition-all active:scale-90"
                style={{ background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(8px)', border: `1px solid ${item.user_liked ? 'rgba(244,63,94,0.4)' : 'rgba(255,255,255,0.08)'}`, fontSize: 9, color: item.user_liked ? '#f43f5e' : 'rgba(255,255,255,0.55)', fontWeight: 500, lineHeight: 1, cursor: 'pointer' }}>
                <svg className="w-2.5 h-2.5 shrink-0" fill={item.user_liked ? '#f43f5e' : 'none'} stroke={item.user_liked ? '#f43f5e' : 'rgba(255,255,255,0.55)'} viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z"/></svg>
                {item.likes ?? 0}
              </button>
            </div>
            </div>

            {/* Полоска с именем и типом — только десктоп */}
            <div className="hidden sm:flex flex-col items-center gap-1.5 text-center shrink-0 px-5 pt-3 pb-4"
              style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`, background: isDark ? '#18181c' : '#ffffff' }}>
              <p className="font-semibold text-[15px] leading-tight" style={{ color: isDark ? '#fff' : '#0f172a' }}>{item.name}</p>
              {item.description && (
                <p className="text-[11px] leading-snug max-w-[240px] line-clamp-2" style={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.45)' }}>{item.description}</p>
              )}
            </div>

          </div>

          {/* ПРАВАЯ КОЛОНКА — превью + кнопка */}
          <div className="flex flex-col gap-3 px-4 pt-2 pb-5 sm:px-3 sm:pb-5 sm:pt-2 sm:w-[280px] shrink-0">

            {/* Переключатель ПК / телефон */}
            <div className="hidden sm:flex items-center gap-1 rounded-xl p-1" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)'}` }}>
              {[
                { mode: 'pc', label: 'ПК', icon: (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <rect x="2" y="3" width="20" height="14" rx="2"/>
                    <path strokeLinecap="round" d="M8 21h8M12 17v4"/>
                  </svg>
                )},
                { mode: 'phone', label: 'Телефон', icon: (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <rect x="5" y="2" width="14" height="20" rx="2"/>
                    <circle cx="12" cy="18" r="1" fill="currentColor" stroke="none"/>
                  </svg>
                )},
              ].map(({ mode, label, icon }) => (
                <button key={mode} type="button"
                  onClick={() => setPreviewMode(mode)}
                  title={label}
                  className="flex-1 flex items-center justify-center py-1.5 rounded-lg transition-all duration-150"
                  style={{
                    background: previewMode === mode ? RC.accent + '22' : 'transparent',
                    color: previewMode === mode ? RC.pill : (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.45)'),
                    border: previewMode === mode ? `1px solid ${RC.accent}44` : '1px solid transparent',
                    boxShadow: previewMode === mode ? `0 0 10px ${RC.accent}22` : 'none',
                  }}>
                  {icon}
                </button>
              ))}
            </div>

            {/* Превью — только десктоп */}
            <div className="relative hidden sm:flex-1 sm:flex sm:flex-col min-h-0 overflow-hidden">
              <div className="absolute inset-0 flex flex-col" style={{ visibility: previewMode === 'pc' ? 'visible' : 'hidden', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: previewMode === 'pc' ? 'none' : 'auto', cursor: previewMode === 'pc' ? 'crosshair' : 'default' }}
                onMouseMove={e => {
                  if (navigator.maxTouchPoints > 0) return;
                  const wrap = e.currentTarget.querySelector('[data-spc-wrap]');
                  if (!wrap) return;
                  const rect = wrap.getBoundingClientRect();
                  const x = e.clientX - rect.left, y = e.clientY - rect.top;
                  if (x < 0 || y < 0 || x > rect.width || y > rect.height) { const l = wrap.querySelector('[data-spc-lens]'); if (l) l.style.opacity = '0'; return; }
                  const Z = 2.5, LS = 100;
                  const lens = wrap.querySelector('[data-spc-lens]');
                  if (!lens) return;
                  const dsk = lens.querySelector('[data-spc-dsk]');
                  if (dsk) { dsk.style.width = rect.width + 'px'; dsk.style.height = rect.height + 'px'; dsk.style.transform = `translate(${LS/2 - x*Z}px,${LS/2 - y*Z}px) scale(${Z})`; }
                  lens.style.opacity = '1';
                  lens.style.left = Math.min(Math.max(x + 14, 0), rect.width - LS - 4) + 'px';
                  lens.style.top = Math.min(Math.max(y - LS - 14, 0), rect.height - LS - 4) + 'px';
                }}
                onMouseLeave={e => { const w = e.currentTarget.querySelector('[data-spc-wrap]'); const l = w?.querySelector('[data-spc-lens]'); if (l) l.style.opacity = '0'; }}
                onTouchStart={e => {
                  const el = e.currentTarget; const t = e.touches[0];
                  const wrap = el.querySelector('[data-spc-wrap]'); if (!wrap) return;
                  const rect = wrap.getBoundingClientRect();
                  const Z = 2.5, LS = 110;
                  el._spcTx = t.clientX - rect.left; el._spcTy = t.clientY - rect.top; el._spcRect = rect;
                  el._spcTimer = setTimeout(() => {
                    const tx = el._spcTx, ty = el._spcTy, r = el._spcRect;
                    const lens = el.querySelector('[data-spc-mob]'); if (!lens) return;
                    const dinr = lens.querySelector('[data-spc-inner]');
                    if (dinr) { dinr.style.width = r.width + 'px'; dinr.style.height = r.height + 'px'; dinr.style.transform = `translate(${LS/2 - tx*Z}px,${LS/2 - ty*Z}px) scale(${Z})`; }
                    if (navigator.vibrate) navigator.vibrate(10);
                    lens.style.display = 'block';
                    lens.style.transform = 'scale(0.6)'; lens.style.opacity = '0';
                    requestAnimationFrame(() => { lens.style.transform = 'scale(1)'; lens.style.opacity = '1'; });
                    lens.style.left = Math.min(Math.max(tx - LS/2, 4), r.width - LS - 4) + 'px';
                    lens.style.top = Math.max(ty - LS - 24, 4) + 'px';
                  }, 280);
                }}
                onTouchMove={e => {
                  const el = e.currentTarget; const t = e.touches[0];
                  const wrap = el.querySelector('[data-spc-wrap]'); if (!wrap) return;
                  const rect = wrap.getBoundingClientRect();
                  const Z = 2.5, LS = 110, tx = t.clientX - rect.left, ty = t.clientY - rect.top;
                  el._spcTx = tx; el._spcTy = ty;
                  const lens = el.querySelector('[data-spc-mob]');
                  if (!lens || lens.style.display !== 'block') return;
                  const dinr = lens.querySelector('[data-spc-inner]');
                  if (dinr) dinr.style.transform = `translate(${LS/2 - tx*Z}px,${LS/2 - ty*Z}px) scale(${Z})`;
                  lens.style.left = Math.min(Math.max(tx - LS/2, 4), rect.width - LS - 4) + 'px';
                  lens.style.top = Math.max(ty - LS - 24, 4) + 'px';
                }}
                onTouchEnd={e => {
                  const el = e.currentTarget; clearTimeout(el._spcTimer);
                  const l = el.querySelector('[data-spc-mob]');
                  if (!l || l.style.display !== 'block') return;
                  l.style.transform = 'scale(0.6)'; l.style.opacity = '0';
                  setTimeout(() => { l.style.display = 'none'; }, 150);
                }}>
                <div data-spc-wrap="" style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <PcPreview bannerAspect={2.4} topPadMul={0.17} topPadMin={10} />
                  {/* Desktop loupe */}
                  <div data-spc-lens="" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.15s', width: 100, height: 100, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 4px 24px rgba(0,0,0,0.7)', overflow: 'hidden', zIndex: 50, top: 0, left: 0 }}>
                    <div data-spc-dsk="" style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0', display: 'flex', flexDirection: 'column', width: '100%', flex: 1 }}>
                      <PcPreview observe={false} bannerAspect={2.4} topPadMul={0.17} topPadMin={10} />
                    </div>
                  </div>
                  {/* Mobile loupe */}
                  <div data-spc-mob="" style={{ display: 'none', position: 'absolute', zIndex: 50, pointerEvents: 'none', width: 110, height: 110, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.4)', boxShadow: '0 8px 32px rgba(0,0,0,0.8)', overflow: 'hidden', transition: 'transform 0.18s cubic-bezier(0.34,1.56,0.64,1), opacity 0.15s', top: 0, left: 0 }}>
                    <div data-spc-inner="" style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0', display: 'flex', flexDirection: 'column', width: '100%', flex: 1 }}>
                      <PcPreview observe={false} bannerAspect={2.4} topPadMul={0.17} topPadMin={10} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center" style={{ visibility: previewMode === 'phone' ? 'visible' : 'hidden', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: previewMode === 'phone' ? 'none' : 'auto', cursor: previewMode === 'phone' ? 'crosshair' : 'default' }}
                onMouseMove={e => {
                  if (navigator.maxTouchPoints > 0) return;
                  const inner = e.currentTarget.querySelector('[data-spm-wrap]');
                  if (!inner) return;
                  const rect = inner.getBoundingClientRect();
                  const x = e.clientX - rect.left, y = e.clientY - rect.top;
                  if (x < 0 || y < 0 || x > rect.width || y > rect.height) { const l = inner.querySelector('[data-spm-lens]'); if (l) l.style.opacity = '0'; return; }
                  const Z = 2.5, LS = 100;
                  const lens = inner.querySelector('[data-spm-lens]');
                  if (!lens) return;
                  const dsk = lens.querySelector('[data-spm-dsk]');
                  if (dsk) { dsk.style.width = rect.width + 'px'; dsk.style.height = rect.height + 'px'; dsk.style.transform = `translate(${LS/2 - x*Z}px,${LS/2 - y*Z}px) scale(${Z})`; }
                  lens.style.opacity = '1';
                  lens.style.left = (inner.offsetLeft + Math.min(Math.max(x + 14, 0), rect.width - LS - 4)) + 'px';
                  lens.style.top = (inner.offsetTop + Math.min(Math.max(y - LS - 14, 0), rect.height - LS - 4)) + 'px';
                }}
                onMouseLeave={e => { const l = e.currentTarget.querySelector('[data-spm-lens]'); if (l) l.style.opacity = '0'; }}
                onTouchStart={e => {
                  const el = e.currentTarget; const t = e.touches[0];
                  const inner = el.querySelector('[data-spm-wrap]'); if (!inner) return;
                  const rect = inner.getBoundingClientRect();
                  const Z = 2.5, LS = 110;
                  el._spmTx = t.clientX - rect.left; el._spmTy = t.clientY - rect.top; el._spmRect = rect; el._spmInner = inner;
                  el._spmTimer = setTimeout(() => {
                    const tx = el._spmTx, ty = el._spmTy, r = el._spmRect, wr = el._spmInner;
                    const lens = el.querySelector('[data-spm-mob]'); if (!lens) return;
                    const dinr = lens.querySelector('[data-spm-inner]');
                    if (dinr) { dinr.style.width = r.width + 'px'; dinr.style.height = r.height + 'px'; dinr.style.transform = `translate(${LS/2 - tx*Z}px,${LS/2 - ty*Z}px) scale(${Z})`; }
                    if (navigator.vibrate) navigator.vibrate(10);
                    lens.style.display = 'block';
                    lens.style.transform = 'scale(0.6)'; lens.style.opacity = '0';
                    requestAnimationFrame(() => { lens.style.transform = 'scale(1)'; lens.style.opacity = '1'; });
                    const wrapRect = wr.getBoundingClientRect();
                    const containerRect = el.getBoundingClientRect();
                    const relX = wrapRect.left - containerRect.left + tx;
                    const relY = wrapRect.top - containerRect.top + ty;
                    lens.style.left = Math.min(Math.max(relX - LS/2, 4), containerRect.width - LS - 4) + 'px';
                    lens.style.top = Math.max(relY - LS - 24, 4) + 'px';
                  }, 280);
                }}
                onTouchMove={e => {
                  const el = e.currentTarget; const t = e.touches[0];
                  const inner = el.querySelector('[data-spm-wrap]'); if (!inner) return;
                  const rect = inner.getBoundingClientRect();
                  const Z = 2.5, LS = 110, tx = t.clientX - rect.left, ty = t.clientY - rect.top;
                  el._spmTx = tx; el._spmTy = ty;
                  const lens = el.querySelector('[data-spm-mob]');
                  if (!lens || lens.style.display !== 'block') return;
                  const dinr = lens.querySelector('[data-spm-inner]');
                  if (dinr) dinr.style.transform = `translate(${LS/2 - tx*Z}px,${LS/2 - ty*Z}px) scale(${Z})`;
                  const wrapRect = inner.getBoundingClientRect();
                  const containerRect = el.getBoundingClientRect();
                  const relX = wrapRect.left - containerRect.left + tx;
                  const relY = wrapRect.top - containerRect.top + ty;
                  lens.style.left = Math.min(Math.max(relX - LS/2, 4), containerRect.width - LS - 4) + 'px';
                  lens.style.top = Math.max(relY - LS - 24, 4) + 'px';
                }}
                onTouchEnd={e => {
                  const el = e.currentTarget; clearTimeout(el._spmTimer);
                  const l = el.querySelector('[data-spm-mob]');
                  if (!l || l.style.display !== 'block') return;
                  l.style.transform = 'scale(0.6)'; l.style.opacity = '0';
                  setTimeout(() => { l.style.display = 'none'; }, 150);
                }}
>
                <div data-spm-wrap="" style={{ position: 'relative' }}>
                  <PhonePreview topPadMul={0.33} topPadMin={12} />
                  {/* Desktop loupe */}
                  <div data-spm-lens="" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.15s', width: 100, height: 100, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 4px 24px rgba(0,0,0,0.7)', overflow: 'hidden', zIndex: 50, top: 0, left: 0 }}>
                    <div data-spm-dsk="" style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0' }}>
                      <PhonePreview observe={false} topPadMul={0.33} topPadMin={12} />
                    </div>
                  </div>
                  {/* Mobile loupe — positioned relative to outer container via JS */}
                </div>
                <div data-spm-mob="" style={{ display: 'none', position: 'absolute', zIndex: 50, pointerEvents: 'none', width: 110, height: 110, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.4)', boxShadow: '0 8px 32px rgba(0,0,0,0.8)', overflow: 'hidden', transition: 'transform 0.18s cubic-bezier(0.34,1.56,0.64,1), opacity 0.15s' }}>
                  <div data-spm-inner="" style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0' }}>
                    <PhonePreview observe={false} topPadMul={0.33} topPadMin={12} />
                  </div>
                </div>
              </div>
            </div>

            {/* Мобильная сводка: редкость + описание — теперь в оверлее изображения */}
            <div className="hidden flex-col gap-1 pb-1">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: RC.accent, boxShadow: `0 0 6px ${RC.accent}88` }} />
                <span className="text-[11px] font-semibold" style={{ color: RC.pill }}>{RC.label}</span>
                <span className="ml-auto text-[10px] uppercase tracking-widest font-bold" style={{ color: 'rgba(255,255,255,0.25)' }}>
                  {TYPE_LABEL[item.item_type] || item.item_type}
                </span>
              </div>
              {item.description && (
                <p className="text-white/40 text-[11px] leading-snug line-clamp-2">{item.description}</p>
              )}
            </div>

            {/* Название + описание — только мобилка */}
            <div className="sm:hidden flex flex-col items-center gap-1 pt-1 pb-0.5 text-center">
              <p className="font-bold text-[17px] leading-tight" style={{ color: isDark ? '#fff' : '#0f172a' }}>{item.name}</p>
              {item.description && <p className="text-[11px] leading-snug" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>{item.description}</p>}
              {item.author_user_id ? (
                <button type="button" onClick={e => { e.stopPropagation(); goToAuthor(item.author_user_id); }}
                  className="flex items-center justify-center gap-1 leading-none mt-0.5 cursor-pointer active:scale-95 transition-transform"
                  style={{ fontSize: 10, color: '#facc15', fontWeight: 600, background: 'none', border: 'none', padding: 0 }}>
                  {item.author_user_avatar
                    ? <img src={resolveAvatarSrc(item.author_user_avatar)} alt="" style={{ width: 14, height: 14, borderRadius: '50%', objectFit: 'cover' }} />
                    : <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="8" r="4"/><path strokeLinecap="round" d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>}
                  идея: {item.author_user_fio}
                </button>
              ) : item.author_name ? (
                <p className="flex items-center justify-center gap-1 leading-none mt-0.5" style={{ fontSize: 10, color: '#facc15', fontWeight: 600 }}>
                  <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.74V17h8v-2.26A7 7 0 0012 2z"/></svg>
                  идея: {item.author_name}
                </p>
              ) : item.uploader_name && (
                <p className="flex items-center justify-center gap-1 leading-none mt-0.5" style={{ fontSize: 10, color: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.32)' }}>
                  <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="8" r="4"/><path strokeLinecap="round" d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                  {item.uploader_name}
                </p>
              )}
              {user?.role === 'admin' && item.created_at && (() => {
                const dt = new Date(item.created_at + 'Z');
                const isBackfill = dt.getUTCFullYear() < 2010;
                return (
                  <p className="flex items-center justify-center gap-1 leading-none mt-1"
                     style={{ fontSize: 10, color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.40)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                     title="Виден только администраторам">
                    <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2"/><path strokeLinecap="round" d="M3 10h18M8 2v4M16 2v4"/></svg>
                    {isBackfill
                      ? 'дата загрузки неизвестна'
                      : `добавлен ${dt.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                  </p>
                );
              })()}
              <div className="flex items-center justify-center gap-4 mt-1">
                <span className="flex items-center gap-1" style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.30)' : 'rgba(0,0,0,0.35)' }}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                  {item.views ?? 0}
                </span>
                <button type="button" onClick={onLike}
                  className="flex items-center gap-1 transition-all active:scale-90"
                  style={{ fontSize: 11, color: item.user_liked ? '#f43f5e' : (isDark ? 'rgba(255,255,255,0.30)' : 'rgba(0,0,0,0.35)'), background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                  <svg className="w-3.5 h-3.5" fill={item.user_liked ? '#f43f5e' : 'none'} stroke={item.user_liked ? '#f43f5e' : 'currentColor'} viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z"/></svg>
                  {item.likes ?? 0}
                </button>
              </div>
            </div>

            {/* Кнопка цены */}
            <div className="mt-auto pt-1 flex items-stretch gap-2">
              <style>{`@keyframes devDlPop{0%{transform:scale(0) rotate(-12deg);opacity:0}55%{transform:scale(1.3) rotate(0);opacity:1}100%{transform:scale(1);opacity:1}}@keyframes devDlRing{0%{transform:scale(.6);opacity:.9}100%{transform:scale(1.9);opacity:0}}`}</style>
              <div className="flex-1 min-w-0">
              {item.in_rotation === false ? (
                <div className="w-full py-4 sm:py-2.5 rounded-2xl sm:rounded-xl flex items-center justify-center"
                  style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
                  <span className="font-semibold text-[14px] sm:text-[13px]" style={{ color: isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.28)' }}>Не доступно</span>
                </div>
              ) : item.owned ? (
                <button type="button" onClick={onGoToCollection}
                  className="w-full rounded-2xl sm:rounded-xl py-4 sm:py-2.5 font-semibold text-[14px] sm:text-[12px] flex items-center justify-center gap-1.5 transition-all hover:brightness-125 active:scale-[0.97]"
                  style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#6ee7b7' }}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/>
                  </svg>
                  В коллекции
                </button>
              ) : (
                <button type="button"
                  disabled={buying}
                  onClick={() => { if (!canAfford) return; onBuy(); }}
                  onMouseEnter={() => setBuyHovered(true)}
                  onMouseLeave={() => setBuyHovered(false)}
                  className="w-full py-4 sm:py-2.5 rounded-2xl sm:rounded-xl font-bold text-[16px] sm:text-[14px] transition-all duration-200 active:scale-[0.97] flex items-center justify-center overflow-hidden"
                  style={{
                    background: buyHovered
                      ? canAfford
                        ? `linear-gradient(135deg, ${RC.accent}, ${RC.accent}cc)`
                        : 'rgba(239,68,68,0.15)'
                      : `${RC.accent}1a`,
                    color: buyHovered ? (canAfford ? '#000' : '#fca5a5') : RC.pill,
                    border: buyHovered
                      ? canAfford ? `1.5px solid ${RC.accent}` : '1.5px solid rgba(239,68,68,0.45)'
                      : `1.5px solid ${RC.accent}${canAfford ? '66' : '22'}`,
                    boxShadow: buyHovered && canAfford ? `0 0 20px ${RC.accent}44` : 'none',
                    opacity: !canAfford && !buyHovered ? 0.55 : 1,
                  }}>
                  {buying
                    ? <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    : <div className="relative flex items-center justify-center" style={{ height: '1.25em', minWidth: 64 }}>
                        <div className="absolute inset-0 flex items-center justify-center gap-1.5 transition-all duration-200"
                          style={{ opacity: buyHovered ? 0 : 1, transform: buyHovered ? 'translateY(-110%)' : 'translateY(0)' }}>
                          <img src="/paperclip.svg" alt="" className="w-3.5 h-3.5"
                            style={{ filter: canAfford ? `drop-shadow(0 0 3px ${RC.accent})` : 'none' }} />
                          {item.price}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center whitespace-nowrap transition-all duration-200"
                          style={{ opacity: buyHovered ? 1 : 0, transform: buyHovered ? 'translateY(0)' : 'translateY(110%)' }}>
                          {canAfford ? 'Купить' : 'Не хватает'}
                        </div>
                      </div>
                  }
                </button>
              )}
              </div>

              {user?.is_developer && item?.asset && (
                <button type="button" onClick={handleDevDownload} disabled={downloading}
                  className="shrink-0 self-stretch rounded-full flex items-center justify-center transition-all duration-200 active:scale-90 hover:brightness-125 relative overflow-visible"
                  style={{
                    width: 44, minWidth: 44, aspectRatio: '1 / 1',
                    background: downloadDone ? 'rgba(16,185,129,0.14)' : 'rgba(99,102,241,0.12)',
                    border: `1px solid ${downloadDone ? 'rgba(16,185,129,0.42)' : 'rgba(99,102,241,0.34)'}`,
                    color: downloadDone ? (isDark ? '#6ee7b7' : '#047857') : (isDark ? '#a5b4fc' : '#4338ca'),
                    cursor: downloading ? 'wait' : 'pointer',
                  }}
                  title="Скачать исходный файл (dev-only)">
                  {ripple > 0 && (
                    <span key={ripple} aria-hidden="true"
                      className="pointer-events-none absolute inset-0 rounded-full"
                      style={{
                        background: downloadDone ? 'rgba(16,185,129,0.45)' : 'rgba(99,102,241,0.4)',
                        animation: 'devDlRing .7s ease-out 1 forwards',
                      }} />
                  )}
                  <span className="relative inline-flex items-center justify-center w-4 h-4">
                    {downloading ? (
                      <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    ) : downloadDone ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.6}
                        style={{ animation: 'devDlPop .42s cubic-bezier(.2,1.6,.3,1) both' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"/>
                      </svg>
                    )}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

function PhotoEditor({ src, onConfirm, onClose }) {
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  // offset in pixels from center
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [circR, setCircR] = useState(140);
  const [cvSize, setCvSize] = useState({ w: 300, h: 300 });
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const imgRef = useRef(null);
  const dragging = useRef(false);
  const lastPt = useRef({ x: 0, y: 0 });
  const lastDist = useRef(null);

  // Загружаем изображение один раз, потом триггерим редрав
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      setOffset(o => ({ ...o })); // trigger redraw
    };
    img.src = src;
  }, [src]);

  // Измеряем контейнер
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      setCircR(Math.min(width, height) * 0.44);
      setCvSize({ w: width, h: height });
      const canvas = canvasRef.current;
      if (canvas) { canvas.width = width; canvas.height = height; }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Рисуем canvas при любом изменении
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const W = canvas.width, H = canvas.height;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    ctx.save();
    ctx.translate(W / 2 + offset.x, H / 2 + offset.y);
    if (flipH) ctx.scale(-1, 1);
    ctx.rotate((rotation * Math.PI) / 180);
    const isRot90 = rotation === 90 || rotation === 270;
    const nw = isRot90 ? img.naturalHeight : img.naturalWidth;
    const nh = isRot90 ? img.naturalWidth : img.naturalHeight;
    const cover = Math.max(W / nw, H / nh) * scale;
    const dw = nw * cover, dh = nh * cover;
    ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }, [offset, scale, rotation, flipH, circR]);

  // Перерисовываем когда imgRef обновляется
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    img.onload = () => { setOffset(o => ({ ...o })); }; // force redraw
  }, [src]);

  const minScale = () => {
    const img = imgRef.current;
    if (!img || !cvSize.w || !cvSize.h) return 0.3;
    // Минимальный масштаб: изображение покрывает круг (diameter = 2*circR)
    const d = circR * 2;
    const cover = Math.max(d / img.naturalWidth, d / img.naturalHeight);
    const base = Math.max(cvSize.w / img.naturalWidth, cvSize.h / img.naturalHeight);
    return Math.max(cover / base, 0.3);
  };
  const clampScale = v => Math.max(minScale(), Math.min(8, v));

  const onPointerDown = e => {
    dragging.current = true;
    lastPt.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onPointerMove = e => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPt.current.x;
    const dy = e.clientY - lastPt.current.y;
    lastPt.current = { x: e.clientX, y: e.clientY };
    setOffset(p => {
      const img = imgRef.current;
      if (img) {
        const { w: CW, h: CH } = cvSize;
        const cov = Math.max(CW / img.naturalWidth, CH / img.naturalHeight) * scale;
        const maxX = img.naturalWidth * cov / 2;
        const maxY = img.naturalHeight * cov / 2;
        return {
          x: Math.max(-maxX, Math.min(maxX, p.x + dx)),
          y: Math.max(-maxY, Math.min(maxY, p.y + dy)),
        };
      }
      return { x: p.x + dx, y: p.y + dy };
    });
  };

  const onTouchStart = e => {
    if (e.touches.length === 2) {
      lastDist.current = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
    }
  };
  const onTouchMove = e => {
    if (e.touches.length === 2 && lastDist.current) {
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setScale(s => clampScale(s * (dist / lastDist.current)));
      lastDist.current = dist;
    }
  };
  const onWheel = e => {
    e.preventDefault();
    setScale(s => clampScale(s * (e.deltaY < 0 ? 1.1 : 0.91)));
  };

  const handleConfirm = () => {
    const size = 512;
    const exportC = document.createElement('canvas');
    exportC.width = size; exportC.height = size;
    const ctx = exportC.getContext('2d');

    const draw = img => {
      const prevEl = previewRef.current;
      const PW = prevEl ? prevEl.getBoundingClientRect().width : size;
      const PH = prevEl ? prevEl.getBoundingClientRect().height : size;
      const safeCircR = circR > 0 ? circR : Math.min(PW, PH) * 0.44;
      const ef = (size / 2) / safeCircR;
      ctx.save();
      ctx.translate(size / 2 + offset.x * ef, size / 2 + offset.y * ef);
      if (flipH) ctx.scale(-1, 1);
      ctx.rotate((rotation * Math.PI) / 180);
      const rot90 = rotation === 90 || rotation === 270;
      const nw = rot90 ? img.naturalHeight : img.naturalWidth;
      const nh = rot90 ? img.naturalWidth : img.naturalHeight;
      const cover = Math.max(PW / nw, PH / nh) * scale * ef;
      const dw = nw * cover, dh = nh * cover;
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
      exportC.toBlob(b => {
        if (b) {
          onConfirm(b);
        } else {
          // Fallback: toBlob вернул null, пробуем без сжатия
          exportC.toBlob(b2 => { if (b2) onConfirm(b2); }, 'image/png');
        }
      }, 'image/jpeg', 0.92);
    };

    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      draw(imgRef.current);
    } else {
      const img = new Image();
      img.onload = () => { imgRef.current = img; draw(img); };
      img.onerror = () => onConfirm(null);
      img.src = src;
    }
  };

  const Tool = ({ onClick, icon, label, active }) => (
    <button type="button" onClick={onClick} className="flex flex-col items-center gap-1.5">
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${active ? 'bg-blue-500' : 'bg-white/10 hover:bg-white/20'}`}>
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">{icon}</svg>
      </div>
      <span className="text-white/50 text-[11px]">{label}</span>
    </button>
  );

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black flex flex-col">
      <div className="flex items-center justify-between px-5 pt-12 pb-4 shrink-0">
        <button type="button" onClick={onClose} className="text-white/60 text-[15px] font-medium px-2 py-1">Отмена</button>
        <span className="text-white text-[16px] font-semibold">Редактировать</span>
        <button type="button" onClick={handleConfirm}
          className="w-11 h-11 bg-green-500 hover:bg-green-400 rounded-full flex items-center justify-center shadow-lg transition-colors active:scale-95">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
          </svg>
        </button>
      </div>

      {/* Preview */}
      <div
        ref={previewRef}
        className="flex-1 relative overflow-hidden select-none"
        style={{ touchAction: 'none', cursor: 'grab' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => { dragging.current = false; }}
        onPointerCancel={() => { dragging.current = false; }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onWheel={onWheel}
      >
        {/* Canvas — фото рисуется здесь */}
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

        {/* Тёмная плёнка поверх canvas */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: `radial-gradient(circle ${circR}px at 50% 50%, transparent 99%, rgba(0,0,0,0.74) 100%)`,
        }} />

        {/* SVG: сетка + круг — пиксельные координаты, всё совпадает */}
        {(() => {
          const cx = cvSize.w / 2, cy = cvSize.h / 2, r = circR;
          const t = r / 3;
          return (
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              <defs>
                <clipPath id="cc">
                  <circle cx={cx} cy={cy} r={r} />
                </clipPath>
              </defs>
              <g clipPath="url(#cc)" stroke="rgba(255,255,255,0.25)" strokeWidth="1">
                {/* Вертикальные */}
                <line x1={cx - t} y1={cy - r} x2={cx - t} y2={cy + r} />
                <line x1={cx + t} y1={cy - r} x2={cx + t} y2={cy + r} />
                {/* Горизонтальные */}
                <line x1={cx - r} y1={cy - t} x2={cx + r} y2={cy - t} />
                <line x1={cx - r} y1={cy + t} x2={cx + r} y2={cy + t} />
              </g>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
            </svg>
          );
        })()}
      </div>

      {/* Слайдер приближения — thumb растёт вправо */}
      <div className="flex justify-center pt-4 pb-1 shrink-0">
        <div className="w-[216px]">
          {(() => {
            const mn = minScale();
            const pct = ((scale - mn) / (4 - mn)) * 100;
            const thumbPx = Math.round(10 + pct / 100 * 16);
            return (
              <input
                type="range"
                min={mn}
                max={4}
                step={0.05}
                value={scale}
                onChange={e => setScale(Number(e.target.value))}
                className="zoom-slider w-full"
                style={{
                  '--zoom-thumb': `${thumbPx}px`,
                  background: `linear-gradient(to right, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.75) ${pct}%, rgba(255,255,255,0.2) ${pct}%, rgba(255,255,255,0.2) 100%)`,
                }}
              />
            );
          })()}
        </div>
      </div>

      <div className="flex items-center justify-center gap-6 py-6 shrink-0">
        <Tool onClick={() => setRotation(r => (r - 90 + 360) % 360)} label="Влево"
          icon={<path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9"/>} />
        <Tool onClick={() => setRotation(r => (r + 90) % 360)} label="Вправо"
          icon={<path strokeLinecap="round" strokeLinejoin="round" d="M20 4v5h-.581m0 0a8.003 8.003 0 00-15.357 2m15.357-2H15"/>} />
        <Tool onClick={() => setFlipH(f => !f)} label="Отразить" active={flipH}
          icon={<><path strokeLinecap="round" strokeLinejoin="round" d="M8 3H5a2 2 0 00-2 2v14a2 2 0 002 2h3m8-18h3a2 2 0 012 2v14a2 2 0 01-2 2h-3"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18"/></>} />
      </div>
    </div>,
    document.body
  );
}

function AdminPanel() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center justify-between w-full px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100/70 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
            <svg className="w-[18px] h-[18px] text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <p className="text-[15px] font-semibold text-slate-800">Панель администратора</p>
            <p className="text-[12px] text-slate-400 mt-0.5">Пользователи, приглашения, QR</p>
          </div>
        </div>
        <svg
          className={`w-4 h-4 text-slate-300 shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-slate-100">
          <AdminAccessPanel />
        </div>
      )}
    </>
  );
}

function FloatingInput({ label, value, onChange, type = 'text', autoCorrect, autoCapitalize }) {
  const [focused, setFocused] = useState(false);
  const active = focused || !!value;
  return (
    <div className="relative pt-5">
      <span className={`absolute pointer-events-none select-none transition-all duration-200 leading-none ${
        active
          ? 'top-0 left-1 text-[12px] font-medium text-violet-400'
          : 'top-[42px] left-4 -translate-y-1/2 text-[15px] text-slate-400'
      }`}>
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoCorrect={autoCorrect}
        autoCapitalize={autoCapitalize}
        className={`w-full px-4 py-3.5 bg-white rounded-2xl outline-none border-2 text-[16px] text-slate-900 transition-colors duration-200 ${
          focused ? 'border-violet-400' : 'border-slate-200'
        }`}
      />
    </div>
  );
}

function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}


function formatRelativeTime(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'только что';
  if (mins < 60) return `${mins} мин. назад`;
  if (hours < 24) return `${hours} ч. назад`;
  if (days < 7) return `${days} д. назад`;
  return new Date(isoString).toLocaleDateString('ru-RU');
}

// Parse session.device_name + is_mobile → platform/browser icon hints
function parseDeviceInfo(session) {
  const raw = (session?.device_name || '').trim();
  const low = raw.toLowerCase();
  let osKey = 'other';
  let browserKey = 'other';
  let primary = raw || 'Неизвестное устройство';
  let secondary = '';

  // Browser·OS format: "Chrome · Windows"
  const dotMatch = raw.match(/^(.+?)\s*[·•]\s*(.+)$/);
  if (dotMatch) {
    primary = dotMatch[2];
    secondary = dotMatch[1];
  }

  if (/iphone|ipad|ipod|ios/.test(low)) osKey = 'ios';
  else if (/samsung|xiaomi|huawei|honor|oppo|vivo|realme|poco|redmi|oneplus|google pixel|android/.test(low)) osKey = 'android';
  else if (/windows/.test(low)) osKey = 'windows';
  else if (/mac/.test(low)) osKey = 'mac';
  else if (/chromeos|cros/.test(low)) osKey = 'chromeos';
  else if (/linux/.test(low)) osKey = 'linux';
  else if (session?.is_mobile) osKey = 'android';

  if (/chrome/.test(low) && !/edge|edg/.test(low)) browserKey = 'chrome';
  else if (/edge|edg\//.test(low)) browserKey = 'edge';
  else if (/safari/.test(low) && !/chrome/.test(low)) browserKey = 'safari';
  else if (/firefox/.test(low)) browserKey = 'firefox';
  else if (/opera|opr\//.test(low)) browserKey = 'opera';
  else if (/яндекс|yandex|yabrowser/.test(low)) browserKey = 'yandex';

  // mobile device shortname (iPhone / Samsung X / etc) — put model as primary, OS as secondary
  if (session?.is_mobile && !dotMatch) {
    primary = raw || (osKey === 'ios' ? 'iPhone' : 'Телефон');
    secondary = osKey === 'ios' ? 'iOS' : osKey === 'android' ? 'Android' : '';
  }

  return { osKey, browserKey, primary, secondary, isMobile: !!session?.is_mobile };
}

function OsIcon({ osKey, isMobile, className = 'w-5 h-5', color = 'currentColor' }) {
  // Minimal, filled brand-style monochrome icons
  switch (osKey) {
    case 'ios':
      return (
        <svg viewBox="0 0 24 24" className={className} fill={color} aria-hidden="true">
          <path d="M16.37 12.4c-.02-2.17 1.77-3.21 1.85-3.26-1.01-1.48-2.59-1.68-3.15-1.7-1.34-.14-2.62.79-3.3.79-.69 0-1.74-.77-2.86-.75-1.47.02-2.83.85-3.59 2.17-1.53 2.65-.39 6.57 1.1 8.73.73 1.05 1.6 2.24 2.74 2.2 1.1-.04 1.52-.71 2.86-.71 1.33 0 1.71.71 2.87.69 1.19-.02 1.94-1.07 2.66-2.14.84-1.22 1.19-2.42 1.21-2.49-.03-.01-2.33-.9-2.39-3.53zM14.2 5.86c.6-.72 1-1.73.89-2.73-.86.03-1.9.57-2.52 1.29-.55.64-1.04 1.66-.91 2.65.96.07 1.94-.49 2.54-1.21z"/>
        </svg>
      );
    case 'mac':
      return (
        <svg viewBox="0 0 24 24" className={className} fill={color} aria-hidden="true">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83zM13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
      );
    case 'windows':
      return (
        <svg viewBox="0 0 24 24" className={className} fill={color} aria-hidden="true">
          <path d="M3 5.48 10.5 4.5v6.75H3V5.48zM10.5 12.5v6.75L3 18.27V12.5h7.5zM11.5 4.37 21 3v8.25h-9.5V4.37zM21 12.5v8.25l-9.5-1.37V12.5H21z"/>
        </svg>
      );
    case 'android':
      return (
        <svg viewBox="0 0 24 24" className={className} fill={color} aria-hidden="true">
          <path d="M17.2 8.33 18.56 6a.4.4 0 0 0-.14-.55.4.4 0 0 0-.55.14l-1.38 2.37A8.37 8.37 0 0 0 12 7c-1.6 0-3.1.4-4.49 1.1L6.13 5.6a.4.4 0 0 0-.55-.14.4.4 0 0 0-.14.55L6.8 8.33A7.49 7.49 0 0 0 3 14.75h18a7.49 7.49 0 0 0-3.8-6.42zM8.5 12.6a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8zm7 0a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8zM3 15.75h18V18a2 2 0 0 1-2 2h-2v2a1 1 0 1 1-2 0v-2h-6v2a1 1 0 1 1-2 0v-2H5a2 2 0 0 1-2-2v-2.25z"/>
        </svg>
      );
    case 'chromeos':
      return (
        <svg viewBox="0 0 24 24" className={className} fill={color} aria-hidden="true">
          <path d="M12 2a10 10 0 0 1 8.66 5H12a5 5 0 0 0-4.33 7.5L3.34 7A10 10 0 0 1 12 2zm0 20a10 10 0 0 1-8.66-5l4.33-7.5A5 5 0 0 0 12 17h8.66A10 10 0 0 1 12 22zm0-6a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"/>
        </svg>
      );
    case 'linux':
      return (
        <svg viewBox="0 0 24 24" className={className} fill={color} aria-hidden="true">
          <path d="M12 2c-2.2 0-3.5 1.4-3.5 3.5 0 1.1.4 2 1 3.1-.2.5-1.1 1.4-1.8 2.4-.8 1.1-1.7 2.9-1.7 4.5 0 1.1.4 2 1 2.7-.3.4-.5.9-.5 1.4 0 1.1.9 1.4 1.7 1.7.6.2 1 .4 1 .8 0 .4.3.8.8.9.7.1 1.4-.2 1.4-.7 0-.3-.3-.4-.3-.7 0-.5.8-.5 1.5-.5.8 0 1.6 0 1.6.5 0 .3-.3.4-.3.7 0 .5.7.8 1.4.7.5-.1.8-.5.8-.9 0-.4.4-.6 1-.8.8-.3 1.7-.6 1.7-1.7 0-.5-.2-1-.5-1.4.6-.7 1-1.6 1-2.7 0-1.6-.9-3.4-1.7-4.5-.7-1-1.6-1.9-1.8-2.4.6-1.1 1-2 1-3.1C15.5 3.4 14.2 2 12 2zm-.9 2.6c.5 0 .9.5.9 1.1 0 .3-.1.6-.3.8-.1-.1-.3-.1-.5-.2.2-.1.3-.3.3-.6 0-.3-.2-.6-.4-.6-.2 0-.4.3-.4.6 0 .2.1.3.2.4-.2.1-.3.2-.4.3-.3-.2-.4-.5-.4-.7 0-.6.4-1.1 1-1.1zm1.8 0c.5 0 1 .5 1 1.1 0 .2-.1.5-.3.7-.2-.1-.3-.2-.4-.3.1-.1.2-.3.2-.4 0-.3-.2-.6-.4-.6-.3 0-.4.3-.4.6 0 .3.1.5.3.6-.2.1-.4.1-.5.2-.2-.2-.3-.5-.3-.8 0-.6.4-1.1.8-1.1z"/>
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {isMobile
            ? <><rect x="7" y="2.5" width="10" height="19" rx="2.5"/><path d="M11 18.5h2"/></>
            : <><rect x="2.5" y="4" width="19" height="12" rx="2"/><path d="M8 20h8M12 16v4"/></>}
        </svg>
      );
  }
}

function resolveAvatarSrc(avatarUrl) {
  if (!avatarUrl) return null;
  if (/^https?:\/\//i.test(avatarUrl) || avatarUrl.startsWith("data:")) {
    return avatarUrl;
  }
  return `${window.location.origin}${avatarUrl}`;
}

export default function Account() {
  const { user, setUser, logout } = useContext(AuthCtx);
  const { isDark, setPreference } = useTheme();
  const navigate = useNavigate();

  function handleThemeToggle() {
    document.documentElement.classList.add('theme-anim');
    setPreference(isDark ? 'light' : 'dark');
    setTimeout(() => document.documentElement.classList.remove('theme-anim'), 400);
  }

  const email = useMemo(
    () =>
      (user?.email ||
        localStorage.getItem("userEmail") ||
        "guest@example.com")
        .toLowerCase()
        .trim(),
    [user]
  );

  // --- АВАТАР ---
  const [avatar, setAvatar] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [avatarPos, setAvatarPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('avatarPos') || 'null') || { x: 50, y: 50 }; }
    catch { return { x: 50, y: 50 }; }
  });
  const [editView, setEditView] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editPatronymic, setEditPatronymic] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);
  const editGroupRef = useRef(null);
  const editPanelRef = useRef(null);
  const [editGroupModalOpen, setEditGroupModalOpen] = useState(false);
  const [editGroupModalSearch, setEditGroupModalSearch] = useState('');
  const [editGroupKbHeight, setEditGroupKbHeight] = useState(0);

  useEffect(() => {
    if (!editGroupModalOpen) { setEditGroupKbHeight(0); return; }
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setEditGroupKbHeight(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, [editGroupModalOpen]);

  const [editStudyGroup, setEditStudyGroup] = useState('');
  const [editGradientId, setEditGradientId] = useState('');
  const [profileTextDark, setProfileTextDark] = useState(false);
  const [editTextDark, setEditTextDark] = useState(false);
  const [bannerZoom, setBannerZoom] = useState(1);
  const [bannerZoomMobile, setBannerZoomMobile] = useState(1);
  const [isRealMobile, setIsRealMobile] = useState(() => window.matchMedia('(max-width: 639px)').matches);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSrc, setEditorSrc] = useState(null);
  const fileInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerBusy, setBannerBusy] = useState(false);
  const [bannerError, setBannerError] = useState("");
  const [bannerPos, setBannerPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bannerPos') || 'null') || { x: 50, y: 50 }; }
    catch { return { x: 50, y: 50 }; }
  });
  const [bannerPosMobile, setBannerPosMobile] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bannerPosMobile') || 'null') || null; }
    catch { return null; }
  });
  const bannerPosSaveTimer = useRef(null);
  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [groupModal, setGroupModal] = useState(false);
  const [avatarRemoveOpen, setAvatarRemoveOpen] = useState(false);
  const [disciplinesOpen, setDisciplinesOpen] = useState(true);
  const [discOverflow, setDiscOverflow] = useState("visible");
  const discOverflowTimer = useRef(null);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokeLoadingId, setRevokeLoadingId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null); // selected session for details sheet
  const [sessionConfirm, setSessionConfirm] = useState(null); // {kind:'one'|'all', sid?, label?}
  const [sessionsError, setSessionsError] = useState('');
  const [sessionToast, setSessionToast] = useState(null); // {msg, kind:'ok'|'err'}
  const sessionToastTimerRef = useRef(null);
  const showSessionToast = (msg, kind = 'ok') => {
    setSessionToast({ msg, kind });
    if (sessionToastTimerRef.current) clearTimeout(sessionToastTimerRef.current);
    sessionToastTimerRef.current = setTimeout(() => setSessionToast(null), 2600);
  };
  useEffect(() => () => { if (sessionToastTimerRef.current) clearTimeout(sessionToastTimerRef.current); }, []);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupSearch, setGroupSearch] = useState("");
  const filteredGroups = groups.filter(g =>
    g.toLowerCase().includes(groupSearch.toLowerCase())
  );
  const needsGroupSelection = !user?.study_group && (user?.role === 'student' || user?.is_curator);

  const [favoriteGroups, setFavoriteGroups] = useState([]);
  const [editFavoriteGroups, setEditFavoriteGroups] = useState([]);

  // --- СКРЕПКИ ---
  const [skrepBalance, setSkrepBalance] = useState(null);
  const [candyBalance, setCandyBalance] = useState(null);
  const [candyExchangeOpen, setCandyExchangeOpen] = useState(false);
  const [candyExchangeAmount, setCandyExchangeAmount] = useState(3);
  const [candyExchangeBusy, setCandyExchangeBusy] = useState(false);
  const [candyExchangeError, setCandyExchangeError] = useState('');
  const [candyBurst, setCandyBurst] = useState([]);
  useEffect(() => {
    if (!candyExchangeOpen) return;
    try { navigator.vibrate?.(14); } catch { /* ignore */ }
  }, [candyExchangeOpen]);
  const [reaperModalOpen, setReaperModalOpen] = useState(false);
  const [reaperSearch, setReaperSearch] = useState('');
  const [reaperUsers, setReaperUsers] = useState([]);
  const [reaperSending, setReaperSending] = useState(false);
  const [reaperError, setReaperError] = useState('');
  const [reaperSentTo, setReaperSentTo] = useState(null);
  const [reaperType, setReaperType] = useState('bats');
  const [displayBalance, setDisplayBalance] = useState(null);
  const prevBalanceRef = useRef(null);
  const balanceAnimRef = useRef(null);
  const contentReadyFiredRef = useRef(false);
  const [skrepBroken, setSkrepBroken] = useState(0);
  const [goldenClips, setGoldenClips] = useState(0);
  const [isCrownHolder, setIsCrownHolder] = useState(false);
  const [crownEquipped, setCrownEquipped] = useState(false);
  const [pityChances, setPityChances] = useState({ common: 10, epic: 5, legendary: 2, random: 3 });
  const [skrepView, setSkrepView] = useState(() => sessionStorage.getItem('aud_skrep_view') === '1');
  const [collectionView, setCollectionView] = useState(() => sessionStorage.getItem('aud_collection_view') === '1');
  const [sellConfirm, setSellConfirm] = useState(null);
  const [sellAnim, setSellAnim] = useState(null);
  const [awardModal, setAwardModal] = useState(false);
  const [awardAmount, setAwardAmount] = useState('');
  const [awardReason, setAwardReason] = useState('');
  const [awardSearch, setAwardSearch] = useState('');
  const [awardRecipients, setAwardRecipients] = useState([]);
  const [awardSuggestions, setAwardSuggestions] = useState([]);
  const [awardBusy, setAwardBusy] = useState(false);
  const [awardError, setAwardError] = useState('');
  const [giftModal, setGiftModal] = useState(null);
  const [giftEmail, setGiftEmail] = useState('');
  const [giftRecipient, setGiftRecipient] = useState(null);
  const [giftBusy, setGiftBusy] = useState(false);
  const [giftLookupError, setGiftLookupError] = useState('');
  const [giftSuggestions, setGiftSuggestions] = useState([]);
  const [giftAnonymous, setGiftAnonymous] = useState(false);
  const [giftAnim, setGiftAnim] = useState(null);
  const [unboxAnim, setUnboxAnim] = useState(null); // { asset, name }
  const [skrepAwardAnim, setSkrepAwardAnim] = useState(null); // { amount, reason }
  const [cardMenu, setCardMenu] = useState(null);
  const longPressTimer = useRef(null);
  const [profileContainerEl, setProfileContainerEl] = useState(null);
  const [caseResult, setCaseResult] = useState(null);
  const [caseBusy, setCaseBusy] = useState(false);
  const caseBusyRef = useRef(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [openAnim, setOpenAnim] = useState(null);
  // openAnim: null | { phase:'drop'|'shake'|'reveal', result, caseData }
  const [inventory, setInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [shopItems, setShopItems] = useState([]);
  const [shopSets, setShopSets] = useState([]);
  const [shopPromos, setShopPromos] = useState([]);
  const [promoNow, setPromoNow] = useState(() => Date.now());
  const [shopLoading, setShopLoading] = useState(false);
  const [rerollInfo, setRerollInfo] = useState({ free_remaining: 2, free_total: 2, next_cost: 0, cost_skrep: 5, used: 0 });
  const [rerollBusy, setRerollBusy] = useState(false);
  const [rerollError, setRerollError] = useState('');
  const [rerollArmed, setRerollArmed] = useState(false);
  const [rerollSpinning, setRerollSpinning] = useState(false);
  const [rerollKey, setRerollKey] = useState(0);
  const [rerollDenied, setRerollDenied] = useState(false);
  const rerollArmedTimerRef = useRef(null);
  const rerollDeniedTimerRef = useRef(null);
  const diceLottieRefDesktop = useRef(null);
  const diceLottieRefMobile = useRef(null);
  const [showDevInfo, setShowDevInfo] = useState(false);
  const [showBadgeInfo, setShowBadgeInfo] = useState(false);
  const [shopBuying, setShopBuying] = useState(null);
  const [premiumItems, setPremiumItems] = useState([]);
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [premiumBuyingId, setPremiumBuyingId] = useState(null);
  const [premiumBurstMap, setPremiumBurstMap] = useState({});  // { [itemId]: counter }
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingCoverFor, setEditingCoverFor] = useState(null);
  const [uploadTypeOpen, setUploadTypeOpen] = useState(false);
  const [uploadRarityOpen, setUploadRarityOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState(0);
  const [uploadStepDir, setUploadStepDir] = useState(1);
  const [uploadStepKey, setUploadStepKey] = useState(0);
  const [uploadTransitioning, setUploadTransitioning] = useState(false);
  // Multi-mode admin modal: null (chooser) | 'item' | 'set' | 'promo'
  const [uploadMode, setUploadMode] = useState(null);
  useEffect(() => {
    if (shopPromos.length === 0) return;
    const id = setInterval(() => setPromoNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, [shopPromos.length]);
  useEffect(() => {
    if (uploadMode !== 'promo') return;
    const t = getToken();
    if (!t) return;
    (async () => {
      try {
        const r = await fetch('/skrep/sets', { headers: { Authorization: `Bearer ${t}` } });
        if (r.ok) {
          const data = await r.json();
          setAvailableSets(Array.isArray(data?.sets) ? data.sets : []);
        }
      } catch {}
      try {
        const r = await fetch('/skrep/shop', { headers: { Authorization: `Bearer ${t}` } });
        if (r.ok) {
          const data = await r.json();
          setAvailableShopItemsForPromo(Array.isArray(data) ? data : []);
        }
      } catch {}
    })();
  }, [uploadMode]);
  const [setForm, setSetForm] = useState({
    name: '', description: '', effect_type: 'orange_pulse',
    rarity: 'epic', price_frame: 50, price_banner: 50, price_badge: 30,
    bundle_price: null,
    is_halloween: true,
    frame_name: '', banner_name: '', badge_name: '',
    frameCover: null, bannerCover: null, badgeCover: null,
    setCover: null,
  });
  const [promoForm, setPromoForm] = useState({
    name: '', description: '', start_date: '', end_date: '', discount_percent: 20,
    source: 'set', set_id: '', item_ids: [],
  });
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [setSubmitting, setSetSubmitting] = useState(false);
  const [availableSets, setAvailableSets] = useState([]);
  const [availableShopItemsForPromo, setAvailableShopItemsForPromo] = useState([]);
  const frameDragRef = useRef({ active: false, startX: 0, startY: 0, ox: 0, oy: 0, moved: false });
  const frameCoverInputRef = useRef(null);
  const iframeRef = useRef(null);
  const openAnimRef = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      if (!frameDragRef.current.active) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = ((cx - frameDragRef.current.startX) / 164) * 100;
      const dy = ((cy - frameDragRef.current.startY) / 164) * 100;
      if (Math.abs(cx - frameDragRef.current.startX) > 2 || Math.abs(cy - frameDragRef.current.startY) > 2)
        frameDragRef.current.moved = true;
      setUploadForm(f => ({
        ...f,
        frameOffsetX: Math.round(Math.max(-50, Math.min(50, frameDragRef.current.ox + dx))),
        frameOffsetY: Math.round(Math.max(-50, Math.min(50, frameDragRef.current.oy + dy))),
      }));
      if (e.cancelable) e.preventDefault();
    };
    const onUp = () => { frameDragRef.current.active = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);
  useEffect(() => {
    if (showUploadModal) {
      document.body.style.overflow = 'hidden';
      if (!editingCoverFor) {
        setUploadStep(HALLOWEEN_MODE ? 0 : 1);
        setUploadStepDir(1);
        setUploadStepKey(0);
      }
      setUploadTransitioning(false);
      setUploadError('');
      if (!authorSuggestions.length) {
        fetch('/auth/users', { headers: { Authorization: `Bearer ${getToken()}` } })
          .then(r => r.ok ? r.json() : [])
          .then(list => setAuthorSuggestions(Array.isArray(list) ? list : []))
          .catch(() => {});
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showUploadModal, editingCoverFor]);  // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const locked = !!(sellConfirm || giftModal);
    document.body.style.overflow = locked ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sellConfirm, giftModal]);

  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadForm, setUploadForm] = useState({ name: "", price: "", description: "", item_type: "badge", rarity: "common", category: "regular", cover: null, coverPreview: null, coverMeta: null, frameOffsetY: 0, frameOffsetX: 0, frameScale: 150, authorName: "", authorUserId: null, authorUserFio: "", authorUserAvatar: null, isPremium: false, priceRub: "", effectType: "", effectParams: "" });
  const [authorSuggestions, setAuthorSuggestions] = useState([]);
  const [authorSearch, setAuthorSearch] = useState("");
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", price: "", rarity: "common", cover: null, coverPreview: null, clear_author: false, is_anonymous: false });
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState("");
  const [equipPreviewItem, setEquipPreviewItem] = useState(null);
  const [collectionTypeFilter, setCollectionTypeFilter] = useState('all');
  const [collectionSearch, setCollectionSearch] = useState('');
  const [collectionRarityFilter, setCollectionRarityFilter] = useState('all');
  const [collectionOpenDropdown, setCollectionOpenDropdown] = useState(null);
  const [collAnimKey, setCollAnimKey] = useState(0);
  const [collGridOut, setCollGridOut] = useState(false);
  const collGridTimer = useRef(null);
  const collFilterFirst = useRef(true);
  useEffect(() => {
    if (collFilterFirst.current) { collFilterFirst.current = false; return; }
    clearTimeout(collGridTimer.current);
    setCollGridOut(true);
    collGridTimer.current = setTimeout(() => {
      setCollGridOut(false);
      setCollAnimKey(k => k + 1);
    }, 130);
    return () => clearTimeout(collGridTimer.current);
  }, [collectionSearch, collectionTypeFilter, collectionRarityFilter]);
  const [sellDupsConfirm, setSellDupsConfirm] = useState(false);
  const [sellDupsBusy, setSellDupsBusy] = useState(false);
  const sellDupsTimer = useRef(null);
  const [sellItemConfirm, setSellItemConfirm] = useState(null);
  const sellItemTimer = useRef(null);
  const collectionScrollRef = useRef(null);
  const skrepScrollRef = useRef(null);
  const [shopPreviewItem, setShopPreviewItem] = useState(null);
  const [skrepSubTab, setSkrepSubTab] = useState('shop');
  const [highlightCollectionId, setHighlightCollectionId] = useState(null);
  const [shopTab, setShopTab] = useState('shop');
  const [vaultType, setVaultType] = useState('all');

  // Lazy-fetch premium catalog when user opens Premium tab
  useEffect(() => {
    if (shopTab !== 'premium') return;
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    setPremiumLoading(true);
    (async () => {
      try {
        const r = await fetch('/payments/premium/catalog', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setPremiumItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setPremiumItems([]);
      } finally {
        if (!cancelled) setPremiumLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shopTab]);

  const [shopRarityFilter, setShopRarityFilter] = useState(null);
  const [shopNewOnly, setShopNewOnly] = useState(false);
  const [shopSearch, setShopSearch] = useState('');
  const [shopGridAtEnd, setShopGridAtEnd] = useState(false);
  const [shopGridAtStart, setShopGridAtStart] = useState(true);
  const [pumpkinFeeding, setPumpkinFeeding] = useState(false);
  const [pumpkinLastDrop, setPumpkinLastDrop] = useState(null);
  const [pumpkinMessage, setPumpkinMessage] = useState(null);
  const [pumpkinSvg, setPumpkinSvg] = useState(null);
  const [pumpkinDropSvgs, setPumpkinDropSvgs] = useState([]);
  const [pumpkinDropIndex, setPumpkinDropIndex] = useState(0);
  const [pumpkinRejectSvgs, setPumpkinRejectSvgs] = useState([]);
  const [pumpkinRejectIndex, setPumpkinRejectIndex] = useState(0);
  const [pumpkinSelected, setPumpkinSelected] = useState(false);
  const [pumpkinAnimActive, setPumpkinAnimActive] = useState(false);
  const [pumpkinAnimKind, setPumpkinAnimKind] = useState('drop');
  const [pumpkinAnimEnded, setPumpkinAnimEnded] = useState(false);
  const [pumpkinAvailable, setPumpkinAvailable] = useState(true);
  useEffect(() => {
    if (!pumpkinAnimActive) { setPumpkinAnimEnded(false); return; }
    const delay = pumpkinAnimKind === 'reject' ? 4500 : 5500;
    const t = setTimeout(() => setPumpkinAnimEnded(true), delay);
    return () => clearTimeout(t);
  }, [pumpkinAnimActive, pumpkinAnimKind]);
  useEffect(() => {
    if (pumpkinSvg) return;
    let cancelled = false;
    fetch('/pumpkin_hover.svg')
      .then((r) => r.text())
      .then((t) => {
        if (cancelled) return;
        const sized = t.replace(/<svg([^>]*)>/, (m, attrs) => {
          const stripped = attrs.replace(/\s(width|height)="[^"]*"/g, '');
          return `<svg${stripped} width="100%" height="100%" style="display:block">`;
        });
        setPumpkinSvg(sized);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pumpkinSvg]);
  useEffect(() => {
    if (pumpkinDropSvgs.length) return;
    let cancelled = false;
    const urls = ['/pumpkin_candy1.svg', '/pumpkin_candy2.svg', '/pumpkin_candy3.svg'];
    Promise.all(urls.map((u) => fetch(u).then((r) => r.text()).catch(() => '')))
      .then((texts) => {
        if (cancelled) return;
        const prepared = texts
          .filter(Boolean)
          .map((t) => t
            .replace(/<script[\s\S]*?<\/script>/g, '')
            .replace(/(\d+(?:\.\d+)?s)\s+infinite/g, '$1 1 forwards')
            .replace(/<svg([^>]*)>/, (m, attrs) => {
              const noDim = attrs.replace(/\s(width|height)="[^"]*"/g, '');
              const newVb = noDim.replace(/viewBox="[^"]*"/, 'viewBox="-78 -128 238 318"');
              return `<svg${newVb} width="100%" height="100%" style="display:block">`;
            })
          );
        if (prepared.length) setPumpkinDropSvgs(prepared);
      });
    return () => { cancelled = true; };
  }, [pumpkinDropSvgs.length]);
  const pumpkinBurpAudioRef = useRef([]);
  const pumpkinAnimDivRef = useRef(null);
  const pumpkinDropHostRef = useRef(null);
  const [pumpkinDropWidgetReady, setPumpkinDropWidgetReady] = useState(() => typeof window !== 'undefined' && !!window.pumpkinDrop);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.pumpkinDrop) { setPumpkinDropWidgetReady(true); return; }
    const existing = document.querySelector('script[data-pumpkin-drop]');
    if (existing) {
      existing.addEventListener('load', () => setPumpkinDropWidgetReady(!!window.pumpkinDrop), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = '/pumpkin-drop.js';
    s.async = true;
    s.dataset.pumpkinDrop = '1';
    s.onload = () => setPumpkinDropWidgetReady(!!window.pumpkinDrop);
    document.head.appendChild(s);
  }, []);
  useEffect(() => {
    if (!pumpkinAnimActive) return;
    if (pumpkinAnimKind !== 'drop') return;
    if (!pumpkinDropWidgetReady) return;
    if (!window.pumpkinDrop) return;
    const host = pumpkinDropHostRef.current;
    if (!host || !pumpkinLastDrop) return;
    const rarityMap = { common: 'common', epic: 'epic', legendary: 'legendary', chromatic: 'chromo' };
    const mapped = rarityMap[pumpkinLastDrop.rarity] || 'common';
    const item = { rarity: mapped, name: pumpkinLastDrop.name || 'Предмет', image: pumpkinLastDrop.asset || undefined, description: pumpkinLastDrop.description || '' };
    try { window.pumpkinDrop(host, { item, sound: true, volume: 0.8 }); } catch { /* noop */ }
    return () => { if (host && host.__pkdStop) try { host.__pkdStop(); } catch { /* noop */ } };
  }, [pumpkinAnimActive, pumpkinAnimKind, pumpkinDropWidgetReady, pumpkinLastDrop]);
  useEffect(() => {
    if (pumpkinRejectSvgs.length) return;
    let cancelled = false;
    const urls = ['/pumpkin_rejects_lollipop.svg', '/pumpkin_rejects_caramel.svg', '/pumpkin_rejects_corn.svg'];
    Promise.all(urls.map((u) => fetch(u).then((r) => r.text()).catch(() => '')))
      .then((texts) => {
        if (cancelled) return;
        const nonEmpty = texts.filter(Boolean);
        if (!nonEmpty.length) return;
        const allUris = new Set();
        nonEmpty.forEach((t) => {
          const uris = t.match(/data:audio\/mpeg;base64,[A-Za-z0-9+/=]+/g) || [];
          uris.forEach((u) => allUris.add(u));
        });
        pumpkinBurpAudioRef.current = [...allUris].map((u) => {
          const a = new Audio(u);
          a.preload = 'auto';
          return a;
        });
        const prepared = nonEmpty.map((t) => t
          .replace(/<script[\s\S]*?<\/script>/g, '')
          .replace(/(\d+(?:\.\d+)?s)\s+infinite/g, '$1 1 forwards')
          .replace(/<svg([^>]*)>/, (m, attrs) => {
            const noDim = attrs.replace(/\s(width|height)="[^"]*"/g, '');
            const newVb = noDim.replace(/viewBox="[^"]*"/, 'viewBox="-78 -128 238 318"');
            return `<svg${newVb} width="100%" height="100%" style="display:block">`;
          })
        );
        setPumpkinRejectSvgs(prepared);
      });
    return () => { cancelled = true; };
  }, [pumpkinRejectSvgs.length]);
  useEffect(() => {
    if (!pumpkinAnimActive) return;
    const pickCv = setTimeout(() => {
      const cvs = document.querySelectorAll('.pumpkin-anim-wrap .cv');
      if (!cvs.length) return;
      const pick = Math.floor(Math.random() * cvs.length);
      cvs.forEach((el, i) => {
        el.style.animation = 'none';
        if (i === pick) {
          el.style.display = '';
          el.style.opacity = '1';
          el.style.visibility = 'visible';
        } else {
          el.style.display = 'none';
          el.style.opacity = '0';
          el.style.visibility = 'hidden';
        }
      });
    }, 0);
    if (pumpkinAnimKind !== 'reject') {
      return () => clearTimeout(pickCv);
    }
    const audios = pumpkinBurpAudioRef.current;
    if (!audios.length) return () => clearTimeout(pickCv);
    audios.forEach((a) => {
      try { a.muted = true; const p = a.play(); if (p && p.then) p.then(() => { a.pause(); a.muted = false; a.currentTime = 0; }).catch(() => { a.muted = false; }); } catch { /* noop */ }
    });
    const burpTimer = setTimeout(() => {
      const a = audios[Math.floor(Math.random() * audios.length)];
      try { a.currentTime = 0; const p = a.play(); if (p && p.catch) p.catch(() => {}); } catch { /* noop */ }
    }, 2960);
    return () => { clearTimeout(pickCv); clearTimeout(burpTimer); };
  }, [pumpkinAnimActive, pumpkinAnimKind]);
  useEffect(() => {
    if (!pumpkinAnimActive) return;
    const el = pumpkinAnimDivRef.current;
    if (!el) return;
    const isReject = pumpkinAnimKind === 'reject';
    const content = isReject
      ? (pumpkinRejectSvgs[pumpkinRejectIndex] || pumpkinRejectSvgs[0])
      : (pumpkinDropSvgs[pumpkinDropIndex] || pumpkinDropSvgs[0]);
    if (!content) return;
    el.innerHTML = content;
  }, [pumpkinAnimActive, pumpkinAnimKind, pumpkinDropIndex, pumpkinRejectIndex, pumpkinDropSvgs, pumpkinRejectSvgs]);
  const shopGridRef = useRef(null);
  const shopScrollRaf = useRef(null);
  const shopEdgeRaf = useRef(null);
  const scrollEased = (el, delta, duration = 480) => {
    if (!el) return;
    if (shopScrollRaf.current) cancelAnimationFrame(shopScrollRaf.current);
    const start = el.scrollLeft;
    const target = Math.max(0, Math.min(el.scrollWidth - el.clientWidth, start + delta));
    const dist = target - start;
    if (!dist) return;
    const t0 = performance.now();
    const tick = now => {
      const p = Math.min(1, (now - t0) / duration);
      const ease = 1 - Math.pow(1 - p, 4); // ease-out-quart
      el.scrollLeft = start + dist * ease;
      if (p < 1) shopScrollRaf.current = requestAnimationFrame(tick);
    };
    shopScrollRaf.current = requestAnimationFrame(tick);
  };
  useEffect(() => {
    const el = shopGridRef.current;
    if (!el) return;
    const handler = (e) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const canScrollH = el.scrollWidth > el.clientWidth;
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
      if (!canScrollH || (e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
      e.preventDefault();
      scrollEased(el, e.deltaY * 2.5, 320);
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  });
  const [equipPreviewPos, setEquipPreviewPos] = useState({ x: 50, y: 50 });
  const [equipPreviewZoom, setEquipPreviewZoom] = useState(1);
  const [equipPreviewPosMobile, setEquipPreviewPosMobile] = useState({ x: 50, y: 50 });
  const [equipPreviewZoomMobile, setEquipPreviewZoomMobile] = useState(1);

  useEffect(() => {
    sessionStorage.setItem('aud_skrep_view', skrepView ? '1' : '');
    if (skrepView) {
      const el = skrepScrollRef.current;
      if (el) { const y = parseInt(sessionStorage.getItem('aud_skrep_scroll') || '0', 10); requestAnimationFrame(() => { el.scrollTop = y; }); }
      // Notify clip spawner that shop panel content is now on-screen
      requestAnimationFrame(() => window.dispatchEvent(new CustomEvent("pageContentReady")));
    }
  }, [skrepView]);
  useEffect(() => {
    sessionStorage.setItem('aud_collection_view', collectionView ? '1' : '');
    if (collectionView) {
      const el = collectionScrollRef.current;
      if (el) { const y = parseInt(sessionStorage.getItem('aud_collection_scroll') || '0', 10); requestAnimationFrame(() => { el.scrollTop = y; }); }
      // Notify clip spawner that collection panel content is now on-screen
      requestAnimationFrame(() => window.dispatchEvent(new CustomEvent("pageContentReady")));
    }
  }, [collectionView]);

  const fetchSkrepBalance = () => {
    const token = getToken();
    if (!token) return;
    fetch('/skrep/balance', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setSkrepBalance(data.balance);
          setSkrepBroken(data.broken_clips);
          setGoldenClips(data.golden_clips ?? 0);
          if (data.pity_chances) setPityChances(data.pity_chances);
          setIsCrownHolder(!!data.is_crown_holder);
          setCrownEquipped(!!data.crown_equipped);
        }
      })
      .catch(() => {});
  };

  const fetchCandyBalance = () => {
    const token = getToken();
    if (!token) return;
    fetch('/candy/balance', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setCandyBalance(data.balance); })
      .catch(() => {});
    fetch('/candy/pumpkin/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPumpkinAvailable(!!data.available); })
      .catch(() => {});
  };

  const openCandyExchange = () => {
    setCandyExchangeAmount(0);
    setCandyExchangeError('');
    setCandyExchangeOpen(true);
  };

  const openReaperModal = () => {
    setReaperModalOpen(true);
    setReaperSearch('');
    setReaperError('');
    setReaperSentTo(null);
    fetch('/candy/scareable-users', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.ok ? r.json() : [])
      .then(list => setReaperUsers(list))
      .catch(() => {});
  };

  const sendReaper = async (targetUser) => {
    if (reaperSending) return;
    setReaperSending(true);
    setReaperError('');
    try {
      const r = await fetch('/candy/scare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ target_user_id: targetUser.id, scare_type: reaperType }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setReaperError(data.detail || 'Ошибка отправки');
        if (r.status === 403) {
          setReaperUsers(prev => prev.filter(u => u.id !== targetUser.id));
        }
        return;
      }
      setReaperSentTo(targetUser);
      if (data.candy_balance != null) setCandyBalance(data.candy_balance);
      setTimeout(() => setReaperModalOpen(false), 1400);
    } catch { setReaperError('Ошибка сети'); }
    finally { setReaperSending(false); }
  };

  const feedPumpkin = async () => {
    if (pumpkinFeeding) return { ok: false };
    setPumpkinFeeding(true);
    setPumpkinMessage(null);
    try {
      const r = await fetch('/candy/pumpkin/feed', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setPumpkinMessage({ ok: false, text: data.detail || 'Ошибка' });
        return { ok: false };
      }
      if (data.candy_balance != null) setCandyBalance(data.candy_balance);
      if (data.dropped) {
        setPumpkinLastDrop(data.dropped);
        setPumpkinMessage({ ok: true, text: `Тыква отрыгнула: ${data.dropped.name}!` });
        fetchInventory();
        return { ok: true, dropped: data.dropped };
      }
      return { ok: true, dropped: null };
    } catch {
      setPumpkinMessage({ ok: false, text: 'Ошибка сети' });
      return { ok: false };
    } finally {
      setPumpkinFeeding(false);
    }
  };

  const handleCandyExchange = async () => {
    if (candyExchangeBusy) return;
    // Early haptic — fires as soon as user commits, even if request slow.
    try { navigator.vibrate?.([25, 40, 25]); } catch { /* ignore */ }
    setCandyExchangeBusy(true);
    setCandyExchangeError('');
    try {
      const r = await fetch('/candy/convert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ skrep_amount: candyExchangeAmount }),
      });
      const data = await r.json();
      if (!r.ok) {
        try { navigator.vibrate?.([120]); } catch { /* ignore */ }
        setCandyExchangeError(data.detail || 'Ошибка обмена');
        return;
      }
      setSkrepBalance(data.skrep_balance);
      setCandyBalance(data.candy_balance);
      // Success — stronger haptic + burst animation
      try { navigator.vibrate?.([15, 40, 15, 40, 60]); } catch { /* ignore */ }
      const burstCount = Math.min(14, Math.max(6, Math.floor(candyExchangeAmount / 3)));
      const now = Date.now();
      const nextBurst = Array.from({ length: burstCount }).map((_, i) => ({
        id: `${now}-${i}`,
        angle: (Math.PI * 2 * i) / burstCount + (Math.random() - 0.5) * 0.4,
        distance: 80 + Math.random() * 90,
        delay: Math.random() * 90,
        emoji: ['🍬', '🍭', '🍫'][Math.floor(Math.random() * 3)],
      }));
      setCandyBurst(nextBurst);
      setTimeout(() => setCandyBurst([]), 1200);
      setTimeout(() => setCandyExchangeOpen(false), 450);
    } catch {
      try { navigator.vibrate?.([120]); } catch { /* ignore */ }
      setCandyExchangeError('Ошибка сети');
    }
    finally { setCandyExchangeBusy(false); }
  };

  useEffect(() => {
    fetchSkrepBalance();
    if (HALLOWEEN_MODE) fetchCandyBalance();
    fetchInventory();
    fetchShop();
  }, []);

  useEffect(() => {
    if (!HALLOWEEN_MODE) return;
    const onCandy = (e) => { if (e.detail?.balance != null) setCandyBalance(e.detail.balance); };
    window.addEventListener("candyCollected", onCandy);
    return () => window.removeEventListener("candyCollected", onCandy);
  }, []);

  useEffect(() => {
    if (skrepBalance !== null && !contentReadyFiredRef.current) {
      contentReadyFiredRef.current = true;
      window.dispatchEvent(new CustomEvent("pageContentReady"));
    }
  }, [skrepBalance]);

  useEffect(() => {
    if (skrepBalance === null) { setDisplayBalance(null); prevBalanceRef.current = null; return; }
    const from = prevBalanceRef.current ?? skrepBalance;
    prevBalanceRef.current = skrepBalance;
    const diff = skrepBalance - from;
    if (!diff) { setDisplayBalance(skrepBalance); return; }
    const duration = Math.min(1400, Math.max(500, Math.abs(diff) * 9));
    const startTime = performance.now();
    if (balanceAnimRef.current) cancelAnimationFrame(balanceAnimRef.current);
    const tick = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(2, -10 * t);
      setDisplayBalance(Math.round(from + diff * eased));
      if (t < 1) balanceAnimRef.current = requestAnimationFrame(tick);
      else balanceAnimRef.current = null;
    };
    balanceAnimRef.current = requestAnimationFrame(tick);
  }, [skrepBalance]);

  useEffect(() => {
    let busy = false;
    const checkUnboxes = () => {
      if (busy) return;
      busy = true;
      fetch('/skrep/pending-unboxes', { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => r.ok ? r.json() : { unboxes: [] })
        .then(({ unboxes }) => {
          busy = false;
          if (!unboxes?.length) return;
          let i = 0;
          const showNext = () => {
            if (i >= unboxes.length) return;
            const u = unboxes[i++];
            setUnboxAnim({ asset: u.asset, name: u.name, onClose: () => { setUnboxAnim(null); setTimeout(showNext, 300); } });
          };
          setTimeout(showNext, 400);
        })
        .catch(() => { busy = false; });
    };

    checkUnboxes();

    const onVisible = () => { if (document.visibilityState === 'visible') checkUnboxes(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkUnboxes);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', checkUnboxes);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.balance != null) setSkrepBalance(e.detail.balance);
      if (e.detail?.golden_clips != null) setGoldenClips(e.detail.golden_clips);
      if (e.detail?.crown_transferred) {
        setIsCrownHolder(true);
        setCrownEquipped(true);
      }
    };
    window.addEventListener("skrepCollected", handler);
    return () => window.removeEventListener("skrepCollected", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const { golden_clips, balance } = e.detail || {};
      if (balance != null) setSkrepBalance(balance);
      if (golden_clips != null) setGoldenClips(golden_clips);
    };
    window.addEventListener('goldenClipCollected', handler);
    return () => window.removeEventListener('goldenClipCollected', handler);
  }, []);

  useEffect(() => {
    let busy = false;
    const checkAwards = () => {
      if (busy) return;
      busy = true;
      fetch('/skrep/pending-awards', { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => r.ok ? r.json() : { awards: [] })
        .then(({ awards }) => {
          busy = false;
          if (!awards?.length) return;
          let i = 0;
          const showNext = () => {
            if (i >= awards.length) { fetchSkrepBalance(); return; }
            const a = awards[i++];
            setSkrepAwardAnim({ amount: a.amount, reason: a.reason, onClose: () => { setSkrepAwardAnim(null); setTimeout(showNext, 300); } });
          };
          setTimeout(showNext, 400);
        })
        .catch(() => { busy = false; });
    };
    checkAwards();
    const interval = setInterval(checkAwards, 45_000);
    const onVisible = () => { if (document.visibilityState === 'visible') checkAwards(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkAwards);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', checkAwards);
    };
  }, []);

  const fetchInventory = () => {
    const token = getToken();
    if (!token) return;
    setInventoryLoading(true);
    fetch('/skrep/inventory', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setInventory(data))
      .catch(() => {})
      .finally(() => setInventoryLoading(false));
  };

  const fetchShop = () => {
    const token = getToken();
    if (!token) return;
    setShopLoading(true);
    fetch('/skrep/shop', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setShopItems(data))
      .catch(() => {})
      .finally(() => setShopLoading(false));
    fetch('/skrep/sets', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { sets: [] })
      .then(d => setShopSets(Array.isArray(d?.sets) ? d.sets : []))
      .catch(() => {});
    fetch('/skrep/promotions', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { promotions: [] })
      .then(d => setShopPromos(Array.isArray(d?.promotions) ? d.promotions.filter(p => p.status === 'active') : []))
      .catch(() => {});
    fetch('/skrep/rotation', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { console.log('[rotation] status=', r.status); return r.ok ? r.json() : null; })
      .then(data => { console.log('[rotation] data=', data); if (data && data.reroll) setRerollInfo(data.reroll); })
      .catch(e => console.error('[rotation] err', e));
  };

  const disarmReroll = () => {
    if (rerollArmedTimerRef.current) {
      clearTimeout(rerollArmedTimerRef.current);
      rerollArmedTimerRef.current = null;
    }
    setRerollArmed(false);
  };

  const executeReroll = async () => {
    const token = getToken();
    if (!token || rerollBusy) return;
    setRerollBusy(true);
    setRerollError('');
    setRerollSpinning(true);
    const exitPromise = new Promise(res => setTimeout(res, 340));
    try {
      const [r] = await Promise.all([
        fetch('/skrep/rotation/user-reroll', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }),
        exitPromise,
      ]);
      const data = await r.json();
      console.log('[reroll] response status=', r.status, 'data=', data);
      if (r.ok) {
        if (typeof data.balance === 'number') setSkrepBalance(data.balance);
        if (data.reroll) setRerollInfo(data.reroll);
        fetchShop();
        setRerollKey(k => k + 1);
        if ('vibrate' in navigator) {
          try { navigator.vibrate([8, 40, 8]); } catch { /* ignore */ }
        }
      } else {
        setRerollError(data.detail || 'Ошибка реролла');
        setRerollDenied(true);
        if ('vibrate' in navigator) {
          try { navigator.vibrate([30, 60, 30]); } catch { /* ignore */ }
        }
        if (rerollDeniedTimerRef.current) clearTimeout(rerollDeniedTimerRef.current);
        rerollDeniedTimerRef.current = setTimeout(() => {
          setRerollDenied(false);
          rerollDeniedTimerRef.current = null;
        }, 600);
      }
    } catch (e) {
      setRerollError('Сеть недоступна');
      console.error('[reroll] fetch error:', e);
    } finally {
      setRerollBusy(false);
      setRerollSpinning(false);
      disarmReroll();
    }
  };

  const playDiceAnim = () => {
    [diceLottieRefDesktop.current, diceLottieRefMobile.current].forEach(ref => {
      if (!ref) return;
      try { ref.stop(); ref.play(); } catch {}
    });
  };
  useEffect(() => {
    if (!rerollBusy) return;
    const t = setTimeout(() => playDiceAnim(), 20);
    return () => clearTimeout(t);
  }, [rerollBusy]);
  const triggerRerollDenied = (msg) => {
    setRerollError(msg);
    setRerollDenied(true);
    if ('vibrate' in navigator) {
      try { navigator.vibrate([40, 50, 40]); } catch { /* ignore */ }
    }
    if (rerollDeniedTimerRef.current) clearTimeout(rerollDeniedTimerRef.current);
    rerollDeniedTimerRef.current = setTimeout(() => {
      setRerollDenied(false);
      rerollDeniedTimerRef.current = null;
    }, 600);
  };
  const doReroll = () => {
    if (rerollBusy) return;
    const freeLeft = rerollInfo?.free_remaining ?? 0;
    const nextCost = rerollInfo?.next_cost ?? 0;
    if (freeLeft > 0) {
      if ('vibrate' in navigator) {
        try { navigator.vibrate(10); } catch { /* ignore */ }
      }
      executeReroll();
      return;
    }
    if (nextCost <= 0) return;
    if ((skrepBalance ?? 0) < nextCost) {
      triggerRerollDenied(`Нужно ${nextCost} скрепок`);
      return;
    }
    if (!rerollArmed) {
      setRerollArmed(true);
      if ('vibrate' in navigator) {
        try { navigator.vibrate([12, 20, 12]); } catch { /* ignore */ }
      }
      if (rerollArmedTimerRef.current) clearTimeout(rerollArmedTimerRef.current);
      rerollArmedTimerRef.current = setTimeout(() => {
        setRerollArmed(false);
        rerollArmedTimerRef.current = null;
      }, 3000);
      return;
    }
    if ('vibrate' in navigator) {
      try { navigator.vibrate(14); } catch { /* ignore */ }
    }
    executeReroll();
  };

  const buyShopItem = async (item) => {
    const token = getToken();
    if (!token || shopBuying) return;
    setShopBuying(item.id);
    try {
      const r = await fetch(`/skrep/shop/${item.id}/buy`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (r.ok) {
        setSkrepBalance(data.balance);
        fetchShop();
        fetchInventory();
      } else {
        alert(data.detail || 'Ошибка покупки');
      }
    } catch { /* ignore */ }
    finally { setShopBuying(null); }
  };

  const openShopPreview = (item) => {
    setShopPreviewItem(item);
    const token = getToken();
    if (!token) return;
    fetch(`/skrep/shop/${item.id}/view`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setShopItems(prev => prev.map(i => i.id === item.id ? { ...i, views: data.views } : i)); })
      .catch(() => {});
  };

  const toggleShopLike = (item) => {
    const token = getToken();
    if (!token) return;
    setShopItems(prev => prev.map(i => i.id === item.id
      ? { ...i, user_liked: !i.user_liked, likes: (i.likes ?? 0) + (i.user_liked ? -1 : 1) }
      : i
    ));
    if (shopPreviewItem?.id === item.id) {
      setShopPreviewItem(prev => prev ? { ...prev, user_liked: !prev.user_liked, likes: (prev.likes ?? 0) + (prev.user_liked ? -1 : 1) } : prev);
    }
    fetch(`/skrep/shop/${item.id}/like`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setShopItems(prev => prev.map(i => i.id === item.id ? { ...i, user_liked: data.liked, likes: data.likes } : i));
          if (shopPreviewItem?.id === item.id) setShopPreviewItem(prev => prev ? { ...prev, user_liked: data.liked, likes: data.likes } : prev);
        }
      })
      .catch(() => {});
  };

  const submitUploadItem = async () => {
    const token = getToken();
    if (!token || uploadBusy) return;
    const _priceOk = uploadForm.isPremium
      ? (() => { const n = parseFloat(String(uploadForm.priceRub).replace(',', '.')); return !isNaN(n) && n > 0; })()
      : !!uploadForm.price;
    if (!uploadForm.name.trim() || !_priceOk || !uploadForm.cover) {
      setUploadError("Заполни обязательные поля: название, цена, обложка");
      return;
    }
    const _fmt = uploadForm.coverMeta?.format || '';
    const _keepOrig = ['gif', 'svg', 'webp', 'json'].includes(_fmt);
    const _maxBytes = _fmt === 'json' ? 4 * 1024 * 1024 : _keepOrig ? 8 * 1024 * 1024 : 20 * 1024 * 1024;
    if (uploadForm.cover.size > _maxBytes) {
      setUploadError(`Файл слишком большой (${(uploadForm.cover.size / 1024 / 1024).toFixed(1)} МБ). Максимум ${_keepOrig ? '8 МБ (GIF/SVG/WebP хранятся без сжатия)' : '20 МБ'}`);
      return;
    }
    setUploadBusy(true);
    setUploadError("");
    try {
      const fd = new FormData();
      fd.append("name", uploadForm.name.trim());
      if (uploadForm.isPremium) {
        fd.append("is_premium", "true");
        const kopeks = Math.round(parseFloat(String(uploadForm.priceRub).replace(',', '.')) * 100);
        fd.append("price_rub", String(kopeks));
      } else {
        fd.append("price", uploadForm.price);
      }
      if (uploadForm.effectType && uploadForm.effectType.trim()) {
        fd.append("effect_type", uploadForm.effectType.trim());
      }
      if (uploadForm.effectParams && uploadForm.effectParams.trim()) {
        fd.append("effect_params", uploadForm.effectParams.trim());
      }
      fd.append("item_type", uploadForm.item_type);
      fd.append("rarity", uploadForm.rarity);
      fd.append("is_halloween", String(uploadForm.category === 'halloween'));
      fd.append("description", uploadForm.description);
      fd.append("frame_offset_y", uploadForm.frameOffsetY);
      fd.append("frame_offset_x", uploadForm.frameOffsetX);
      fd.append("frame_scale", uploadForm.frameScale);
      if (uploadForm.authorUserId) {
        fd.append("author_user_id", String(uploadForm.authorUserId));
      } else if (uploadForm.authorName && uploadForm.authorName.trim()) {
        fd.append("author_name", uploadForm.authorName.trim());
      }
      fd.append("cover", uploadForm.cover);
      const r = await fetch("/skrep/admin/items/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setUploadError(data.detail || `Ошибка ${r.status}`); return; }
      setShowUploadModal(false);
      setUploadForm({ name: "", price: "", description: "", item_type: "badge", rarity: "common", category: "regular", cover: null, coverPreview: null, coverMeta: null, frameOffsetY: 0, frameOffsetX: 0, frameScale: 150, authorName: "", authorUserId: null, authorUserFio: "", authorUserAvatar: null, isPremium: false, priceRub: "", effectType: "", effectParams: "" });
      setAuthorSearch("");
      fetchShop();
    } catch { setUploadError("Ошибка сети"); }
    finally { setUploadBusy(false); }
  };

  const handleDeleteItem = async (item, e) => {
    e?.stopPropagation();
    const token = localStorage.getItem('access_token');
    const r = await fetch(`/skrep/admin/items/${item.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) { setShopItems(prev => prev.filter(i => i.id !== item.id)); fetchInventory(); }
  };

  const handleOpenEditModal = (item, e) => {
    e.stopPropagation();
    setEditModal(item);
    setEditForm({ name: item.name, description: item.description || "", price: String(item.price), rarity: item.rarity, cover: null, coverPreview: null, clear_author: false, is_anonymous: !!item.is_anonymous });
    setEditError("");
  };

  const handleEditSubmit = async () => {
    if (!editModal) return;
    const token = localStorage.getItem('access_token');
    setEditBusy(true);
    setEditError("");
    try {
      const fd = new FormData();
      fd.append("name", editForm.name.trim());
      fd.append("description", editForm.description);
      fd.append("price", editForm.price);
      fd.append("rarity", editForm.rarity);
      if (editForm.clear_author) fd.append("clear_author", "true");
      fd.append("is_anonymous", editForm.is_anonymous ? "true" : "false");
      if (editForm.cover) fd.append("cover", editForm.cover);
      const r = await fetch(`/skrep/admin/items/${editModal.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setEditError(data.detail || `Ошибка ${r.status}`); return; }
      setShopItems(prev => prev.map(i => i.id === editModal.id ? { ...i, ...data } : i));
      setEditModal(null);
    } catch { setEditError("Ошибка сети"); }
    finally { setEditBusy(false); }
  };

  const handleOpenCase = async (mode, caseData) => {
    if (caseBusyRef.current) return;
    caseBusyRef.current = true;
    setCaseBusy(true);
    setCaseResult(null);
    setOpenAnim({ phase: 'drop', result: null, caseData });
    setTimeout(() => setOpenAnim(s => s ? { ...s, phase: 'shake' } : null), 850);
    const [apiResult] = await Promise.all([
      fetch('/skrep/open', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ mode, case_type: caseData?.case_type ?? 'random' }),
      }).then(async r => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.detail || 'Ошибка');
        return data;
      }).catch(e => ({ error: e.message })),
      new Promise(r => setTimeout(r, 2600)),
    ]);
    setOpenAnim(s => {
      if (!s) return null;
      const next = { ...s, result: apiResult };
      if (s.phase === 'anim_done') return { ...next, phase: 'reveal' };
      return next;
    });
    if (!apiResult.error) {
      setSkrepBalance(apiResult.balance);
      setSkrepBroken(apiResult.broken_clips ?? 0);
      if (apiResult.win_chance_next != null) {
        setPityChances(prev => ({ ...prev, [caseData?.case_type ?? 'random']: apiResult.win_chance_next }));
      }
      if (apiResult.result === 'win') {
        fetchInventory();
        if (apiResult.item?.item_id) {
          const wonId = apiResult.item.item_id;
          setShopItems(items => items.map(i => i.id === wonId ? { ...i, owned: true } : i));
        }
      }
    }
    caseBusyRef.current = false;
    setCaseBusy(false);
  };

  const handleOpenCaseGolden = async (caseData) => {
    if (caseBusyRef.current) return;
    const cost = caseData?.case_type === 'random' ? 2 : 1;
    if ((goldenClips ?? 0) < cost) return;
    caseBusyRef.current = true;
    setCaseBusy(true);
    setCaseResult(null);
    setOpenAnim({ phase: 'drop', result: null, caseData, golden: true });
    setTimeout(() => setOpenAnim(s => s ? { ...s, phase: 'shake' } : null), 850);
    const [apiResult] = await Promise.all([
      fetch('/skrep/open', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ mode: 'golden', case_type: caseData?.case_type ?? 'random' }),
      }).then(async r => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.detail || 'Ошибка');
        return data;
      }).catch(e => ({ error: e.message })),
      new Promise(r => setTimeout(r, 2600)),
    ]);
    setOpenAnim(s => {
      if (!s) return null;
      const next = { ...s, result: apiResult };
      if (s.phase === 'anim_done') return { ...next, phase: 'reveal' };
      return next;
    });
    if (!apiResult.error) {
      setSkrepBalance(apiResult.balance);
      setSkrepBroken(apiResult.broken_clips ?? 0);
      setGoldenClips(apiResult.golden_clips ?? 0);
      if (apiResult.win_chance_next != null) {
        setPityChances(prev => ({ ...prev, [caseData?.case_type ?? 'random']: apiResult.win_chance_next }));
      }
      if (apiResult.result === 'win') {
        fetchInventory();
        if (apiResult.item?.item_id) {
          const wonId = apiResult.item.item_id;
          setShopItems(items => items.map(i => i.id === wonId ? { ...i, owned: true } : i));
        }
      }
    }
    caseBusyRef.current = false;
    setCaseBusy(false);
  };

  const getSellPrice = (item) => item.sell_price ?? Math.max(1, Math.floor((item.price || 10) / 20));

  const handleSell = async () => {
    if (!sellConfirm) return;
    const { item, cardEl } = sellConfirm;
    setSellConfirm(null);
    const cardRect = cardEl?.getBoundingClientRect();
    const balEl = document.getElementById('skrep-balance-display');
    if (cardRect && balEl) {
      const balRect = balEl.getBoundingClientRect();
      setSellAnim({
        sx: cardRect.left + cardRect.width / 2,
        sy: cardRect.top + cardRect.height / 2,
        tx: balRect.left + balRect.width / 2,
        ty: balRect.top + balRect.height / 2,
      });
      setTimeout(() => setSellAnim(null), 1000);
    }
    try {
      const r = await fetch('/skrep/sell', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ item_id: item.item_id }),
      });
      if (!r.ok) return;
      const data = await r.json();
      setSkrepBalance(data.new_balance);
      setInventory(prev => {
        const idx = prev.findIndex(i => i.inventory_id === item.inventory_id);
        if (idx === -1) return prev;
        const updated = [...prev];
        if (updated[idx].duplicate_count > 0) {
          updated[idx] = { ...updated[idx], duplicate_count: updated[idx].duplicate_count - 1, is_equipped: false };
        } else {
          updated.splice(idx, 1);
        }
        return updated;
      });
      if (item.is_equipped) {
        const meRes = await fetch('/auth/me', { headers: { Authorization: `Bearer ${getToken()}` } });
        if (meRes.ok) { const d = await meRes.json(); setUser(prev => ({ ...prev, ...d })); }
      }
    } catch (_) {}
  };

  const openGiftModal = (item) => {
    setGiftModal(item);
    setGiftEmail('');
    setGiftRecipient(null);
    setGiftLookupError('');
    setGiftAnonymous(false);
    setGiftSuggestions([]);
    fetch('/auth/users', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        setGiftSuggestions(list.filter(u => u.id !== user?.id));
      })
      .catch(() => {});
  };

  const openAwardModal = () => {
    setAwardModal(true);
    setAwardAmount('');
    setAwardReason('');
    setAwardSearch('');
    setAwardRecipients([]);
    setAwardError('');
    fetch('/auth/users', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.ok ? r.json() : [])
      .then(list => setAwardSuggestions(list.filter(u => u.id !== user?.id)))
      .catch(() => {});
  };

  const handleAward = async () => {
    if (!awardRecipients.length || !awardAmount || !awardReason.trim()) {
      setAwardError('Заполни все поля и добавь получателей'); return;
    }
    setAwardBusy(true); setAwardError('');
    try {
      const r = await fetch('/skrep/admin/award', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: awardRecipients.map(u => u.id), amount: parseInt(awardAmount), reason: awardReason }),
      });
      const data = await r.json();
      if (!r.ok) { setAwardError(data.detail || 'Ошибка'); return; }
      setAwardModal(false);
      setGiftAnim({ name: `🏆 ${awardReason} · +${data.amount} 📎 × ${data.awarded}` });
      setTimeout(() => setGiftAnim(null), 2600);
    } catch { setAwardError('Ошибка сети'); }
    finally { setAwardBusy(false); }
  };

  const lookupGiftRecipient = async () => {
    if (!giftEmail.trim()) return;
    setGiftBusy(true);
    setGiftLookupError('');
    try {
      const r = await fetch(`/auth/find-user?email=${encodeURIComponent(giftEmail.trim())}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setGiftLookupError(data.detail || 'Пользователь не найден'); return; }
      setGiftRecipient(data);
    } catch (_) { setGiftLookupError('Ошибка сети'); }
    finally { setGiftBusy(false); }
  };

  const handleGift = async () => {
    if (!giftModal || !giftRecipient || giftBusy) return;
    setGiftBusy(true);
    const animItem = giftModal;
    setGiftModal(null);
    setGiftRecipient(null);
    setGiftAnim({ asset: animItem.asset, name: animItem.name });
    setTimeout(() => setGiftAnim(null), 2600);
    try {
      const r = await fetch('/skrep/gift', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ item_id: animItem.item_id, recipient_id: giftRecipient.id, anonymous: giftAnonymous }),
      });
      if (!r.ok) return;
      setInventory(prev => {
        const idx = prev.findIndex(i => i.inventory_id === animItem.inventory_id);
        if (idx === -1) return prev;
        const updated = [...prev];
        if (updated[idx].duplicate_count > 0) {
          updated[idx] = { ...updated[idx], duplicate_count: updated[idx].duplicate_count - 1, is_equipped: false };
        } else {
          updated.splice(idx, 1);
        }
        return updated;
      });
      if (animItem.is_equipped) {
        const meRes = await fetch('/auth/me', { headers: { Authorization: `Bearer ${getToken()}` } });
        if (meRes.ok) { const d = await meRes.json(); setUser(prev => ({ ...prev, ...d })); }
      }
    } catch (_) {}
    finally { setGiftBusy(false); }
  };

  const handleEquip = async (item) => {
    try {
      const res = await fetch('/skrep/equip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ item_id: item.is_equipped ? null : item.item_id, slot: item.item_type }),
      });
      if (!res.ok) return;
      fetchInventory();
      // Refresh user profile to apply banner/frame changes
      const meRes = await fetch('/auth/me', { headers: { Authorization: `Bearer ${getToken()}` } });
      if (meRes.ok) {
        const meData = await meRes.json();
        setUser(prev => ({ ...prev, ...meData }));
      }
    } catch (_) {}
  };


  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch('/auth/me/preferences', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setFavoriteGroups(data.favorite_groups || []); });
  }, []);

  // --- DEVICES / SESSIONS ---
  const handleFetchSessions = () => {
    setSessionsLoading(true);
    setSessionsError('');
    fetch('/auth/me/sessions', { headers: getAuthHeaders() })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        setSessions(data.sessions || []);
      })
      .catch(() => {
        setSessionsError('Не удалось загрузить сеансы');
      })
      .finally(() => setSessionsLoading(false));
  };

  const _silentRefreshSessions = () => {
    if (!navigator.onLine) return;
    fetch('/auth/me/sessions', { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setSessions(data.sessions || []);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!devicesOpen) return;
    const id = setInterval(_silentRefreshSessions, 15_000);
    const onVis = () => { if (!document.hidden) _silentRefreshSessions(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [devicesOpen]);

  // esc closes topmost overlay
  useEffect(() => {
    if (!devicesOpen) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (sessionConfirm) { setSessionConfirm(null); return; }
      if (sessionDetail) { setSessionDetail(null); return; }
      setDevicesOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [devicesOpen, sessionConfirm, sessionDetail]);

  const _hap = (ms = 10) => { try { navigator.vibrate?.(ms); } catch (_) {} };

  const _doRevokeSession = async (sid) => {
    setRevokeLoadingId(sid);
    try {
      const res = await fetch(`/auth/me/sessions/${sid}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.sid !== sid));
        setSessionDetail(null);
        showSessionToast('Сеанс завершён', 'ok');
        _hap(14);
      } else {
        showSessionToast('Не удалось завершить сеанс', 'err');
      }
    } catch (_) {
      showSessionToast('Нет связи с сервером', 'err');
    }
    finally { setRevokeLoadingId(null); }
  };

  const _doRevokeAllSessions = async () => {
    try {
      const res = await fetch('/auth/me/sessions', {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setSessions(prev => prev.filter(s => s.is_current));
        showSessionToast(`Завершено сеансов: ${data.revoked ?? '—'}`, 'ok');
        _hap(18);
      } else {
        showSessionToast('Не удалось завершить сеансы', 'err');
      }
    } catch (_) {
      showSessionToast('Нет связи с сервером', 'err');
    }
  };

  const handleRevokeSession = (sid) => {
    const s = sessions.find(x => x.sid === sid);
    setSessionConfirm({ kind: 'one', sid, label: s?.device_name || 'сеанс' });
  };

  const handleRevokeAllSessions = () => {
    const count = sessions.filter(s => !s.is_current).length;
    if (!count) return;
    setSessionConfirm({ kind: 'all', count });
  };

  // --- EMAIL CHANGE ---
  const [emailChangeStep, setEmailChangeStep] = useState('idle'); // idle | input | sending | code | confirming | done
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailChangeCode, setEmailChangeCode] = useState('');
  const [emailChangeErr, setEmailChangeErr] = useState('');
  const [emailChangeCooldown, setEmailChangeCooldown] = useState(0);

  useEffect(() => {
    if (emailChangeCooldown <= 0) return;
    const t = setInterval(() => setEmailChangeCooldown(v => v - 1), 1000);
    return () => clearInterval(t);
  }, [emailChangeCooldown]);

  const handleRequestEmailChange = async () => {
    if (!newEmailInput.trim() || !newEmailInput.includes('@')) {
      setEmailChangeErr('Введите корректный email'); return;
    }
    setEmailChangeStep('sending'); setEmailChangeErr('');
    try {
      const res = await fetch('/auth/me/email/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ new_email: newEmailInput.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : 'Ошибка отправки кода';
        setEmailChangeErr(detail); setEmailChangeStep('input'); return;
      }
      setEmailChangeStep('code'); setEmailChangeCooldown(60);
    } catch {
      setEmailChangeErr('Не удалось отправить код'); setEmailChangeStep('input');
    }
  };

  const handleConfirmEmailChange = async () => {
    if (emailChangeCode.trim().length < 6) { setEmailChangeErr('Введите 6-значный код'); return; }
    setEmailChangeStep('confirming'); setEmailChangeErr('');
    try {
      const res = await fetch('/auth/me/email/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ code: emailChangeCode.trim().toUpperCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : 'Неверный код';
        setEmailChangeErr(detail); setEmailChangeStep('code'); return;
      }
      setUser(prev => ({ ...prev, email: data.email, email_verified: true }));
      setEmailChangeStep('done');
    } catch {
      setEmailChangeErr('Ошибка при смене email'); setEmailChangeStep('code');
    }
  };

  // --- EMAIL VERIFICATION ---
  const [verifyStep, setVerifyStep] = useState('idle'); // idle | sending | input | confirming | done
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyErr, setVerifyErr] = useState('');
  const [verifyCooldown, setVerifyCooldown] = useState(0);

  useEffect(() => {
    if (verifyCooldown <= 0) return;
    const t = setInterval(() => setVerifyCooldown(v => v - 1), 1000);
    return () => clearInterval(t);
  }, [verifyCooldown]);

  const handleSendVerifyCode = async () => {
    setVerifyStep('sending'); setVerifyErr('');
    try {
      await fetch('/auth/verify-email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ email: user.email }),
      });
      setVerifyStep('input');
      setVerifyCooldown(60);
    } catch {
      setVerifyErr('Не удалось отправить код'); setVerifyStep('idle');
    }
  };

  const handleConfirmVerifyCode = async () => {
    if (verifyCode.trim().length < 6) { setVerifyErr('Введите 6-значный код'); return; }
    setVerifyStep('confirming'); setVerifyErr('');
    try {
      const res = await fetch('/auth/verify-email/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ email: user.email, code: verifyCode.trim().toUpperCase() }),
      });
      if (res.ok) {
        setUser(prev => ({ ...prev, email_verified: true }));
        setVerifyStep('done');
      } else {
        const data = await res.json().catch(() => ({}));
        const detail = typeof data.detail === 'string' ? data.detail : 'Неверный код';
        setVerifyErr(detail); setVerifyStep('input');
      }
    } catch {
      setVerifyErr('Ошибка при проверке кода'); setVerifyStep('input');
    }
  };

  // --- PUSH NOTIFICATIONS ---
  const [pushSupported] = useState(() =>
    'serviceWorker' in navigator && 'PushManager' in window
  );
  const [pushEnabled, setPushEnabled] = useState(null); // null = ещё проверяем
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState("");
  const [pushBlockedModal, setPushBlockedModal] = useState(false);
  const [clipPushEnabled, setClipPushEnabled] = useState(false);
  const [clipPushSecretVisible, setClipPushSecretVisible] = useState(false);
  const clipPushLongRef = useRef(null);

  // --- GRADIENT CUSTOMIZATION ---
  const GRADIENTS = [
    { id: 'blue',   bg: 'linear-gradient(135deg,#3b82f6 0%,#2563eb 60%,#7c3aed 100%)', dot: '#3b82f6' },
    { id: 'ocean',  bg: 'linear-gradient(135deg,#06b6d4 0%,#0ea5e9 60%,#6366f1 100%)', dot: '#06b6d4' },
    { id: 'green',  bg: 'linear-gradient(135deg,#10b981 0%,#059669 60%,#0891b2 100%)', dot: '#10b981' },
    { id: 'rose',   bg: 'linear-gradient(135deg,#f43f5e 0%,#ec4899 60%,#a855f7 100%)', dot: '#f43f5e' },
    { id: 'orange', bg: 'linear-gradient(135deg,#f97316 0%,#ef4444 60%,#dc2626 100%)', dot: '#f97316' },
    { id: 'dark',   bg: 'linear-gradient(135deg,#52555e 0%,#2a2c32 60%,#1a1b1f 100%)', dot: '#475569' },
  ];
  const [gradientId, setGradientId] = useState(
    () => user?.profile_gradient || localStorage.getItem('profileGradient') || 'blue'
  );

  useEffect(() => {
    if (user?.profile_gradient && user.profile_gradient !== gradientId) {
      setGradientId(user.profile_gradient);
    }
  }, [user?.profile_gradient]);

  useEffect(() => {
    if (user?.profile_text_dark !== undefined) {
      setProfileTextDark(!!user.profile_text_dark);
    }
  }, [user?.profile_text_dark]);

  useEffect(() => {
    if (user?.avatar_position) {
      const [x, y] = user.avatar_position.split(' ').map(Number);
      if (!isNaN(x) && !isNaN(y)) setAvatarPos({ x, y });
    }
  }, [user?.avatar_position]);
  const currentGradient = GRADIENTS.find(g => g.id === gradientId) || GRADIENTS[0];

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const original = meta.getAttribute('content');
    meta.setAttribute('content', isDark ? '#000000' : '#ffffff');
    return () => meta.setAttribute('content', original);
  }, [isDark]);

  // Check current push subscription status on mount (no permission request)
  useEffect(() => {
    if (!pushSupported || !user) return;
    navigator.serviceWorker.ready.then(async reg => {
      const sub = await reg.pushManager.getSubscription();
      setPushEnabled(!!sub);
    });
  }, [pushSupported, user]);

  useEffect(() => {
    if (user?.clip_push_enabled != null) setClipPushEnabled(Boolean(user.clip_push_enabled));
  }, [user?.clip_push_enabled]);

  const handlePushToggle = async () => {
    if (!pushSupported || pushBusy) return;
    setPushBusy(true);
    setPushError("");
    try {
      const swTimeout = new Promise((_, rej) =>
        setTimeout(() => rej(new Error('SW timeout')), 6000)
      );
      const reg = await Promise.race([navigator.serviceWorker.ready, swTimeout]);

      if (pushEnabled) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const endpoint = sub.endpoint;
          await sub.unsubscribe();
          await fetch('/auth/me/push-subscription', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ endpoint }),
          });
        }
        setPushEnabled(false);
      } else {
        if (Notification.permission === 'denied') {
          setPushBlockedModal(true);
          return;
        }
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          setPushBlockedModal(true);
          return;
        }

        const keyRes = await fetch('/auth/vapid-public-key');
        const { public_key } = await keyRes.json();

        const padding = '='.repeat((4 - public_key.length % 4) % 4);
        const raw = atob(public_key.replace(/-/g, '+').replace(/_/g, '/') + padding);
        const vapidKey = new Uint8Array([...raw].map(c => c.charCodeAt(0)));

        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidKey,
        });

        await fetch('/auth/me/push-subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(sub.toJSON()),
        });
        setPushEnabled(true);
      }
    } catch (err) {
      console.error('Push toggle error:', err);
      setPushError(err?.message || 'Ошибка подписки');
    } finally {
      setPushBusy(false);
    }
  };

  const handleClipPushToggle = async () => {
    const next = !clipPushEnabled;
    setClipPushEnabled(next);
    try {
      await fetch('/auth/me/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ clip_push_enabled: next }),
      });
    } catch {
      setClipPushEnabled(!next);
    }
  };

  const startClipLongPress = () => {
    clipPushLongRef.current = setTimeout(() => {
      setClipPushSecretVisible(true);
      clipPushLongRef.current = null;
    }, 700);
  };
  const cancelClipLongPress = () => {
    if (clipPushLongRef.current) {
      clearTimeout(clipPushLongRef.current);
      clipPushLongRef.current = null;
    }
  };

  useEffect(() => {
    if (!needsGroupSelection) return;
    setGroupModal(true);
    fetch('/schedule/all-entities?source=all')
      .then(r => r.json())
      .then(data => {
        setGroups(data.groups || []);
      });
  }, [needsGroupSelection]);
  useEffect(() => {
    setAvatar(resolveAvatarSrc(user?.avatar_url));
  }, [user?.avatar_url]);

  useEffect(() => {
    if (user?.banner_url) {
      setBannerUrl(`${window.location.origin}${user.banner_url.split('?')[0]}?v=${Date.now()}`);
    } else {
      setBannerUrl(null);
    }
  }, [user?.banner_url]);

  useEffect(() => {
    if (user?.banner_zoom != null) setBannerZoom(Number(user.banner_zoom) || 1);
  }, [user?.banner_zoom]);

  useEffect(() => {
    if (user?.banner_zoom_mobile != null) setBannerZoomMobile(Number(user.banner_zoom_mobile) || 1);
  }, [user?.banner_zoom_mobile]);

  useEffect(() => {
    if (user?.banner_position) {
      const [x, y] = user.banner_position.split(' ').map(Number);
      if (!isNaN(x) && !isNaN(y)) {
        const pos = { x, y };
        setBannerPos(pos);
        localStorage.setItem('bannerPos', JSON.stringify(pos));
      }
    }
  }, [user?.banner_position]);

  useEffect(() => {
    if (user?.banner_position_mobile) {
      const [x, y] = user.banner_position_mobile.split(' ').map(Number);
      if (!isNaN(x) && !isNaN(y)) {
        const pos = { x, y };
        setBannerPosMobile(pos);
        localStorage.setItem('bannerPosMobile', JSON.stringify(pos));
      }
    }
  }, [user?.banner_position_mobile]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = e => setIsRealMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    document.body.classList.add('account-page');
    return () => document.body.classList.remove('account-page');
  }, []);

  useEffect(() => {
    if (editView) {
      document.body.classList.add('edit-panel-open');
    } else {
      document.body.classList.remove('edit-panel-open');
    }
    return () => {
      document.body.classList.remove('edit-panel-open');
    };
  }, [editView]);

  const processAvatarFile = (file) => {
    if (!file) return;
    setAvatarError("");
    if (!file.type.startsWith("image/")) {
      setAvatarError("Пожалуйста, выберите файл изображения");
      return;
    }
    setAvatarBusy(true);
    const rawUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(rawUrl);
      const MAX = 2048;
      let w = img.naturalWidth, h = img.naturalHeight;
      if (Math.max(w, h) > MAX) {
        const r = MAX / Math.max(w, h);
        w = Math.round(w * r);
        h = Math.round(h * r);
      }
      const cv = document.createElement('canvas');
      cv.width = w; cv.height = h;
      cv.getContext('2d').drawImage(img, 0, 0, w, h);
      cv.toBlob(blob => {
        setAvatarBusy(false);
        if (blob) {
          setEditorSrc(URL.createObjectURL(blob));
          setEditorOpen(true);
        } else {
          setAvatarError("Не удалось обработать изображение");
        }
      }, 'image/jpeg', 0.92);
    };
    img.onerror = () => {
      URL.revokeObjectURL(rawUrl);
      setAvatarBusy(false);
      setAvatarError("Не удалось загрузить фото. Скачайте его на устройство и попробуйте снова");
    };
    img.src = rawUrl;
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    processAvatarFile(file);
  };


  const handleEditorConfirm = async (blob) => {
    setEditorOpen(false);
    if (editorSrc) { URL.revokeObjectURL(editorSrc); setEditorSrc(null); }
    if (!blob) { setAvatarError("Не удалось обработать изображение"); return; }
    setAvatarBusy(true);
    setAvatarError("");
    try {
      const formData = new FormData();
      formData.append("file", blob, "avatar.jpg");
      const res = await fetch(`${window.location.origin}/auth/avatar`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
        body: formData,
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok || !payload) throw new Error(payload?.detail || `Ошибка ${res.status}`);
      const updatedUser = payload?.avatar_url
        ? { ...payload, avatar_url: `${payload.avatar_url}?v=${Date.now()}` }
        : payload;
      setUser(prev => ({ ...prev, ...updatedUser }));
      setAvatar(resolveAvatarSrc(updatedUser?.avatar_url));
    } catch (err) {
      setAvatarError(err?.message || "Не удалось загрузить аватар");
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleAvatarRemove = () => {
    if (!avatar || avatarBusy) return;
    setAvatarRemoveOpen(true);
  };

  const confirmAvatarRemove = async () => {
    setAvatarRemoveOpen(false);
    setAvatarBusy(true);
    setAvatarError("");
    try {
      const res = await fetch(`${window.location.origin}/auth/avatar`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.detail || `Ошибка ${res.status}`);
      setUser(prev => ({ ...prev, ...(payload || {}), avatar_url: null }));
      setAvatar(null);
    } catch (err) {
      setAvatarError(err?.message || "Не удалось убрать фото");
    } finally {
      setAvatarBusy(false);
    }
  };

  useEffect(() => {
    if (!editView || !user) return;
    const parts = (user?.fio || '').trim().split(/\s+/);
    setEditLastName(parts[0] || '');
    setEditFirstName(parts[1] || '');
    setEditPatronymic(parts[2] || '');
    setEditUsername(user?.username || '');
    setEditStudyGroup(user?.study_group || '');
    setEditFavoriteGroups(favoriteGroups);
    setEditGradientId(gradientId);
    setEditTextDark(profileTextDark);
    setBannerPreview(bannerUrl || null);
    setBannerError("");
    setBannerPos(prev => prev);
    if (groups.length === 0) {
      fetch('/schedule/all-entities?source=all')
        .then(r => r.json())
        .then(data => {
          setGroups(data.groups || []);
        });
    }
  }, [editView]);

  const handleSave = async () => {
    setSaveBusy(true);
    try {
      const fio = [editLastName.trim(), editFirstName.trim(), editPatronymic.trim()].filter(Boolean).join(' ');
      const res = await fetch('/auth/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          fio,
          username: editUsername.trim() || undefined,
          study_group: editStudyGroup || null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        // Применяем градиент и сохраняем в preferences
        const prefPatch = {};
        if (editGradientId !== gradientId) {
          setGradientId(editGradientId);
          localStorage.setItem('profileGradient', editGradientId);
          prefPatch.profile_gradient = editGradientId;
        }
        if (editTextDark !== profileTextDark) {
          setProfileTextDark(editTextDark);
          prefPatch.profile_text_dark = editTextDark;
        }
        const finalFavoriteGroups =
          editStudyGroup && !editFavoriteGroups.includes(editStudyGroup)
            ? [editStudyGroup, ...editFavoriteGroups]
            : editFavoriteGroups;
        prefPatch.favorite_groups = finalFavoriteGroups;
        fetch('/auth/me/preferences', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(prefPatch),
        });
        setFavoriteGroups(finalFavoriteGroups);
        setUser(prev => ({
          ...prev,
          ...data,
          avatar_url: prev.avatar_url,
          avatar_frame: prev.avatar_frame,
          avatar_position: prev.avatar_position,
          equipped_badge_asset: prev.equipped_badge_asset,
          dev_badge_equipped: prev.dev_badge_equipped,
          profile_gradient: editGradientId,
          profile_text_dark: editTextDark,
          study_group: editStudyGroup || null,
        }));
      }
    } catch (e) {
      console.error('Save error:', e);
    } finally {
      setSaveBusy(false);
      setEditView(false);
    }
  };

  const isAdmin = user?.role === "admin";

  const isFioValid = (fio) => {
    if (!fio?.trim()) return false;
    const words = fio.trim().split(/\s+/);
    if (words.length < 2 || words.length > 3) return false;
    return words.every(w => w.split('-').every(p => /^[А-ЯЁ][а-яё]{1,}$/.test(p)));
  };

  return (
    <>
    {avatarRemoveOpen && createPortal(
      <div
        className="fixed inset-0 z-[10000] backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
        style={{ animation: 'fadeIn 150ms ease-out', background: isDark ? 'rgba(0,0,0,0.65)' : 'rgba(0,0,0,0.55)' }}
        onClick={() => setAvatarRemoveOpen(false)}
      >
        <div
          onClick={e => e.stopPropagation()}
          className="w-full max-w-[320px] rounded-3xl overflow-hidden"
          style={{
            animation: 'zoomIn95 160ms ease-out',
            background: isDark ? '#1c1c1e' : '#ffffff',
            boxShadow: isDark
              ? '0 25px 50px -12px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.06)'
              : '0 25px 50px -12px rgba(0,0,0,0.25)',
          }}
        >
          <div className="px-6 pt-6 pb-5 text-center">
            <div
              className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full"
              style={{ background: isDark ? 'rgba(239,68,68,0.16)' : '#fef2f2' }}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                style={{ color: isDark ? '#f87171' : '#ef4444' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 3h4a1 1 0 011 1v3H9V4a1 1 0 011-1z" />
              </svg>
            </div>
            <h3
              className="text-[17px] font-semibold leading-tight"
              style={{ color: isDark ? '#f5f5f7' : '#0f172a' }}
            >
              Убрать фото профиля?
            </h3>
            <p
              className="mt-1.5 text-[13px] leading-snug"
              style={{ color: isDark ? 'rgba(235,235,245,0.6)' : '#64748b' }}
            >
              Аватар заменится на инициалы. Вы сможете загрузить новое фото в любой момент.
            </p>
          </div>
          <div
            className="grid grid-cols-2"
            style={{ borderTop: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9' }}
          >
            <button
              type="button"
              onClick={() => setAvatarRemoveOpen(false)}
              className="py-3.5 text-[15px] font-medium transition-colors avatar-rm-cancel"
              style={{
                color: isDark ? 'rgba(235,235,245,0.78)' : '#475569',
                borderRight: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f1f5f9',
              }}
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={confirmAvatarRemove}
              className="py-3.5 text-[15px] font-semibold transition-colors avatar-rm-confirm"
              style={{ color: isDark ? '#f87171' : '#dc2626' }}
            >
              Убрать
            </button>
          </div>
        </div>
        <style>{`
          @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
          @keyframes zoomIn95 { from { opacity: 0; transform: scale(0.95) } to { opacity: 1; transform: scale(1) } }
          .avatar-rm-cancel:hover { background: ${isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc'}; }
          .avatar-rm-cancel:active { background: ${isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}; }
          .avatar-rm-confirm:hover { background: ${isDark ? 'rgba(239,68,68,0.10)' : '#fef2f2'}; }
          .avatar-rm-confirm:active { background: ${isDark ? 'rgba(239,68,68,0.18)' : '#fee2e2'}; }
        `}</style>
      </div>,
      document.body
    )}
    {groupModal && (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
              <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-300">
                {/* Шапка */}
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 px-8 py-8 text-white text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 backdrop-blur">
                    <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold">
                    {user?.is_curator ? 'Выберите группу под кураторством' : 'Выберите вашу группу'}
                  </h2>
                  <p className="mt-2 text-sm text-blue-200">
                    {user?.is_curator
                      ? 'Расписание этой группы будет доступно быстро'
                      : 'Начните вводить название группы'}
                  </p>
                </div>

                {/* Поле ввода с подсказками */}
                <div className="px-6 pt-6 pb-4 flex flex-col gap-3">
                  {selectedGroup && (
                    <div className="flex items-center justify-between bg-blue-50 px-4 py-3 rounded-2xl">
                      <span className="text-sm font-semibold text-blue-700">Выбрана: {selectedGroup}</span>
                      <button type="button" onClick={() => setSelectedGroup("")} className="text-xs font-medium text-red-500 hover:text-red-700">
                        ✕ Сбросить
                      </button>
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="Например: ИС-31..."
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    className="w-full h-14 px-5 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:bg-white rounded-2xl outline-none text-slate-900 placeholder:text-slate-400 shadow-sm text-lg"
                    autoFocus
                  />

                  {groupSearch && filteredGroups.length > 0 && !selectedGroup && (
                    <div className="rounded-2xl border border-slate-200 overflow-hidden">
                      <div className="max-h-52 overflow-y-auto divide-y divide-slate-100">
                        {filteredGroups.map(g => (
                          <button
                            key={g}
                            type="button"
                            onClick={() => { setSelectedGroup(g); setGroupSearch(g); }}
                            className="w-full text-left px-5 py-3.5 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                          >
                            {g}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {!groupSearch && groups.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Популярные группы</p>
                      <div className="flex flex-wrap gap-2">
                        {groups.slice(0, 8).map(g => (
                          <button
                            key={g}
                            type="button"
                            onClick={() => { setSelectedGroup(g); setGroupSearch(g); }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              selectedGroup === g ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-blue-100'
                            }`}
                          >
                            {g}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Кнопки */}
                <div className="px-6 pb-6 flex gap-3">
                  <button
                    onClick={() => setGroupModal(false)}
                    className="flex-1 h-12 rounded-2xl text-sm font-semibold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors"
                  >
                    Позже
                  </button>
                  <button
                    disabled={!selectedGroup}
                    onClick={async () => {
                      const res = await fetch('/auth/me/group', {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                        body: JSON.stringify({ group: selectedGroup }),
                      });
                      if (!res.ok) return;
                      const prefsRes = await fetch('/auth/me/preferences', {
                        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                      });
                      const existingPrefs = prefsRes.ok ? await prefsRes.json() : {};
                      const existingFavorites = existingPrefs.favorite_groups || [];
                      if (!existingFavorites.includes(selectedGroup)) {
                        await fetch('/auth/me/preferences', {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                          body: JSON.stringify({ favorite_groups: [selectedGroup, ...existingFavorites] }),
                        });
                      }
                      setGroupModal(false);
                      window.location.reload();
                    }}
                    className={`flex-1 h-12 rounded-2xl text-sm font-semibold transition-all ${
                      selectedGroup
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    Сохранить
                  </button>
                </div>
              </div>
            </div>
          )}
    <div
      ref={node => { if (node && !profileContainerEl) setProfileContainerEl(node); }}
      className="-mt-8 -mx-3 sm:mx-0 relative sm:[overflow-x:clip]"
      style={(editView || devicesOpen) ? { minHeight: 'calc(100vh - 80px)' } : undefined}
    >
      {/* ── PROFILE PANEL ── */}
      <div
        className="transition-transform duration-300 ease-in-out"
        style={(editView || devicesOpen || skrepView || collectionView) ? { transform: 'translateX(-100%)' } : {}}
      >
      {/* HERO CARD — весь градиентный */}
      <section
        className="relative isolate rounded-none sm:rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.15)] transition-all duration-500"
      >
        {/* Фоновый слой — обрезается по скруглениям, но не обрезает рамку аватара */}
        <div className="absolute inset-0 rounded-none sm:rounded-2xl overflow-hidden" style={{ zIndex: -1 }}>
          {bannerUrl ? (() => {
            const effPos = isRealMobile ? (bannerPosMobile || bannerPos) : bannerPos;
            const effZoom = isRealMobile ? (bannerPosMobile ? bannerZoomMobile : bannerZoom) : bannerZoom;
            const isVideo = bannerUrl.split('?')[0].toLowerCase().endsWith('.mp4');
            if (isVideo) {
              return (<>
                <video src={bannerUrl} autoPlay muted loop playsInline
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(22px) brightness(0.55) saturate(1.4)', transform: 'scale(1.08)', transformOrigin: 'center' }} />
                <video src={bannerUrl} autoPlay muted loop playsInline
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: effZoom === 1 ? 'cover' : 'contain', objectPosition: `${effPos.x}% ${effPos.y}%`, transform: effZoom === 1 ? 'none' : `scale(${effZoom})`, transformOrigin: `${effPos.x}% ${effPos.y}%` }} />
              </>);
            }
            return (<>
              <div style={{ position: 'absolute', inset: 0, backgroundImage: `url('${bannerUrl}')`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(22px) brightness(0.55) saturate(1.4)', transform: 'scale(1.08)', transformOrigin: 'center' }} />
              <div style={{ position: 'absolute', inset: 0, backgroundImage: `url('${bannerUrl}')`, backgroundSize: effZoom === 1 ? 'cover' : `${Math.round(effZoom * 100)}%`, backgroundPosition: `${effPos.x}% ${effPos.y}%`, backgroundRepeat: 'no-repeat' }} />
            </>);
          })() : <div style={{ position: 'absolute', inset: 0, background: currentGradient.bg }} />}
        </div>
        {/* Кнопки: тема + выход */}
        {(() => {
          const tc = profileTextDark ? 'rgba(15,23,42,0.88)' : 'rgba(255,255,255,0.95)';
          const btnBg = profileTextDark ? 'rgba(15,23,42,0.12)' : 'rgba(255,255,255,0.20)';
          const btnHoverBg = profileTextDark ? 'rgba(15,23,42,0.22)' : 'rgba(255,255,255,0.35)';
          return (
            <div className="absolute top-3 right-3 flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleThemeToggle}
                className="w-8 h-8 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors active:scale-90"
                style={{ background: btnBg, color: tc }}
                onMouseEnter={e => e.currentTarget.style.background = btnHoverBg}
                onMouseLeave={e => e.currentTarget.style.background = btnBg}
                title={isDark ? 'Светлая тема' : 'Тёмная тема'}
              >
                <span key={isDark ? 'sun' : 'moon'} className="theme-icon-swap">
                  {isDark ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                      <circle cx="12" cy="12" r="5"/>
                      <path strokeLinecap="round" d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
                    </svg>
                  )}
                </span>
              </button>
              <button
                type="button"
                onClick={logout}
                className="w-8 h-8 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-200 active:scale-90"
                style={{ background: btnBg, color: tc }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.85)'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = btnBg; e.currentTarget.style.color = tc; }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          );
        })()}

        {/* Контент */}
        <div className="flex flex-col items-center pt-20 pb-8 px-5 text-center" style={{ minHeight: HERO_MIN_HEIGHT }}>
          <div className="relative mb-4 flex gap-2 items-end">
            {/* Открыть лайтбокс */}
            <div className="relative" style={{ width: 80, height: 80 }}>
              <button
                type="button"
                onClick={() => avatar && setLightboxOpen(true)}
                className="block w-full h-full rounded-full"
              >
                <div className={`rounded-full overflow-hidden shadow-lg bg-white/20 flex items-center justify-center w-full h-full${user?.avatar_frame ? '' : ' ring-4 ring-white/30'}`}>
                  {avatar ? (
                    <img
                      src={avatar}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: `${avatarPos.x}% ${avatarPos.y}%` }}
                    />
                  ) : (
                    <span className="text-3xl text-white font-medium select-none">
                      {user?.fio?.charAt(0)?.toUpperCase() || "?"}
                    </span>
                  )}
                </div>
              </button>
              {user?.avatar_frame && (
                user.avatar_frame.toLowerCase().endsWith('.mp4') ? (
                  <video
                    src={user.avatar_frame}
                    autoPlay muted loop playsInline
                    className="absolute pointer-events-none select-none"
                    style={{ inset: 0, width: '100%', height: '100%', zIndex: 10, objectFit: 'contain', transform: `translateX(${user?.avatar_frame_offset_x ?? 0}%) translateY(${user?.avatar_frame_offset_y ?? 0}%) scale(${(user?.avatar_frame_scale ?? 100) / 100})`, transformOrigin: 'center' }}
                  />
                ) : (
                  <img
                    src={user.avatar_frame}
                    alt=""
                    className="absolute pointer-events-none select-none"
                    style={{ inset: 0, width: '100%', height: '100%', zIndex: 10, transform: `translateX(${user?.avatar_frame_offset_x ?? 0}%) translateY(${user?.avatar_frame_offset_y ?? 0}%) scale(${(user?.avatar_frame_scale ?? 100) / 100})`, transformOrigin: 'center' }}
                  />
                )
              )}
              {isCrownHolder && crownEquipped && (
                <img
                  src="/badges/golden_crown.svg"
                  alt="Корона лидера"
                  className="absolute pointer-events-none select-none"
                  style={{ top: -44, left: '50%', transform: 'translateX(-50%)', width: 64, height: 64, objectFit: 'contain', zIndex: 20 }}
                />
              )}
              {Array.isArray(user?.active_sets) && user.active_sets.map(s => {
                if (s.effect_type === 'orange_pulse') {
                  return (
                    <div key={`set-fx-${s.id}`}
                      className="absolute pointer-events-none select-none rounded-full"
                      style={{
                        inset: -8,
                        zIndex: 5,
                        boxShadow: '0 0 0 2px rgba(255,138,43,0.6), 0 0 32px 8px rgba(255,138,43,0.4)',
                        animation: 'setOrangePulse 2.2s ease-in-out infinite',
                      }}
                    />
                  );
                }
                if (s.effect_type === 'pumpkin_orbit') {
                  const orbs = [0, 72, 144, 216, 288];
                  return (
                    <div key={`set-fx-${s.id}`}
                      className="absolute pointer-events-none select-none"
                      style={{
                        inset: -32, zIndex: 6,
                        animation: 'setPumpkinOrbit 9s linear infinite',
                      }}>
                      {orbs.map(deg => (
                        <span key={deg}
                          className="absolute"
                          style={{
                            top: '50%', left: '50%',
                            transform: `rotate(${deg}deg) translateY(-72px)`,
                            fontSize: 22,
                            filter: 'drop-shadow(0 0 6px rgba(255,138,43,0.75))',
                          }}>🎃</span>
                      ))}
                    </div>
                  );
                }
                if (s.effect_type === 'ghost_floater') {
                  return (
                    <span key={`set-fx-${s.id}`}
                      className="absolute pointer-events-none select-none"
                      style={{
                        top: '-20%', left: '-30%', fontSize: 32, zIndex: 6,
                        filter: 'drop-shadow(0 0 12px rgba(220,220,255,0.55))',
                        animation: 'setGhostFloat 8s ease-in-out infinite',
                      }}>👻</span>
                  );
                }
                if (s.effect_type === 'spider_web') {
                  return (
                    <svg key={`set-fx-${s.id}`} aria-hidden="true"
                      className="absolute pointer-events-none select-none"
                      style={{ top: -24, left: -24, width: 72, height: 72, zIndex: 6, opacity: 0.6 }}
                      viewBox="0 0 72 72">
                      <g fill="none" stroke="#f3f2ea" strokeWidth="1.2" strokeLinecap="round">
                        <path d="M0 0 L72 0" /><path d="M0 0 L0 72" />
                        <path d="M0 0 L72 72" /><path d="M0 0 L72 36" /><path d="M0 0 L36 72" />
                        <path d="M12 0 Q14 12 0 14" /><path d="M24 0 Q28 24 0 28" />
                        <path d="M44 0 Q50 44 0 50" />
                      </g>
                    </svg>
                  );
                }
                return null;
              })}
              <style>{`
                @keyframes setOrangePulse {
                  0%,100% { box-shadow: 0 0 0 2px rgba(255,138,43,0.6), 0 0 24px 6px rgba(255,138,43,0.3); }
                  50%     { box-shadow: 0 0 0 3px rgba(255,138,43,0.85), 0 0 40px 12px rgba(255,138,43,0.55); }
                }
                @keyframes setPumpkinOrbit {
                  from { transform: rotate(0deg); }
                  to   { transform: rotate(360deg); }
                }
                @keyframes setGhostFloat {
                  0%,100% { transform: translate(0,0); opacity: 0.75; }
                  50%     { transform: translate(170%, -8%); opacity: 1; }
                }
              `}</style>
            </div>
            {/* Кнопка редактирования */}
            <button
              type="button"
              onClick={() => setEditView(true)}
              className="absolute -bottom-1 -right-1 w-7 h-7 bg-white/20 hover:bg-white/35 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
            >
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>
          {lightboxOpen && avatar && <AvatarLightbox src={avatar} onClose={() => setLightboxOpen(false)} />}
          {bannerModalOpen && (
            <BannerPreviewModal
              src={bannerPreview}
              pos={bannerPos}
              posMobile={bannerPosMobile || { x: 50, y: 50 }}
              fio={user?.fio}
              avatarSrc={avatar}
              avatarPos={avatarPos}
              onChange={next => {
                const val = typeof next === 'function' ? next(bannerPos) : next;
                setBannerPos(val);
                localStorage.setItem('bannerPos', JSON.stringify(val));
                clearTimeout(bannerPosSaveTimer.current);
                bannerPosSaveTimer.current = setTimeout(() => {
                  fetch('/auth/me/preferences', {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                    body: JSON.stringify({ banner_position: `${Math.round(val.x)} ${Math.round(val.y)}` }),
                  });
                }, 600);
              }}
              onChangeMobile={next => {
                const val = typeof next === 'function' ? next(bannerPosMobile || { x: 50, y: 50 }) : next;
                setBannerPosMobile(val);
                localStorage.setItem('bannerPosMobile', JSON.stringify(val));
                clearTimeout(bannerPosSaveTimer.current);
                bannerPosSaveTimer.current = setTimeout(() => {
                  fetch('/auth/me/preferences', {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                    body: JSON.stringify({ banner_position_mobile: `${Math.round(val.x)} ${Math.round(val.y)}` }),
                  });
                }, 600);
              }}
              zoom={bannerZoom}
              zoomMobile={bannerZoomMobile}
              onZoomChange={z => {
                setBannerZoom(z);
                fetch('/auth/me/preferences', {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                  body: JSON.stringify({ banner_zoom: z }),
                });
              }}
              onZoomChangeMobile={z => {
                setBannerZoomMobile(z);
                fetch('/auth/me/preferences', {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                  body: JSON.stringify({ banner_zoom_mobile: z }),
                });
              }}
              onSave={() => setBannerModalOpen(false)}
              onClose={() => setBannerModalOpen(false)}
            />
          )}
          {equipPreviewItem && equipPreviewItem.item_type === 'banner' && (
            <BannerPreviewModal
              src={equipPreviewItem.asset}
              pos={equipPreviewPos}
              posMobile={equipPreviewPosMobile}
              onChange={next => setEquipPreviewPos(prev => typeof next === 'function' ? next(prev) : next)}
              onChangeMobile={next => setEquipPreviewPosMobile(prev => typeof next === 'function' ? next(prev) : next)}
              zoom={equipPreviewZoom}
              zoomMobile={equipPreviewZoomMobile}
              onZoomChange={z => setEquipPreviewZoom(z)}
              onZoomChangeMobile={z => setEquipPreviewZoomMobile(z)}
              fio={user?.fio}
              avatarSrc={avatar}
              avatarPos={avatarPos}
              onSave={() => {
                handleEquip(equipPreviewItem);
                const pos = equipPreviewPos;
                const z = equipPreviewZoom;
                const posMob = equipPreviewPosMobile;
                const zMob = equipPreviewZoomMobile;
                setBannerPos(pos);
                setBannerZoom(z);
                setBannerPosMobile(posMob);
                setBannerZoomMobile(zMob);
                localStorage.setItem('bannerPos', JSON.stringify(pos));
                localStorage.setItem('bannerPosMobile', JSON.stringify(posMob));
                fetch('/auth/me/preferences', {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                  body: JSON.stringify({
                    banner_position: `${Math.round(pos.x)} ${Math.round(pos.y)}`,
                    banner_zoom: z,
                    banner_position_mobile: `${Math.round(posMob.x)} ${Math.round(posMob.y)}`,
                    banner_zoom_mobile: zMob,
                  }),
                });
                setEquipPreviewItem(null);
                setEquipPreviewZoom(1);
                setEquipPreviewZoomMobile(1);
              }}
              onClose={() => { setEquipPreviewItem(null); setEquipPreviewZoom(1); setEquipPreviewZoomMobile(1); }}
            />
          )}
          {equipPreviewItem && equipPreviewItem.item_type !== 'banner' && (
            <EquipPreviewModal
              item={equipPreviewItem}
              user={user}
              avatarSrc={avatar}
              avatarPos={avatarPos}
              bannerPos={bannerPos}
              onConfirm={() => { handleEquip(equipPreviewItem); setEquipPreviewItem(null); }}
              onClose={() => setEquipPreviewItem(null)}
            />
          )}
          {shopPreviewItem && (
            <ShopItemPreviewModal
              item={shopPreviewItem}
              user={user}
              avatarSrc={avatar}
              avatarPos={avatarPos}
              balance={skrepBalance}
              buying={shopBuying === shopPreviewItem.id}
              onBuy={() => buyShopItem(shopPreviewItem)}
              onLike={() => toggleShopLike(shopPreviewItem)}
              onClose={() => setShopPreviewItem(null)}
              onGoToCollection={() => {
                const itemId = shopPreviewItem.id;
                setHighlightCollectionId(itemId);
                setShopPreviewItem(null);
                setSkrepView(false);
                setTimeout(() => {
                  document.getElementById(`collection-item-${itemId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 350);
                setTimeout(() => setHighlightCollectionId(null), 2500);
              }}
            />
          )}

          <h1 className="flex items-center justify-center gap-1 text-[20px] font-semibold leading-tight text-center" style={{ color: profileTextDark ? 'rgba(15,23,42,0.9)' : 'rgba(255,255,255,0.95)', textShadow: profileTextDark ? 'none' : '0 1px 3px rgba(0,0,0,0.25)' }}>
            {(() => {
              const fio = user?.fio || "Пользователь";
              const badge = (user?.is_developer && user?.dev_badge_equipped !== false) ? (
                <button type="button" onClick={() => setShowDevInfo(true)} className="focus:outline-none shrink-0" style={{ display: 'inline-flex' }}>
                  <img src="/badges/developer_status.svg" alt="Разработчик" style={{ width: 28, height: 28, display: 'block' }} />
                </button>
              ) : user?.equipped_badge_asset ? (
                <button type="button" onClick={() => setShowBadgeInfo(true)} className="focus:outline-none shrink-0" style={{ display: 'inline-flex' }}>
                  {user.equipped_badge_asset.toLowerCase().endsWith('.mp4') ? (
                    <video src={user.equipped_badge_asset} autoPlay muted loop playsInline style={{ width: 28, height: 28, display: 'block', objectFit: 'contain' }} />
                  ) : (
                    <CosmeticAsset asset={user.equipped_badge_asset} alt="" style={{ width: 28, height: 28, display: 'block', objectFit: 'contain' }} />
                  )}
                </button>
              ) : null;
              return <><span className="truncate" style={{ maxWidth: badge ? 'calc(100% - 36px)' : '88%' }}>{fio}</span>{badge}</>;
            })()}
            {showDevInfo && createPortal(
              <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => setShowDevInfo(false)}>
                <style>{`@keyframes devBadgeIn{from{opacity:0;transform:scale(0.05)}to{opacity:1;transform:scale(1)}}@keyframes devBdropIn{from{opacity:0}to{opacity:1}}`}</style>
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" style={{ animation: 'devBdropIn 200ms ease both' }} />
                <div className="relative bg-white rounded-2xl shadow-2xl p-6 mx-4 max-w-xs w-full flex flex-col items-center gap-3"
                  style={{ animation: 'devBadgeIn 300ms cubic-bezier(0.34,1.56,0.64,1) both', transformOrigin: 'center' }}
                  onClick={e => e.stopPropagation()}>
                  <button type="button" onClick={() => setShowDevInfo(false)} className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-colors">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  </button>
                  <img src="/badges/developer_status.svg" alt="Разработчик" style={{ width: 96, height: 96 }} />
                  <p className="text-[15px] font-semibold text-slate-800 text-center">Разработчик</p>
                  <p className="text-[13px] text-slate-500 text-center leading-relaxed">Этот бейдж выдаётся только разработчикам проекта AudAssist.</p>
                </div>
              </div>,
              document.body
            )}
            {showBadgeInfo && user?.equipped_badge_asset && (() => {
              const _bi = inventory.find(i => i.asset === user.equipped_badge_asset);
              const _bg = isDark ? '#1c1c1e' : '#ffffff';
              const _titleCol = isDark ? '#ffffff' : '#0f172a';
              const _descCol = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';
              const _closCol = isDark ? 'rgba(255,255,255,0.4)' : undefined;
              const _closHov = isDark ? 'rgba(255,255,255,0.08)' : undefined;
              const _rar = _bi?.rarity;
              const _glow = _rar === 'chromatic' ? 'rgba(217,70,239,0.18)' : _rar === 'legendary' ? 'rgba(251,191,36,0.18)' : _rar === 'epic' ? 'rgba(139,92,246,0.18)' : _rar === 'rare' ? 'rgba(59,130,246,0.15)' : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(100,116,139,0.10)';
              return createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => setShowBadgeInfo(false)}>
                  <style>{`@keyframes devBadgeIn{from{opacity:0;transform:scale(0.05)}to{opacity:1;transform:scale(1)}}@keyframes devBdropIn{from{opacity:0}to{opacity:1}}`}</style>
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" style={{ animation: 'devBdropIn 200ms ease both' }} />
                  <div className="relative rounded-2xl shadow-2xl p-6 mx-4 max-w-xs w-full flex flex-col items-center gap-3"
                    style={{ background: _bg, animation: 'devBadgeIn 300ms cubic-bezier(0.34,1.56,0.64,1) both', transformOrigin: 'center' }}
                    onClick={e => e.stopPropagation()}>
                    <button type="button" onClick={() => setShowBadgeInfo(false)} className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                      style={{ color: _closCol, backgroundColor: 'transparent' }}
                      onMouseEnter={e => { if (_closHov) e.currentTarget.style.backgroundColor = _closHov; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    </button>
                    <div style={{ width: 128, height: 128, borderRadius: 16, background: _glow, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                      <ItemEffect effectType={user.equipped_badge_effect_type} effectParams={user.equipped_badge_effect_params} trigger="click">
                      {user.equipped_badge_asset.toLowerCase().endsWith('.mp4') ? (
                        <video src={user.equipped_badge_asset} autoPlay muted loop playsInline style={{ width: 104, height: 104, objectFit: 'contain' }} />
                      ) : (
                        <CosmeticAsset asset={user.equipped_badge_asset} alt="" style={{ width: 104, height: 104, objectFit: 'contain' }} />
                      )}
                      </ItemEffect>
                    </div>
                    {(_bi?.name) && (
                      <p className="text-[15px] font-semibold text-center" style={{ color: _titleCol }}>{_bi.name}</p>
                    )}
                    {(_bi?.description) ? (
                      <p className="text-[13px] text-center leading-relaxed" style={{ color: _descCol }}>{_bi.description}</p>
                    ) : !_bi?.name ? (
                      <p className="text-[13px] text-center leading-relaxed" style={{ color: _descCol }}>Кастомный бейдж</p>
                    ) : null}
                  </div>
                </div>,
                document.body
              );
            })()}
          </h1>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-[12px] text-white/70">В сети</span>
          </div>
        </div>
      </section>

      {/* FIO warning */}
      {!isFioValid(user?.fio) && !editView && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 mb-3 flex items-start gap-3">
          <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-amber-800">Некорректное ФИО</p>
            <p className="text-[12px] text-amber-700 mt-0.5">Укажите фамилию и имя кириллицей с заглавной буквы</p>
          </div>
          <button
            type="button"
            onClick={() => setEditView(true)}
            className="shrink-0 text-[12px] font-semibold text-amber-700 hover:text-amber-900 transition-colors whitespace-nowrap"
          >
            Исправить
          </button>
        </div>
      )}


      {/* SETTINGS */}
      <section className="rounded-2xl bg-white border border-slate-200/50 shadow-sm mb-3 overflow-hidden">

        {/* Notifications — full-row interactive */}
        {pushSupported && pushEnabled !== null && (
          <>
            <div
              onMouseDown={startClipLongPress}
              onMouseUp={cancelClipLongPress}
              onMouseLeave={cancelClipLongPress}
              onTouchStart={startClipLongPress}
              onTouchEnd={cancelClipLongPress}
              onTouchCancel={cancelClipLongPress}
            >
              <button
                type="button"
                onClick={handlePushToggle}
                disabled={pushBusy}
                title={pushError || undefined}
                className="w-full flex items-center gap-3 pl-4 pr-5 py-3.5 hover:bg-slate-50 active:bg-slate-100/70 transition-colors text-left"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-200 ${pushEnabled ? 'bg-[#eff6ff]' : 'bg-slate-100'}`}>
                  <svg className={`w-[18px] h-[18px] transition-colors duration-200 ${pushEnabled ? 'text-[#3b82f6]' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17H9m3 4c.552 0 1-.448 1-1h-2c0 .552.448 1 1 1zM5.07 17l.93-.93V11a6 6 0 1112 0v5.07l.93.93H5.07z" />
                  </svg>
                </div>
                <span className={`text-[14px] font-medium flex-1 transition-colors duration-200 ${pushEnabled ? 'text-slate-800' : 'text-slate-500'}`}>
                  Уведомления об изменениях
                </span>
                <div className={`w-11 h-[26px] rounded-full transition-colors duration-200 flex items-center px-0.5 shrink-0 ${pushEnabled ? 'bg-[#3b82f6]' : 'bg-slate-200'} ${pushBusy ? 'opacity-60' : ''}`}>
                  <div className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 ${pushEnabled ? 'translate-x-[21px]' : 'translate-x-0'}`} />
                </div>
              </button>
            </div>

            {clipPushSecretVisible && (
              <button
                type="button"
                onClick={handleClipPushToggle}
                className="w-full flex items-center gap-3 pl-4 pr-5 py-3.5 hover:bg-slate-50 active:bg-slate-100/70 transition-colors text-left"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-200 ${clipPushEnabled ? 'bg-amber-50' : 'bg-slate-100'}`}>
                  <svg className={`w-[18px] h-[18px] transition-colors duration-200 ${clipPushEnabled ? 'text-amber-500' : 'text-slate-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                  </svg>
                </div>
                <span className={`text-[14px] font-medium flex-1 transition-colors duration-200 ${clipPushEnabled ? 'text-slate-800' : 'text-slate-500'}`}>
                  Уведомления о скрепках
                </span>
                <div className={`w-11 h-[26px] rounded-full transition-colors duration-200 flex items-center px-0.5 shrink-0 ${clipPushEnabled ? 'bg-amber-400' : 'bg-slate-200'}`}>
                  <div className={`w-[22px] h-[22px] bg-white rounded-full shadow-sm transition-transform duration-200 ${clipPushEnabled ? 'translate-x-[21px]' : 'translate-x-0'}`} />
                </div>
              </button>
            )}

            <div className="h-px bg-slate-100 mx-4" />
          </>
        )}

        {/* Email */}
        <div className="px-4 py-3.5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
              <svg className="w-[18px] h-[18px] text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 leading-none mb-1">Почта</p>
              <span className="text-[14px] text-slate-800 font-medium truncate block">{email}</span>
            </div>
            {user?.email_verified === false && verifyStep !== 'done' && (
              <span className="shrink-0 text-[11px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
                Не подтверждена
              </span>
            )}
            {(user?.email_verified === true || verifyStep === 'done') && (
              <div className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-700/50 flex items-center justify-center shrink-0">
                <svg className="w-3 h-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>

          {/* Inline verification flow */}
          {user?.email_verified === false && verifyStep !== 'done' && (
            <div className="mt-2.5 ml-12">
              {verifyStep === 'idle' && (
                <button
                  type="button"
                  onClick={handleSendVerifyCode}
                  className="text-[13px] font-semibold text-[#3b82f6] hover:text-[#1e3a8a] transition-colors"
                >
                  Подтвердить почту →
                </button>
              )}
              {verifyStep === 'sending' && (
                <span className="text-[13px] text-slate-400">Отправляем код...</span>
              )}
              {(verifyStep === 'input' || verifyStep === 'confirming') && (
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    maxLength={6}
                    value={verifyCode}
                    onChange={e => setVerifyCode(e.target.value.toUpperCase())}
                    placeholder="Код из письма"
                    className="w-36 h-9 px-3 rounded-xl border border-slate-200 focus:border-[#3b82f6] focus:ring-2 focus:ring-blue-500/10 outline-none text-[13px] font-mono tracking-widest text-slate-900 bg-white transition-all"
                  />
                  <button
                    type="button"
                    onClick={handleConfirmVerifyCode}
                    disabled={verifyStep === 'confirming'}
                    className="h-9 px-4 rounded-xl bg-[#3b82f6] hover:bg-[#1e3a8a] text-white text-[13px] font-semibold transition-colors disabled:opacity-50 active:scale-[0.97]"
                  >
                    {verifyStep === 'confirming' ? '...' : 'Подтвердить'}
                  </button>
                  <button
                    type="button"
                    disabled={verifyCooldown > 0 || verifyStep === 'confirming'}
                    onClick={handleSendVerifyCode}
                    className="text-[12px] text-slate-400 hover:text-slate-600 transition-colors disabled:cursor-not-allowed"
                  >
                    {verifyCooldown > 0 ? `Повторить через ${verifyCooldown}с` : 'Отправить снова'}
                  </button>
                </div>
              )}
              {verifyErr && (
                <p className="text-[12px] text-red-500 font-medium mt-1.5">{verifyErr}</p>
              )}
            </div>
          )}
          {verifyStep === 'done' && (
            <p className="mt-1 ml-12 text-[12px] text-emerald-600 font-medium">Почта подтверждена</p>
          )}
        </div>

        {user?.username && (
          <>
            <div className="h-px bg-slate-100 mx-4" />
            <div className="flex items-center gap-3 px-4 py-3.5">
              <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center shrink-0">
                <svg className="w-[18px] h-[18px] text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 leading-none mb-1">Никнейм</p>
                <span className="text-[14px] text-slate-800 font-medium">@{user.username}</span>
              </div>
            </div>
          </>
        )}

        <div className="h-px bg-slate-100 mx-4" />
        <div className="flex items-center gap-3 px-4 py-3.5">
          <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
            <svg className="w-[18px] h-[18px] text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 leading-none mb-1">Роль</p>
            <span className="text-[14px] text-slate-800 font-medium">
              {user?.role === 'admin' ? 'Администратор' :
               user?.role === 'teacher' ? 'Преподаватель' :
               user?.role === 'мастер сикс севен' ? '✦ Мастер Сикс Севен ✦' : 'Студент'}
              {user?.is_curator ? ' · Куратор' : ''}
            </span>
          </div>
        </div>

        {(user?.study_group || user?.role !== 'teacher') && (
          <>
            <div className="h-px bg-slate-100 mx-4" />
            {user?.is_curator && user?.study_group ? (
              <button
                type="button"
                onClick={() => {
                  try {
                    localStorage.setItem('audassist.schedule.filterMode', 'groups');
                    localStorage.setItem('audassist.schedule.selectedGroupFilter', JSON.stringify(user.study_group));
                  } catch {}
                  navigate('/schedule');
                }}
                className="flex items-center gap-3 px-4 py-3.5 w-full text-left hover:bg-slate-50 active:bg-slate-100/70 transition-colors"
              >
                <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                  <svg className="w-[18px] h-[18px] text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 leading-none mb-1">Моя группа</p>
                  <span className="text-[14px] font-medium text-slate-800">{user.study_group}</span>
                </div>
                <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <div className="flex items-center gap-3 px-4 py-3.5">
                <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                  <svg className="w-[18px] h-[18px] text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 leading-none mb-1">Моя группа</p>
                  <span className={`text-[14px] font-medium ${user?.study_group ? 'text-slate-800' : 'text-slate-400'}`}>
                    {user?.study_group || 'Не указана'}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Скрепки */}
        <div className="h-px bg-slate-100 mx-4" />
        <button
          type="button"
          onClick={() => { setSkrepView(true); setCaseResult(null); fetchInventory(); fetchShop(); }}
          className="flex items-center gap-3 px-4 py-3.5 w-full text-left hover:bg-slate-50 active:bg-slate-100/70 transition-colors"
        >
          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
            <img src="/paperclip.svg" alt="" className="w-[22px] h-[22px] object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 leading-none mb-1">Магазин</p>
            <span className="text-[14px] font-medium text-slate-500">Дипломаты и скрепки</span>
          </div>
          <svg className="w-4 h-4 text-slate-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Инвентарь */}
        <div className="h-px bg-slate-100 mx-4" />
        <button
          type="button"
          onClick={() => { setCollectionView(true); fetchInventory(); }}
          className="flex items-center gap-3 px-4 py-3.5 w-full text-left hover:bg-slate-50 active:bg-slate-100/70 transition-colors"
        >
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"/>
              <path d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"/>
              <path d="M16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 leading-none mb-1">Инвентарь</p>
            <span className="text-[14px] font-medium text-slate-500">
              {inventoryLoading ? 'Загрузка…' : inventory.length === 0 ? 'Нет предметов' : `${inventory.length} предмет${inventory.length === 1 ? '' : inventory.length < 5 ? 'а' : 'ов'}`}
            </span>
          </div>
          <svg className="w-4 h-4 text-slate-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Устройства */}
        <div className={`h-px mx-4 ${isDark ? 'bg-white/5' : 'bg-slate-100'}`} />
        <button
          type="button"
          onClick={() => {
            _hap(8);
            setDevicesOpen(true);
            if (sessions.length === 0) handleFetchSessions();
          }}
          className={`flex items-center gap-3 px-4 py-3.5 w-full text-left transition-colors ${isDark ? 'hover:bg-white/[0.03] active:bg-white/[0.05]' : 'hover:bg-slate-50 active:bg-slate-100/70'}`}
        >
          <div className={`relative w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-indigo-500/15' : 'bg-indigo-50'}`}>
            <svg className={`w-5 h-5 ${isDark ? 'text-indigo-300' : 'text-indigo-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
              <rect x="1" y="3.5" width="16" height="11" rx="1.5"/>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 14.5v2M11.5 14.5v2M6.5 16.5h5"/>
              <rect x="13.5" y="8" width="9.5" height="15" rx="1.5" fill={isDark ? 'rgba(99,102,241,0.18)' : '#eef2ff'}/>
              <path strokeLinecap="round" d="M16 21h5"/>
            </svg>
            {sessions.filter(s => !s.is_current).length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9.5px] font-bold flex items-center justify-center" style={{ boxShadow: isDark ? '0 0 0 2px #000' : '0 0 0 2px #fff' }}>
                {sessions.filter(s => !s.is_current).length}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[11px] font-bold uppercase tracking-widest leading-none mb-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Устройства</p>
            <span className={`text-[14px] font-medium ${isDark ? 'text-white/70' : 'text-slate-500'}`}>
              {sessions.length > 0
                ? `${sessions.length} ${sessions.length === 1 ? 'активный сеанс' : sessions.length < 5 ? 'активных сеанса' : 'активных сеансов'}`
                : 'Активные сеансы'}
            </span>
          </div>
          <svg className={`w-4 h-4 shrink-0 ${isDark ? 'text-white/25' : 'text-slate-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Панель администратора */}
        {isAdmin && (
          <>
            <div className="h-px bg-slate-100 mx-4" />
            <AdminPanel />
          </>
        )}

        {/* Обратная связь */}
        <div className="h-px bg-slate-100 mx-4" />
        <a
          href="https://t.me/vialontrop"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between w-full px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100/70 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <svg className="w-[18px] h-[18px] text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M7 8h10M7 12h6m-6 4h10M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-[15px] font-semibold text-slate-800">Сообщить о баге или идее</p>
              <p className="text-[12px] text-slate-400 mt-0.5">Написать в Telegram</p>
            </div>
          </div>
          <svg className="w-4 h-4 text-slate-300 shrink-0 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </a>

      </section>

      {/* Аналитика преподавателя */}
      {user?.role === 'teacher' && user?.fio && (
        <section className={`rounded-2xl bg-white border border-slate-200/50 shadow-sm ${discOverflow === "hidden" ? "overflow-hidden" : ""}`}>
          <button
            type="button"
            onClick={() => {
              const willOpen = disciplinesOpen === false;
              setDisciplinesOpen(o => !o);
              if (discOverflowTimer.current) clearTimeout(discOverflowTimer.current);
              if (willOpen) {
                setDiscOverflow("hidden");
                discOverflowTimer.current = setTimeout(() => setDiscOverflow("visible"), 340);
              } else {
                setDiscOverflow("hidden");
              }
            }}
            className="flex items-center gap-3 px-4 py-3 w-full text-left"
            style={{ borderBottom: disciplinesOpen ? "1px solid #f1f5f9" : "none", background: "none", cursor: "pointer", outline: "none" }}
          >
            <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <svg className="w-[15px] h-[15px] text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <p className="flex-1 text-[11px] font-bold uppercase tracking-widest text-slate-400 leading-none">Аналитика</p>
            <svg
              width="16" height="16" viewBox="0 0 16 16" fill="none"
              style={{ color: "#cbd5e1", transform: disciplinesOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 280ms cubic-bezier(0.34,1.56,0.64,1)", flexShrink: 0 }}
            >
              <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div style={{
            display: "grid",
            gridTemplateRows: disciplinesOpen ? "1fr" : "0fr",
            transition: "grid-template-rows 300ms cubic-bezier(0.2,0.9,0.2,1)",
          }}>
            <div style={{ overflow: discOverflow }}>
              <TeacherDisciplineChart fio={user.fio} />
            </div>
          </div>
        </section>
      )}

      {/* ── COLLECTION PANEL (portal, slide from right) ── */}
      {profileContainerEl && createPortal(
      <div
        ref={collectionScrollRef}
        className="fixed inset-0 z-[70] overflow-y-auto hide-scrollbar sm:absolute sm:left-0 sm:right-0 sm:top-0 sm:bottom-auto sm:z-auto sm:overflow-y-auto sm:max-h-[calc(100vh-120px)] sm:min-h-full transition-transform duration-300 ease-in-out will-change-transform pb-14 sm:pb-0"
        style={{ transform: collectionView ? 'translateX(0)' : 'translateX(105%)' }}
        onScroll={e => sessionStorage.setItem('aud_collection_scroll', e.currentTarget.scrollTop)}
      >
        <div className="flex items-center justify-between px-4 py-2 bg-white/90 dark:bg-[#1c1c1e]/90 backdrop-blur-sm border-b border-slate-100 dark:border-white/10 sticky top-0 z-[70]">
          <button type="button" onClick={() => setCollectionView(false)} className="flex items-center gap-0.5 text-blue-500 font-medium text-[15px] py-2 pr-3 -ml-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
            Назад
          </button>
          <span className="font-semibold text-[16px]" style={{ color: isDark ? '#fff' : '#0f172a' }}>Инвентарь</span>
          <div className="py-2 pl-3 -mr-1 min-w-[44px] text-right">
            <span className="text-[13px] font-semibold" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
              {inventory.length > 0 ? inventory.length : ''}
            </span>
          </div>
        </div>
        <div className="px-4 pt-4 pb-8">
          {(() => {
            const rarityLabels = { chromatic: 'Хроматический', legendary: 'Легендарный', epic: 'Эпический', rare: 'Редкий', common: 'Обычный' };
            const getRC = (rarity) => rarity === 'chromatic'
              ? { accent: isDark ? '#d946ef' : '#a21caf', pill: isDark ? '#f0abfc' : '#701a75', pillBg: isDark ? 'rgba(217,70,239,0.18)' : 'rgba(217,70,239,0.10)', imgBg: isDark ? 'rgba(217,70,239,0.09)' : 'rgba(217,70,239,0.05)' }
              : rarity === 'legendary'
              ? { accent: isDark ? '#f59e0b' : '#d97706', pill: isDark ? '#fde68a' : '#92400e', pillBg: isDark ? 'rgba(251,191,36,0.18)' : 'rgba(251,191,36,0.12)', imgBg: isDark ? 'rgba(245,158,11,0.09)' : 'rgba(245,158,11,0.05)' }
              : rarity === 'epic'
              ? { accent: isDark ? '#8b5cf6' : '#7c3aed', pill: isDark ? '#c4b5fd' : '#4c1d95', pillBg: isDark ? 'rgba(139,92,246,0.18)' : 'rgba(139,92,246,0.10)', imgBg: isDark ? 'rgba(139,92,246,0.09)' : 'rgba(139,92,246,0.05)' }
              : { accent: isDark ? '#94a3b8' : '#64748b', pill: isDark ? '#cbd5e1' : '#334155', pillBg: isDark ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.10)', imgBg: isDark ? 'rgba(148,163,184,0.06)' : 'rgba(0,0,0,0.03)' };

            const searchTrimmed = collectionSearch.trim().toLowerCase();
            const byType = collectionTypeFilter === 'all' ? inventory : inventory.filter(i => i.item_type === collectionTypeFilter);
            const byRarity = collectionRarityFilter === 'all' ? byType : byType.filter(i => i.rarity === collectionRarityFilter);
            const bySearch = !searchTrimmed ? byRarity : byRarity.filter(i => i.name.toLowerCase().includes(searchTrimmed));
            const filteredItems = [...bySearch].sort((a, b) => (b.is_equipped ? 1 : 0) - (a.is_equipped ? 1 : 0));
            const devBadgeVisible = (collectionTypeFilter === 'all' || collectionTypeFilter === 'badge') && !!user?.is_developer && collectionRarityFilter === 'all' && (!searchTrimmed || 'разработчик'.includes(searchTrimmed));
            const isBannerOnly = collectionTypeFilter === 'banner';

            const vibrate = (pattern) => { try { if (navigator.vibrate) navigator.vibrate(pattern); } catch {} };

            const controlBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)';
            const controlBorder = isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.08)';
            const controlColor = isDark ? '#f1f5f9' : '#0f172a';
            const rarityDots = { chromatic: '#d946ef', legendary: '#f59e0b', epic: '#8b5cf6', common: isDark ? '#94a3b8' : '#64748b' };

            const typeOptions = [
              { value: 'all', label: 'Все типы', icon: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg> },
              { value: 'frame', label: 'Рамки', icon: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><rect x="2" y="2" width="20" height="20" rx="2"/><rect x="7" y="7" width="10" height="10" rx="1"/></svg> },
              { value: 'badge', label: 'Бейджи', icon: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2" strokeLinecap="round"/><circle cx="9" cy="10" r="1" fill="currentColor" stroke="none"/><circle cx="15" cy="10" r="1" fill="currentColor" stroke="none"/></svg> },
              { value: 'banner', label: 'Баннеры', icon: <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinejoin="round"><rect x="2" y="6" width="20" height="13" rx="2"/><path d="M2 14l5-5 4 4 3-3 6 5" strokeLinecap="round"/></svg> },
            ];
            const rarityOptions = [
              { value: 'all', label: 'Все редкости' },
              { value: 'common', label: 'Обычный' },
              { value: 'epic', label: 'Эпический' },
              { value: 'legendary', label: 'Легендарный' },
              { value: 'chromatic', label: 'Хроматический' },
            ];

            const Dropdown = ({ id, value, options, onChange, showDots, inline }) => {
              const isOpen = collectionOpenDropdown === id;
              const current = options.find(o => o.value === value);
              const hasFilter = value !== 'all';
              const activeDot = showDots ? rarityDots[value] : null;
              const btnBg = inline ? 'transparent' : (isOpen
                ? (isDark ? 'rgba(255,255,255,0.13)' : 'rgba(0,0,0,0.08)')
                : hasFilter ? (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)')
                : controlBg);
              const btnBorderColor = inline ? 'transparent' : (isOpen
                ? (isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)')
                : hasFilter ? (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.13)')
                : controlBorder);
              const btnColor = inline && hasFilter ? (isDark ? '#f1f5f9' : '#0f172a') : controlColor;
              return (
                <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                  {isOpen && <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => { setCollectionOpenDropdown(null); vibrate(4); }} />}
                  <button type="button"
                    onClick={() => { const next = isOpen ? null : id; setCollectionOpenDropdown(next); if (next) vibrate(8); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: inline ? '9px 10px 9px 14px' : '7px 10px 7px 12px', borderRadius: inline ? 0 : 14, fontSize: 12, fontWeight: hasFilter ? 600 : 500, background: btnBg, color: btnColor, border: inline ? 'none' : `1px solid ${btnBorderColor}`, transition: 'background 0.15s, border-color 0.15s', cursor: 'pointer', outline: 'none' }}
                    onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.96)'; e.currentTarget.style.transition = 'transform 0.08s cubic-bezier(0.2,0.9,0.2,1)'; }}
                    onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.22s cubic-bezier(0.16,1,0.3,1), background 0.15s, border-color 0.15s'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.22s cubic-bezier(0.16,1,0.3,1), background 0.15s, border-color 0.15s'; }}
                    onTouchStart={e => { e.currentTarget.style.transform = 'scale(0.96)'; e.currentTarget.style.transition = 'transform 0.08s cubic-bezier(0.2,0.9,0.2,1)'; }}
                    onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.22s cubic-bezier(0.16,1,0.3,1), background 0.15s, border-color 0.15s'; }}
                  >
                    {activeDot
                      ? <span style={{ width: 7, height: 7, borderRadius: '50%', background: activeDot, flexShrink: 0, boxShadow: `0 0 5px ${activeDot}99` }} />
                      : current?.icon
                        ? <span style={{ width: 14, height: 14, flexShrink: 0, display: 'flex', alignItems: 'center', color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b' }}>{current.icon}</span>
                        : null
                    }
                    <span style={{ flex: 1, textAlign: 'left' }}>{current?.label}</span>
                    <svg style={{ width: 12, height: 12, flexShrink: 0, color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8', transition: 'transform 0.24s cubic-bezier(0.16,1,0.3,1)', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  {isOpen && (
                    <div style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 6px)', zIndex: 50, borderRadius: 18, overflow: 'hidden', background: isDark ? 'rgba(38,38,40,0.97)' : 'rgba(255,255,255,0.98)', backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)'}`, boxShadow: isDark ? '0 24px 64px rgba(0,0,0,0.68), 0 6px 16px rgba(0,0,0,0.44), inset 0 1px 0 rgba(255,255,255,0.07)' : '0 24px 64px rgba(0,0,0,0.13), 0 6px 16px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,1)', animation: 'collDropIn 220ms cubic-bezier(0.16,1,0.3,1) both' }}>
                      {options.map((opt, idx) => {
                        const active = opt.value === value;
                        const dot = showDots ? rarityDots[opt.value] : null;
                        const isLast = idx === options.length - 1;
                        return (
                          <button key={opt.value} type="button"
                            onClick={() => { onChange(opt.value); setCollectionOpenDropdown(null); vibrate(12); }}
                            onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.97)'; }}
                            onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', fontSize: 13, fontWeight: active ? 600 : 400, color: active ? controlColor : (isDark ? 'rgba(255,255,255,0.5)' : '#64748b'), background: active ? (isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.04)') : 'transparent', borderBottom: !isLast ? `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}` : 'none', animation: `collOptIn 220ms cubic-bezier(0.16,1,0.3,1) ${idx * 32}ms both`, cursor: 'pointer', outline: 'none', textAlign: 'left', transition: 'background 0.12s, transform 0.1s cubic-bezier(0.2,0.9,0.2,1)' }}>
                            {dot
                              ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0, boxShadow: `0 0 7px ${dot}aa` }} />
                              : opt.icon
                                ? <span style={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', color: active ? controlColor : (isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8') }}>{opt.icon}</span>
                                : <span style={{ width: 8, flexShrink: 0 }} />
                            }
                            <span style={{ flex: 1 }}>{opt.label}</span>
                            {active && (
                              <svg style={{ width: 14, height: 14, flexShrink: 0, color: isDark ? '#e2e8f0' : '#0f172a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            };

            return (
              <div className="pb-20 sm:pb-0 flex flex-col gap-4">
                <span data-clip-zone="collection" style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }} />
                <style>{`
                  @keyframes collDropIn {
                    from { opacity: 0; transform: scale(0.88) translateY(-10px); filter: blur(4px); }
                    to   { opacity: 1; transform: scale(1)    translateY(0);     filter: blur(0); }
                  }
                  @keyframes collOptIn {
                    from { opacity: 0; transform: translateX(-10px); }
                    to   { opacity: 1; transform: translateX(0); }
                  }
                  @keyframes collCardIn {
                    from { opacity: 0; transform: translateY(22px); filter: blur(4px); }
                    to   { opacity: 1; transform: translateY(0);   filter: blur(0); }
                  }
                  @keyframes collCheckIn {
                    0%   { opacity: 0; transform: scale(0) rotate(-20deg); }
                    55%  { transform: scale(1.36) rotate(6deg); }
                    78%  { transform: scale(0.86) rotate(-3deg); }
                    100% { opacity: 1; transform: scale(1) rotate(0deg); }
                  }
                  @media (prefers-reduced-motion: reduce) {
                    [style*="collCardIn"], [style*="collCheckIn"], [style*="collDropIn"], [style*="collOptIn"] { animation: none !important; }
                  }
                `}</style>

                {/* ── Search + filters (unified pill panel) ── */}
                <div style={{ borderRadius: 18, border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)', boxShadow: isDark ? '0 2px 20px rgba(0,0,0,0.28)' : '0 2px 12px rgba(0,0,0,0.06)', overflow: 'visible' }}>
                  {/* Search row */}
                  <div className="relative">
                    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[15px] h-[15px] pointer-events-none" style={{ color: isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/></svg>
                    <input
                      type="search"
                      placeholder="Поиск..."
                      value={collectionSearch}
                      onChange={e => setCollectionSearch(e.target.value)}
                      className="w-full pl-10 pr-4 text-[13px] outline-none"
                      style={{ background: 'transparent', color: controlColor, border: 'none', padding: '11px 14px 11px 36px', WebkitAppearance: 'none', display: 'block', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  {/* Divider */}
                  <div style={{ height: 1, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)', margin: '0 14px' }} />
                  {/* Dropdowns row */}
                  <div style={{ display: 'flex', alignItems: 'stretch' }}>
                    <Dropdown id="type" value={collectionTypeFilter} options={typeOptions} onChange={setCollectionTypeFilter} showDots={false} inline />
                    <div style={{ width: 1, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)', margin: '8px 0', flexShrink: 0 }} />
                    <Dropdown id="rarity" value={collectionRarityFilter} options={rarityOptions} onChange={setCollectionRarityFilter} showDots={true} inline />
                  </div>
                </div>

                {/* ── Sell duplicates strip ── */}
                {(() => {
                  const dupTotal = inventory.reduce((s, i) => s + (i.duplicate_count || 0), 0);
                  if (dupTotal === 0) return null;
                  const handleSellFirst = () => {
                    vibrate(8);
                    setSellDupsConfirm(true);
                    clearTimeout(sellDupsTimer.current);
                    sellDupsTimer.current = setTimeout(() => setSellDupsConfirm(false), 4000);
                  };
                  const handleSellConfirm = async () => {
                    clearTimeout(sellDupsTimer.current);
                    setSellDupsBusy(true);
                    try {
                      const r = await fetch('/skrep/sell-all-duplicates', {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${getToken()}` },
                      });
                      if (r.ok) {
                        const { new_balance } = await r.json();
                        setSkrepBalance(new_balance);
                        vibrate([10, 50, 15, 50, 20]);
                        fetchInventory();
                      }
                    } catch {}
                    setSellDupsBusy(false);
                    setSellDupsConfirm(false);
                  };
                  const suffix = dupTotal === 1 ? 'дубликат' : dupTotal < 5 ? 'дубликата' : 'дубликатов';
                  return (
                    <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-2xl"
                      style={{ background: sellDupsConfirm ? (isDark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.06)') : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'), border: `1px solid ${sellDupsConfirm ? (isDark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.18)') : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)')}`, transition: 'background 0.2s, border-color 0.2s' }}>
                      <span className="text-[12px] font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : '#94a3b8' }}>
                        {sellDupsConfirm ? 'Продать все дубликаты?' : `${dupTotal} ${suffix}`}
                      </span>
                      <div className="flex items-center gap-2">
                        {sellDupsConfirm && (
                          <button type="button"
                            onClick={() => { setSellDupsConfirm(false); clearTimeout(sellDupsTimer.current); }}
                            className="text-[11px] font-medium px-2.5 py-1 rounded-lg"
                            style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8', background: 'transparent' }}>
                            Отмена
                          </button>
                        )}
                        <button type="button"
                          disabled={sellDupsBusy}
                          onClick={sellDupsConfirm ? handleSellConfirm : handleSellFirst}
                          onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.94)'; e.currentTarget.style.transition = 'transform 0.08s cubic-bezier(0.2,0.9,0.2,1)'; }}
                          onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                          onTouchStart={e => { e.currentTarget.style.transform = 'scale(0.94)'; e.currentTarget.style.transition = 'transform 0.08s cubic-bezier(0.2,0.9,0.2,1)'; }}
                          onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                          className="text-[11px] font-semibold px-3 py-1.5 rounded-xl shrink-0"
                          style={sellDupsConfirm
                            ? { background: isDark ? 'rgba(239,68,68,0.22)' : 'rgba(239,68,68,0.12)', color: isDark ? '#fca5a5' : '#b91c1c', border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : 'rgba(239,68,68,0.22)'}`, transition: 'background 0.2s, border-color 0.2s' }
                            : { background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', color: isDark ? 'rgba(255,255,255,0.6)' : '#64748b', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, transition: 'background 0.2s, border-color 0.2s' }
                          }>
                          {sellDupsBusy ? '...' : sellDupsConfirm ? 'Продать' : 'Продать все'}
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* ── Content ── */}
                {inventoryLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="w-6 h-6 rounded-full animate-spin border-2"
                      style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0', borderTopColor: isDark ? 'rgba(255,255,255,0.5)' : '#64748b' }} />
                  </div>
                ) : (!devBadgeVisible && filteredItems.length === 0) ? (
                  <div className="rounded-2xl px-5 py-12 text-center"
                    style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}` }}>
                    <div className="text-4xl mb-3">{searchTrimmed ? '🔍' : '🗂'}</div>
                    <p className="text-[14px] font-medium" style={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                      {searchTrimmed ? 'Ничего не найдено' : collectionTypeFilter === 'all' ? 'Коллекция пуста' : 'Нет предметов этого типа'}
                    </p>
                    <p className="text-[12px] mt-1" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
                      {searchTrimmed ? 'Попробуй другой запрос' : 'Открой дипломат, чтобы получить первый предмет'}
                    </p>
                  </div>
                ) : (
                  <div
                    key={collAnimKey}
                    style={{
                      opacity: collGridOut ? 0 : 1,
                      transform: collGridOut ? 'scale(0.97) translateY(6px)' : 'scale(1) translateY(0)',
                      filter: collGridOut ? 'blur(4px)' : 'blur(0)',
                      transition: collGridOut ? 'opacity 130ms ease, transform 130ms ease, filter 130ms ease' : 'none',
                    }}
                  >
                  <div className={isBannerOnly ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : 'grid grid-cols-2 sm:grid-cols-3 gap-3'}>

                    {/* Dev badge */}
                    {devBadgeVisible && (() => {
                      const devEquipped = user?.dev_badge_equipped !== false;
                      const rc = { accent: '#6366f1', pill: isDark ? '#a5b4fc' : '#3730a3', pillBg: isDark ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)', imgBg: isDark ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.06)' };
                      return (
                        <div key="dev-badge"
                          className="relative flex flex-col rounded-2xl overflow-hidden"
                          style={{ background: isDark ? '#1c1c1e' : '#ffffff', border: `1.5px solid ${devEquipped ? rc.accent + 'cc' : isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`, animation: 'collCardIn 360ms cubic-bezier(0.16,1,0.3,1) 0ms both' }}>
                          {devEquipped && (
                            <div className="absolute top-2 right-2 z-10 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: rc.accent, animation: 'collCheckIn 420ms cubic-bezier(0.34,1.56,0.64,1) both' }}>
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l2.5 2.5L10 3"/></svg>
                            </div>
                          )}
                          <button type="button"
                            onClick={e => { e.stopPropagation(); setCardMenu({ item: { name: 'Разработчик', item_type: 'badge', rarity: 'developer', asset: '/badges/developer_status.svg', item_id: '__dev__', inventory_id: '__dev__' }, rect: e.currentTarget.closest('[data-dev-card]')?.getBoundingClientRect() || null }); }}
                            className="absolute top-2 left-2 z-10 w-6 h-6 rounded-full items-center justify-center hidden sm:flex"
                            style={{ background: 'rgba(0,0,0,0.28)', color: 'rgba(255,255,255,0.55)' }}>⋯</button>
                          <div data-dev-card="" className="w-full aspect-square flex items-center justify-center p-6" style={{ background: rc.imgBg }}>
                            <img src="/badges/developer_status.svg" alt="Разработчик" className="w-full h-full object-contain" style={{ filter: `drop-shadow(0 4px 16px ${rc.accent}88)` }} />
                          </div>
                          <div className="px-3 pt-2.5 pb-3 flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-[13px] font-semibold leading-tight truncate" style={{ color: isDark ? '#fff' : '#0f172a' }}>Разработчик</p>
                              <span className="text-[8px] font-black uppercase tracking-wide px-1.5 py-[2px] rounded-full shrink-0" style={{ background: rc.pillBg, color: rc.pill }}>Разраб</span>
                            </div>
                            <button type="button"
                              className="w-full rounded-xl py-2 text-[12px] font-semibold"
                              style={devEquipped
                                ? { background: isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.07)', color: isDark ? '#fca5a5' : '#b91c1c', border: isDark ? '1px solid rgba(239,68,68,0.2)' : '1px solid rgba(239,68,68,0.15)' }
                                : { background: rc.pillBg, color: rc.pill, border: `1px solid ${rc.accent}44` }}
                              onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.96)'; e.currentTarget.style.transition = 'transform 0.08s cubic-bezier(0.2,0.9,0.2,1)'; }}
                              onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                              onTouchStart={e => { e.stopPropagation(); e.currentTarget.style.transform = 'scale(0.96)'; e.currentTarget.style.transition = 'transform 0.08s cubic-bezier(0.2,0.9,0.2,1)'; }}
                              onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                              onClick={() => {
                                vibrate(devEquipped ? [5, 35, 5] : [8, 45, 10]);
                                const next = !devEquipped;
                                setUser(prev => ({ ...prev, dev_badge_equipped: next, equipped_badge_asset: next ? null : prev.equipped_badge_asset }));
                                fetch('/auth/me/preferences', { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ dev_badge_equipped: next }) }).catch(() => {});
                                if (next) { fetch('/skrep/equip', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ item_id: null, slot: 'badge' }) }).then(() => fetchInventory()).catch(() => {}); }
                              }}>
                              {devEquipped ? 'Снять' : 'Надеть'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Crown badge (special — only current weekly #1) */}
                    {isCrownHolder && (collectionTypeFilter === 'all' || collectionTypeFilter === 'badge') && (() => {
                      const rc = { accent: '#f59e0b', pill: isDark ? '#fcd34d' : '#92400e', pillBg: isDark ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.10)', imgBg: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.05)' };
                      return (
                        <div key="crown-badge"
                          className="relative flex flex-col rounded-2xl overflow-hidden"
                          style={{ background: isDark ? '#1c1c1e' : '#ffffff', border: `1.5px solid ${crownEquipped ? rc.accent + 'cc' : isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`, animation: 'collCardIn 360ms cubic-bezier(0.16,1,0.3,1) 0ms both' }}>
                          {crownEquipped && (
                            <div className="absolute top-2 right-2 z-10 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: rc.accent, animation: 'collCheckIn 420ms cubic-bezier(0.34,1.56,0.64,1) both' }}>
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l2.5 2.5L10 3"/></svg>
                            </div>
                          )}
                          <div className="w-full aspect-square flex items-center justify-center p-8" style={{ background: rc.imgBg }}>
                            <img src="/badges/golden_crown.svg" alt="Корона" className="w-full h-full object-contain" style={{ filter: `drop-shadow(0 4px 20px ${rc.accent}99)` }} />
                          </div>
                          <div className="px-3 pt-2.5 pb-3 flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-[13px] font-semibold leading-tight truncate" style={{ color: isDark ? '#fff' : '#0f172a' }}>Корона лидера</p>
                              <span className="text-[8px] font-black uppercase tracking-wide px-1.5 py-[2px] rounded-full shrink-0" style={{ background: rc.pillBg, color: rc.pill }}>Топ недели</span>
                            </div>
                            <p className="text-[11px]" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Чем дольше носишь — тем больше золотых скрепок получишь</p>
                            <button type="button"
                              className="w-full rounded-xl py-2 text-[12px] font-semibold"
                              style={crownEquipped
                                ? { background: isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.07)', color: isDark ? '#fca5a5' : '#b91c1c', border: isDark ? '1px solid rgba(239,68,68,0.2)' : '1px solid rgba(239,68,68,0.15)' }
                                : { background: rc.pillBg, color: rc.pill, border: `1px solid ${rc.accent}44` }}
                              onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.96)'; e.currentTarget.style.transition = 'transform 0.08s'; }}
                              onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s'; }}
                              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s'; }}
                              onTouchStart={e => { e.stopPropagation(); e.currentTarget.style.transform = 'scale(0.96)'; e.currentTarget.style.transition = 'transform 0.08s'; }}
                              onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s'; }}
                              onClick={async () => {
                                vibrate(crownEquipped ? [5, 35, 5] : [8, 45, 10]);
                                try {
                                  const res = await fetch('/skrep/crown/toggle', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` } });
                                  if (res.ok) {
                                    const d = await res.json();
                                    setCrownEquipped(d.crown_equipped);
                                  }
                                } catch {}
                              }}>
                              {crownEquipped ? 'Снять' : 'Надеть'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Inventory items */}
                    {filteredItems.map((item, idx) => {
                      const rc = getRC(item.rarity);
                      const isBanner = item.item_type === 'banner';
                      return (
                        <div key={item.inventory_id}
                          id={`collection-item-${item.item_id}`}
                          className={`${!isBannerOnly && isBanner ? 'col-span-2' : ''} relative flex flex-col rounded-2xl overflow-hidden${item.item_id === highlightCollectionId ? ' animate-soft-bounce' : ''}`}
                          style={{ background: isDark ? '#1c1c1e' : '#ffffff', border: `1.5px solid ${item.is_equipped ? rc.accent + 'cc' : isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', animation: `collCardIn 360ms cubic-bezier(0.16,1,0.3,1) ${Math.min(idx * 48, 360)}ms both` }}
                          onContextMenu={e => e.preventDefault()}
                          onMouseDown={() => { longPressTimer.current = setTimeout(() => setCardMenu({ item, rect: null }), 500); }}
                          onMouseUp={() => { clearTimeout(longPressTimer.current); }}
                          onMouseLeave={() => { clearTimeout(longPressTimer.current); }}
                          onTouchStart={e => { const rect = e.currentTarget.getBoundingClientRect(); longPressTimer.current = setTimeout(() => setCardMenu({ item, rect }), 500); }}
                          onTouchEnd={() => { clearTimeout(longPressTimer.current); }}
                          onTouchMove={() => { clearTimeout(longPressTimer.current); }}
                        >
                          {/* Equipped checkmark */}
                          {item.is_equipped && (
                            <div className="absolute top-2 right-2 z-10 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: rc.accent, animation: 'collCheckIn 420ms cubic-bezier(0.34,1.56,0.64,1) both' }}>
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l2.5 2.5L10 3"/></svg>
                            </div>
                          )}
                          {/* ⋯ menu (desktop) */}
                          <button type="button"
                            onClick={e => { e.stopPropagation(); const r = e.currentTarget.closest(`#collection-item-${item.item_id}`)?.getBoundingClientRect(); setCardMenu({ item, rect: r || null }); }}
                            className="absolute top-2 left-2 z-10 w-6 h-6 rounded-full items-center justify-center hidden sm:flex"
                            style={{ background: 'rgba(0,0,0,0.28)', color: 'rgba(255,255,255,0.55)' }}>⋯</button>

                          {/* Image area */}
                          {item.item_type === 'frame' ? (
                            <div className="w-full aspect-square flex items-center justify-center" style={{ background: rc.imgBg }}>
                              <div style={{ position: 'relative', width: 88, height: 88 }}>
                                <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {avatar
                                    ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${avatarPos?.x ?? 50}% ${avatarPos?.y ?? 50}%` }} />
                                    : <span style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#64748b', fontSize: 30, fontWeight: 700, lineHeight: 1 }}>{(user?.fio || '?')[0].toUpperCase()}</span>
                                  }
                                </div>
                                <img src={item.asset} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transform: `translateX(${item.frame_offset_x ?? 0}%) translateY(${item.frame_offset_y ?? 0}%) scale(${(item.frame_scale ?? 100) / 100})`, transformOrigin: 'center', pointerEvents: 'none' }} />
                              </div>
                            </div>
                          ) : isBanner ? (
                            <div style={{ width: '100%', aspectRatio: '16/7', overflow: 'hidden', background: rc.imgBg }}>
                              <img src={item.asset} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                          ) : (
                            <div className="w-full aspect-square flex items-center justify-center p-6" style={{ background: rc.imgBg }}>
                              <img src={item.asset} alt={item.name} className="w-full h-full object-contain" style={{ filter: `drop-shadow(0 4px 14px ${rc.accent}66)` }} />
                            </div>
                          )}

                          {/* Info */}
                          <div className="px-3 pt-2.5 pb-3 flex flex-col gap-2">
                            <div className="flex items-start justify-between gap-1.5">
                              <div className="flex flex-col flex-1 min-w-0">
                                <p className="text-[13px] font-semibold leading-tight line-clamp-2" style={{ color: isDark ? '#fff' : '#0f172a' }}>{item.name}</p>
                                {item.uploader_name && (
                                  <p className="flex items-center gap-0.5 mt-0.5 leading-none" style={{ fontSize: 9, color: isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.30)' }}>
                                    <svg className="w-2.5 h-2.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="8" r="4"/><path strokeLinecap="round" d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                                    {item.uploader_name}
                                  </p>
                                )}
                              </div>
                              <span className="text-[8px] font-black uppercase tracking-wide px-1.5 py-[2px] rounded-full shrink-0 mt-px"
                                style={{ background: rc.pillBg, color: rc.pill }}>{rarityLabels[item.rarity] ?? item.rarity}</span>
                            </div>
                            {item.duplicate_count > 0 && (
                              <div className="flex items-center justify-between gap-1">
                                <span className="text-[10px] font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.28)' : '#94a3b8' }}>
                                  +{item.duplicate_count} лишних
                                </span>
                                <button type="button"
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (sellItemConfirm === item.item_id) {
                                      clearTimeout(sellItemTimer.current);
                                      setSellItemConfirm(null);
                                      vibrate([8, 45, 10]);
                                      fetch('/skrep/sell-item-duplicates', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                                        body: JSON.stringify({ item_id: item.item_id }),
                                      }).then(r => r.ok ? r.json() : null).then(data => {
                                        if (data) { setSkrepBalance(data.new_balance); fetchInventory(); }
                                      }).catch(() => {});
                                    } else {
                                      vibrate(8);
                                      setSellItemConfirm(item.item_id);
                                      clearTimeout(sellItemTimer.current);
                                      sellItemTimer.current = setTimeout(() => setSellItemConfirm(null), 4000);
                                    }
                                  }}
                                  onMouseDown={e => { e.stopPropagation(); e.currentTarget.style.transform = 'scale(0.92)'; e.currentTarget.style.transition = 'transform 0.08s cubic-bezier(0.2,0.9,0.2,1)'; }}
                                  onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                                  onTouchStart={e => { e.stopPropagation(); e.currentTarget.style.transform = 'scale(0.92)'; e.currentTarget.style.transition = 'transform 0.08s cubic-bezier(0.2,0.9,0.2,1)'; }}
                                  onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                                  className="text-[10px] font-semibold px-2 py-[3px] rounded-lg shrink-0"
                                  style={sellItemConfirm === item.item_id
                                    ? { background: isDark ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.1)', color: isDark ? '#fca5a5' : '#b91c1c', border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : 'rgba(239,68,68,0.2)'}`, transition: 'background 0.15s, border-color 0.15s' }
                                    : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`, transition: 'background 0.15s, border-color 0.15s' }
                                  }>
                                  {sellItemConfirm === item.item_id ? 'Продать?' : `×${item.duplicate_count}`}
                                </button>
                              </div>
                            )}
                            <button type="button"
                              className="w-full rounded-xl py-2 text-[12px] font-semibold"
                              style={item.is_equipped
                                ? { background: isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.07)', color: isDark ? '#fca5a5' : '#b91c1c', border: isDark ? '1px solid rgba(239,68,68,0.2)' : '1px solid rgba(239,68,68,0.15)' }
                                : { background: rc.pillBg, color: rc.pill, border: `1px solid ${rc.accent}44` }}
                              onMouseDown={e => { e.stopPropagation(); e.currentTarget.style.transform = 'scale(0.96)'; e.currentTarget.style.transition = 'transform 0.08s cubic-bezier(0.2,0.9,0.2,1)'; }}
                              onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                              onTouchStart={e => { e.stopPropagation(); e.currentTarget.style.transform = 'scale(0.96)'; e.currentTarget.style.transition = 'transform 0.08s cubic-bezier(0.2,0.9,0.2,1)'; }}
                              onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.transition = 'transform 0.28s cubic-bezier(0.16,1,0.3,1)'; }}
                              onClick={e => {
                                e.stopPropagation();
                                vibrate(item.is_equipped ? [5, 35, 5] : [8, 45, 10]);
                                if (item.is_equipped) {
                                  handleEquip(item);
                                } else {
                                  setEquipPreviewItem(item);
                                  setEquipPreviewPos(item.item_type === 'banner' ? { ...bannerPos } : { x: 50, y: 50 });
                                  setEquipPreviewPosMobile(item.item_type === 'banner' ? (bannerPosMobile || { x: 50, y: 50 }) : { x: 50, y: 50 });
                                  setEquipPreviewZoom(item.item_type === 'banner' ? bannerZoom : 1);
                                  setEquipPreviewZoomMobile(item.item_type === 'banner' ? bannerZoomMobile : 1);
                                }
                              }}>
                              {item.is_equipped ? 'Снять' : 'Надеть'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
      , profileContainerEl)}

      </div>{/* /profile panel */}

      {/* ── CASE OPENING ANIMATION PORTAL ── */}
      {openAnim && createPortal((() => {
        const C = openAnim.caseData || { b1:'#252e3f',b2:'#0c1220',h1:'#7a5c28',h2:'#d4a043',h3:'#f5c842',h4:'#9a6e20',name:'',rarity:'' };
        const phase  = openAnim.phase;
        const result = openAnim.result;
        const isBroken = result?.result === 'broken';
        const isWin    = result?.result === 'win';
        const closeAnim = () => setOpenAnim(null);
        const animRarity = openAnim.caseData?.case_type === 'random' ? 'chroma' : (openAnim.caseData?.case_type || 'common');
        const particles = Array.from({ length: 14 }, (_, i) => {
          const angle = (i / 14) * Math.PI * 2;
          const dist  = 60 + (i % 5) * 20;
          return { px: Math.round(Math.cos(angle) * dist), py: Math.round(Math.sin(angle) * dist),
                   size: 3 + (i % 4), color: [C.h2,C.h3,'rgba(255,255,255,0.9)',C.h1][i % 4] };
        });
        return (
          <div className="fixed inset-0 z-[500]" onClick={phase === 'reveal' ? closeAnim : undefined}>
            {/* Scrim */}
            <div className="absolute inset-0 bg-black/70 pointer-events-none"/>

            {/* Animation — always visible (stays during reveal so opened case stays on screen) */}
            <div className="absolute inset-0" style={{ pointerEvents: phase === 'reveal' ? 'none' : 'auto' }}>
              <CaseOpeningAnim
                rarity={animRarity}
                golden={openAnim.golden ?? false}
                broken={result ? result.result === 'broken' : undefined}
                itemAsset={result?.result === 'win' ? (result.item?.asset || null) : null}
                itemType={result?.item?.item_type || null}
                onDone={() => {
                  setOpenAnim(s => {
                    if (!s) return null;
                    if (s.result) return { ...s, phase: 'reveal' };
                    return { ...s, phase: 'anim_done' };
                  });
                }}
              />
            </div>

            {phase === 'reveal' && (
              <div className="absolute inset-0 pointer-events-auto select-none">
                <div className="absolute w-full flex flex-col items-center gap-1.5 text-center"
                  style={{ top: 'calc(50% + min(46vw, 235px) + 16px)' }}>
                  {isWin ? (
                    <>
                      <span className="font-black text-[28px] text-white" style={{ textShadow:`0 0 22px ${C.h3}` }}>{result.item?.name}</span>
                      <span className="font-bold text-[14px] uppercase tracking-widest" style={{ color:C.h3 }}>{result.item?.rarity}</span>
                      {result.item?.is_duplicate && <span className="text-orange-300 text-[13px]">дубликат · +{result.item.skrep_refund} <img src="/paperclip.svg" alt="" className="inline w-3 h-3"/></span>}
                    </>
                  ) : isBroken ? (
                    <>
                      <span className="font-black text-[30px] text-red-400" style={{ textShadow:'0 0 22px rgba(239,68,68,0.8)' }}>Сломалась!</span>
                      {result?.recycled_clips > 0 && <span className="text-emerald-400 text-[15px] font-semibold">+{result.recycled_clips} целая из обломков!</span>}
                    </>
                  ) : null}
                  {result?.skrep_bonus > 0 && (
                    <span className="font-black text-[22px] mt-1" style={{ color:'#f5c842', textShadow:'0 0 18px rgba(245,200,66,0.8)' }}>
                      +{result.skrep_bonus} скрепок из хромо!
                    </span>
                  )}
                  {result?.error && <span className="text-red-400 text-[15px]">{result.error}</span>}
                  <span className="text-white/40 text-[12px] mt-1">Нажми чтобы закрыть</span>
                </div>
              </div>
            )}
          </div>
        );
      })(), document.body)}

      {pumpkinAnimActive && createPortal(
        (() => {
          const isReject = pumpkinAnimKind === 'reject';
          const rejectUrls = ['/pumpkin_rejects_lollipop.svg', '/pumpkin_rejects_caramel.svg', '/pumpkin_rejects_corn.svg'];
          const svgContent = isReject
            ? (pumpkinRejectSvgs[pumpkinRejectIndex] || pumpkinRejectSvgs[0] || null)
            : null;
          const fallbackSrc = isReject ? rejectUrls[pumpkinRejectIndex % rejectUrls.length] : null;
          return (
            <div
              className="fixed inset-0 z-[500] flex items-center justify-center overflow-hidden"
              style={{ cursor: pumpkinAnimEnded ? 'pointer' : 'default' }}
              onClick={() => { if (pumpkinAnimEnded) setPumpkinAnimActive(false); }}
            >
              <div className="absolute inset-0 bg-black/80 pointer-events-none" />
              {pumpkinAnimEnded && (
                <span
                  className="absolute left-1/2 -translate-x-1/2 text-white/60 text-[13px] font-medium"
                  style={{ bottom: 'max(32px, env(safe-area-inset-bottom, 0px) + 24px)', animation: 'pumpkinHintIn 400ms ease-out both' }}
                >
                  Нажмите чтобы выйти
                </span>
              )}
              <style>{`@keyframes pumpkinHintIn { 0%{opacity:0;transform:translate(-50%,8px);} 100%{opacity:1;transform:translate(-50%,0);} }`}</style>
              {isReject ? (
                <div className="relative" style={{ width: 'min(110vw, calc(100vh * 238 / 318))', height: 'min(100vh, calc(110vw * 318 / 238))', pointerEvents: 'none' }}>
                  <div
                    ref={pumpkinAnimDivRef}
                    className="pumpkin-anim-wrap w-full h-full"
                  />
                  {!svgContent && (
                    <img src={fallbackSrc} alt="" className="w-full h-full object-contain absolute inset-0" />
                  )}
                </div>
              ) : (
                <div className="relative" style={{ width: 'min(110vw, calc(100vh * 238 / 318))', height: 'min(100vh, calc(110vw * 318 / 238))', pointerEvents: 'none' }}>
                  <div ref={pumpkinDropHostRef} className="w-full h-full" />
                </div>
              )}
            </div>
          );
        })(),
        document.body
      )}

      {/* ── SKREP PANEL (slide from right) ── */}
      <div
        ref={skrepScrollRef}
        className={`fixed inset-0 z-[70] overflow-y-auto hide-scrollbar sm:absolute sm:left-0 sm:right-0 sm:top-0 sm:bottom-auto sm:z-auto sm:overflow-y-auto sm:max-h-[calc(100vh-120px)] sm:min-h-full transition-transform duration-300 ease-in-out will-change-transform pb-14 sm:pb-0 ${HALLOWEEN_MODE ? '' : 'bg-[#f2f2f7] dark:bg-[#0d0d14]'}`}
        style={{
          transform: skrepView ? 'translateX(0)' : 'translateX(105%)',
          ...(HALLOWEEN_MODE ? {
            background: isDark
              ? 'radial-gradient(ellipse at 50% -10%, #2a0b3a 0%, #150624 35%, #0a0410 70%, #050208 100%)'
              : 'radial-gradient(ellipse at 50% -10%, #ffe4bd 0%, #ffcf96 40%, #fab878 100%)',
          } : {}),
        }}
        onScroll={e => sessionStorage.setItem('aud_skrep_scroll', e.currentTarget.scrollTop)}
      >
        {HALLOWEEN_MODE && (
          <>
            {/* Spider web — top-left corner */}
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute top-0 left-0 select-none"
              width="160" height="160" viewBox="0 0 160 160"
              style={{ opacity: isDark ? 0.22 : 0.28, zIndex: 1 }}
            >
              <g fill="none" stroke={isDark ? '#f3f2ea' : '#4a2f1a'} strokeWidth="1.2" strokeLinecap="round">
                <path d="M0 0 L160 0" />
                <path d="M0 0 L0 160" />
                <path d="M0 0 L160 160" />
                <path d="M0 0 L160 80" />
                <path d="M0 0 L80 160" />
                <path d="M0 0 L120 40" />
                <path d="M0 0 L40 120" />
                <path d="M20 0 Q22 20 0 22" />
                <path d="M40 0 Q44 40 0 44" />
                <path d="M60 0 Q66 60 0 66" />
                <path d="M90 0 Q100 90 0 100" />
                <path d="M130 0 Q140 130 0 140" />
              </g>
            </svg>
            {/* Spider web — top-right corner (mirrored) */}
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute top-0 right-0 select-none"
              width="160" height="160" viewBox="0 0 160 160"
              style={{ opacity: isDark ? 0.22 : 0.28, zIndex: 1, transform: 'scaleX(-1)' }}
            >
              <g fill="none" stroke={isDark ? '#f3f2ea' : '#4a2f1a'} strokeWidth="1.2" strokeLinecap="round">
                <path d="M0 0 L160 0" />
                <path d="M0 0 L0 160" />
                <path d="M0 0 L160 160" />
                <path d="M0 0 L160 80" />
                <path d="M0 0 L80 160" />
                <path d="M20 0 Q22 20 0 22" />
                <path d="M40 0 Q44 40 0 44" />
                <path d="M60 0 Q66 60 0 66" />
                <path d="M90 0 Q100 90 0 100" />
                <path d="M130 0 Q140 130 0 140" />
              </g>
            </svg>
          </>
        )}
        {/* Header */}
        <div className="relative flex items-center justify-between px-4 py-2 bg-white/90 dark:bg-[#0d0d14]/95 backdrop-blur-sm border-b border-slate-100 dark:border-slate-800 sticky top-0 z-10">
          <button
            type="button"
            onClick={() => { setSkrepView(false); setCaseResult(null); }}
            className="flex items-center gap-0.5 text-blue-500 font-medium text-[15px] py-2 pr-3 -ml-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
            Назад
          </button>
          <span className="absolute left-1/2 -translate-x-1/2 font-semibold text-[16px] pointer-events-none z-20 flex items-center gap-1.5"
            style={{
              color: HALLOWEEN_MODE ? '#ff8a2b' : (isDark ? '#f1f5f9' : '#0f172a'),
              textShadow: HALLOWEEN_MODE ? '0 0 12px rgba(255,138,43,0.55)' : undefined,
              letterSpacing: HALLOWEEN_MODE ? '0.04em' : undefined,
            }}>
            {HALLOWEEN_MODE && <span style={{ fontSize: 18, filter: 'drop-shadow(0 0 6px rgba(255,138,43,0.7))' }}>🎃</span>}
            Магазин
            {HALLOWEEN_MODE && <span style={{ fontSize: 18, filter: 'drop-shadow(0 0 6px rgba(255,138,43,0.7))' }}>🦇</span>}
          </span>
          <div className="flex flex-nowrap items-center py-2 pl-2 -mr-1 gap-4 sm:gap-6 overflow-x-auto hide-scrollbar [&>*]:shrink-0" style={{ maxWidth: 'calc(50% - 48px)', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
            <style>{`.hide-scrollbar::-webkit-scrollbar{display:none}`}</style>
            <div className="flex items-center gap-0.5">
              <style>{`
                @keyframes balDigitIn {
                  0%   { opacity: 0; transform: translateY(-6px); }
                  100% { opacity: 1; transform: translateY(0); }
                }
              `}</style>
              <img src="/paperclip_silver.svg" alt="" className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />
              <span id="skrep-balance-display" className="font-bold text-[13px] sm:text-[15px] text-slate-800" style={{ fontVariantNumeric: 'tabular-nums', display: 'inline-flex' }}>
                {displayBalance == null ? '—' : String(displayBalance).split('').map((d, i) => (
                  <span key={`${i}-${d}`} style={{ display: 'inline-block', animation: 'balDigitIn 120ms cubic-bezier(0.16,1,0.3,1) both', animationDelay: `${i * 18}ms` }}>{d}</span>
                ))}
              </span>
            </div>
            {(goldenClips ?? 0) > 0 && (
              <div className="flex items-center gap-0.5">
                <img src="/paperclip_gold.svg" alt="" className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />
                <span className="font-bold text-[13px] sm:text-[15px]" style={{ color: '#f2c14e' }}>{goldenClips}</span>
              </div>
            )}
            {(skrepBroken ?? 0) > 0 && (
              <div className="flex items-center gap-0.5">
                <img src="/paperclip_broken.svg" alt="" className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />
                <span className="font-bold text-[13px] sm:text-[14px]" style={{ color: '#e0605a' }}>{skrepBroken}</span>
              </div>
            )}
            {HALLOWEEN_MODE && (
              <button
                type="button"
                onClick={openCandyExchange}
                title="Обменять скрепки на конфеты (3 → 1)"
                className="flex items-center gap-0.5 bg-transparent border-0 cursor-pointer p-0 active:scale-95 transition-transform"
              >
                <img src="/candy_pile_3_pixel.svg" alt="" className="w-6 h-6 sm:w-7 sm:h-7 object-contain" style={{ imageRendering: 'pixelated' }} />
                <span className="font-bold text-[13px] sm:text-[15px]" style={{ color: '#ff9228' }}>{candyBalance ?? 0}</span>
              </button>
            )}
            {isAdmin && (
              <button type="button" onClick={openAwardModal} title="Начислить скрепки"
                className="ml-1 w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                style={{
                  background: 'rgba(59,130,246,0.1)',
                  border: '1px solid rgba(59,130,246,0.3)',
                  color: '#1d4ed8',
                }}>
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="px-4 pt-0 pb-8 space-y-4">

          {true && <>
          <span data-clip-zone="shop" style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }} />

          {/* Дипломаты — выбор кейса */}
          {(() => {
            const CASE_CHANCES   = { common: 10, epic: 5, legendary: 2, random: 3 };
            const CASE_GUAR_COST = { common: 10, epic: 25, legendary: 60, random: 150 };
            const CASES = [
              { id: 0, case_type: 'common',    name: 'Обычный',     rarity: 'Обычный',   img: '/case-rare.png',
                desc: '',
                b1: isDark ? '#060f1e' : '#f2f3f5', b2: isDark ? '#030810' : '#e8eaed', h1: '#1d4ed8', h2: '#3b82f6', h3: '#60a5fa', h4: '#1e40af', band: isDark ? '#04090d' : '#c7d9fa',
                glow: '0 0 0 2px #3b82f677, 0 0 28px 8px rgba(59,130,246,0.40)',
                pillBg: 'rgba(59,130,246,0.15)', pillBorder: '#3b82f6', pillColor: isDark ? '#93c5fd' : '#1d4ed8' },
              { id: 1, case_type: 'epic',      name: 'Эпический',   rarity: 'Эпический', img: '/case-epic.png',
                desc: 'Редкость чувствуется с первого взгляда.',
                b1: isDark ? '#130830' : '#f2f3f5', b2: isDark ? '#080418' : '#e8eaed', h1: '#7c3aed', h2: '#8b5cf6', h3: '#a78bfa', h4: '#6d28d9', band: isDark ? '#0d0522' : '#d4befa',
                glow: '0 0 0 2px #8b5cf677, 0 0 28px 8px rgba(139,92,246,0.45)',
                pillBg: 'rgba(139,92,246,0.15)', pillBorder: '#8b5cf6', pillColor: isDark ? '#c4b5fd' : '#6d28d9' },
              { id: 2, case_type: 'legendary', name: 'Легендарный', rarity: 'Legendary', img: '/case-legendary.png',
                desc: 'Здесь уже не про удачу. Это — награда.',
                b1: isDark ? '#1a0a00' : '#f2f3f5', b2: isDark ? '#0a0400' : '#e8eaed', h1: '#d97706', h2: '#f59e0b', h3: '#fbbf24', h4: '#92400e', band: isDark ? '#120700' : '#fde68a',
                glow: '0 0 0 2px #f59e0b77, 0 0 28px 8px rgba(251,159,11,0.45)',
                pillBg: 'rgba(251,191,36,0.15)', pillBorder: '#f59e0b', pillColor: isDark ? '#fde68a' : '#92400e' },
              { id: 3, case_type: 'random',    name: 'Хромо',       rarity: 'Random',    img: '/case-chrome.png', isChrome: true,
                desc: 'Любая редкость. Любой исход.',
                b1: isDark ? '#0f0f1a' : '#f2f3f5', b2: isDark ? '#080810' : '#e8eaed', h1: '#e879f9', h2: '#f0abfc', h3: '#f5d0fe', h4: '#c026d3', band: isDark ? '#0a0a14' : '#e8b4fc',
                glow: '0 0 0 2px rgba(200,100,240,0.6), 0 0 28px 8px rgba(200,100,240,0.35)',
                pillBg: 'rgba(232,121,249,0.15)', pillBorder: '#e879f9', pillColor: isDark ? '#f5d0fe' : '#a21caf' },
            ];
            const sc = selectedCase;
            return (
              <div>
                <style>{`
                  @keyframes chrome-ring {
                    0%   { box-shadow: 0 0 0 2px #ff80c0cc, 0 0 30px 8px rgba(255,128,192,0.45); }
                    20%  { box-shadow: 0 0 0 2px #80b0ffcc, 0 0 30px 8px rgba(128,176,255,0.45); }
                    40%  { box-shadow: 0 0 0 2px #80ffd0cc, 0 0 30px 8px rgba(128,255,208,0.45); }
                    60%  { box-shadow: 0 0 0 2px #fff080cc, 0 0 30px 8px rgba(255,240,128,0.45); }
                    80%  { box-shadow: 0 0 0 2px #d080ffcc, 0 0 30px 8px rgba(208,128,255,0.45); }
                    100% { box-shadow: 0 0 0 2px #ff80c0cc, 0 0 30px 8px rgba(255,128,192,0.45); }
                  }
                  @keyframes chrome-hue {
                    from { filter: hue-rotate(0deg) saturate(1.8) brightness(1.15); }
                    to   { filter: hue-rotate(360deg) saturate(1.8) brightness(1.15); }
                  }
                  @keyframes chrome-pill-shift {
                    from { filter: hue-rotate(0deg) saturate(1.4); }
                    to   { filter: hue-rotate(360deg) saturate(1.4); }
                  }
                  /* Infinite box-shadow + filter:hue-rotate animations devastate iOS WebKit
                     compositor (full-layer repaint each frame). Enable only on desktop pointers. */
                  @media (hover: hover) and (pointer: fine) {
                    .chrome-card { animation: chrome-ring 4s linear infinite !important; }
                    .chrome-svg  { animation: chrome-hue 5s linear infinite; }
                    .chrome-pill { animation: chrome-pill-shift 4s linear infinite; }
                  }
                  @media (hover: none) {
                    .chrome-card {
                      box-shadow: 0 0 0 2px rgba(200,100,240,0.6), 0 0 24px 6px rgba(200,100,240,0.3) !important;
                    }
                    .chrome-svg  { filter: saturate(1.6) brightness(1.1); }
                    .chrome-pill { filter: saturate(1.3); }
                  }
                `}</style>
                <div className="px-3 pt-3 pb-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 py-2">
                    {HALLOWEEN_MODE && (() => {
                      const pumpkinDisabled = pumpkinFeeding || (candyBalance ?? 0) < 1 || !pumpkinAvailable;
                      const triggerPumpkinOpen = async () => {
                        if (pumpkinDisabled) {
                          if (!pumpkinAvailable) setPumpkinMessage({ ok: false, text: 'Пока нет товаров — тыкву нельзя кормить' });
                          else if ((candyBalance ?? 0) < 1) setPumpkinMessage({ ok: false, text: 'Нужна 1 конфета' });
                          return;
                        }
                        setPumpkinSelected(false);
                        const result = await feedPumpkin();
                        if (!result || !result.ok) return;
                        const kind = result.dropped ? 'drop' : 'reject';
                        if (kind === 'drop' && pumpkinDropSvgs.length) {
                          setPumpkinDropIndex(Math.floor(Math.random() * pumpkinDropSvgs.length));
                        } else if (kind === 'reject' && pumpkinRejectSvgs.length) {
                          setPumpkinRejectIndex(Math.floor(Math.random() * pumpkinRejectSvgs.length));
                        }
                        setPumpkinAnimKind(kind);
                        setPumpkinAnimActive(true);
                      };
                      return (
                        <div className="col-span-2 sm:col-span-4 flex justify-center order-first mb-2">
                          <div className="w-[calc(100%-0.5rem)] sm:w-[calc(50%-0.75rem)] flex flex-col items-center">
                            <div
                              className="relative select-none w-full flex flex-col items-center"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setPumpkinSelected((s) => !s)}
                              role="button"
                              aria-label="Тыква"
                            >
                              <div className="relative flex justify-center w-full" style={{ height: '280px' }}>
                                {pumpkinSvg ? (
                                  <div
                                    style={{ height: '100%', aspectRatio: '87 / 80' }}
                                    dangerouslySetInnerHTML={{ __html: pumpkinSvg }}
                                  />
                                ) : (
                                  <img
                                    src="/pumpkin_hover.svg"
                                    alt="Тыква"
                                    className="h-full object-contain"
                                  />
                                )}
                                {pumpkinSelected && (
                                  <button
                                    type="button"
                                    disabled={pumpkinDisabled}
                                    onClick={(e) => { e.stopPropagation(); triggerPumpkinOpen(); }}
                                    className="group/p1 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[38px] px-5 rounded-full disabled:opacity-30 overflow-hidden active:scale-[0.97]"
                                    style={{
                                      minWidth: '140px',
                                      border: '1px solid rgba(255,138,28,0.6)',
                                      background: 'rgba(10,6,0,0.72)',
                                      backdropFilter: 'blur(6px)',
                                      WebkitBackdropFilter: 'blur(6px)',
                                      boxShadow: '0 6px 24px rgba(255,138,28,0.35), 0 0 0 2px rgba(255,138,28,0.15)',
                                      transition: 'transform 120ms cubic-bezier(0.2,0.9,0.2,1), background 150ms ease, border-color 150ms ease',
                                      cursor: pumpkinDisabled ? 'not-allowed' : 'pointer',
                                    }}
                                    onMouseEnter={SUPPORTS_HOVER ? (e => {
                                      if (!pumpkinDisabled) {
                                        e.currentTarget.style.borderColor = 'rgba(255,138,28,0.95)';
                                        e.currentTarget.style.background = 'rgba(20,10,0,0.82)';
                                      }
                                    }) : undefined}
                                    onMouseLeave={SUPPORTS_HOVER ? (e => {
                                      e.currentTarget.style.borderColor = 'rgba(255,138,28,0.6)';
                                      e.currentTarget.style.background = 'rgba(10,6,0,0.72)';
                                    }) : undefined}
                                  >
                                    <span className="absolute inset-0 flex items-center justify-center gap-1.5 group-hover/p1:opacity-0 transition-opacity duration-150">
                                      <img src="/candy_pile_3_pixel.svg" alt="" className="object-contain" style={{ width: '22px', height: '22px', opacity: 1, imageRendering: 'pixelated' }} />
                                      <span className="font-semibold text-[13px] tracking-wide" style={{ color: '#ffcc80' }}>1</span>
                                    </span>
                                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/p1:opacity-100 transition-opacity duration-150">
                                      <span className="font-black text-[16px]" style={{ color: '#ffcc80' }}>15%</span>
                                    </span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    {CASES.map((c) => {
                      const isSel = sc === c.id;
                      return (
                        <div
                          key={c.id}
                          className={`group relative rounded-2xl overflow-hidden select-none${c.isChrome ? ' chrome-card' : ''}`}
                          style={{
                            minHeight: '220px',
                            background: `linear-gradient(168deg, ${c.b1} 0%, ${c.b2} 100%)`,
                            border: `1.5px solid ${isSel ? c.h3 + '70' : c.h3 + '22'}`,
                            boxShadow: isSel
                              ? `${c.glow}, inset 0 1px 0 rgba(255,255,255,0.14), inset 0 32px 32px -22px ${c.h3}22`
                              : `inset 0 1px 0 rgba(255,255,255,0.07), inset 0 16px 24px -16px ${c.h3}14`,
                            transform: isSel ? 'scale(1.04) translateY(-2px)' : 'scale(1)',
                            transition: 'transform 220ms cubic-bezier(0.2,0.9,0.2,1), box-shadow 220ms ease, border-color 160ms ease',
                            cursor: isSel ? 'default' : 'pointer',
                          }}
                          onClick={() => { if (!isSel) { setSelectedCase(c.id); setCaseResult(null); } }}
                          onMouseEnter={SUPPORTS_HOVER ? (e => {
                            if (!isSel) {
                              e.currentTarget.style.transform = 'scale(1.05) translateY(-4px)';
                              e.currentTarget.style.boxShadow = `${c.glow}, inset 0 1px 0 rgba(255,255,255,0.14), inset 0 32px 32px -22px ${c.h3}22`;
                              e.currentTarget.style.borderColor = `${c.h3}55`;
                            }
                          }) : undefined}
                          onMouseLeave={SUPPORTS_HOVER ? (e => {
                            if (!isSel) {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.boxShadow = `inset 0 1px 0 rgba(255,255,255,0.07), inset 0 16px 24px -16px ${c.h3}14`;
                              e.currentTarget.style.borderColor = `${c.h3}22`;
                            }
                          }) : undefined}
                        >
                          {/* Diagonal shine on hover */}
                          <div
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none z-10 transition-opacity duration-300"
                            style={{ background: 'linear-gradient(128deg, transparent 22%, rgba(255,255,255,0.065) 48%, transparent 74%)' }}
                          />

                          {/* Top accent stripe */}
                          <div className="absolute top-0 left-0 right-0 h-[3px] z-10" style={{ background: `linear-gradient(90deg, transparent, ${c.h3}cc, transparent)` }} />

                          {/* Card body */}
                          <div className="relative z-0 flex flex-col items-center pt-4 pb-3 px-2 gap-1.5 text-center">
                            {/* Pill */}
                            {c.isChrome ? (
                              <span className="chrome-pill inline-flex items-center gap-1 text-[8px] font-black uppercase tracking-[0.13em] px-2.5 py-[3px] rounded-full"
                                style={{ border: '1px solid rgba(200,140,255,0.7)', background: 'rgba(180,100,255,0.12)' }}>
                                <span style={{ background: 'linear-gradient(90deg,#ff80c0,#90b0ff,#70ffd0,#ffd070,#d080ff,#ff80c0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                  ✦ Хромо ✦
                                </span>
                              </span>
                            ) : c.case_type === 'legendary' ? (
                              <span className="inline-flex items-center gap-1 text-[8px] font-black uppercase tracking-[0.13em] px-2.5 py-[3px] rounded-full"
                                style={{ border: '1px solid #f59e0bcc', background: isDark ? 'rgba(251,191,36,0.13)' : 'rgba(251,191,36,0.20)', color: isDark ? '#fde68a' : '#92400e', boxShadow: '0 0 8px rgba(251,191,36,0.3)' }}>
                                ✦ Легендарный ✦
                              </span>
                            ) : (
                              <span className="text-[8px] font-black uppercase tracking-[0.13em] px-2.5 py-[3px] rounded-full"
                                style={{ border: `1px solid ${c.pillBorder}cc`, background: c.pillBg, color: c.pillColor }}>
                                {c.rarity}
                              </span>
                            )}

                            {/* Title */}

                            {/* Фото дипломата */}
                            <div className={`flex justify-center w-full mt-2${c.isChrome ? ' chrome-svg' : ''}`}>
                              <img
                                src={c.img}
                                alt={c.name}
                                className="w-full object-contain"
                                style={{ maxHeight: '120px', filter: `drop-shadow(0 4px 16px ${c.h3}88)` }}
                              />
                            </div>
                          </div>

                          {/* Overlay: selected state with pills */}
                          {isSel && (
                            <div
                              className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-2.5 py-3 z-20"
                              style={{
                                background: `linear-gradient(168deg, ${c.b1}f2 0%, ${c.b2}f8 100%)`,
                                boxShadow: `inset 0 1px 0 ${c.h3}45, inset 0 36px 36px -24px ${c.h3}20`,
                                border: `1.5px solid ${c.h3}55`,
                              }}
                            >
                              {/* Close */}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSelectedCase(null); setCaseResult(null); }}
                                className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-150 cursor-pointer leading-none"
                                style={{ color: `${c.h3}90`, background: `${c.h3}15` }}
                                onMouseEnter={SUPPORTS_HOVER ? (e => { e.currentTarget.style.background = `${c.h3}30`; e.currentTarget.style.color = c.h3; }) : undefined}
                                onMouseLeave={SUPPORTS_HOVER ? (e => { e.currentTarget.style.background = `${c.h3}15`; e.currentTarget.style.color = `${c.h3}90`; }) : undefined}
                              >
                                <svg viewBox="0 0 10 10" className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                                  <path d="M2 2l6 6M8 2l-6 6"/>
                                </svg>
                              </button>


                              {/* Pill 1 — single */}
                              <button
                                type="button"
                                disabled={caseBusy || (skrepBalance ?? 0) < 1}
                                onClick={(e) => { e.stopPropagation(); handleOpenCase('single', c); }}
                                className="group/s1 relative w-full h-[34px] rounded-full disabled:opacity-30 overflow-hidden active:scale-[0.97]"
                                style={{
                                  border: isDark ? '1px dashed rgba(255,255,255,0.32)' : `1px solid ${c.h2}44`,
                                  background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.55)',
                                  transition: 'transform 120ms cubic-bezier(0.2,0.9,0.2,1), background 150ms ease, border-color 150ms ease',
                                  cursor: (caseBusy || (skrepBalance ?? 0) < 1) ? 'not-allowed' : 'pointer',
                                }}
                                onMouseEnter={SUPPORTS_HOVER ? (e => {
                                  if (!(caseBusy || (skrepBalance ?? 0) < 1)) {
                                    e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.6)' : `${c.h2}88`;
                                    e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.82)';
                                  }
                                }) : undefined}
                                onMouseLeave={SUPPORTS_HOVER ? (e => {
                                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.32)' : `${c.h2}44`;
                                  e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.55)';
                                }) : undefined}
                              >
                                <span className="absolute inset-0 flex items-center justify-center gap-1.5 group-hover/s1:opacity-0 transition-opacity duration-150">
                                  {caseBusy
                                    ? <div className="w-3 h-3 border-[1.5px] rounded-full animate-spin" style={{ borderColor: isDark ? 'rgba(255,255,255,0.25)' : `${c.h2}35`, borderTopColor: isDark ? 'rgba(255,255,255,0.8)' : c.h2 }} />
                                    : <>
                                        <img src="/paperclip_silver.svg" alt="" className="object-contain" style={{ width: '20px', height: '20px', opacity: 0.9 }} />
                                        <span className="font-semibold text-[11.5px] tracking-wide" style={{ color: isDark ? 'rgba(255,255,255,0.85)' : c.h1 }}>1</span>
                                      </>
                                  }
                                </span>
                                <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/s1:opacity-100 transition-opacity duration-150">
                                  <span className="font-black text-[16px]" style={{ color: isDark ? 'rgba(255,255,255,0.95)' : c.h1 }}>
                                    {pityChances[c.case_type] ?? CASE_CHANCES[c.case_type] ?? 10}%
                                  </span>
                                </span>
                              </button>

                              {/* Pill 2 — golden clip */}
                              {(() => {
                                const gCost = c.case_type === 'random' ? 2 : 1;
                                const hasGolden = (goldenClips ?? 0) >= gCost;
                                return (
                                  <button
                                    type="button"
                                    disabled={caseBusy || !hasGolden}
                                    onClick={(e) => { e.stopPropagation(); handleOpenCaseGolden(c); }}
                                    className="group/g1 relative w-full h-[30px] rounded-full disabled:opacity-30 overflow-hidden active:scale-[0.97]"
                                    style={{
                                      background: hasGolden
                                        ? isDark
                                          ? 'linear-gradient(135deg, rgba(146,64,14,0.55) 0%, rgba(180,83,9,0.6) 50%, rgba(217,119,6,0.55) 100%)'
                                          : 'linear-gradient(135deg, rgba(253,230,138,0.7) 0%, rgba(251,191,36,0.75) 60%, rgba(245,158,11,0.7) 100%)'
                                        : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                      border: hasGolden
                                        ? `1px solid ${isDark ? 'rgba(251,191,36,0.45)' : 'rgba(217,119,6,0.5)'}`
                                        : isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.07)',
                                      boxShadow: hasGolden ? `0 1px 8px rgba(251,191,36,${isDark ? '0.25' : '0.2'})` : 'none',
                                      transition: 'transform 120ms cubic-bezier(0.2,0.9,0.2,1)',
                                      cursor: (caseBusy || !hasGolden) ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    <span className="absolute inset-0 flex items-center justify-center gap-1 group-hover/g1:opacity-0 transition-opacity duration-150">
                                      <img src="/paperclip_gold.svg" alt="" className="object-contain" style={{ width: '22px', height: '22px', opacity: hasGolden ? 1 : 0.3, filter: hasGolden ? 'drop-shadow(0 0 4px rgba(251,191,36,0.6))' : 'none' }} />
                                      <span className="font-semibold text-[11.5px] tracking-wide" style={{
                                        color: hasGolden
                                          ? isDark ? '#fde68a' : '#92400e'
                                          : isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.2)',
                                      }}>
                                        {gCost === 2 ? '2' : '1'}
                                      </span>
                                    </span>
                                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/g1:opacity-100 transition-opacity duration-150">
                                      <span className="font-black text-[14px]" style={{ color: hasGolden ? (isDark ? '#fde68a' : '#92400e') : (isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.2)') }}>100%</span>
                                    </span>
                                  </button>
                                );
                              })()}

                              {/* Текущий шанс выигрыша */}
                              <div className="w-full flex items-center justify-center gap-1 mt-0.5">
                                <span className="text-[10px] font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.38)' : `${c.h2}99` }}>
                                  шанс:
                                </span>
                                <span className="text-[10px] font-bold tabular-nums" style={{ color: isDark ? 'rgba(255,255,255,0.6)' : c.h1 }}>
                                  {pityChances[c.case_type] ?? CASE_CHANCES[c.case_type] ?? 10}%
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Магазин */}
          {(() => {
            const SHOP_MODES = [
              { id: 'shop',    label: 'Витрина' },
              { id: 'sets',    label: 'Сеты' },
              { id: 'vault',   label: 'Хранилище' },
              { id: 'premium', label: 'Премиум', premium: true },
            ];
            const VAULT_TYPES = [
              { id: 'all',    label: 'Всё' },
              { id: 'frame',  label: 'Рамки' },
              { id: 'banner', label: 'Баннеры' },
              { id: 'badge',  label: 'Бейджики' },
            ];
            const RARITY_ORDER = { chromatic: 0, legendary: 1, epic: 2, common: 3 };
            const RARITY_GLOW = {
              chromatic: { ring: '#d946ef', glow: 'rgba(217,70,239,0.45)', label: '#f0abfc', bg: 'rgba(217,70,239,0.13)', ru: 'Хроматический', lLabel: '#701a75', lRing: '#a21caf', lBg: 'rgba(217,70,239,0.10)' },
              legendary: { ring: '#facc15', glow: 'rgba(250,204,21,0.40)', label: '#fde68a', bg: 'rgba(250,204,21,0.13)', ru: 'Легендарный',   lLabel: '#92400e', lRing: '#d97706', lBg: 'rgba(251,191,36,0.12)' },
              epic:      { ring: '#a855f7', glow: 'rgba(168,85,247,0.40)', label: '#e9d5ff', bg: 'rgba(168,85,247,0.13)', ru: 'Эпический',     lLabel: '#4c1d95', lRing: '#7c3aed', lBg: 'rgba(139,92,246,0.10)' },
              common:    { ring: '#94a3b8', glow: 'rgba(148,163,184,0.18)', label: '#cbd5e1', bg: 'rgba(148,163,184,0.07)', ru: 'Обычный',     lLabel: '#334155', lRing: '#64748b', lBg: 'rgba(148,163,184,0.08)' },
            };
            const TYPE_LABEL = { frame: 'Рамка', badge: 'Бейджик', title: 'Титул', banner: 'Баннер' };
            const TYPE_ICON  = { frame: '🖼', badge: '🏅', title: '📛', banner: '🎨' };
            const equippedIds = new Set(inventory.filter(i => i.is_equipped).map(i => i.item_id));

            const rotationUntil = shopItems.find(i => i.rotation_until)?.rotation_until ?? null;

            const isVault = shopTab === 'vault';
            const isSetsTab = shopTab === 'sets';
            const isPremiumTab = shopTab === 'premium';
            let filtered = isVault
              ? shopItems.filter(i => i.in_rotation === false)
              : shopItems.filter(i => i.in_rotation !== false);
            if (isVault && vaultType !== 'all') {
              filtered = filtered.filter(i => i.item_type === vaultType);
            }
            if (shopRarityFilter) filtered = filtered.filter(i => i.rarity === shopRarityFilter);
            if (shopNewOnly) {
              const NEW_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;
              const nowMs = Date.now();
              filtered = filtered.filter(i => {
                if (!i.created_at) return false;
                const ms = new Date(i.created_at + 'Z').getTime();
                return ms > 0 && (nowMs - ms) < NEW_WINDOW_MS;
              });
            }
            if (shopSearch.trim()) filtered = filtered.filter(i => i.name.toLowerCase().includes(shopSearch.toLowerCase()));
            filtered.sort((a, b) => (RARITY_ORDER[a.rarity] ?? 5) - (RARITY_ORDER[b.rarity] ?? 5));

            const gridItems = filtered;

            return (
              <div>


                {/* Mode switch + поиск + редкость */}
                <div className="-mx-4 px-4 mb-4" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
                  {/* Строка табов + десктоп-контролы */}
                  <div className="flex items-end gap-2">
                    {/* Фильтр (лупа) — слева от Витрины */}
                    <div className="block">
                      <ShopFilterMorph
                        isDark={isDark}
                        compact
                        align="left"
                        search={shopSearch}
                        onSearch={setShopSearch}
                        rarity={shopRarityFilter}
                        onRarity={setShopRarityFilter}
                        type={vaultType}
                        onType={setVaultType}
                        showType={isVault}
                        newOnly={shopNewOnly}
                        onNewOnly={setShopNewOnly}
                        rarityOptions={[
                          { value: 'chromatic', label: 'Хроматический', color: RARITY_GLOW.chromatic.ring },
                          { value: 'legendary', label: 'Легендарный',   color: RARITY_GLOW.legendary.ring },
                          { value: 'epic',      label: 'Эпический',      color: RARITY_GLOW.epic.ring },
                          { value: 'common',    label: 'Обычный',        color: RARITY_GLOW.common.ring },
                        ]}
                        typeOptions={VAULT_TYPES.map(t => ({ value: t.id, label: t.label }))}
                      />
                    </div>
                    <div className="flex overflow-x-auto hide-scrollbar flex-1 min-w-0 items-end">
                      {SHOP_MODES.map(tab => {
                        const isActive = shopTab === tab.id;
                        const isPrem = tab.premium;
                        const premGradient = 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)';
                        return (
                          <button key={tab.id} type="button"
                            onClick={() => {
                              if (shopTab === tab.id) return;
                              setShopTab(tab.id);
                              setShopRarityFilter(null);
                              setShopNewOnly(false);
                              setShopSearch('');
                              if (tab.id !== 'vault') setVaultType('all');
                              if ('vibrate' in navigator) {
                                try { navigator.vibrate(8); } catch { /* ignore */ }
                              }
                            }}
                            className="relative shrink-0 px-3 pb-2.5 pt-1 text-[13px] font-semibold whitespace-nowrap transition-colors inline-flex items-center gap-1"
                            style={{
                              color: isPrem
                                ? 'transparent'
                                : (isActive ? (isDark ? '#fff' : '#111827') : (isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.4)')),
                              backgroundImage: isPrem ? premGradient : undefined,
                              backgroundClip: isPrem ? 'text' : undefined,
                              WebkitBackgroundClip: isPrem ? 'text' : undefined,
                              opacity: isPrem && !isActive ? 0.75 : 1,
                              fontWeight: isPrem ? 800 : 600,
                            }}>
                            {isPrem && <span style={{ fontSize: 11, color: '#ec4899' }}>💎</span>}
                            {tab.label}
                            {isActive && (
                              <span className="absolute bottom-0 left-2 right-2 h-[2px] rounded-full"
                                style={{ background: isPrem ? premGradient : (isDark ? '#fff' : '#111827') }} />
                            )}
                          </button>
                        );
                      })}
                      {(isAdmin || user?.is_developer) && (
                        <button type="button"
                          onClick={() => { setUploadError(""); setUploadMode(null); setShowUploadModal(true); }}
                          title="Добавить"
                          className="shrink-0 mb-1.5 ml-1 flex w-7 h-7 items-center justify-center active:scale-90 transition-transform self-end bg-transparent border-0"
                          style={{
                            color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)',
                          }}>
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
                            <path d="M12 5v14M5 12h14"/>
                          </svg>
                        </button>
                      )}
                    </div>
                    {!isPremiumTab && !isSetsTab && (() => {
                      const freeLeft = rerollInfo?.free_remaining ?? 0;
                      const cost = rerollInfo?.next_cost ?? 0;
                      const isFree = freeLeft > 0;
                      const armed = !isFree && rerollArmed;
                      const title = isFree
                        ? `Бесплатных рероллов: ${freeLeft}/${rerollInfo?.free_total ?? 2}`
                        : armed
                          ? `Нажми ещё раз · ${cost} скрепок`
                          : `Реролл за ${cost} скрепок`;
                      const accent = isFree || armed;
                      return (
                        <div
                          className="shrink-0 mb-1.5 inline-flex items-center h-7 rounded-full"
                          style={{
                            background: rerollDenied
                              ? (isDark ? 'rgba(239,68,68,0.14)' : 'rgba(239,68,68,0.08)')
                              : accent
                                ? (isDark ? 'rgba(59,130,246,0.10)' : 'rgba(59,130,246,0.06)')
                                : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                            border: `1px solid ${rerollDenied
                              ? (isDark ? 'rgba(248,113,113,0.5)' : 'rgba(239,68,68,0.4)')
                              : accent
                                ? (isDark ? 'rgba(96,165,250,0.4)' : 'rgba(59,130,246,0.3)')
                                : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')}`,
                            animation: rerollDenied ? 'rerollShake 420ms cubic-bezier(0.36,0.07,0.19,0.97)' : undefined,
                            transition: 'background 220ms, border-color 220ms',
                          }}
                        >
                          <style>{`
                            @keyframes rerollShake {
                              0%,100% { transform: translateX(0); }
                              10%,30%,50%,70% { transform: translateX(-5px); }
                              20%,40%,60%,80% { transform: translateX(5px); }
                              90% { transform: translateX(-2px); }
                            }
                          `}</style>
                          <div className="flex items-center h-full px-2.5">
                            {rotationUntil
                              ? <RotationClock rotationUntil={rotationUntil} isDark={isDark} onExpired={fetchShop} />
                              : <span className="font-mono font-bold tabular-nums text-[13px]"
                                  style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>—</span>
                            }
                          </div>
                          <>
                            <div className="w-px h-4" style={{ background: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)' }} />
                            <button type="button" onClick={doReroll} disabled={rerollBusy}
                                title={rerollError || title}
                                className="h-full inline-flex items-center gap-1.5 text-[11px] font-semibold transition-colors active:scale-95 disabled:opacity-40 rounded-r-full bg-transparent border-0"
                                style={{
                                  paddingLeft: 6,
                                  paddingRight: accent ? 10 : 8,
                                  color: accent
                                    ? (isDark ? '#93c5fd' : '#1d4ed8')
                                    : (isDark ? 'rgba(255,255,255,0.75)' : '#475569'),
                                }}>
                                {(!armed || rerollBusy) && (
                                  <Lottie
                                    src={diceRollAnimData}
                                    lottieRef={diceLottieRefDesktop}
                                    autoplay={false}
                                    loop={false}
                                    style={{ width: 48, height: 48, marginLeft: -10, marginRight: -8, marginTop: -17, marginBottom: -4, flexShrink: 0, display: 'block' }}
                                  />
                                )}
                                {isFree && <span className="tabular-nums">{freeLeft}</span>}
                                {armed && (
                                  <>
                                    <img src="/paperclip.svg" alt="" className="w-3 h-3" style={{ opacity: 0.85 }} />
                                    <span className="tabular-nums">{cost}</span>
                                  </>
                                )}
                            </button>
                          </>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {shopLoading ? (
                  <div className="flex justify-center py-10">
                    <div className={`w-5 h-5 border-2 rounded-full animate-spin ${isDark ? 'border-white/15 border-t-white/60' : 'border-slate-200 border-t-slate-500'}`} />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="rounded-2xl px-5 py-10 text-center" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)'}` }}>
                    <p className="text-[13px] mb-2" style={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.4)' }}>Нет товаров</p>
                    {shopRarityFilter && (
                      <button type="button" onClick={() => setShopRarityFilter(null)}
                        className="text-[11px] font-semibold px-3 py-1 rounded-full"
                        style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}>
                        Сбросить фильтр
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Grid */}
                    {(gridItems.length > 0 || isAdmin || user?.is_developer) && (
                      <div className="relative">
                      <style>{`
                        @keyframes shopCardIn {
                          0%   { opacity: 0; transform: translateY(14px) scale(0.94); filter: blur(6px); }
                          60%  { opacity: 1; filter: blur(0); }
                          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
                        }
                        @keyframes shopCardHypeIn {
                          0%   { opacity: 0; transform: translateY(32px) scale(0.55) rotate(-12deg); filter: blur(14px); }
                          55%  { opacity: 1; transform: translateY(-6px) scale(1.06) rotate(3deg); filter: blur(0); }
                          78%  { transform: translateY(0) scale(0.98) rotate(-1deg); }
                          100% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); filter: blur(0); }
                        }
                        @keyframes shopCardOut {
                          0%   { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); filter: blur(0); }
                          100% { opacity: 0; transform: translateY(-10px) scale(0.6) rotate(14deg); filter: blur(10px); }
                        }
                        @keyframes shopGridFlash {
                          0%   { opacity: 0; }
                          25%  { opacity: 1; }
                          100% { opacity: 0; }
                        }
                        @keyframes shopDiceSpin {
                          0%   { transform: rotate(0deg); }
                          100% { transform: rotate(360deg); }
                        }
                        @keyframes shopNewDot {
                          0%, 100% { box-shadow: 0 0 0 0 rgba(52,211,153,0.55); }
                          50%      { box-shadow: 0 0 0 4px rgba(52,211,153,0); }
                        }
                        @keyframes shopTypeReveal {
                          0%   { opacity: 0; transform: translate(-50%, -4px); }
                          100% { opacity: 1; transform: translate(-50%, 0); }
                        }
                      `}</style>
                      {rerollSpinning && (
                        <div className="pointer-events-none absolute inset-0 z-20"
                          style={{
                            background: `radial-gradient(circle at center, ${isDark ? 'rgba(96,165,250,0.22)' : 'rgba(59,130,246,0.18)'} 0%, transparent 60%)`,
                            animation: 'shopGridFlash 480ms cubic-bezier(0.2,0.9,0.2,1) both',
                          }} />
                      )}
                      {isPremiumTab && (() => {
                        const premGradient = 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)';
                        const formatRub = (k) => {
                          if (k == null) return '—';
                          const r = Math.floor(k / 100);
                          const kop = k % 100;
                          return kop === 0 ? `${r} ₽` : `${r},${String(kop).padStart(2, '0')} ₽`;
                        };
                        const handlePremiumBuy = async (item) => {
                          if (premiumBuyingId || item.owned) return;
                          const token = getToken();
                          if (!token) return;
                          setPremiumBuyingId(item.id);
                          try {
                            const returnUrl = `${window.location.origin}/payment/done?pid={PAYMENT_ID}`;
                            const r = await fetch('/payments/create', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ item_id: item.id, return_url: returnUrl.replace('{PAYMENT_ID}', 'pending') }),
                            });
                            const data = await r.json().catch(() => ({}));
                            if (!r.ok) throw new Error(data.detail || 'Не удалось создать платёж');
                            if (!data.confirmation_url) throw new Error('Нет ссылки на оплату');
                            try { sessionStorage.setItem('pendingPaymentId', String(data.payment_id)); } catch {}
                            window.location.href = data.confirmation_url;
                          } catch (e) {
                            alert(e.message || 'Ошибка оплаты');
                            setPremiumBuyingId(null);
                          }
                        };
                        return (
                          <div className="pt-5 pb-4">
                            {premiumLoading && (
                              <div className="text-center py-10 text-[14px]" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Загрузка каталога…</div>
                            )}
                            {!premiumLoading && premiumItems.length === 0 && (
                              <div className="rounded-2xl p-10 text-center" style={{
                                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)',
                                border: `1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
                              }}>
                                <div style={{ fontSize: 36, marginBottom: 8 }}>✨</div>
                                <div className="font-bold text-[14px]" style={{ color: isDark ? '#fff' : '#111827' }}>Пока ничего нет</div>
                                <div className="text-[12px] opacity-70 mt-1">Новые премиум-товары скоро появятся.</div>
                              </div>
                            )}
                            {!premiumLoading && premiumItems.length > 0 && (
                              <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                                {premiumItems.map((item) => {
                                  const rar = RARITY_GLOW[item.rarity] || RARITY_GLOW.common;
                                  const isImg = typeof item.asset === 'string' && item.asset.startsWith('/');
                                  const busy = premiumBuyingId === item.id;
                                  const owned = !!item.owned;
                                  return (
                                    <div key={`prem-${item.id}`}
                                      className="relative rounded-2xl p-3 flex flex-col gap-2 transition-transform active:scale-[0.98]"
                                      style={{
                                        background: isDark
                                          ? 'linear-gradient(165deg, rgba(26,13,34,1) 0%, rgba(42,10,32,1) 100%)'
                                          : 'linear-gradient(165deg, #ffffff 0%, #fdf4ff 100%)',
                                        border: `1px solid ${isDark ? 'rgba(236,72,153,0.3)' : 'rgba(168,85,247,0.25)'}`,
                                        boxShadow: isDark
                                          ? '0 10px 30px -16px rgba(168,85,247,0.5), 0 0 0 1px rgba(236,72,153,0.15) inset'
                                          : '0 10px 24px -14px rgba(168,85,247,0.35)',
                                      }}>
                                      {/* Premium ribbon */}
                                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-black tracking-wider uppercase"
                                        style={{ background: premGradient, color: '#fff', letterSpacing: 0.6 }}>
                                        PREMIUM
                                      </div>

                                      {/* Preview */}
                                      <div
                                        className="aspect-square rounded-xl flex items-center justify-center overflow-hidden relative"
                                        style={{
                                          background: `radial-gradient(circle at 50% 40%, ${rar.ring}33 0%, transparent 70%), ${isDark ? '#0f0a19' : '#faf5ff'}`,
                                          border: `1px solid ${rar.ring}44`,
                                        }}>
                                        <ItemEffect effectType={item.effect_type} effectParams={item.effect_params} trigger="click">
                                          {isImg ? (
                                            <CosmeticAsset asset={item.asset} alt={item.name}
                                              style={{ width: '82%', height: '82%', objectFit: 'contain', filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.4))' }} />
                                          ) : (
                                            <span style={{ fontSize: 56 }}>{item.asset}</span>
                                          )}
                                        </ItemEffect>
                                      </div>

                                      {/* Meta */}
                                      <div className="flex items-center gap-2">
                                        <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
                                          style={{ color: rar.ring, background: `${rar.ring}1a`, letterSpacing: 0.5 }}>
                                          {TYPE_LABEL[item.item_type] || item.item_type}
                                        </span>
                                        <span className="text-[9px] font-bold opacity-70">· {rar.ru}</span>
                                      </div>
                                      <div className="font-bold text-[14px] leading-tight" style={{ color: isDark ? '#fff' : '#1a0b1f' }}>
                                        {item.name}
                                      </div>
                                      {item.description && (
                                        <div className="text-[11px] opacity-65 leading-snug line-clamp-2">{item.description}</div>
                                      )}

                                      {/* Buy */}
                                      <button
                                        disabled={owned || busy || !!premiumBuyingId}
                                        onClick={() => handlePremiumBuy(item)}
                                        className="mt-auto h-11 rounded-xl text-[13px] font-black flex items-center justify-center gap-1.5 transition-all"
                                        style={{
                                          background: owned
                                            ? (isDark ? 'rgba(16,185,129,0.18)' : 'rgba(16,185,129,0.12)')
                                            : busy
                                              ? 'rgba(168,85,247,0.3)'
                                              : premGradient,
                                          color: owned ? (isDark ? '#6ee7b7' : '#047857') : '#fff',
                                          boxShadow: owned ? 'none' : '0 6px 18px -6px rgba(168,85,247,0.55)',
                                          opacity: (premiumBuyingId && !busy) ? 0.5 : 1,
                                          cursor: (owned || busy) ? 'default' : 'pointer',
                                        }}>
                                        {owned ? '✓ В инвентаре' : busy ? 'Создаём платёж…' : formatRub(item.price_rub)}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            <div className="mt-5 pt-4 text-[11px] opacity-60 leading-relaxed border-t"
                              style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
                              Оплата через ЮKassa. Товар выдаётся автоматически после подтверждения.
                              Если выдача задержится — возврат автоматически.
                              Покупая, вы соглашаетесь с{' '}
                              <a href="/offer" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa', textDecoration: 'underline' }}>офертой</a>
                              {' '}и{' '}
                              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa', textDecoration: 'underline' }}>политикой конфиденциальности</a>.
                            </div>
                          </div>
                        );
                      })()}
                      {isSetsTab && (
                        <div className="pt-5 pb-4 grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
                          {shopSets.length === 0 && (
                            <div className="col-span-full text-center py-10 text-[14px]" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Сетов пока нет</div>
                          )}
                          {shopSets.map(s => {
                            const setItems = (s.item_ids || []).map(iid => shopItems.find(x => x.id === iid)).filter(Boolean);
                            const owned = setItems.filter(x => x.owned).length;
                            const total = s.item_ids?.length || setItems.length;
                            const isHal = s.is_halloween;
                            const frameItem  = setItems.find(x => x.item_type === 'frame');
                            const bannerItem = setItems.find(x => x.item_type === 'banner');
                            const badgeItem  = setItems.find(x => x.item_type === 'badge');
                            const SlotTile = ({ item, label, aspect, placeholderIcon }) => {
                              const rar = item ? (RARITY_GLOW[item.rarity] || RARITY_GLOW.common) : RARITY_GLOW.common;
                              return (
                                <button type="button"
                                  disabled={!item}
                                  onClick={() => item && openShopPreview(item)}
                                  className="relative rounded-xl overflow-hidden text-left flex flex-col group active:scale-[0.98] transition-transform"
                                  style={{
                                    aspectRatio: aspect,
                                    background: isDark ? `radial-gradient(circle at 50% 50%, ${rar.ring}2a 0%, ${rar.bg} 70%)` : `radial-gradient(circle at 50% 50%, ${rar.lRing}22 0%, ${rar.lBg} 70%)`,
                                    boxShadow: `inset 0 0 0 1.5px ${isDark ? rar.ring : rar.lRing}36`,
                                    cursor: item ? 'pointer' : 'default',
                                    border: 'none',
                                    opacity: item ? (item.owned ? 1 : 0.9) : 0.4,
                                  }}>
                                  {item?.asset?.startsWith('/') ? (
                                    item.item_type === 'banner'
                                      ? <img src={item.asset} alt="" loading="lazy" className="w-full h-full object-cover" />
                                      : <div className="w-full h-full flex items-center justify-center p-4">
                                          <img src={item.asset} alt="" loading="lazy" className="w-full h-full object-contain" />
                                        </div>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-4xl opacity-40">{placeholderIcon}</div>
                                  )}
                                  <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider"
                                    style={{
                                      background: isDark ? 'rgba(22,22,30,0.85)' : 'rgba(255,255,255,0.9)',
                                      color: isDark ? '#f1f5f9' : '#0f172a',
                                      letterSpacing: '0.08em',
                                    }}>{label}</div>
                                  {item?.owned && (
                                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black"
                                      style={{ background: '#22c55e', color: '#fff' }}>✓</div>
                                  )}
                                  {item && !item.owned && item.price != null && (
                                    <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                                      style={{
                                        background: isDark ? 'rgba(22,22,30,0.85)' : 'rgba(255,255,255,0.9)',
                                        color: isDark ? '#f1f5f9' : '#0f172a',
                                      }}>
                                      <img src="/paperclip.svg" alt="" className="w-3 h-3" />
                                      <span className="tabular-nums">{item.price}</span>
                                    </div>
                                  )}
                                </button>
                              );
                            };
                            return (
                              <div key={`sg-${s.id}`}
                                className="rounded-2xl p-4 flex flex-col gap-3"
                                style={{
                                  background: isHal
                                    ? (isDark ? 'linear-gradient(160deg, #1a0d1f 0%, #2a1109 100%)' : 'linear-gradient(160deg, #fff7ee 0%, #ffe4c4 100%)')
                                    : (isDark ? '#16161e' : '#ffffff'),
                                  boxShadow: isHal
                                    ? '0 0 0 1px rgba(255,138,43,0.5), 0 0 24px rgba(255,138,43,0.28), 0 6px 24px rgba(0,0,0,0.4)'
                                    : (isDark ? '0 0 0 1px rgba(255,255,255,0.07), 0 6px 24px rgba(0,0,0,0.4)' : '0 0 0 1px rgba(0,0,0,0.06), 0 6px 18px rgba(0,0,0,0.08)'),
                                }}>
                                {s.cover_image && (
                                  <div className="w-full rounded-xl overflow-hidden -mt-1" style={{ aspectRatio: '16 / 7' }}>
                                    <img src={s.cover_image} alt="" className="w-full h-full object-cover" />
                                  </div>
                                )}
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    {isHal && <span style={{ fontSize: 22 }}>🎃</span>}
                                    <div className="min-w-0">
                                      <div className="font-black text-[16px] truncate" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>{s.name}</div>
                                      {s.description && <div className="text-[12px] truncate" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{s.description}</div>}
                                    </div>
                                  </div>
                                  <div className="px-2.5 py-1 rounded-full text-[11px] font-black shrink-0"
                                    style={{
                                      background: owned === total && total > 0 ? '#22c55e' : (isHal ? 'linear-gradient(135deg, #a21caf, #6366f1)' : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)')),
                                      color: owned === total && total > 0 ? '#fff' : (isDark ? '#fff' : '#fff'),
                                      boxShadow: owned === total && total > 0 ? '0 2px 10px rgba(34,197,94,0.45)' : (isHal ? '0 2px 10px rgba(162,28,175,0.4)' : 'none'),
                                    }}>{owned}/{total}</div>
                                </div>
                                <SlotTile item={bannerItem} label="Баннер"  aspect="3 / 1" placeholderIcon="🎨" />
                                <div className="grid grid-cols-2 gap-3">
                                  <SlotTile item={frameItem} label="Рамка"   aspect="1 / 1" placeholderIcon="🖼" />
                                  <SlotTile item={badgeItem} label="Бейджик" aspect="1 / 1" placeholderIcon="🏅" />
                                </div>
                                {s.effect_type && s.effect_type !== 'none' && (
                                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider mt-1" style={{ color: isHal ? '#ff8a2b' : (isDark ? '#a855f7' : '#7c3aed') }}>
                                    <span>✨</span>
                                    <span>Эффект: {s.effect_type.replace(/_/g, ' ')}</span>
                                  </div>
                                )}
                                {(() => {
                                  const toBuyItems = setItems.filter(x => !x.owned);
                                  if (toBuyItems.length === 0) {
                                    return (
                                      <div className="mt-1 w-full h-11 rounded-xl flex items-center justify-center gap-1.5 text-[13px] font-bold"
                                        style={{
                                          background: isDark ? 'rgba(16,185,129,0.14)' : 'rgba(16,185,129,0.10)',
                                          border: `1px solid ${isDark ? 'rgba(52,211,153,0.4)' : 'rgba(16,185,129,0.32)'}`,
                                          color: isDark ? '#6ee7b7' : '#047857',
                                        }}>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                                        </svg>
                                        Сет собран
                                      </div>
                                    );
                                  }
                                  const sumPrice = toBuyItems.reduce((acc, x) => acc + (x.price || 0), 0);
                                  const useBundle = s.bundle_price != null && toBuyItems.length === setItems.length;
                                  const base = useBundle ? s.bundle_price : sumPrice;
                                  const canAfford = (skrepBalance ?? 0) >= base;
                                  return (
                                    <button type="button"
                                      disabled={!canAfford || shopBuying === `set-${s.id}`}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (!canAfford) return;
                                        setShopBuying(`set-${s.id}`);
                                        try {
                                          const t = getToken();
                                          const r = await fetch(`/skrep/sets/${s.id}/buy`, {
                                            method: 'POST', headers: { Authorization: `Bearer ${t}` },
                                          });
                                          if (r.ok) {
                                            const data = await r.json();
                                            setSkrepBalance(data.balance);
                                            fetchShop();
                                            if (typeof fetchInventory === 'function') fetchInventory();
                                          } else {
                                            const err = await r.json().catch(() => ({}));
                                            alert(err.detail || 'Ошибка покупки сета');
                                          }
                                        } finally {
                                          setShopBuying(null);
                                        }
                                      }}
                                      className="mt-1 w-full h-11 rounded-xl flex items-center justify-center gap-2 text-[13px] font-bold transition-all active:scale-[0.98]"
                                      style={{
                                        background: canAfford
                                          ? (isHal ? 'linear-gradient(135deg, #ff8a2b 0%, #a21caf 100%)' : (isDark ? 'linear-gradient(135deg, rgba(168,85,247,0.3), rgba(99,102,241,0.3))' : 'linear-gradient(135deg, #c084fc, #818cf8)'))
                                          : (isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)'),
                                        border: `1px solid ${canAfford ? 'transparent' : (isDark ? 'rgba(239,68,68,0.3)' : 'rgba(239,68,68,0.25)')}`,
                                        color: canAfford ? '#fff' : (isDark ? '#fca5a5' : '#b91c1c'),
                                        boxShadow: canAfford && isHal ? '0 6px 20px rgba(255,138,43,0.4)' : 'none',
                                        cursor: canAfford ? 'pointer' : 'not-allowed',
                                      }}>
                                      <img src="/paperclip.svg" alt="" className="w-4 h-4"
                                        style={{ filter: canAfford ? 'brightness(2) drop-shadow(0 0 3px rgba(255,255,255,0.6))' : 'grayscale(0.5) opacity(0.6)' }} />
                                      <span>Купить сет</span>
                                      {useBundle && sumPrice > base ? (
                                        <span className="flex items-baseline gap-1.5">
                                          <span className="tabular-nums">{base}</span>
                                          <span className="tabular-nums text-[10px] line-through opacity-70">{sumPrice}</span>
                                        </span>
                                      ) : (
                                        <span className="tabular-nums">{base}</span>
                                      )}
                                    </button>
                                  );
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {!isSetsTab && !isPremiumTab && shopPromos.length > 0 && shopPromos.map(p => {
                        const endMs = p.end_date ? new Date(p.end_date).getTime() : 0;
                        const diffMs = Math.max(0, endMs - promoNow);
                        const days = Math.floor(diffMs / 86400000);
                        const hours = Math.floor((diffMs % 86400000) / 3600000);
                        const minutes = Math.floor((diffMs % 3600000) / 60000);
                        const timeStr = days > 0
                          ? `${days}д ${hours}ч ${minutes}м`
                          : hours > 0 ? `${hours}ч ${minutes}м` : `${minutes}м`;
                        return (
                          <div key={`promo-${p.id}`}
                            className="rounded-2xl p-3 mb-2"
                            style={{
                              background: 'linear-gradient(135deg, rgba(255,138,43,0.18) 0%, rgba(239,68,68,0.14) 60%, rgba(162,28,175,0.14) 100%)',
                              border: '1.5px solid rgba(255,138,43,0.45)',
                              boxShadow: '0 0 24px rgba(255,138,43,0.25)',
                            }}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span style={{ fontSize: 22 }}>🔥</span>
                                <div className="min-w-0">
                                  <div className="font-black text-[14px] truncate" style={{ color: isDark ? '#fff' : '#1a0b0b' }}>{p.name}</div>
                                  {p.description && <div className="text-[11px] truncate" style={{ color: isDark ? 'rgba(255,255,255,0.75)' : 'rgba(60,20,20,0.75)' }}>{p.description}</div>}
                                </div>
                              </div>
                              <div className="flex flex-col items-end shrink-0">
                                {p.discount_percent > 0 && (
                                  <div className="px-2 py-0.5 rounded-full text-[11px] font-black"
                                    style={{ background: '#ef4444', color: '#fff' }}>−{p.discount_percent}%</div>
                                )}
                                <div className="text-[10px] font-bold mt-0.5" style={{ color: '#ff8a2b', fontVariantNumeric: 'tabular-nums' }}>⏱ {timeStr}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {!isSetsTab && !isPremiumTab && (
                      <div ref={shopGridRef}
                        className="grid gap-3 overflow-x-auto hide-scrollbar -mx-4 px-4 pt-5 pb-1"
                        style={{ gridTemplateRows: 'repeat(2, auto)', gridAutoFlow: 'column', gridAutoColumns: 'min(240px, calc(50% - 20px))' }}
                        onScroll={e => {
                          const el = e.currentTarget;
                          if (shopEdgeRaf.current) return;
                          shopEdgeRaf.current = requestAnimationFrame(() => {
                            shopEdgeRaf.current = null;
                            setShopGridAtStart(el.scrollLeft <= 8);
                            setShopGridAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 16);
                          });
                        }}>
                        {gridItems.map((item, idx) => {
                          const r = RARITY_GLOW[item.rarity] || RARITY_GLOW.common;
                          const isImg = item.asset?.startsWith('/');
                          const typeIcon = TYPE_ICON[item.item_type] || '🎨';
                          const typeLabel = TYPE_LABEL[item.item_type] || item.item_type;
                          const createdAtMs = item.created_at ? new Date(item.created_at + 'Z').getTime() : 0;
                          const isNew = createdAtMs > 0 && (Date.now() - createdAtMs) < 3 * 24 * 60 * 60 * 1000;
                          const hype = rerollKey > 0;
                          const stagger = Math.min(idx, 10);
                          // На мобилке анимации только для первых 4 карточек (видимых) — иначе GPU trash на WebKit.
                          const skipAnim = isRealMobile && idx >= 4;
                          const exitAnim = rerollSpinning && !skipAnim
                            ? `shopCardOut 320ms cubic-bezier(0.4,0,0.6,1) both ${stagger * 22}ms`
                            : null;
                          const enterAnim = skipAnim
                            ? null
                            : (hype
                              ? `shopCardHypeIn ${520 + stagger * 48}ms cubic-bezier(0.2,0.9,0.2,1) both`
                              : `shopCardIn ${220 + stagger * 38}ms cubic-bezier(0.2,0.9,0.2,1) both`);
                          const enterDelay = hype ? stagger * 55 : stagger * 30;
                          const canAfford = (skrepBalance ?? 0) >= (item.price ?? 0);
                          const notAvailable = item.in_rotation === false && !isVault;
                          const isHalloweenCard = HALLOWEEN_MODE && item.is_halloween;
                          // Set membership + progress ("2/3 собрано")
                          const belongsToSet = item.set_id ? shopSets.find(s => s.id === item.set_id) : null;
                          let setProgress = null;
                          if (belongsToSet) {
                            const setItemIds = belongsToSet.item_ids || [];
                            const ownedCount = setItemIds.filter(iid => {
                              const si = shopItems.find(s => s.id === iid);
                              return si && si.owned;
                            }).length;
                            setProgress = { total: setItemIds.length, owned: ownedCount, name: belongsToSet.name };
                          }
                          // Promo discount display
                          const promoDisc = item.promo_discount || 0;
                          const discountedPrice = promoDisc > 0 && item.price
                            ? Math.max(0, item.price - Math.floor(item.price * promoDisc / 100))
                            : item.price;
                          return (
                            <div key={`r${rerollKey}-${item.id}`}
                              onClick={() => openShopPreview(item)}
                              className="group shop-card-anim relative rounded-2xl flex flex-col cursor-pointer active:scale-[0.98] transition-transform select-none"
                              style={{
                                background: isHalloweenCard
                                  ? (isDark ? 'linear-gradient(160deg, #1a0d1f 0%, #2a1109 100%)' : 'linear-gradient(160deg, #fff7ee 0%, #ffe4c4 100%)')
                                  : (isDark ? '#16161e' : '#ffffff'),
                                boxShadow: isHalloweenCard
                                  ? `0 0 0 1px rgba(255,138,43,0.55), 0 0 24px rgba(255,138,43,0.28), 0 4px 20px rgba(0,0,0,0.4)`
                                  : (isDark ? `0 0 0 1px rgba(255,255,255,0.07), 0 4px 20px rgba(0,0,0,0.4)` : `0 0 0 1px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.08)`),
                                animation: exitAnim || enterAnim || undefined,
                                animationDelay: exitAnim || !enterAnim ? '0ms' : `${enterDelay}ms`,
                                transformOrigin: 'center',
                                transform: 'translateZ(0)',
                                willChange: enterAnim || exitAnim ? 'transform, opacity' : 'auto',
                              }}>
                              {isNew && (
                                <img
                                  src="/novinka.svg"
                                  alt="Новинка"
                                  className="absolute pointer-events-none select-none"
                                  style={{
                                    top: '-12px',
                                    left: '-12px',
                                    width: '56px',
                                    height: '56px',
                                    filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.35))',
                                    zIndex: 10,
                                  }}
                                />
                              )}
                              {isHalloweenCard && (
                                <div
                                  className="absolute pointer-events-none select-none flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase"
                                  style={{
                                    top: '-10px',
                                    right: '-10px',
                                    zIndex: 11,
                                    background: 'linear-gradient(135deg, #ff8a2b 0%, #a21caf 100%)',
                                    color: '#fff',
                                    boxShadow: '0 0 14px rgba(255,138,43,0.75), inset 0 0 0 1px rgba(255,255,255,0.25)',
                                    letterSpacing: '0.08em',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                                  }}
                                >
                                  <span style={{ fontSize: 11 }}>🎃</span>
                                  <span>Halloween</span>
                                </div>
                              )}
                              {setProgress && (
                                <div
                                  className="absolute pointer-events-none select-none flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase"
                                  style={{
                                    bottom: '46px',
                                    left: '8px',
                                    zIndex: 11,
                                    background: 'linear-gradient(135deg, #a21caf 0%, #6366f1 100%)',
                                    color: '#fff',
                                    boxShadow: '0 2px 10px rgba(162,28,175,0.45)',
                                    letterSpacing: '0.06em',
                                  }}
                                >
                                  <span style={{ fontSize: 10 }}>✨</span>
                                  <span>SET {setProgress.owned}/{setProgress.total}</span>
                                </div>
                              )}
                              {promoDisc > 0 && (
                                <div
                                  className="absolute pointer-events-none select-none px-2 py-0.5 rounded-full text-[10px] font-black"
                                  style={{
                                    bottom: '8px',
                                    left: '-6px',
                                    zIndex: 11,
                                    background: '#ef4444',
                                    color: '#fff',
                                    boxShadow: '0 2px 10px rgba(239,68,68,0.5)',
                                  }}
                                >
                                  −{promoDisc}%
                                </div>
                              )}

                              {/* === ЗОНА 1: обложка === */}
                              <div className="relative w-full shrink-0 overflow-hidden rounded-t-2xl"
                                style={{
                                  aspectRatio: '1 / 1',
                                  background: isDark
                                    ? `radial-gradient(circle at 50% 55%, ${r.ring}2a 0%, ${r.bg} 68%)`
                                    : `radial-gradient(circle at 50% 55%, ${r.lRing}22 0%, ${r.lBg} 68%)`,
                                  boxShadow: `inset 0 0 0 1px ${isDark ? r.ring : r.lRing}1f`,
                                  opacity: notAvailable ? 0.55 : 1,
                                }}>
                                {isImg
                                  ? (item.item_type === 'banner'
                                    ? <img src={item.asset} alt="" loading="lazy" className="w-full h-full object-cover" />
                                    : <div className="w-full h-full flex items-center justify-center p-4">
                                        <img src={item.asset} alt="" loading="lazy" className="w-full h-full object-contain" style={isRealMobile ? undefined : { imageRendering: 'pixelated' }} />
                                      </div>)
                                  : <div className="w-full h-full flex items-center justify-center text-5xl opacity-50">{typeIcon}</div>
                                }

                                {/* Тип — всплывает сверху по центру при hover/tap */}
                                <div
                                  className="pointer-events-none absolute left-1/2 top-2.5 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-150 flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase z-[2]"
                                  style={{
                                    transform: 'translateX(-50%)',
                                    background: isDark ? 'rgba(22,22,30,0.82)' : 'rgba(255,255,255,0.88)',
                                    color: isDark ? 'rgba(255,255,255,0.78)' : 'rgba(15,23,42,0.78)',
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'}`,
                                    ...(isRealMobile ? {} : { backdropFilter: 'blur(6px)' }),
                                    letterSpacing: '0.08em',
                                  }}>
                                  {typeLabel}
                                </div>

                                {equippedIds.has(item.id) && (
                                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shadow-md z-[2]"
                                    style={{ background: 'rgba(16,185,129,0.95)', color: '#fff' }}>✓</div>
                                )}
                                {(isAdmin || (user?.is_developer && item.uploaded_by === user?.id)) && !equippedIds.has(item.id) && (
                                  <button type="button"
                                    onClick={e => handleOpenEditModal(item, e)}
                                    className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all active:scale-90 opacity-0 group-hover:opacity-100 z-[2]"
                                    style={{
                                      background: isDark ? 'rgba(22,22,30,0.75)' : 'rgba(255,255,255,0.85)',
                                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                                      color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(15,23,42,0.65)',
                                      ...(isRealMobile ? {} : { backdropFilter: 'blur(6px)' }),
                                    }}>
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                                    </svg>
                                  </button>
                                )}

                                {/* Overlay footer: rarity + views + likes */}
                                <div className="absolute left-0 right-0 bottom-0 flex items-center justify-between gap-2 px-2.5 pt-7 pb-1.5 pointer-events-none"
                                  style={{
                                    background: isDark
                                      ? 'linear-gradient(to bottom, rgba(22,22,30,0) 0%, rgba(22,22,30,0.6) 55%, rgba(22,22,30,0.85) 100%)'
                                      : 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 55%, rgba(255,255,255,0.9) 100%)',
                                  }}>
                                  <div className="flex items-center gap-1 min-w-0">
                                    <span className="w-1.5 h-1.5 rounded-full shrink-0"
                                      style={{
                                        background: isDark ? r.ring : r.lRing,
                                        boxShadow: `0 0 6px ${isDark ? r.ring : r.lRing}aa`,
                                      }} />
                                    <span className="text-[9px] font-bold uppercase tracking-wider truncate"
                                      style={{
                                        color: isDark ? r.label : r.lLabel,
                                        letterSpacing: '0.08em',
                                      }}>
                                      {r.ru}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2 shrink-0 pointer-events-auto">
                                    <span className="flex items-center gap-0.5 text-[10px] font-semibold"
                                      style={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.6)' }}>
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
                                        <circle cx="12" cy="12" r="3"/>
                                      </svg>
                                      {item.views ?? 0}
                                    </span>
                                    <button type="button"
                                      onClick={e => { e.stopPropagation(); toggleShopLike(item); }}
                                      className="flex items-center gap-0.5 text-[10px] font-semibold transition-all active:scale-90 py-1 -my-1 px-0.5"
                                      style={{
                                        color: item.user_liked
                                          ? '#f43f5e'
                                          : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.6)'),
                                        background: 'none', border: 'none', cursor: 'pointer',
                                      }}>
                                      <svg className="w-3 h-3"
                                        fill={item.user_liked ? '#f43f5e' : 'none'}
                                        stroke={item.user_liked ? '#f43f5e' : 'currentColor'}
                                        viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round"
                                          d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z"/>
                                      </svg>
                                      {item.likes ?? 0}
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* === ЗОНА 2: текст === */}
                              <div className="flex flex-col px-3.5 pt-3 pb-3 gap-3 flex-1">
                                <div className="min-w-0">
                                  <p className="font-semibold text-[14px] leading-[1.3] line-clamp-2 tracking-[-0.01em]"
                                    style={{
                                      color: isDark ? '#f1f5f9' : '#0f172a',
                                    }}>
                                    {item.name}
                                  </p>
                                  {item.description && (
                                  <p className="text-[11.5px] leading-[1.45] line-clamp-1 truncate mt-1"
                                    style={{
                                      color: isDark ? 'rgba(255,255,255,0.52)' : 'rgba(15,23,42,0.55)',
                                    }}>
                                    {item.description || ' '}
                                  </p>
                                  )}
                                </div>

                                {/* === ЗОНА 3: unified footer === */}
                                <div className="mt-auto">
                                  {notAvailable ? (
                                    <div className="w-full h-10 rounded-xl flex items-center justify-center text-[12px] font-semibold"
                                      style={{
                                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)',
                                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`,
                                        color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)',
                                      }}>
                                      Недоступно
                                    </div>
                                  ) : isVault ? (
                                    <div className="w-full h-10 rounded-xl flex items-center justify-center gap-1.5 text-[13px] font-bold"
                                      style={{
                                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)',
                                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`,
                                        color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
                                      }}>
                                      <img src="/paperclip.svg" alt="" className="w-3.5 h-3.5 opacity-50" />
                                      <span className="tabular-nums">{item.price}</span>
                                    </div>
                                  ) : item.owned ? (
                                    <button type="button"
                                      onClick={e => { e.stopPropagation(); openShopPreview(item); }}
                                      className="w-full h-10 rounded-xl flex items-center justify-center gap-1.5 text-[12px] font-bold transition-all active:scale-[0.97]"
                                      style={{
                                        background: isDark ? 'rgba(16,185,129,0.14)' : 'rgba(16,185,129,0.10)',
                                        border: `1px solid ${isDark ? 'rgba(52,211,153,0.4)' : 'rgba(16,185,129,0.32)'}`,
                                        color: isDark ? '#6ee7b7' : '#047857',
                                      }}>
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                                      </svg>
                                      В коллекции
                                    </button>
                                  ) : (
                                    <button type="button"
                                      onClick={e => { e.stopPropagation(); openShopPreview(item); }}
                                      className="w-full h-10 rounded-xl flex items-center justify-center gap-2 text-[13px] font-bold transition-all active:scale-[0.97] relative overflow-hidden"
                                      style={{
                                        background: canAfford
                                          ? (isDark
                                              ? `linear-gradient(135deg, ${r.ring}38 0%, ${r.ring}18 100%)`
                                              : `linear-gradient(135deg, ${r.lRing}2a 0%, ${r.lRing}10 100%)`)
                                          : (isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.06)'),
                                        border: `1px solid ${
                                          canAfford
                                            ? (isDark ? `${r.ring}70` : `${r.lRing}60`)
                                            : (isDark ? 'rgba(239,68,68,0.35)' : 'rgba(239,68,68,0.3)')
                                        }`,
                                        color: canAfford
                                          ? (isDark ? r.label : r.lLabel)
                                          : (isDark ? '#fca5a5' : '#b91c1c'),
                                        boxShadow: canAfford
                                          ? (isDark ? `0 0 14px ${r.ring}22` : 'none')
                                          : 'none',
                                      }}>
                                      <img src="/paperclip.svg" alt=""
                                        className="w-4 h-4"
                                        style={{
                                          filter: canAfford
                                            ? `drop-shadow(0 0 3px ${isDark ? r.ring : r.lRing}99)`
                                            : 'grayscale(0.6) opacity(0.7)',
                                        }} />
                                      {promoDisc > 0 ? (
                                        <span className="flex items-baseline gap-1.5">
                                          <span className="tabular-nums text-[14px]">{discountedPrice}</span>
                                          <span className="tabular-nums text-[10px] line-through opacity-60">{item.price}</span>
                                        </span>
                                      ) : (
                                        <span className="tabular-nums text-[14px]">{item.price}</span>
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      )}
                      {!isSetsTab && !isPremiumTab && !shopGridAtStart && (
                        <button type="button"
                          className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg z-10"
                          style={{ background: '#fff' }}
                          onClick={() => scrollEased(shopGridRef.current, -400)}>
                          <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
                          </svg>
                        </button>
                      )}
                      {!isSetsTab && !isPremiumTab && !shopGridAtEnd && (
                        <button type="button"
                          className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg z-10"
                          style={{ background: '#fff' }}
                          onClick={() => scrollEased(shopGridRef.current, 400)}>
                          <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                          </svg>
                        </button>
                      )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* Модалка загрузки кастомного предмета — wizard */}
          {showUploadModal && uploadMode === null && createPortal(
            <div className="fixed inset-0 z-[650] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(22px)' }}
              onClick={e => { if (e.target === e.currentTarget) setShowUploadModal(false); }}>
              <div className="w-full max-w-[420px] rounded-[28px] p-6"
                style={{
                  background: isDark ? '#15151d' : '#ffffff',
                  boxShadow: isDark ? '0 40px 80px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.06)' : '0 40px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                  animation: 'wizIn 0.35s cubic-bezier(0.16,1,0.3,1) both',
                }}>
                <style>{`@keyframes wizIn{from{opacity:0;transform:translateY(22px) scale(0.97);filter:blur(8px)}to{opacity:1;transform:translateY(0) scale(1);filter:blur(0)}}`}</style>
                <div className="flex items-center justify-between mb-4">
                  <p style={{ fontSize: 18, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>Что добавляем?</p>
                  <button type="button" onClick={() => setShowUploadModal(false)}
                    style={{ width: 28, height: 28, borderRadius: 14, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer', color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, fontWeight: 700 }}>✕</button>
                </div>
                <div className="grid grid-cols-1 gap-2.5">
                  {[
                    { v: 'item', icon: '🎁', title: 'Товар', desc: 'Одна косметическая вещь', accent: '#3b82f6' },
                    { v: 'set', icon: '✨', title: 'Сет', desc: 'Рамка + баннер + бейдж с общим эффектом', accent: '#a21caf' },
                    { v: 'promo', icon: '🔥', title: 'Акция', desc: 'Скидка на items/сет с таймером', accent: '#ef4444' },
                  ].map(opt => (
                    <button key={opt.v} type="button"
                      onClick={() => setUploadMode(opt.v)}
                      className="flex items-center gap-3 text-left transition-all active:scale-[0.98]"
                      style={{
                        padding: '14px 16px', borderRadius: 16,
                        background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                        border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7'}`,
                        cursor: 'pointer',
                      }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: opt.accent + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{opt.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>{opt.title}</div>
                        <div style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b', marginTop: 2 }}>{opt.desc}</div>
                      </div>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#64748b' : '#94a3b8'} strokeWidth={2.5}><path d="M9 6l6 6-6 6" strokeLinecap="round"/></svg>
                    </button>
                  ))}
                </div>
              </div>
            </div>,
            document.body)}

          {showUploadModal && uploadMode === 'item' && createPortal(
            (() => {
              const TYPES = [
                { v: 'badge',  icon: '🏅', label: 'Бейджик' },
                { v: 'frame',  icon: '🖼',  label: 'Рамка' },
                { v: 'title',  icon: '📛', label: 'Титул' },
                { v: 'banner', icon: '🎨', label: 'Баннер' },
              ];
              const isHalloweenCat = uploadForm.category === 'halloween';
              const RARITIES = isHalloweenCat ? [
                { v: 'common',    color: '#94a3b8', label: 'Обычный' },
                { v: 'epic',      color: '#8b5cf6', label: 'Эпический' },
                { v: 'legendary', color: '#f59e0b', label: 'Легендарный' },
              ] : [
                { v: 'common',    color: '#94a3b8', label: 'Обычный' },
                { v: 'epic',      color: '#8b5cf6', label: 'Эпический' },
                { v: 'legendary', color: '#f59e0b', label: 'Легендарный' },
                { v: 'chromatic', color: '#d946ef', label: 'Хроматический' },
              ];
              const HAS_CAT = HALLOWEEN_MODE;
              const START_STEP = HAS_CAT ? 0 : 1;
              const STEP_CAT = 0, STEP_TYPE = 1, STEP_COVER = 2, STEP_RARITY = 3, STEP_DETAILS = 4, STEP_PREVIEW = 5;
              const RARITY_MAP_REG = {
                chromatic: { accent: '#d946ef', pill: isDark ? '#f0abfc' : '#701a75', pillBg: isDark ? 'rgba(217,70,239,0.18)' : 'rgba(217,70,239,0.10)', label: 'Хроматический' },
                legendary: { accent: '#f59e0b', pill: isDark ? '#fde68a' : '#92400e', pillBg: isDark ? 'rgba(251,191,36,0.18)' : 'rgba(251,191,36,0.12)', label: 'Легендарный' },
                epic:      { accent: '#8b5cf6', pill: isDark ? '#c4b5fd' : '#4c1d95', pillBg: isDark ? 'rgba(139,92,246,0.18)' : 'rgba(139,92,246,0.10)', label: 'Эпический' },
                common:    { accent: '#94a3b8', pill: isDark ? '#cbd5e1' : '#334155', pillBg: isDark ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.10)', label: 'Обычный' },
              };
              const RARITY_MAP = RARITY_MAP_REG;
              const pRC = RARITY_MAP[uploadForm.rarity] || RARITY_MAP.common;
              const selType = TYPES.find(t => t.v === uploadForm.item_type);
              const TOTAL_STEPS = HAS_CAT ? 6 : 5;
              const goToStep = (n) => {
                setUploadStepDir(n > uploadStep ? 1 : -1);
                setUploadTransitioning(true);
                setTimeout(() => {
                  setUploadStep(n);
                  setUploadStepKey(k => k + 1);
                  setUploadTransitioning(false);
                }, 180);
              };
              const canAdvance = () => {
                if (uploadStep === STEP_COVER) {
                  if (!uploadForm.cover || !uploadForm.coverMeta) return false;
                  const VALID = { badge: ['png','jpg','jpeg','svg','gif','webp','apng','json'], frame: ['png','jpg','jpeg','svg','gif','webp','apng','json'], title: ['png','jpg','jpeg','svg','gif','webp','json'], banner: ['png','jpg','jpeg','webp','gif','json'] };
                  return (VALID[uploadForm.item_type] || ['png','svg','gif','webp']).includes(uploadForm.coverMeta.format) && uploadForm.coverMeta.sizeOk;
                }
                if (uploadStep === STEP_DETAILS) {
                  if (!uploadForm.name.trim()) return false;
                  if (uploadForm.isPremium) {
                    const n = parseFloat(String(uploadForm.priceRub).replace(',', '.'));
                    return !isNaN(n) && n > 0;
                  }
                  return !!uploadForm.price;
                }
                return true;
              };
              const inp = { width: '100%', padding: '13px 14px', borderRadius: 14, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7'}`, background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', fontSize: 15, color: isDark ? '#f1f5f9' : '#0f172a', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s', fontFamily: 'inherit' };
              const progress = ((uploadStep - START_STEP + 1) / TOTAL_STEPS) * 100;
              return (
            <div className="fixed inset-0 z-[650] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(22px)' }}
              onClick={e => { if (e.target === e.currentTarget) { setShowUploadModal(false); if (editingCoverFor) { setEditModal(editingCoverFor); setEditingCoverFor(null); } } }}>
              <style>{`
                @keyframes wizIn {
                  from { opacity:0; transform:translateY(22px) scale(0.97); filter:blur(8px); }
                  to   { opacity:1; transform:translateY(0) scale(1);      filter:blur(0); }
                }
                @keyframes morphIn {
                  from { opacity:0; transform:scale(1.07); filter:blur(10px); }
                  to   { opacity:1; transform:scale(1);    filter:blur(0px); }
                }
                @keyframes morphOut {
                  from { opacity:1; transform:scale(1);    filter:blur(0px); }
                  to   { opacity:0; transform:scale(0.88); filter:blur(10px); }
                }
                @keyframes metaBadgeIn {
                  from { opacity:0; transform:scale(0.82); }
                  to   { opacity:1; transform:scale(1); }
                }
                .upload-input:focus { border-color: #3b82f6 !important; }
                .wiz-tile, .wiz-row, .wiz-cta { -webkit-tap-highlight-color: transparent; outline: none; }
                .wiz-tile:focus, .wiz-row:focus, .wiz-cta:focus { outline: none; }
                .wiz-tile:active  { transform:scale(0.93); }
                .wiz-row:active   { transform:scale(0.98); }
                .wiz-cta:hover:not(:disabled) { filter:brightness(1.1); }
                .wiz-cta:active:not(:disabled){ transform:scale(0.96); }
                .cat-icon { transition: filter 0.22s cubic-bezier(0.34,1.56,0.64,1), transform 0.22s cubic-bezier(0.34,1.56,0.64,1); }
                .wiz-tile[data-cat="regular"]:hover .cat-icon,
                .wiz-tile[data-cat="regular"][data-active="1"] .cat-icon {
                  filter: drop-shadow(0 0 10px rgba(255,255,255,0.85)) drop-shadow(0 0 20px rgba(191,219,254,0.5));
                  transform: scale(1.08);
                }
                .wiz-tile[data-cat="halloween"]:hover .cat-icon,
                .wiz-tile[data-cat="halloween"][data-active="1"] .cat-icon {
                  filter: drop-shadow(0 0 10px rgba(255,138,28,0.9)) drop-shadow(0 0 20px rgba(234,88,12,0.55));
                  transform: scale(1.08);
                }
              `}</style>

              <div className="w-full max-w-[380px] rounded-[28px] overflow-hidden"
                style={{
                  background: isDark ? '#15151d' : '#ffffff',
                  maxHeight: '92svh',
                  animation: 'wizIn 0.35s cubic-bezier(0.16,1,0.3,1) both',
                  boxShadow: isDark ? '0 40px 80px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.06)' : '0 40px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                  display: 'flex', flexDirection: 'column',
                }}>

                {/* Progress line */}
                <div style={{ height: 2, background: isDark ? 'rgba(255,255,255,0.05)' : '#eef2f7', flexShrink: 0 }}>
                  <div style={{ height: '100%', width: progress + '%', background: pRC.accent, transition: 'width 0.45s cubic-bezier(0.16,1,0.3,1), background 0.4s', borderRadius: '0 2px 2px 0' }} />
                </div>

                {/* Header */}
                <div style={{ padding: '18px 20px 0', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <div style={{ width: 28, flexShrink: 0 }}>
                    {uploadStep > START_STEP && (
                      <button type="button"
                        onClick={() => {
                          if (uploadTransitioning) return;
                          if (editingCoverFor) {
                            setShowUploadModal(false);
                            setEditModal(editingCoverFor);
                            setEditingCoverFor(null);
                            return;
                          }
                          goToStep(uploadStep - 1);
                        }}
                        style={{ width: 28, height: 28, borderRadius: 14, background: 'none', border: 'none', cursor: uploadTransitioning ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#64748b' : '#94a3b8', opacity: uploadTransitioning ? 0.3 : 1, transition: 'opacity 0.15s' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
                      </button>
                    )}
                  </div>
                  <p style={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', letterSpacing: '-0.2px' }}>Новый предмет</p>
                  <button type="button" onClick={() => {
                    setShowUploadModal(false);
                    if (editingCoverFor) {
                      setEditModal(editingCoverFor);
                      setEditingCoverFor(null);
                    }
                  }}
                    style={{ width: 28, height: 28, borderRadius: 14, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>✕</button>
                </div>

                {/* Step content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '22px 22px 26px', pointerEvents: uploadTransitioning ? 'none' : 'all' }}>
                  <div key={uploadStepKey}
                    style={{ animation: uploadTransitioning ? 'morphOut 0.18s cubic-bezier(0.4,0,1,1) forwards' : 'morphIn 0.32s cubic-bezier(0.16,1,0.3,1) both' }}>

                    {/* Step CAT: Категория (halloween/regular) — только при HALLOWEEN_MODE */}
                    {uploadStep === STEP_CAT && HAS_CAT && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <p style={{ fontSize: 24, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', letterSpacing: '-0.4px', lineHeight: 1.1 }}>Какой это товар?</p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
                          {[
                            { v: 'regular',   label: 'Обычный',      accent: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  ring: 'rgba(59,130,246,0.18)',  icon: '/icon-regular.svg' },
                            { v: 'halloween', label: 'Хеллоуинский', accent: '#ff8a1c', bg: 'rgba(255,138,28,0.12)', ring: 'rgba(255,138,28,0.22)', icon: '/icon-halloween.svg' },
                          ].map(({ v, label, accent, bg, ring, icon }) => {
                            const active = uploadForm.category === v;
                            return (
                              <button key={v} type="button" className="wiz-tile" data-cat={v} data-active={active ? '1' : '0'}
                                onClick={() => {
                                  setUploadForm(f => ({ ...f, category: v }));
                                  setUploadTransitioning(true);
                                  setTimeout(() => { setUploadStep(STEP_TYPE); setUploadStepKey(k => k+1); setUploadTransitioning(false); }, 200);
                                }}
                                style={{
                                  padding: '22px 12px 18px', borderRadius: 20,
                                  border: `1.5px solid ${active ? accent : isDark ? 'rgba(255,255,255,0.07)' : '#eef2f7'}`,
                                  background: active ? bg : isDark ? 'rgba(255,255,255,0.025)' : '#f8fafc',
                                  cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9,
                                  boxShadow: active ? `0 0 0 3px ${ring}` : 'none',
                                  color: active ? accent : isDark ? '#cbd5e1' : '#334155',
                                  transition: 'all 0.18s cubic-bezier(0.34,1.56,0.64,1)',
                                }}>
                                <img src={icon} alt="" className="cat-icon" style={{ width: 42, height: 42, objectFit: 'contain' }} />
                                <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Step TYPE: Тип */}
                    {uploadStep === STEP_TYPE && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <p style={{ fontSize: 24, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', letterSpacing: '-0.4px', lineHeight: 1.1 }}>Что добавляем?</p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
                          {TYPES.map(({ v, icon, label }) => {
                            const active = uploadForm.item_type === v;
                            return (
                              <button key={v} type="button" className="wiz-tile"
                                onClick={() => {
                                  setUploadForm(f => ({ ...f, item_type: v }));
                                  setUploadTransitioning(true);
                                  setTimeout(() => { setUploadStep(STEP_COVER); setUploadStepKey(k => k+1); setUploadTransitioning(false); }, 200);
                                }}
                                style={{
                                  padding: '22px 12px 18px', borderRadius: 20,
                                  border: `1.5px solid ${active ? '#3b82f6' : isDark ? 'rgba(255,255,255,0.07)' : '#eef2f7'}`,
                                  background: active ? 'rgba(59,130,246,0.1)' : isDark ? 'rgba(255,255,255,0.025)' : '#f8fafc',
                                  cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9,
                                  boxShadow: active ? '0 0 0 3px rgba(59,130,246,0.18)' : 'none',
                                  transition: 'all 0.18s cubic-bezier(0.34,1.56,0.64,1)',
                                }}>
                                <span style={{ fontSize: 36, lineHeight: 1 }}>{icon}</span>
                                <span style={{ fontSize: 13, fontWeight: 600, color: active ? '#3b82f6' : isDark ? '#cbd5e1' : '#334155' }}>{label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Step RARITY: Редкость */}
                    {uploadStep === STEP_RARITY && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 26 }}>{selType?.icon}</span>
                          <p style={{ fontSize: 24, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', letterSpacing: '-0.4px', lineHeight: 1.1 }}>Редкость</p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                          {RARITIES.map(({ v, color, label }) => {
                            const active = uploadForm.rarity === v;
                            return (
                              <button key={v} type="button" className="wiz-row"
                                onClick={() => {
                                  setUploadForm(f => ({ ...f, rarity: v }));
                                  setUploadTransitioning(true);
                                  setTimeout(() => { setUploadStep(STEP_DETAILS); setUploadStepKey(k => k+1); setUploadTransitioning(false); }, 200);
                                }}
                                style={{
                                  padding: '15px 18px', borderRadius: 16,
                                  border: `1.5px solid ${active ? color + '70' : isDark ? 'rgba(255,255,255,0.07)' : '#eef2f7'}`,
                                  background: active ? color + '14' : isDark ? 'rgba(255,255,255,0.025)' : '#f8fafc',
                                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14,
                                  boxShadow: active ? `0 0 18px ${color}28` : 'none',
                                  transition: 'all 0.2s cubic-bezier(0.34,1.56,0.64,1)',
                                }}>
                                <span style={{ width: 14, height: 14, borderRadius: '50%', background: color, boxShadow: active ? `0 0 8px ${color}aa` : 'none', flexShrink: 0, transition: 'box-shadow 0.2s' }} />
                                <span style={{ fontSize: 15, fontWeight: active ? 700 : 500, color: active ? color : isDark ? '#cbd5e1' : '#334155', flex: 1, textAlign: 'left' }}>{label}</span>
                                {active && (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                  </svg>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Step DETAILS: Детали */}
                    {uploadStep === STEP_DETAILS && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: pRC.accent, boxShadow: `0 0 8px ${pRC.accent}99`, flexShrink: 0 }} />
                          <p style={{ fontSize: 24, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', letterSpacing: '-0.4px', lineHeight: 1.1 }}>Детали</p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <div>
                            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: isDark ? '#475569' : '#94a3b8', marginBottom: 8 }}>Название <span style={{ color: '#f87171' }}>*</span></p>
                            <input type="text" className="upload-input" value={uploadForm.name}
                              onChange={e => setUploadForm(f => ({ ...f, name: e.target.value }))}
                              placeholder="Например: Cherry Blossom Frame"
                              style={inp} autoFocus />
                          </div>
                          {/* Premium toggle */}
                          <div>
                            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: isDark ? '#475569' : '#94a3b8', marginBottom: 8 }}>Валюта</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <button type="button"
                                onClick={() => setUploadForm(f => ({ ...f, isPremium: false }))}
                                style={{
                                  padding: '11px 10px', borderRadius: 12, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                                  border: `1.5px solid ${!uploadForm.isPremium ? '#3b82f6' : isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7'}`,
                                  background: !uploadForm.isPremium ? (isDark ? 'rgba(59,130,246,0.14)' : 'rgba(59,130,246,0.08)') : isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                                  color: !uploadForm.isPremium ? '#3b82f6' : isDark ? '#64748b' : '#94a3b8',
                                }}>📎 Скрепки</button>
                              <button type="button"
                                onClick={() => setUploadForm(f => ({ ...f, isPremium: true }))}
                                style={{
                                  padding: '11px 10px', borderRadius: 12, cursor: 'pointer', fontSize: 13, fontWeight: 800,
                                  border: `1.5px solid ${uploadForm.isPremium ? '#ec4899' : isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7'}`,
                                  background: uploadForm.isPremium
                                    ? 'linear-gradient(135deg, rgba(168,85,247,0.18) 0%, rgba(236,72,153,0.14) 100%)'
                                    : isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                                  color: uploadForm.isPremium ? '#ec4899' : isDark ? '#64748b' : '#94a3b8',
                                }}>💎 Премиум ₽</button>
                            </div>
                            {uploadForm.isPremium && (
                              <p style={{ fontSize: 10, color: isDark ? '#9ca3af' : '#64748b', marginTop: 6, lineHeight: 1.3 }}>
                                Товар продаётся только за рубли через ЮKassa. В кейсы и в обычную витрину не попадает.
                              </p>
                            )}
                          </div>
                          {uploadForm.isPremium ? (
                            <div>
                              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: isDark ? '#475569' : '#94a3b8', marginBottom: 8 }}>Цена (₽) <span style={{ color: '#f87171' }}>*</span></p>
                              <input type="text" inputMode="decimal" className="upload-input" value={uploadForm.priceRub}
                                onChange={e => setUploadForm(f => ({ ...f, priceRub: e.target.value.replace(/[^0-9.,]/g, '') }))}
                                placeholder="199" style={inp} />
                              <p style={{ fontSize: 10, color: isDark ? '#64748b' : '#94a3b8', marginTop: 4 }}>Копейки через запятую: 199,50</p>
                            </div>
                          ) : (
                            <div>
                              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: isDark ? '#475569' : '#94a3b8', marginBottom: 8 }}>Цена (скрепки) <span style={{ color: '#f87171' }}>*</span></p>
                              <input type="number" min="1" className="upload-input" value={uploadForm.price}
                                onChange={e => setUploadForm(f => ({ ...f, price: e.target.value }))}
                                placeholder="50" style={inp} />
                            </div>
                          )}
                          <div>
                            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: isDark ? '#2d3748' : '#cbd5e1', marginBottom: 8 }}>Описание <span style={{ fontSize: 9, textTransform: 'none', fontWeight: 400 }}>(опц.)</span></p>
                            <textarea className="upload-input" value={uploadForm.description}
                              onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))}
                              placeholder="Краткое описание…"
                              rows={2} style={{ ...inp, resize: 'none' }} />
                          </div>
                          <div>
                            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: isDark ? '#2d3748' : '#cbd5e1', marginBottom: 8 }}>Тег эффекта <span style={{ fontSize: 9, textTransform: 'none', fontWeight: 400 }}>(опц.)</span></p>
                            <input type="text" className="upload-input" value={uploadForm.effectType}
                              onChange={e => setUploadForm(f => ({ ...f, effectType: e.target.value }))}
                              placeholder="clown_balloons · neon_rainbow · …" style={inp} />
                            <p style={{ fontSize: 10, color: isDark ? '#64748b' : '#94a3b8', marginTop: 4 }}>Строка-тег для ItemEffect.jsx. Напиши код case'а в компоненте и задеплой.</p>
                          </div>
                          {uploadForm.effectType && uploadForm.effectType.trim() && (
                            <div>
                              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: isDark ? '#2d3748' : '#cbd5e1', marginBottom: 8 }}>Параметры эффекта <span style={{ fontSize: 9, textTransform: 'none', fontWeight: 400 }}>(JSON, опц.)</span></p>
                              <textarea className="upload-input" value={uploadForm.effectParams}
                                onChange={e => setUploadForm(f => ({ ...f, effectParams: e.target.value }))}
                                placeholder={'{"color":"#ff4444","count":30}'}
                                rows={2} style={{ ...inp, resize: 'none', fontFamily: 'ui-monospace, monospace', fontSize: 12 }} />
                            </div>
                          )}
                          <div>
                            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: isDark ? '#2d3748' : '#cbd5e1', marginBottom: 8 }}>Автор идеи <span style={{ fontSize: 9, textTransform: 'none', fontWeight: 400 }}>(опц.)</span></p>
                            {uploadForm.authorUserId ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: isDark ? 'rgba(250,204,21,0.08)' : 'rgba(250,204,21,0.14)', border: `1px solid ${isDark ? 'rgba(250,204,21,0.25)' : 'rgba(250,204,21,0.4)'}` }}>
                                {uploadForm.authorUserAvatar
                                  ? <img src={resolveAvatarSrc(uploadForm.authorUserAvatar)} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                                  : <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#facc15,#f59e0b)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12 }}>{(uploadForm.authorUserFio || '?')[0].toUpperCase()}</div>}
                                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: isDark ? '#fde68a' : '#92400e' }}>{uploadForm.authorUserFio}</span>
                                <button type="button" onClick={() => { setUploadForm(f => ({ ...f, authorUserId: null, authorUserFio: "", authorUserAvatar: null })); setAuthorSearch(""); }}
                                  style={{ background: 'none', border: 'none', color: isDark ? '#fde68a' : '#92400e', cursor: 'pointer', padding: 4, display: 'flex' }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                </button>
                              </div>
                            ) : (
                              <>
                                <input type="text" className="upload-input" value={authorSearch || uploadForm.authorName}
                                  onChange={e => { const v = e.target.value.slice(0, 120); setAuthorSearch(v); setUploadForm(f => ({ ...f, authorName: v })); }}
                                  placeholder="ФИО из системы или свободный ник"
                                  maxLength={120} style={inp} />
                                {authorSearch.trim().length >= 2 && (() => {
                                  const q = authorSearch.trim().toLowerCase();
                                  const matches = authorSuggestions.filter(u => (u.fio || '').toLowerCase().includes(q)).slice(0, 6);
                                  if (!matches.length) return null;
                                  return (
                                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`, borderRadius: 12, padding: 4, background: isDark ? 'rgba(0,0,0,0.3)' : '#fff' }}>
                                      {matches.map(u => (
                                        <button key={u.id} type="button"
                                          onClick={() => { setUploadForm(f => ({ ...f, authorUserId: u.id, authorUserFio: u.fio, authorUserAvatar: u.avatar_url, authorName: "" })); setAuthorSearch(""); }}
                                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: isDark ? '#e2e8f0' : '#0f172a' }}
                                          onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
                                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                          {u.avatar_url
                                            ? <img src={resolveAvatarSrc(u.avatar_url)} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                                            : <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 11 }}>{(u.fio || '?')[0].toUpperCase()}</div>}
                                          <span style={{ fontSize: 13, fontWeight: 500 }}>{u.fio}</span>
                                        </button>
                                      ))}
                                    </div>
                                  );
                                })()}
                                <p style={{ fontSize: 10, color: isDark ? '#475569' : '#94a3b8', marginTop: 6, lineHeight: 1.3 }}>Выбери из подсказок — ник станет ссылкой на профиль. Иначе сохранится как текст.</p>
                              </>
                            )}
                          </div>
                        </div>
                        <button type="button" className="wiz-cta"
                          disabled={!canAdvance()}
                          onClick={() => canAdvance() && goToStep(STEP_PREVIEW)}
                          style={{ width: '100%', padding: '14px', borderRadius: 16, background: canAdvance() ? '#3b82f6' : isDark ? 'rgba(255,255,255,0.06)' : '#eef2f7', color: canAdvance() ? '#fff' : isDark ? '#334155' : '#94a3b8', fontSize: 15, fontWeight: 600, border: 'none', cursor: canAdvance() ? 'pointer' : 'not-allowed', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          Продолжить
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </button>
                      </div>
                    )}

                    {/* Step COVER: Обложка */}
                    {uploadStep === STEP_COVER && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 26 }}>{selType?.icon}</span>
                          <p style={{ fontSize: 24, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', letterSpacing: '-0.4px', lineHeight: 1.1 }}>Обложка</p>
                        </div>
                        <label style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          width: '100%', height: 164, borderRadius: 20,
                          border: `2px dashed ${uploadForm.coverPreview ? pRC.accent + '55' : isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                          background: uploadForm.coverPreview ? 'transparent' : isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
                          cursor: uploadForm.coverPreview && (uploadForm.item_type === 'frame' || uploadForm.item_type === 'badge') ? 'default' : 'pointer',
                          overflow: 'hidden', position: 'relative', transition: 'border-color 0.2s', boxSizing: 'border-box',
                        }}>
                          {uploadForm.coverPreview ? (
                            uploadForm.item_type === 'frame' ? (
                              <div
                                style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)', cursor: frameDragRef.current.active ? 'grabbing' : 'grab', userSelect: 'none' }}
                                onMouseDown={e => { e.preventDefault(); frameDragRef.current = { active: true, startX: e.clientX, startY: e.clientY, ox: uploadForm.frameOffsetX, oy: uploadForm.frameOffsetY, moved: false }; }}
                                onTouchStart={e => { const t = e.touches[0]; frameDragRef.current = { active: true, startX: t.clientX, startY: t.clientY, ox: uploadForm.frameOffsetX, oy: uploadForm.frameOffsetY, moved: false }; }}
                                onClick={e => { if (frameDragRef.current.moved) e.preventDefault(); }}
                                tabIndex={0}
                                onKeyDown={e => {
                                  const s = e.shiftKey ? 5 : 1;
                                  if (e.key === 'ArrowUp')    { e.preventDefault(); setUploadForm(f => ({ ...f, frameOffsetY: Math.max(-50, f.frameOffsetY - s) })); }
                                  if (e.key === 'ArrowDown')  { e.preventDefault(); setUploadForm(f => ({ ...f, frameOffsetY: Math.min(50,  f.frameOffsetY + s) })); }
                                  if (e.key === 'ArrowLeft')  { e.preventDefault(); setUploadForm(f => ({ ...f, frameOffsetX: Math.max(-50, f.frameOffsetX - s) })); }
                                  if (e.key === 'ArrowRight') { e.preventDefault(); setUploadForm(f => ({ ...f, frameOffsetX: Math.min(50,  f.frameOffsetX + s) })); }
                                }}
                              >
                                <div style={{ position: 'relative', width: 90, height: 90 }}>
                                  <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                                    {user?.avatar_url ? <img src={resolveAvatarSrc(user.avatar_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
                                  </div>
                                  <img src={uploadForm.coverPreview} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', transform: `translateX(${uploadForm.frameOffsetX}%) translateY(${uploadForm.frameOffsetY}%) scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }} />
                                </div>
                              </div>
                            ) : uploadForm.item_type === 'badge' ? (
                              <div
                                style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)', gap: 12, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'none' }}
                                onClick={e => e.preventDefault()}
                                onContextMenu={e => e.preventDefault()}
                                onMouseMove={e => {
                                  if (navigator.maxTouchPoints > 0) return;
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const x = e.clientX - rect.left, y = e.clientY - rect.top;
                                  const Z = 2.5, LS = 100;
                                  const lens = e.currentTarget.querySelector('[data-badge-lens]');
                                  if (!lens) return;
                                  const inner = lens.querySelector('[data-badge-lens-dsk]');
                                  if (inner) { inner.style.width = rect.width + 'px'; inner.style.height = rect.height + 'px'; inner.style.transform = `translate(${LS/2 - x*Z}px,${LS/2 - y*Z}px) scale(${Z})`; }
                                  lens.style.opacity = '1';
                                  lens.style.left = Math.min(Math.max(x + 14, 0), rect.width - LS - 4) + 'px';
                                  lens.style.top = Math.min(Math.max(y - LS - 14, 0), rect.height - LS - 4) + 'px';
                                }}
                                onMouseLeave={e => { const l = e.currentTarget.querySelector('[data-badge-lens]'); if (l) l.style.opacity = '0'; }}
                                onTouchStart={e => {
                                  const el = e.currentTarget; const t = e.touches[0]; const rect = el.getBoundingClientRect();
                                  const Z = 2.5, LS = 110;
                                  el._lensTx = t.clientX - rect.left; el._lensTy = t.clientY - rect.top; el._lensRect = rect;
                                  el._lensTimer = setTimeout(() => {
                                    const tx = el._lensTx, ty = el._lensTy, r = el._lensRect;
                                    const lens = el.querySelector('[data-badge-lens-mobile]'); if (!lens) return;
                                    const inner = lens.querySelector('[data-badge-lens-inner]');
                                    if (inner) { inner.style.width = r.width + 'px'; inner.style.height = r.height + 'px'; inner.style.transform = `translate(${LS/2 - tx*Z}px,${LS/2 - ty*Z}px) scale(${Z})`; }
                                    if (navigator.vibrate) navigator.vibrate(10);
                                    lens.style.display = 'block';
                                    lens.style.transform = 'scale(0.6)'; lens.style.opacity = '0';
                                    requestAnimationFrame(() => { lens.style.transform = 'scale(1)'; lens.style.opacity = '1'; });
                                    lens.style.left = Math.min(Math.max(tx - LS/2, 4), r.width - LS - 4) + 'px';
                                    lens.style.top = Math.max(ty - LS - 24, 4) + 'px';
                                  }, 280);
                                }}
                                onTouchMove={e => {
                                  const el = e.currentTarget; const t = e.touches[0]; const rect = el.getBoundingClientRect();
                                  const Z = 2.5, LS = 110, tx = t.clientX - rect.left, ty = t.clientY - rect.top;
                                  el._lensTx = tx; el._lensTy = ty;
                                  const lens = el.querySelector('[data-badge-lens-mobile]');
                                  if (!lens || lens.style.display !== 'block') return;
                                  const inner = lens.querySelector('[data-badge-lens-inner]');
                                  if (inner) inner.style.transform = `translate(${LS/2 - tx*Z}px,${LS/2 - ty*Z}px) scale(${Z})`;
                                  lens.style.left = Math.min(Math.max(tx - LS/2, 4), rect.width - LS - 4) + 'px';
                                  lens.style.top = Math.max(ty - LS - 24, 4) + 'px';
                                }}
                                onTouchEnd={e => {
                                  const el = e.currentTarget; clearTimeout(el._lensTimer);
                                  const l = el.querySelector('[data-badge-lens-mobile]');
                                  if (!l || l.style.display !== 'block') return;
                                  l.style.transform = 'scale(0.6)'; l.style.opacity = '0';
                                  setTimeout(() => { l.style.display = 'none'; }, 150);
                                }}
                              >
                                <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', flexShrink: 0 }}>
                                  {user?.avatar_url ? <img src={resolveAvatarSrc(user.avatar_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{user?.fio || 'Имя Фамилия'}</span>
                                  {uploadForm.coverMeta?.isLottie && uploadForm.coverLottieData ? (
                                        <div style={{ width: 22, height: 22, transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }}>
                                          <Lottie src={uploadForm.coverLottieData} loop autoplay style={{ width: '100%', height: '100%' }} />
                                        </div>
                                      ) : (
                                        <img src={uploadForm.coverPreview} alt="" style={{ width: 22, height: 22, objectFit: 'contain', transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }} />
                                      )}
                                </div>
                                <div data-badge-lens="" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.15s, transform 0.15s cubic-bezier(0.34,1.56,0.64,1)', width: 100, height: 100, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 4px 24px rgba(0,0,0,0.7)', overflow: 'hidden', zIndex: 20 }}>
                                  <div data-badge-lens-dsk="" style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)', gap: 12 }}>
                                    <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', flexShrink: 0 }}>
                                      {user?.avatar_url ? <img src={resolveAvatarSrc(user.avatar_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{user?.fio || 'Имя Фамилия'}</span>
                                      {uploadForm.coverMeta?.isLottie && uploadForm.coverLottieData ? (
                                        <div style={{ width: 22, height: 22, transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }}>
                                          <Lottie src={uploadForm.coverLottieData} loop autoplay style={{ width: '100%', height: '100%' }} />
                                        </div>
                                      ) : (
                                        <img src={uploadForm.coverPreview} alt="" style={{ width: 22, height: 22, objectFit: 'contain', transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }} />
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div data-badge-lens-mobile="" style={{ display: 'none', position: 'absolute', zIndex: 30, pointerEvents: 'none', width: 110, height: 110, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.4)', boxShadow: '0 8px 32px rgba(0,0,0,0.8)', overflow: 'hidden', transition: 'transform 0.18s cubic-bezier(0.34,1.56,0.64,1), opacity 0.15s' }}>
                                  <div data-badge-lens-inner="" style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)', gap: 12 }}>
                                    <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', flexShrink: 0 }}>
                                      {user?.avatar_url ? <img src={resolveAvatarSrc(user.avatar_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{user?.fio || 'Имя Фамилия'}</span>
                                      {uploadForm.coverMeta?.isLottie && uploadForm.coverLottieData ? (
                                        <div style={{ width: 22, height: 22, transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }}>
                                          <Lottie src={uploadForm.coverLottieData} loop autoplay style={{ width: '100%', height: '100%' }} />
                                        </div>
                                      ) : (
                                        <img src={uploadForm.coverPreview} alt="" style={{ width: 22, height: 22, objectFit: 'contain', transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }} />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : uploadForm.coverMeta?.isLottie ? (
                              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#1e1b4b,#4c1d95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {uploadForm.coverLottieData ? (
                                  <Lottie src={uploadForm.coverLottieData} loop autoplay style={{ width: '100%', height: '100%' }} />
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: '#fff' }}>
                                    <span style={{ fontSize: 28 }}>✨</span>
                                    <span style={{ fontSize: 11, fontWeight: 700 }}>Загружаем…</span>
                                  </div>
                                )}
                                <span style={{ position: 'absolute', bottom: 6, right: 8, fontSize: 10, color: '#fff', opacity: 0.6 }}>JSON · {uploadForm.coverMeta?.sizeKb} КБ</span>
                              </div>
                            ) : (
                              <img src={uploadForm.coverPreview} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                            )
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#475569' : '#94a3b8'} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
                              </svg>
                              <span style={{ fontSize: 13, color: isDark ? '#475569' : '#94a3b8' }}>Выбрать файл</span>
                              <span style={{ fontSize: 10, color: isDark ? '#2d3748' : '#cbd5e1' }}>PNG · SVG · GIF · WebP · JPG · Lottie JSON</span>
                            </div>
                          )}
                          <input ref={frameCoverInputRef} type="file" accept="image/*,.svg,.heic,.heif,.json,application/json" className="hidden" onChange={e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const url = URL.createObjectURL(file);
                            // Samsung object-cutout и share-sheet часто дают файл без расширения
                            // или с чужим — поэтому доверяем MIME как источнику истины.
                            const MIME_TO_EXT = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/webp': 'webp', 'image/gif': 'gif', 'image/svg+xml': 'svg', 'image/apng': 'apng', 'image/heic': 'heic', 'image/heif': 'heif', 'application/json': 'json' };
                            const nameExt = (file.name.includes('.') ? file.name.split('.').pop() : '').toLowerCase();
                            const mimeExt = MIME_TO_EXT[file.type?.toLowerCase?.()];
                            const KNOWN = ['png','jpg','jpeg','svg','gif','webp','apng','heic','heif','json'];
                            const ext = KNOWN.includes(nameExt) ? nameExt : (mimeExt || nameExt || 'png');
                            const sizeKb = Math.round(file.size / 1024);
                            const VALID = { badge: ['png','jpg','jpeg','svg','gif','webp','apng','json'], frame: ['png','jpg','jpeg','svg','gif','webp','apng','json'], title: ['png','jpg','jpeg','svg','gif','webp','json'], banner: ['png','jpg','jpeg','webp','gif','json'] };
                            const formatOk = (VALID[uploadForm.item_type] || ['png','svg','gif','webp']).includes(ext);
                            const keepOrig = ['gif', 'svg', 'webp', 'json'].includes(ext);
                            const sizeOk = ext === 'json' ? sizeKb <= 4096 : keepOrig ? sizeKb <= 8192 : sizeKb <= 20480;
                            const baseMeta = { format: ext, sizeKb, formatOk, sizeOk };
                            // Lottie JSON — пропускаем img.onload (не изображение)
                            if (ext === 'json') {
                              file.text().then(txt => {
                                let data = null;
                                try { data = JSON.parse(txt); } catch { data = null; }
                                setUploadForm(f => ({ ...f, cover: file, coverPreview: url, coverMeta: { ...baseMeta, isLottie: true }, coverLottieData: data }));
                              });
                              return;
                            }
                            const isFrame = uploadForm.item_type === 'frame';
                            // Frame auto-fit: raster only — svg stays vector, gif preserves animation
                            const canFit = isFrame && !['svg', 'gif'].includes(ext);
                            const img = new Image();
                            img.onload = () => {
                              const W = img.naturalWidth, H = img.naturalHeight;
                              if (canFit && W > 0 && H > 0 && !(W === H && W === 512)) {
                                // Pad/scale to 512×512 square with transparency
                                const T = 512, sz = Math.max(W, H), sc = T / sz;
                                const cv = document.createElement('canvas');
                                cv.width = T; cv.height = T;
                                const ctx = cv.getContext('2d');
                                ctx.clearRect(0, 0, T, T);
                                ctx.drawImage(img, (T - W * sc) / 2, (T - H * sc) / 2, W * sc, H * sc);
                                cv.toBlob(blob => {
                                  if (!blob) { setUploadForm(f => ({ ...f, cover: file, coverPreview: url, coverMeta: { ...baseMeta, dims: `${W}×${H}` } })); return; }
                                  const fitted = new File([blob], file.name.replace(/\.[^.]+$/, '.png'), { type: 'image/png' });
                                  const fUrl = URL.createObjectURL(fitted);
                                  const fKb = Math.round(blob.size / 1024);
                                  URL.revokeObjectURL(url);
                                  setUploadForm(f => ({ ...f, cover: fitted, coverPreview: fUrl, coverMeta: { format: 'png', sizeKb: fKb, formatOk: true, sizeOk: fKb <= 20480, dims: '512×512', originalDims: `${W}×${H}`, fitted: true } }));
                                }, 'image/png');
                                return;
                              }
                              if (isFrame) {
                                // Already 512×512, or gif/svg-without-dims — just add dims
                                const meta = { ...baseMeta, dims: W > 0 ? `${W}×${H}` : undefined };
                                if (['gif', 'svg'].includes(ext)) { setUploadForm(f => ({ ...f, cover: file, coverPreview: url, coverMeta: meta })); return; }
                                const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
                                cv.getContext('2d').drawImage(img, 0, 0);
                                cv.toBlob(blob => {
                                  const converted = blob ? new File([blob], file.name.replace(/\.[^.]+$/, '.png'), { type: 'image/png' }) : file;
                                  setUploadForm(f => ({ ...f, cover: converted, coverPreview: url, coverMeta: { ...meta, format: 'png' } }));
                                }, 'image/png');
                                return;
                              }
                              // Non-frame: gif/svg/webp/apng сохраняем как есть (анимация/вектор).
                              // Всё остальное (png/jpg/heic/samsung cutout/etc) — всегда переконвертируем
                              // через canvas в чистый PNG, т.к. браузер img.onload уже отрисовал кадр,
                              // а PIL на бэке может не читать проприетарные/битые контейнеры.
                              const KEEP = ['gif','svg','webp','apng'];
                              if (KEEP.includes(ext) || ['image/gif','image/svg+xml','image/webp','image/apng'].includes(file.type)) {
                                setUploadForm(f => ({ ...f, cover: file, coverPreview: url, coverMeta: baseMeta })); return;
                              }
                              const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
                              cv.getContext('2d').drawImage(img, 0, 0);
                              cv.toBlob(blob => {
                                const blobKb = blob ? Math.round(blob.size / 1024) : baseMeta.sizeKb;
                                const baseName = (file.name || 'cover').replace(/\.[^.]+$/, '');
                                const converted = blob ? new File([blob], baseName + '.png', { type: 'image/png' }) : file;
                                setUploadForm(f => ({ ...f, cover: converted, coverPreview: url, coverMeta: { ...baseMeta, format: 'png', formatOk: true, sizeKb: blobKb, sizeOk: blobKb <= 20480 } }));
                              }, 'image/png');
                            };
                            img.onerror = () => setUploadForm(f => ({ ...f, cover: file, coverPreview: url, coverMeta: baseMeta }));
                            // Always load image (need dims; svg naturalWidth may be 0 but that's ok)
                            img.src = url;
                          }} />
                        </label>
                        {uploadForm.item_type === 'badge' && uploadForm.coverPreview && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <button type="button" onClick={() => frameCoverInputRef.current?.click()}
                                style={{ fontSize: 11, padding: '4px 12px', borderRadius: 8, background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, cursor: 'pointer', color: isDark ? '#64748b' : '#94a3b8' }}>Сменить</button>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#475569' : '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Размер бейджика</span>
                                <span style={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: pRC.accent }}>{uploadForm.frameScale}%</span>
                              </div>
                              <input
                                type="range" min={50} max={250} step={5}
                                value={uploadForm.frameScale}
                                onChange={e => setUploadForm(f => ({ ...f, frameScale: Number(e.target.value) }))}
                                style={{ width: '100%', accentColor: pRC.accent, cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: 10, color: isDark ? '#334155' : '#cbd5e1' }}>
                                Иконка {Math.round(22 * uploadForm.frameScale / 100)} × {Math.round(22 * uploadForm.frameScale / 100)} px рядом с именем
                              </span>
                            </div>
                          </>
                        )}
                        {uploadForm.item_type === 'frame' && uploadForm.coverPreview && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 11, color: isDark ? '#475569' : '#94a3b8', fontWeight: 500 }}>X:</span>
                              <span style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: isDark ? '#64748b' : '#94a3b8', minWidth: 34 }}>{uploadForm.frameOffsetX > 0 ? '+' : ''}{uploadForm.frameOffsetX}%</span>
                              <span style={{ fontSize: 11, color: isDark ? '#475569' : '#94a3b8', fontWeight: 500 }}>Y:</span>
                              <span style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: isDark ? '#64748b' : '#94a3b8', minWidth: 34 }}>{uploadForm.frameOffsetY > 0 ? '+' : ''}{uploadForm.frameOffsetY}%</span>
                              {(uploadForm.frameOffsetX !== 0 || uploadForm.frameOffsetY !== 0) && (
                                <button type="button" className="wiz-tile" onClick={() => setUploadForm(f => ({ ...f, frameOffsetX: 0, frameOffsetY: 0 }))}
                                  style={{ fontSize: 10, padding: '3px 8px', borderRadius: 8, background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, cursor: 'pointer', color: isDark ? '#475569' : '#94a3b8' }}>сброс</button>
                              )}
                              <button type="button" className="wiz-tile" onClick={() => frameCoverInputRef.current?.click()}
                                style={{ marginLeft: 'auto', fontSize: 11, padding: '3px 10px', borderRadius: 8, background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`, cursor: 'pointer', color: isDark ? '#64748b' : '#94a3b8' }}>Сменить</button>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#475569' : '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Размер рамки</span>
                                <span style={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: pRC.accent }}>{uploadForm.frameScale}%</span>
                              </div>
                              <input
                                type="range" min={50} max={250} step={5}
                                value={uploadForm.frameScale}
                                onChange={e => setUploadForm(f => ({ ...f, frameScale: Number(e.target.value) }))}
                                style={{ width: '100%', accentColor: pRC.accent, cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: 10, color: isDark ? '#334155' : '#cbd5e1' }}>
                                Аватар 80 × 80 px → слой рамки {Math.round(80 * uploadForm.frameScale / 100)} × {Math.round(80 * uploadForm.frameScale / 100)} px
                              </span>
                            </div>
                          </>
                        )}
                        {uploadForm.coverMeta && (() => {
                          const VALID = { badge: ['png','jpg','jpeg','svg','gif','webp','apng','json'], frame: ['png','jpg','jpeg','svg','gif','webp','apng','json'], title: ['png','jpg','jpeg','svg','gif','webp','json'], banner: ['png','jpg','jpeg','webp','gif','json'] };
                          const liveFormatOk = (VALID[uploadForm.item_type] || ['png','svg','gif','webp']).includes(uploadForm.coverMeta.format);
                          return (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              {[
                                { label: uploadForm.coverMeta.format.toUpperCase(), ok: liveFormatOk, hint: !liveFormatOk ? 'Формат не подходит' : 'OK' },
                                { label: uploadForm.coverMeta.sizeKb + ' КБ', ok: uploadForm.coverMeta.sizeOk, hint: !uploadForm.coverMeta.sizeOk ? (['gif','svg','webp'].includes(uploadForm.coverMeta.format) ? 'Макс. 8 МБ (без сжатия)' : 'Макс. 20 МБ') : 'OK' },
                                ...(uploadForm.coverMeta.dims ? [{ label: uploadForm.coverMeta.fitted ? `✦ ${uploadForm.coverMeta.dims}` : uploadForm.coverMeta.dims, ok: uploadForm.coverMeta.dims === '512×512', hint: uploadForm.coverMeta.fitted ? `Авто-подгонка с ${uploadForm.coverMeta.originalDims}` : uploadForm.coverMeta.dims === '512×512' ? 'Идеальный размер' : 'Рекомендуется 512×512' }] : []),
                              ].map(({ label, ok, hint }, i) => (
                                <span key={i} title={hint} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                                  background: ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                                  color: ok ? '#22c55e' : '#f87171',
                                  border: `1px solid ${ok ? 'rgba(34,197,94,0.28)' : 'rgba(239,68,68,0.28)'}`,
                                  animation: 'metaBadgeIn 0.22s cubic-bezier(0.34,1.56,0.64,1) both',
                                }}>
                                  <span style={{ fontSize: 9, fontWeight: 900 }}>{ok ? '✓' : '✗'}</span>{label}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                        <button type="button" className="wiz-cta"
                          disabled={!canAdvance()}
                          onClick={() => {
                            if (!canAdvance()) return;
                            if (editingCoverFor) {
                              setEditForm(ef => ({
                                ...ef,
                                cover: uploadForm.cover,
                                coverPreview: uploadForm.coverPreview,
                                coverMeta: uploadForm.coverMeta,
                                frameOffsetX: uploadForm.frameOffsetX,
                                frameOffsetY: uploadForm.frameOffsetY,
                                frameScale: uploadForm.frameScale,
                              }));
                              setShowUploadModal(false);
                              setEditModal(editingCoverFor);
                              setEditingCoverFor(null);
                              return;
                            }
                            goToStep(STEP_RARITY);
                          }}
                          style={{ width: '100%', padding: '14px', borderRadius: 16, background: canAdvance() ? '#3b82f6' : isDark ? 'rgba(255,255,255,0.06)' : '#eef2f7', color: canAdvance() ? '#fff' : isDark ? '#334155' : '#94a3b8', fontSize: 15, fontWeight: 600, border: 'none', cursor: canAdvance() ? 'pointer' : 'not-allowed', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          Продолжить
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </button>
                      </div>
                    )}

                    {/* Step PREVIEW: Итог */}
                    {uploadStep === STEP_PREVIEW && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{
                          borderRadius: 22, overflow: 'hidden',
                          border: `1.5px solid ${pRC.accent}30`,
                          background: isDark ? '#0d0d14' : '#f8fafc',
                          boxShadow: `0 8px 32px ${pRC.accent}22`,
                        }}>
                          <div style={{ aspectRatio: '16/9', background: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                            {uploadForm.coverPreview ? (
                              uploadForm.item_type === 'frame' ? (
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)' }}>
                                  <div style={{ position: 'relative', width: 90, height: 90 }}>
                                    <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                                      {user?.avatar_url ? <img src={resolveAvatarSrc(user.avatar_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
                                    </div>
                                    <img src={uploadForm.coverPreview} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', transform: `translateX(${uploadForm.frameOffsetX}%) translateY(${uploadForm.frameOffsetY}%) scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }} />
                                  </div>
                                </div>
                              ) : uploadForm.item_type === 'badge' ? (
                                <div
                                  style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)', gap: 12, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'none' }}
                                  onContextMenu={e => e.preventDefault()}
                                  onMouseMove={e => {
                                    if (navigator.maxTouchPoints > 0) return;
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const x = e.clientX - rect.left, y = e.clientY - rect.top;
                                    const Z = 2.5, LS = 100;
                                    const lens = e.currentTarget.querySelector('[data-badge-lens]');
                                    if (!lens) return;
                                    const inner = lens.querySelector('[data-badge-lens-dsk]');
                                    if (inner) { inner.style.width = rect.width + 'px'; inner.style.height = rect.height + 'px'; inner.style.transform = `translate(${LS/2 - x*Z}px,${LS/2 - y*Z}px) scale(${Z})`; }
                                    lens.style.opacity = '1';
                                    lens.style.left = Math.min(Math.max(x + 14, 0), rect.width - LS - 4) + 'px';
                                    lens.style.top = Math.min(Math.max(y - LS - 14, 0), rect.height - LS - 4) + 'px';
                                  }}
                                  onMouseLeave={e => { const l = e.currentTarget.querySelector('[data-badge-lens]'); if (l) l.style.opacity = '0'; }}
                                  onTouchStart={e => {
                                    const el = e.currentTarget; const t = e.touches[0]; const rect = el.getBoundingClientRect();
                                    const Z = 2.5, LS = 110;
                                    el._lensTx = t.clientX - rect.left; el._lensTy = t.clientY - rect.top; el._lensRect = rect;
                                    el._lensTimer = setTimeout(() => {
                                      const tx = el._lensTx, ty = el._lensTy, r = el._lensRect;
                                      const lens = el.querySelector('[data-badge-lens-mobile]'); if (!lens) return;
                                      const inner = lens.querySelector('[data-badge-lens-inner]');
                                      if (inner) { inner.style.width = r.width + 'px'; inner.style.height = r.height + 'px'; inner.style.transform = `translate(${LS/2 - tx*Z}px,${LS/2 - ty*Z}px) scale(${Z})`; }
                                      if (navigator.vibrate) navigator.vibrate(10);
                                      lens.style.display = 'block';
                                      lens.style.transform = 'scale(0.6)'; lens.style.opacity = '0';
                                      requestAnimationFrame(() => { lens.style.transform = 'scale(1)'; lens.style.opacity = '1'; });
                                      lens.style.left = Math.min(Math.max(tx - LS/2, 4), r.width - LS - 4) + 'px';
                                      lens.style.top = Math.max(ty - LS - 24, 4) + 'px';
                                    }, 280);
                                  }}
                                  onTouchMove={e => {
                                    const el = e.currentTarget; const t = e.touches[0]; const rect = el.getBoundingClientRect();
                                    const Z = 2.5, LS = 110, tx = t.clientX - rect.left, ty = t.clientY - rect.top;
                                    el._lensTx = tx; el._lensTy = ty;
                                    const lens = el.querySelector('[data-badge-lens-mobile]');
                                    if (!lens || lens.style.display !== 'block') return;
                                    const inner = lens.querySelector('[data-badge-lens-inner]');
                                    if (inner) inner.style.transform = `translate(${LS/2 - tx*Z}px,${LS/2 - ty*Z}px) scale(${Z})`;
                                    lens.style.left = Math.min(Math.max(tx - LS/2, 4), rect.width - LS - 4) + 'px';
                                    lens.style.top = Math.max(ty - LS - 24, 4) + 'px';
                                  }}
                                  onTouchEnd={e => {
                                    const el = e.currentTarget; clearTimeout(el._lensTimer);
                                    const l = el.querySelector('[data-badge-lens-mobile]');
                                    if (!l || l.style.display !== 'block') return;
                                    l.style.transform = 'scale(0.6)'; l.style.opacity = '0';
                                    setTimeout(() => { l.style.display = 'none'; }, 150);
                                  }}
                                >
                                  <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', flexShrink: 0 }}>
                                    {user?.avatar_url ? <img src={resolveAvatarSrc(user.avatar_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{user?.fio || 'Имя Фамилия'}</span>
                                    {uploadForm.coverMeta?.isLottie && uploadForm.coverLottieData ? (
                                        <div style={{ width: 22, height: 22, transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }}>
                                          <Lottie src={uploadForm.coverLottieData} loop autoplay style={{ width: '100%', height: '100%' }} />
                                        </div>
                                      ) : (
                                        <img src={uploadForm.coverPreview} alt="" style={{ width: 22, height: 22, objectFit: 'contain', transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }} />
                                      )}
                                  </div>
                                  <div data-badge-lens="" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.15s, transform 0.15s cubic-bezier(0.34,1.56,0.64,1)', width: 100, height: 100, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 4px 24px rgba(0,0,0,0.7)', overflow: 'hidden', zIndex: 20 }}>
                                    <div data-badge-lens-dsk="" style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)', gap: 12 }}>
                                      <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', flexShrink: 0 }}>
                                        {user?.avatar_url ? <img src={resolveAvatarSrc(user.avatar_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{user?.fio || 'Имя Фамилия'}</span>
                                        {uploadForm.coverMeta?.isLottie && uploadForm.coverLottieData ? (
                                        <div style={{ width: 22, height: 22, transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }}>
                                          <Lottie src={uploadForm.coverLottieData} loop autoplay style={{ width: '100%', height: '100%' }} />
                                        </div>
                                      ) : (
                                        <img src={uploadForm.coverPreview} alt="" style={{ width: 22, height: 22, objectFit: 'contain', transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }} />
                                      )}
                                      </div>
                                    </div>
                                  </div>
                                  <div data-badge-lens-mobile="" style={{ display: 'none', position: 'absolute', zIndex: 30, pointerEvents: 'none', width: 110, height: 110, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.4)', boxShadow: '0 8px 32px rgba(0,0,0,0.8)', overflow: 'hidden', transition: 'transform 0.18s cubic-bezier(0.34,1.56,0.64,1), opacity 0.15s' }}>
                                    <div data-badge-lens-inner="" style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e1e2e,#16213e)', gap: 12 }}>
                                      <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', flexShrink: 0 }}>
                                        {user?.avatar_url ? <img src={resolveAvatarSrc(user.avatar_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#fff', fontWeight: 700, fontSize: 26 }}>{(user?.fio || '?')[0].toUpperCase()}</span>}
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{user?.fio || 'Имя Фамилия'}</span>
                                        {uploadForm.coverMeta?.isLottie && uploadForm.coverLottieData ? (
                                        <div style={{ width: 22, height: 22, transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }}>
                                          <Lottie src={uploadForm.coverLottieData} loop autoplay style={{ width: '100%', height: '100%' }} />
                                        </div>
                                      ) : (
                                        <img src={uploadForm.coverPreview} alt="" style={{ width: 22, height: 22, objectFit: 'contain', transform: `scale(${uploadForm.frameScale / 100})`, transformOrigin: 'center' }} />
                                      )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <img src={uploadForm.coverPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              )
                            ) : (
                              <span style={{ fontSize: 52, filter: `drop-shadow(0 4px 18px ${pRC.accent}66)` }}>{selType?.icon || '🏅'}</span>
                            )}
                          </div>
                          <div style={{ padding: '14px 16px 18px' }}>
                            <p style={{ fontSize: 17, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', marginBottom: 7 }}>{uploadForm.name || '—'}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: pRC.pillBg, color: pRC.pill, border: `1px solid ${pRC.accent}44` }}>{pRC.label}</span>
                              <span style={{ fontSize: 10, color: isDark ? '#475569' : '#94a3b8' }}>{selType?.icon} {selType?.label}</span>
                              <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 700, color: isDark ? '#94a3b8' : '#64748b' }}>{uploadForm.price} 📎</span>
                            </div>
                            {uploadForm.description && (
                              <p style={{ fontSize: 12, color: isDark ? '#64748b' : '#94a3b8', marginTop: 7, lineHeight: 1.4 }}>{uploadForm.description}</p>
                            )}
                          </div>
                        </div>
                        {uploadError && (
                          <p style={{ fontSize: 12, color: '#f87171', fontWeight: 500 }}>{uploadError}</p>
                        )}
                        <button type="button" className="wiz-cta"
                          disabled={uploadBusy}
                          onClick={submitUploadItem}
                          style={{ width: '100%', padding: '15px', borderRadius: 16, background: '#3b82f6', color: '#fff', fontSize: 16, fontWeight: 700, border: 'none', cursor: uploadBusy ? 'not-allowed' : 'pointer', opacity: uploadBusy ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.18s cubic-bezier(0.16,1,0.3,1)', letterSpacing: '-0.2px' }}>
                          {uploadBusy ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Добавить в магазин'}
                        </button>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </div>
            );
          })()
          , document.body)}

          {showUploadModal && uploadMode === 'set' && createPortal(
            (() => {
              const EFFECTS = [
                { v: 'orange_pulse',  label: 'Оранжевая пульсация'  },
                { v: 'pumpkin_orbit', label: 'Орбита тыкв'          },
                { v: 'ghost_floater', label: 'Призрак'              },
                { v: 'spider_web',    label: 'Паутина'              },
                { v: 'none',          label: 'Без эффекта'          },
              ];
              const inp = { width: '100%', padding: '13px 14px', borderRadius: 14, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7'}`, background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', fontSize: 15, color: isDark ? '#f1f5f9' : '#0f172a', outline: 'none', boxSizing: 'border-box' };
              const label = { fontSize: 11, fontWeight: 700, color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6, display: 'block' };
              const optStyle = { background: isDark ? '#15151d' : '#ffffff', color: isDark ? '#f1f5f9' : '#0f172a' };
              const canSubmit = setForm.name.trim() && setForm.frame_name.trim() && setForm.banner_name.trim() && setForm.badge_name.trim() && setForm.frameCover && setForm.bannerCover && setForm.badgeCover;
              const submitSet = async () => {
                if (!canSubmit || setSubmitting) return;
                const token = getToken();
                if (!token) { setUploadError('Нет токена'); return; }
                setSetSubmitting(true);
                try {
                  const fd = new FormData();
                  fd.append('name', setForm.name);
                  fd.append('description', setForm.description);
                  fd.append('effect_type', setForm.effect_type);
                  fd.append('effect_params', JSON.stringify({}));
                  fd.append('rarity', setForm.rarity);
                  if (setForm.price_frame != null && setForm.price_frame !== '') fd.append('price_frame', String(setForm.price_frame));
                  if (setForm.price_banner != null && setForm.price_banner !== '') fd.append('price_banner', String(setForm.price_banner));
                  if (setForm.price_badge != null && setForm.price_badge !== '') fd.append('price_badge', String(setForm.price_badge));
                  if (setForm.bundle_price != null && setForm.bundle_price !== '') fd.append('bundle_price', String(setForm.bundle_price));
                  fd.append('is_halloween', setForm.is_halloween ? 'true' : 'false');
                  fd.append('frame_name', setForm.frame_name);
                  fd.append('banner_name', setForm.banner_name);
                  fd.append('badge_name', setForm.badge_name);
                  fd.append('frame_cover', setForm.frameCover);
                  fd.append('banner_cover', setForm.bannerCover);
                  fd.append('badge_cover', setForm.badgeCover);
                  if (setForm.setCover) fd.append('set_cover', setForm.setCover);
                  const r = await fetch('/skrep/admin/sets/create', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd,
                  });
                  if (!r.ok) {
                    const err = await r.json().catch(() => ({}));
                    setUploadError(err.detail || 'Ошибка создания сета');
                    setSetSubmitting(false);
                    return;
                  }
                  setShowUploadModal(false);
                  setUploadMode(null);
                  setSetForm({
                    name: '', description: '', effect_type: 'orange_pulse',
                    rarity: 'epic', price_frame: 50, price_banner: 50, price_badge: 30, bundle_price: null,
                    is_halloween: true, frame_name: '', banner_name: '', badge_name: '',
                    frameCover: null, bannerCover: null, badgeCover: null, setCover: null,
                  });
                  // Refresh shop + sets
                  if (typeof fetchShop === 'function') fetchShop();
                } catch (e) {
                  setUploadError(String(e?.message || e));
                } finally {
                  setSetSubmitting(false);
                }
              };
              return (
            <div className="fixed inset-0 z-[650] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(22px)' }}
              onClick={e => { if (e.target === e.currentTarget) { setShowUploadModal(false); setUploadMode(null); } }}>
              <div className="w-full max-w-[460px] rounded-[28px] overflow-hidden flex flex-col"
                style={{
                  background: isDark ? '#15151d' : '#ffffff',
                  maxHeight: '92svh',
                  boxShadow: isDark ? '0 40px 80px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.06)' : '0 40px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                  animation: 'wizIn 0.35s cubic-bezier(0.16,1,0.3,1) both',
                }}>
                <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#eef2f7'}` }}>
                  <button type="button" onClick={() => setUploadMode(null)}
                    style={{ width: 28, height: 28, borderRadius: 14, background: 'none', border: 'none', cursor: 'pointer', color: isDark ? '#64748b' : '#94a3b8' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M19 12H5M12 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  <p style={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>Новый сет</p>
                  <button type="button" onClick={() => { setShowUploadModal(false); setUploadMode(null); }}
                    style={{ width: 28, height: 28, borderRadius: 14, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer', color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, fontWeight: 700 }}>✕</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={label}>Название сета</label>
                    <input style={inp} value={setForm.name} onChange={e => setSetForm(f => ({ ...f, name: e.target.value }))} placeholder="Halloween 2026" />
                  </div>
                  <div>
                    <label style={label}>Описание</label>
                    <textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={setForm.description} onChange={e => setSetForm(f => ({ ...f, description: e.target.value }))} placeholder="Соберите 3 предмета — получите эффект" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={label}>Эффект</label>
                      <select style={inp} value={setForm.effect_type} onChange={e => setSetForm(f => ({ ...f, effect_type: e.target.value }))}>
                        {EFFECTS.map(e => <option key={e.v} value={e.v} style={optStyle}>{e.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={label}>Редкость</label>
                      <select style={inp} value={setForm.rarity} onChange={e => setSetForm(f => ({ ...f, rarity: e.target.value }))}>
                        <option value="common" style={optStyle}>Обычный</option>
                        <option value="epic" style={optStyle}>Эпический</option>
                        <option value="legendary" style={optStyle}>Легендарный</option>
                        <option value="chromatic" style={optStyle}>Хроматический</option>
                      </select>
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={setForm.is_halloween} onChange={e => setSetForm(f => ({ ...f, is_halloween: e.target.checked }))} />
                    <span style={{ fontSize: 14, color: isDark ? '#f1f5f9' : '#0f172a' }}>Halloween сет 🎃</span>
                  </label>
                  <div>
                    <label style={label}>Обложка сета (опционально)</label>
                    <label style={{ display: 'block', padding: '10px 12px', borderRadius: 10, border: `1.5px dashed ${setForm.setCover ? '#22c55e' : (isDark ? 'rgba(255,255,255,0.14)' : '#cbd5e1')}`, cursor: 'pointer', textAlign: 'center', fontSize: 13, color: setForm.setCover ? '#22c55e' : (isDark ? '#94a3b8' : '#64748b') }}>
                      {setForm.setCover ? `✓ ${setForm.setCover.name}` : 'Выбрать файл'}
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setSetForm(f => ({ ...f, setCover: e.target.files?.[0] || null }))} />
                    </label>
                  </div>
                  <div>
                    <label style={label}>Цена за весь сет (опционально — иначе сумма цен)</label>
                    <input style={inp} type="number" min="0" placeholder="например 100 (скидка от суммы)"
                      value={setForm.bundle_price ?? ''}
                      onChange={e => setSetForm(f => ({ ...f, bundle_price: e.target.value === '' ? null : parseInt(e.target.value, 10) }))} />
                  </div>
                  {[
                    { slot: 'frame',  nameKey: 'frame_name',  coverKey: 'frameCover',  priceKey: 'price_frame',  title: 'Рамка',   icon: '🖼' },
                    { slot: 'banner', nameKey: 'banner_name', coverKey: 'bannerCover', priceKey: 'price_banner', title: 'Баннер',  icon: '🎨' },
                    { slot: 'badge',  nameKey: 'badge_name',  coverKey: 'badgeCover',  priceKey: 'price_badge',  title: 'Бейджик', icon: '🏅' },
                  ].map(({ slot, nameKey, coverKey, priceKey, title, icon }) => (
                    <div key={slot} style={{ padding: 14, borderRadius: 14, background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#eef2f7'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: 20 }}>{icon}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>{title}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 8 }}>
                        <input style={inp} placeholder="Название" value={setForm[nameKey]} onChange={e => setSetForm(f => ({ ...f, [nameKey]: e.target.value }))} />
                        <input style={inp} type="number" min="0" placeholder="Цена" value={setForm[priceKey] ?? ''} onChange={e => setSetForm(f => ({ ...f, [priceKey]: e.target.value === '' ? null : parseInt(e.target.value, 10) }))} />
                      </div>
                      <label style={{ display: 'block', padding: '10px 12px', borderRadius: 10, border: `1.5px dashed ${setForm[coverKey] ? '#22c55e' : (isDark ? 'rgba(255,255,255,0.14)' : '#cbd5e1')}`, cursor: 'pointer', textAlign: 'center', fontSize: 13, color: setForm[coverKey] ? '#22c55e' : (isDark ? '#94a3b8' : '#64748b') }}>
                        {setForm[coverKey] ? `✓ ${setForm[coverKey].name}` : 'Выбрать файл'}
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setSetForm(f => ({ ...f, [coverKey]: e.target.files?.[0] || null }))} />
                      </label>
                    </div>
                  ))}
                  {uploadError && <div style={{ padding: 12, borderRadius: 10, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 13 }}>{uploadError}</div>}
                </div>
                <div style={{ padding: 18, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#eef2f7'}` }}>
                  <button type="button" disabled={!canSubmit || setSubmitting}
                    onClick={submitSet}
                    style={{
                      width: '100%', padding: '14px 20px', borderRadius: 14,
                      background: canSubmit && !setSubmitting ? 'linear-gradient(135deg, #ff8a2b, #a21caf)' : (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'),
                      color: '#fff', border: 'none', fontSize: 15, fontWeight: 700, cursor: canSubmit && !setSubmitting ? 'pointer' : 'not-allowed',
                      boxShadow: canSubmit && !setSubmitting ? '0 10px 30px rgba(255,138,43,0.35)' : 'none',
                    }}>
                    {setSubmitting ? 'Создание…' : 'Создать сет'}
                  </button>
                </div>
              </div>
            </div>
              );
            })()
          , document.body)}

          {showUploadModal && uploadMode === 'promo' && createPortal(
            (() => {
              const inp = { width: '100%', padding: '13px 14px', borderRadius: 14, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7'}`, background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', fontSize: 15, color: isDark ? '#f1f5f9' : '#0f172a', outline: 'none', boxSizing: 'border-box' };
              const label = { fontSize: 11, fontWeight: 700, color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6, display: 'block' };
              const optStyle = { background: isDark ? '#15151d' : '#ffffff', color: isDark ? '#f1f5f9' : '#0f172a' };
              const canSubmit = promoForm.name.trim() && promoForm.start_date && promoForm.end_date
                && (promoForm.source === 'set' ? promoForm.set_id : promoForm.item_ids.length > 0);
              const submitPromo = async () => {
                if (!canSubmit || promoSubmitting) return;
                const token = getToken();
                if (!token) { setUploadError('Нет токена'); return; }
                setPromoSubmitting(true);
                try {
                  const body = {
                    name: promoForm.name,
                    description: promoForm.description,
                    start_date: new Date(promoForm.start_date).toISOString(),
                    end_date: new Date(promoForm.end_date).toISOString(),
                    discount_percent: parseInt(promoForm.discount_percent, 10) || 0,
                  };
                  if (promoForm.source === 'set') body.set_id = parseInt(promoForm.set_id, 10);
                  else body.item_ids = promoForm.item_ids;
                  const r = await fetch('/skrep/admin/promotions/create', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                  });
                  if (!r.ok) {
                    const err = await r.json().catch(() => ({}));
                    setUploadError(err.detail || 'Ошибка создания акции');
                    setPromoSubmitting(false);
                    return;
                  }
                  setShowUploadModal(false);
                  setUploadMode(null);
                  setPromoForm({ name: '', description: '', start_date: '', end_date: '', discount_percent: 20, source: 'set', set_id: '', item_ids: [] });
                  if (typeof fetchShop === 'function') fetchShop();
                } catch (e) {
                  setUploadError(String(e?.message || e));
                } finally {
                  setPromoSubmitting(false);
                }
              };
              return (
            <div className="fixed inset-0 z-[650] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(22px)' }}
              onClick={e => { if (e.target === e.currentTarget) { setShowUploadModal(false); setUploadMode(null); } }}>
              <div className="w-full max-w-[460px] rounded-[28px] overflow-hidden flex flex-col"
                style={{
                  background: isDark ? '#15151d' : '#ffffff',
                  maxHeight: '92svh',
                  boxShadow: isDark ? '0 40px 80px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.06)' : '0 40px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                  animation: 'wizIn 0.35s cubic-bezier(0.16,1,0.3,1) both',
                }}>
                <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#eef2f7'}` }}>
                  <button type="button" onClick={() => setUploadMode(null)}
                    style={{ width: 28, height: 28, borderRadius: 14, background: 'none', border: 'none', cursor: 'pointer', color: isDark ? '#64748b' : '#94a3b8' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M19 12H5M12 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  <p style={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>Новая акция</p>
                  <button type="button" onClick={() => { setShowUploadModal(false); setUploadMode(null); }}
                    style={{ width: 28, height: 28, borderRadius: 14, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer', color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, fontWeight: 700 }}>✕</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={label}>Название</label>
                    <input style={inp} value={promoForm.name} onChange={e => setPromoForm(f => ({ ...f, name: e.target.value }))} placeholder="Halloween Sale" />
                  </div>
                  <div>
                    <label style={label}>Описание</label>
                    <textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={promoForm.description} onChange={e => setPromoForm(f => ({ ...f, description: e.target.value }))} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={label}>Начало</label>
                      <input style={inp} type="datetime-local" value={promoForm.start_date} onChange={e => setPromoForm(f => ({ ...f, start_date: e.target.value }))} />
                    </div>
                    <div>
                      <label style={label}>Конец</label>
                      <input style={inp} type="datetime-local" value={promoForm.end_date} onChange={e => setPromoForm(f => ({ ...f, end_date: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label style={label}>Скидка %</label>
                    <input style={inp} type="number" min="0" max="90" value={promoForm.discount_percent} onChange={e => setPromoForm(f => ({ ...f, discount_percent: e.target.value }))} />
                  </div>
                  <div>
                    <label style={label}>На что действует</label>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      {[
                        { v: 'set',   label: 'Сет'   },
                        { v: 'items', label: 'Items' },
                      ].map(opt => (
                        <button key={opt.v} type="button"
                          onClick={() => setPromoForm(f => ({ ...f, source: opt.v }))}
                          style={{
                            flex: 1, padding: '10px 14px', borderRadius: 10,
                            background: promoForm.source === opt.v ? (isDark ? 'rgba(255,138,43,0.18)' : 'rgba(255,138,43,0.12)') : (isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc'),
                            border: `1.5px solid ${promoForm.source === opt.v ? '#ff8a2b' : (isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7')}`,
                            color: promoForm.source === opt.v ? '#ff8a2b' : (isDark ? '#f1f5f9' : '#0f172a'),
                            fontSize: 14, fontWeight: 700, cursor: 'pointer',
                          }}>{opt.label}</button>
                      ))}
                    </div>
                    {promoForm.source === 'set' ? (
                      <select style={inp} value={promoForm.set_id} onChange={e => setPromoForm(f => ({ ...f, set_id: e.target.value }))}>
                        <option value="" style={optStyle}>— выбери сет —</option>
                        {availableSets.map(s => <option key={s.id} value={s.id} style={optStyle}>{s.name}</option>)}
                      </select>
                    ) : (
                      <div style={{ maxHeight: 180, overflowY: 'auto', padding: 8, borderRadius: 10, background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#eef2f7'}` }}>
                        {availableShopItemsForPromo.length === 0 && <div style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', padding: 8 }}>Нет товаров</div>}
                        {availableShopItemsForPromo.map(it => {
                          const checked = promoForm.item_ids.includes(it.id);
                          return (
                            <label key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', fontSize: 13, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                              <input type="checkbox" checked={checked}
                                onChange={() => setPromoForm(f => ({ ...f, item_ids: checked ? f.item_ids.filter(x => x !== it.id) : [...f.item_ids, it.id] }))} />
                              <span>{it.name}</span>
                              <span style={{ marginLeft: 'auto', fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>{it.item_type}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {uploadError && <div style={{ padding: 12, borderRadius: 10, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 13 }}>{uploadError}</div>}
                </div>
                <div style={{ padding: 18, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#eef2f7'}` }}>
                  <button type="button" disabled={!canSubmit || promoSubmitting}
                    onClick={submitPromo}
                    style={{
                      width: '100%', padding: '14px 20px', borderRadius: 14,
                      background: canSubmit && !promoSubmitting ? 'linear-gradient(135deg, #ff8a2b, #ef4444)' : (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'),
                      color: '#fff', border: 'none', fontSize: 15, fontWeight: 700, cursor: canSubmit && !promoSubmitting ? 'pointer' : 'not-allowed',
                      boxShadow: canSubmit && !promoSubmitting ? '0 10px 30px rgba(255,138,43,0.35)' : 'none',
                    }}>
                    {promoSubmitting ? 'Создание…' : 'Создать акцию'}
                  </button>
                </div>
              </div>
            </div>
              );
            })()
          , document.body)}

          {/* Модалка редактирования товара */}
          {editModal && createPortal(
            (() => {
              const RARITIES = [
                { v: 'common',    color: '#94a3b8', label: 'Обычный' },
                { v: 'epic',      color: '#8b5cf6', label: 'Эпический' },
                { v: 'legendary', color: '#f59e0b', label: 'Легендарный' },
                { v: 'chromatic', color: '#d946ef', label: 'Хроматический' },
              ];
              const inp = { width: '100%', padding: '11px 14px', borderRadius: 12, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7'}`, background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', fontSize: 14, color: isDark ? '#f1f5f9' : '#0f172a', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
              return (
                <div className="fixed inset-0 z-[650] flex items-center justify-center p-4"
                  style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(22px)' }}
                  onClick={e => { if (e.target === e.currentTarget && !editBusy) setEditModal(null); }}>
                  <div className="w-full max-w-[360px] rounded-[24px] overflow-hidden"
                    style={{
                      background: isDark ? '#15151d' : '#ffffff',
                      maxHeight: '92svh',
                      boxShadow: isDark ? '0 40px 80px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.06)' : '0 40px 80px rgba(0,0,0,0.15)',
                      display: 'flex', flexDirection: 'column',
                      animation: 'wizIn 0.3s cubic-bezier(0.16,1,0.3,1) both',
                    }}>
                    <style>{`@keyframes wizIn{from{opacity:0;transform:translateY(18px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
                    {/* Header */}
                    <div style={{ padding: '18px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                      <p style={{ fontSize: 15, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>Редактировать товар</p>
                      <button type="button" onClick={() => !editBusy && setEditModal(null)}
                        style={{ width: 28, height: 28, borderRadius: 14, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, fontWeight: 700 }}>✕</button>
                    </div>
                    {/* Body */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {/* Обложка */}
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#64748b' : '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Обложка</p>
                        <button type="button"
                          onClick={() => {
                            const target = editModal;
                            setEditingCoverFor(target);
                            setEditModal(null);
                            setUploadForm(f => ({
                              ...f,
                              item_type: target.item_type || 'badge',
                              cover: editForm.cover || null,
                              coverPreview: editForm.coverPreview || target.asset || null,
                              coverMeta: editForm.coverMeta || null,
                              frameOffsetX: editForm.frameOffsetX ?? target.frame_offset_x ?? 0,
                              frameOffsetY: editForm.frameOffsetY ?? target.frame_offset_y ?? 0,
                              frameScale: editForm.frameScale ?? target.frame_scale ?? 150,
                            }));
                            setUploadStep(2);
                            setUploadStepKey(k => k + 1);
                            setShowUploadModal(true);
                          }}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', background: 'none', border: 'none', padding: 0, textAlign: 'left' }}>
                          <div style={{ width: 64, height: 64, borderRadius: 12, overflow: 'hidden', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#eef2f7'}`, background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {editForm.coverPreview
                              ? <img src={editForm.coverPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                              : editModal.asset
                                ? <img src={editModal.asset} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                : <span style={{ fontSize: 22, opacity: 0.4 }}>🖼</span>}
                          </div>
                          <div>
                            <p style={{ fontSize: 13, color: isDark ? '#93c5fd' : '#3b82f6', fontWeight: 600 }}>Заменить обложку</p>
                            <p style={{ fontSize: 11, color: isDark ? '#475569' : '#94a3b8', marginTop: 2 }}>PNG/JPG · макс. 20 МБ · GIF/SVG/WebP · макс. 8 МБ</p>
                          </div>
                        </button>
                      </div>
                      {/* Название */}
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#64748b' : '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Название</p>
                        <input style={inp} value={editForm.name} onChange={e => setEditForm(ef => ({ ...ef, name: e.target.value }))} maxLength={64} />
                      </div>
                      {/* Описание */}
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#64748b' : '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Описание</p>
                        <input style={inp} value={editForm.description} onChange={e => setEditForm(ef => ({ ...ef, description: e.target.value }))} maxLength={200} placeholder="Необязательно" />
                      </div>
                      {/* Цена */}
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#64748b' : '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Цена (скрепки)</p>
                        <input style={inp} type="number" min={0} value={editForm.price} onChange={e => setEditForm(ef => ({ ...ef, price: e.target.value }))} />
                      </div>
                      {/* Редкость */}
                      <div>
                        <p style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#64748b' : '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Редкость</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {RARITIES.map(({ v, color, label }) => {
                            const active = editForm.rarity === v;
                            return (
                              <button key={v} type="button"
                                onClick={() => setEditForm(ef => ({ ...ef, rarity: v }))}
                                style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${active ? color : (isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7')}`, background: active ? `${color}22` : 'transparent', color: active ? color : (isDark ? '#94a3b8' : '#64748b'), transition: 'all 0.15s' }}>
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {/* Автор + Анонимная загрузка */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {(() => {
                          const hasAuthor = !editForm.clear_author && (editModal.author_user_fio || editModal.author_user_id || editModal.author_name);
                          const authorLabel = editForm.clear_author
                            ? null
                            : (editModal.author_user_fio || editModal.author_user_id ? `@${editModal.author_user_fio || `id${editModal.author_user_id}`}` : editModal.author_name);
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px', borderRadius: 12, background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7'}` }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                                <svg style={{ width: 14, height: 14, flexShrink: 0, color: isDark ? '#94a3b8' : '#64748b' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.74V17h8v-2.26A7 7 0 0012 2z"/>
                                </svg>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <p style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#64748b' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>Автор идеи</p>
                                  <p style={{ fontSize: 13, color: isDark ? '#f1f5f9' : '#0f172a', fontWeight: 500, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {hasAuthor ? authorLabel : <span style={{ color: isDark ? '#475569' : '#94a3b8', fontWeight: 400 }}>не указан</span>}
                                  </p>
                                </div>
                              </div>
                              {hasAuthor && (
                                <button type="button"
                                  onClick={() => setEditForm(ef => ({ ...ef, clear_author: true }))}
                                  style={{ padding: '6px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${isDark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.2)'}`, background: 'transparent', color: isDark ? 'rgba(252,165,165,0.8)' : '#ef4444', flexShrink: 0 }}>
                                  Убрать
                                </button>
                              )}
                              {editForm.clear_author && (
                                <button type="button"
                                  onClick={() => setEditForm(ef => ({ ...ef, clear_author: false }))}
                                  style={{ padding: '6px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#eef2f7'}`, background: 'transparent', color: isDark ? '#94a3b8' : '#64748b', flexShrink: 0 }}>
                                  Отмена
                                </button>
                              )}
                            </div>
                          );
                        })()}

                        <button type="button"
                          onClick={() => setEditForm(ef => ({ ...ef, is_anonymous: !ef.is_anonymous }))}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px', borderRadius: 12, background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: `1.5px solid ${editForm.is_anonymous ? '#3b82f6' : (isDark ? 'rgba(255,255,255,0.08)' : '#eef2f7')}`, cursor: 'pointer', textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                            <svg style={{ width: 14, height: 14, flexShrink: 0, color: editForm.is_anonymous ? '#3b82f6' : (isDark ? '#94a3b8' : '#64748b') }} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z M3 3l18 18"/>
                            </svg>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <p style={{ fontSize: 13, color: isDark ? '#f1f5f9' : '#0f172a', fontWeight: 600, lineHeight: 1.1 }}>Анонимная загрузка</p>
                              <p style={{ fontSize: 11, color: isDark ? '#64748b' : '#94a3b8', marginTop: 3, lineHeight: 1 }}>Скрыть кто загрузил товар</p>
                            </div>
                          </div>
                          <div style={{ width: 36, height: 20, borderRadius: 10, background: editForm.is_anonymous ? '#3b82f6' : (isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'), position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}>
                            <div style={{ position: 'absolute', top: 2, left: editForm.is_anonymous ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                          </div>
                        </button>
                      </div>

                      {editError && <p style={{ fontSize: 12, color: '#f87171', textAlign: 'center' }}>{editError}</p>}
                    </div>
                    {/* Footer */}
                    <div style={{ padding: '0 20px 20px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button type="button" onClick={handleEditSubmit} disabled={editBusy || !editForm.name.trim() || !editForm.price}
                        style={{ width: '100%', padding: '13px', borderRadius: 14, border: 'none', cursor: editBusy || !editForm.name.trim() || !editForm.price ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700, background: editBusy || !editForm.name.trim() || !editForm.price ? (isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9') : '#3b82f6', color: editBusy || !editForm.name.trim() || !editForm.price ? (isDark ? '#475569' : '#94a3b8') : '#fff', transition: 'all 0.15s' }}>
                        {editBusy ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button type="button" disabled={editBusy}
                        onClick={async e => {
                          if (!window.confirm(`Удалить «${editModal.name}» из магазина?`)) return;
                          await handleDeleteItem(editModal, e);
                          setEditModal(null);
                        }}
                        style={{ width: '100%', padding: '11px', borderRadius: 14, border: `1px solid ${isDark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.2)'}`, cursor: editBusy ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, background: 'transparent', color: isDark ? 'rgba(252,165,165,0.7)' : '#ef4444', transition: 'all 0.15s' }}>
                        Удалить товар
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()
          , document.body)}

          </>}


        </div>
      </div>

      {/* ── DEVICES PANEL (slide from right) ── */}
      <div
        className={`fixed inset-0 z-[70] overflow-y-auto hide-scrollbar sm:absolute sm:left-0 sm:right-0 sm:top-0 sm:bottom-auto sm:z-auto sm:overflow-y-auto sm:max-h-[calc(100vh-120px)] sm:min-h-full transition-transform duration-300 ease-in-out will-change-transform pt-[52px] sm:pt-0 ${isDark ? '' : 'bg-[#f2f2f7]'}`}
        style={{ transform: devicesOpen ? 'translateX(0)' : 'translateX(105%)', background: isDark ? '#141418' : undefined }}
        aria-hidden={!devicesOpen}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-4 py-2 backdrop-blur-sm border-b fixed top-0 left-0 right-0 z-[70] sm:sticky sm:top-0 sm:z-10 ${isDark ? 'border-white/5' : 'border-slate-100'}`}
          style={{ background: isDark ? 'rgba(20,20,24,0.88)' : 'rgba(255,255,255,0.9)' }}
        >
          <button
            type="button"
            onClick={() => setDevicesOpen(false)}
            className="flex items-center gap-0.5 text-blue-500 font-medium text-[15px] py-2 pr-3 -ml-1 active:opacity-60"
            aria-label="Назад"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
            Назад
          </button>
          <div className="flex flex-col items-center leading-tight">
            <span className={`font-semibold text-[16px] ${isDark ? 'text-white' : 'text-slate-900'}`}>Устройства</span>
            {sessions.length > 0 && (
              <span className={`text-[10.5px] mt-0.5 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                {sessions.length} {sessions.length === 1 ? 'активный' : sessions.length < 5 ? 'активных' : 'активных'} {sessions.length === 1 ? 'сеанс' : sessions.length < 5 ? 'сеанса' : 'сеансов'}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => { _hap(8); handleFetchSessions(); }}
            disabled={sessionsLoading}
            className="text-blue-500 font-semibold text-[15px] py-2 pl-3 -mr-1 disabled:opacity-40 active:opacity-60"
            aria-label="Обновить список сеансов"
          >
            {sessionsLoading ? (
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M21 12a9 9 0 1 1-6.3-8.57" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 12a8 8 0 1 1-2.34-5.66L20 9"/><path d="M20 4v5h-5"/>
              </svg>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col gap-4 pb-20">
          {sessionsLoading && sessions.length === 0 ? (
            // skeleton rows
            <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/[0.06] border border-white/[0.07]' : 'bg-white border border-slate-200/50 shadow-sm'}`}>
              {[0, 1, 2].map(i => (
                <div key={i} className={`flex items-center gap-3 px-4 py-3.5 ${i > 0 ? (isDark ? 'border-t border-white/5' : 'border-t border-slate-100') : ''}`}>
                  <div className={`w-10 h-10 rounded-xl shrink-0 ${isDark ? 'bg-white/5' : 'bg-slate-100'} animate-pulse`} />
                  <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                    <div className={`h-3 rounded w-2/3 ${isDark ? 'bg-white/10' : 'bg-slate-200'} animate-pulse`} />
                    <div className={`h-2.5 rounded w-1/2 ${isDark ? 'bg-white/5' : 'bg-slate-100'} animate-pulse`} />
                  </div>
                </div>
              ))}
            </div>
          ) : sessionsError && sessions.length === 0 ? (
            <div className={`rounded-2xl px-4 py-10 text-center ${isDark ? 'bg-white/[0.06] border border-white/[0.07]' : 'bg-white border border-slate-200/50 shadow-sm'}`}>
              <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-red-500/15' : 'bg-red-50'}`}>
                <svg className="w-6 h-6 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
              </div>
              <p className={`text-[14px] ${isDark ? 'text-white/70' : 'text-slate-500'}`}>{sessionsError}</p>
              <button type="button" onClick={handleFetchSessions} className="mt-3 text-[13px] font-semibold text-blue-500 active:opacity-60">Повторить</button>
            </div>
          ) : sessions.length === 0 ? (
            <div className={`rounded-2xl px-4 py-14 text-center ${isDark ? 'bg-white/[0.06] border border-white/[0.07]' : 'bg-white border border-slate-200/50 shadow-sm'}`}>
              <div className={`mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ${isDark ? 'bg-blue-500/15' : 'bg-blue-50'}`}>
                <svg className="w-7 h-7 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8 20h8M12 16v4"/></svg>
              </div>
              <p className={`text-[14px] font-medium ${isDark ? 'text-white/80' : 'text-slate-600'}`}>Нет активных сеансов</p>
              <p className={`text-[12px] mt-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Попробуйте обновить список</p>
            </div>
          ) : (() => {
            const current = sessions.find(s => s.is_current);
            const others = sessions.filter(s => !s.is_current);
            const nowMs = Date.now();

            const SessionRow = ({ s, isCurrent, onClick, showRevoke, isLast }) => {
              const info = parseDeviceInfo(s);
              let createdMs = 0;
              try { if (s.created_at) createdMs = new Date(s.created_at.replace('Z', '')).getTime(); } catch (_) {}
              const isNew = createdMs > 0 && (nowMs - createdMs) < 10 * 60 * 1000;
              const lastUsedMs = (() => { try { return s.last_used ? new Date(s.last_used.replace('Z', '')).getTime() : 0; } catch (_) { return 0; } })();
              const activeNow = isCurrent || (lastUsedMs > 0 && (nowMs - lastUsedMs) < 2 * 60 * 1000);

              const iconWrapBg = isCurrent
                ? (isDark ? 'bg-blue-500/20' : 'bg-blue-100')
                : (isDark ? 'bg-white/[0.06]' : 'bg-slate-100');
              const iconColor = isCurrent
                ? (isDark ? '#60a5fa' : '#3b82f6')
                : (isDark ? '#cbd5e1' : '#64748b');

              return (
                <button
                  type="button"
                  onClick={onClick}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${isDark ? 'active:bg-white/[0.04] hover:bg-white/[0.03]' : 'active:bg-slate-50 hover:bg-slate-50/70'} ${!isLast ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''}`}
                >
                  <div className={`relative w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconWrapBg}`}>
                    <OsIcon osKey={info.osKey} isMobile={info.isMobile} className="w-5 h-5" color={iconColor} />
                    {activeNow && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center" style={{ background: isDark ? '#000' : '#fff' }}>
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className={`text-[13.5px] font-semibold truncate ${isDark ? 'text-white/95' : 'text-slate-800'}`}>{info.primary}</p>
                      {isCurrent && (
                        <span className="shrink-0 text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md" style={{ background: isDark ? 'rgba(96,165,250,0.18)' : 'rgba(59,130,246,0.1)', color: isDark ? '#93c5fd' : '#2563eb' }}>Это устройство</span>
                      )}
                      {!isCurrent && isNew && (
                        <span className="shrink-0 text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md" style={{ background: isDark ? 'rgba(251,146,60,0.18)' : 'rgba(249,115,22,0.1)', color: isDark ? '#fdba74' : '#ea580c' }}>Новый</span>
                      )}
                    </div>
                    <p className={`text-[11.5px] mt-0.5 truncate ${isDark ? 'text-white/45' : 'text-slate-400'}`}>
                      {info.secondary && <span>{info.secondary} · </span>}
                      {s.location && <span>{s.location} · </span>}
                      {activeNow ? 'активен сейчас' : (s.last_used ? formatRelativeTime(s.last_used) : '—')}
                    </p>
                  </div>
                  {showRevoke ? (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Завершить сеанс ${info.primary}`}
                      onClick={(e) => { e.stopPropagation(); if (revokeLoadingId !== s.sid) handleRevokeSession(s.sid); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); handleRevokeSession(s.sid); } }}
                      className={`text-[12px] font-semibold shrink-0 px-2.5 py-1 rounded-lg transition-colors ${isDark ? 'text-red-400 active:bg-red-500/15' : 'text-red-500 active:bg-red-50'} ${revokeLoadingId === s.sid ? 'opacity-50' : ''}`}
                    >
                      {revokeLoadingId === s.sid ? '…' : 'Выйти'}
                    </span>
                  ) : (
                    <svg className={`w-4 h-4 shrink-0 ${isDark ? 'text-white/25' : 'text-slate-300'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                  )}
                </button>
              );
            };

            return (
              <>
                {/* Security note */}
                <div className={`rounded-2xl px-4 py-3 flex items-start gap-3 ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
                  <svg className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-blue-300' : 'text-blue-500'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l7 3v6c0 5-3.5 9-7 11-3.5-2-7-6-7-11V5l7-3z"/><path d="M9 12l2 2 4-4"/></svg>
                  <p className={`text-[12px] leading-snug ${isDark ? 'text-blue-100/90' : 'text-blue-900/80'}`}>
                    Если видите незнакомое устройство — завершите сеанс и смените пароль.
                  </p>
                </div>

                {/* Это устройство */}
                {current && (
                  <div>
                    <p className={`text-[11px] font-semibold uppercase tracking-wider px-1 mb-1.5 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Это устройство</p>
                    <section className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/[0.06] border border-white/[0.07]' : 'bg-white border border-slate-200/50 shadow-sm'}`}>
                      <SessionRow s={current} isCurrent onClick={() => setSessionDetail(current)} showRevoke={false} isLast />
                    </section>
                  </div>
                )}

                {/* Активные сеансы */}
                {others.length > 0 && (
                  <div>
                    <div className="flex items-end justify-between px-1 mb-1.5">
                      <p className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Другие сеансы</p>
                      <p className={`text-[11px] ${isDark ? 'text-white/30' : 'text-slate-400'}`}>{others.length}</p>
                    </div>
                    <section className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/[0.06] border border-white/[0.07]' : 'bg-white border border-slate-200/50 shadow-sm'}`}>
                      {others.map((s, i) => (
                        <SessionRow
                          key={s.sid}
                          s={s}
                          isCurrent={false}
                          onClick={() => setSessionDetail(s)}
                          showRevoke
                          isLast={i === others.length - 1}
                        />
                      ))}
                    </section>
                    <button
                      type="button"
                      onClick={handleRevokeAllSessions}
                      className={`w-full mt-3 py-3 rounded-2xl text-[14px] font-semibold transition-all active:scale-[0.985] ${isDark ? 'text-red-400 bg-red-500/10 border border-red-500/20 active:bg-red-500/15' : 'text-red-500 border border-red-100 bg-white shadow-sm hover:bg-red-50 hover:border-red-200'}`}
                    >
                      Завершить все другие сеансы
                    </button>
                  </div>
                )}

                {!current && others.length === 0 && (
                  <div className={`rounded-2xl px-4 py-10 text-center ${isDark ? 'bg-white/[0.06] border border-white/[0.07]' : 'bg-white border border-slate-200/50 shadow-sm'}`}>
                    <p className={`text-[14px] ${isDark ? 'text-white/60' : 'text-slate-400'}`}>Нет данных о сеансах</p>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* ── SESSION DETAIL SHEET ── */}
        {devicesOpen && sessionDetail && (() => {
          const s = sessionDetail;
          const info = parseDeviceInfo(s);
          let createdStr = '';
          let lastStr = '';
          try { if (s.created_at) createdStr = new Date(s.created_at.replace('Z', '')).toLocaleString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (_) {}
          try { if (s.last_used) lastStr = new Date(s.last_used.replace('Z', '')).toLocaleString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (_) {}
          const rows = [
            { label: 'Устройство', value: info.primary },
            { label: 'Платформа', value: info.secondary || '—' },
            { label: 'Местоположение', value: s.location || '—' },
            { label: 'IP-адрес', value: s.ip || '—' },
            { label: 'Вход выполнен', value: createdStr || '—' },
            { label: 'Последняя активность', value: s.is_current ? 'сейчас' : (lastStr || '—') },
          ];
          return createPortal(
            <div className="fixed inset-0 z-[80] flex items-end sm:items-center sm:justify-center" role="dialog" aria-modal="true" aria-label="Подробности сеанса">
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSessionDetail(null)} />
              <div
                className={`relative w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl overflow-hidden ${isDark ? 'bg-[#1c1c1e]' : 'bg-white'}`}
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
              >
                <div className="flex justify-center pt-2 pb-1 sm:hidden">
                  <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/15' : 'bg-slate-200'}`} />
                </div>
                <div className="px-5 pt-3 pb-5 flex flex-col items-center gap-3">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${s.is_current ? (isDark ? 'bg-blue-500/20' : 'bg-blue-100') : (isDark ? 'bg-white/[0.06]' : 'bg-slate-100')}`}>
                    <OsIcon osKey={info.osKey} isMobile={info.isMobile} className="w-9 h-9" color={s.is_current ? (isDark ? '#60a5fa' : '#3b82f6') : (isDark ? '#cbd5e1' : '#64748b')} />
                  </div>
                  <div className="text-center">
                    <p className={`text-[17px] font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{info.primary}</p>
                    {info.secondary && <p className={`text-[12px] mt-0.5 ${isDark ? 'text-white/50' : 'text-slate-400'}`}>{info.secondary}</p>}
                  </div>
                </div>
                <div className={`mx-5 rounded-2xl overflow-hidden ${isDark ? 'bg-white/[0.04]' : 'bg-slate-50'}`}>
                  {rows.map((row, i) => (
                    <div key={row.label} className={`flex items-start justify-between gap-3 px-4 py-3 ${i > 0 ? (isDark ? 'border-t border-white/5' : 'border-t border-slate-200/50') : ''}`}>
                      <span className={`text-[12px] shrink-0 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{row.label}</span>
                      <span className={`text-[13px] text-right break-all ${isDark ? 'text-white/90' : 'text-slate-800'}`}>{row.value}</span>
                    </div>
                  ))}
                </div>
                <div className="px-5 pt-4 pb-5 flex flex-col gap-2">
                  {!s.is_current && (
                    <button
                      type="button"
                      disabled={revokeLoadingId === s.sid}
                      onClick={() => handleRevokeSession(s.sid)}
                      className={`w-full py-3 rounded-2xl text-[15px] font-semibold transition-all active:scale-[0.985] disabled:opacity-50 ${isDark ? 'bg-red-500/15 text-red-300 border border-red-500/25' : 'bg-red-50 text-red-600 border border-red-100'}`}
                    >
                      {revokeLoadingId === s.sid ? 'Завершаю…' : 'Завершить сеанс'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setSessionDetail(null)}
                    className={`w-full py-3 rounded-2xl text-[15px] font-semibold ${isDark ? 'bg-white/[0.06] text-white/80' : 'bg-slate-100 text-slate-700'}`}
                  >
                    Закрыть
                  </button>
                </div>
              </div>
            </div>,
            document.body
          );
        })()}

        {/* ── CONFIRM ACTION SHEET ── */}
        {devicesOpen && sessionConfirm && createPortal(
          <div className="fixed inset-0 z-[85] flex items-end sm:items-center sm:justify-center" role="alertdialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSessionConfirm(null)} />
            <div
              className={`relative w-full sm:max-w-sm sm:rounded-3xl rounded-t-3xl overflow-hidden ${isDark ? 'bg-[#1c1c1e]' : 'bg-white'}`}
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              <div className="flex justify-center pt-2 pb-1 sm:hidden">
                <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/15' : 'bg-slate-200'}`} />
              </div>
              <div className="px-5 pt-5 pb-4 text-center">
                <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-red-500/15' : 'bg-red-50'}`}>
                  <svg className="w-7 h-7 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l7 3v6c0 5-3.5 9-7 11-3.5-2-7-6-7-11V5l7-3z"/><path d="M12 8v4M12 16h.01"/></svg>
                </div>
                <p className={`text-[16px] font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {sessionConfirm.kind === 'all' ? 'Завершить все другие сеансы?' : 'Завершить сеанс?'}
                </p>
                <p className={`text-[13px] mt-1.5 leading-snug ${isDark ? 'text-white/55' : 'text-slate-500'}`}>
                  {sessionConfirm.kind === 'all'
                    ? `Будет завершено сеансов: ${sessionConfirm.count}. Это устройство останется в системе.`
                    : <>Устройство <span className="font-semibold">{sessionConfirm.label}</span> будет мгновенно отключено. Токен станет недействительным.</>}
                </p>
              </div>
              <div className="px-5 pb-5 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    const c = sessionConfirm;
                    setSessionConfirm(null);
                    if (c.kind === 'all') await _doRevokeAllSessions();
                    else await _doRevokeSession(c.sid);
                  }}
                  className="w-full py-3 rounded-2xl text-[15px] font-semibold bg-red-500 text-white active:scale-[0.985] transition-transform"
                >
                  {sessionConfirm.kind === 'all' ? 'Завершить все' : 'Завершить'}
                </button>
                <button
                  type="button"
                  onClick={() => setSessionConfirm(null)}
                  className={`w-full py-3 rounded-2xl text-[15px] font-medium ${isDark ? 'bg-white/[0.06] text-white/80' : 'bg-slate-100 text-slate-700'}`}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* ── TOAST ── */}
        {devicesOpen && createPortal(
          <div className="pointer-events-none fixed left-0 right-0 z-[90] flex justify-center" style={{ top: 'calc(env(safe-area-inset-top) + 60px)' }} aria-live="polite" aria-atomic="true">
            <div
              className={`transition-all duration-200 ${sessionToast ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}
            >
              {sessionToast && (
                <div
                  className={`px-4 py-2.5 rounded-full shadow-lg text-[13px] font-medium flex items-center gap-2 ${sessionToast.kind === 'err' ? (isDark ? 'bg-red-500/90 text-white' : 'bg-red-500 text-white') : (isDark ? 'bg-emerald-500/90 text-white' : 'bg-slate-900 text-white')}`}
                >
                  {sessionToast.kind === 'err' ? (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                  ) : (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                  )}
                  {sessionToast.msg}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* ── EDIT PANEL (slide from right) ── */}
      <div
        ref={editPanelRef}
        className="fixed inset-x-0 top-0 bottom-auto max-h-screen z-[70] overflow-y-auto hide-scrollbar sm:absolute sm:left-0 sm:right-0 sm:top-0 sm:bottom-auto sm:z-auto sm:overflow-y-auto sm:max-h-[calc(100vh-120px)] sm:min-h-full bg-[#f2f2f7] transition-transform duration-300 ease-in-out will-change-transform pt-[52px] sm:pt-0"
        style={{ transform: editView ? 'translateX(0)' : 'translateX(105%)', WebkitOverflowScrolling: 'touch' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-white/90 backdrop-blur-sm border-b border-slate-100 fixed top-0 left-0 right-0 z-[70] sm:sticky sm:top-0 sm:z-10">
          <button
            type="button"
            onClick={() => setEditView(false)}
            className="flex items-center gap-0.5 text-blue-500 font-medium text-[15px] py-2 pr-3 -ml-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
            Назад
          </button>
          <span className="font-semibold text-[16px] text-slate-900">Редактировать</span>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveBusy}
            className="text-blue-500 font-semibold text-[15px] py-2 pl-3 -mr-1 disabled:opacity-40"
          >
            {saveBusy ? 'Сохр…' : 'Готово'}
          </button>
        </div>

        {/* Аватар + позиционер */}
        <div
          className="flex flex-col items-center pt-4 pb-3"
          style={bannerPreview
            ? { backgroundImage: `url('${bannerPreview}')`, backgroundSize: bannerZoom === 1 ? 'cover' : `${Math.round(bannerZoom * 100)}%`, backgroundPosition: `${bannerPos.x}% ${bannerPos.y}%`, backgroundRepeat: 'no-repeat' }
            : { background: (GRADIENTS.find(g => g.id === editGradientId) || currentGradient).bg }
          }
        >
          <AvatarPositioner
            src={avatar}
            pos={avatarPos}
            busy={avatarBusy}
            onTap={async () => {
              if (avatarBusy) return;
              if ('showOpenFilePicker' in window) {
                try {
                  const [fh] = await window.showOpenFilePicker({
                    startIn: 'pictures',
                    types: [{ description: 'Изображения', accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif', '.avif'] } }],
                    multiple: false,
                  });
                  processAvatarFile(await fh.getFile());
                } catch (err) {
                  if (err.name !== 'AbortError') fileInputRef.current?.click();
                }
              } else {
                fileInputRef.current?.click();
              }
            }}
            onChange={next => {
              const val = typeof next === 'function' ? next(avatarPos) : next;
              setAvatarPos(val);
              localStorage.setItem('avatarPos', JSON.stringify(val));
              fetch('/auth/me/preferences', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ avatar_position: `${Math.round(val.x)} ${Math.round(val.y)}` }),
              });
            }}
            gradient={currentGradient.bg}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif,image/avif"
            className="hidden"
            onChange={handleAvatarChange}
            disabled={avatarBusy}
          />
          {avatar && !avatarBusy && (
            <button
              type="button"
              onClick={handleAvatarRemove}
              className="mt-3 px-3 py-[5px] rounded-full text-[12px] font-medium tracking-wide text-white bg-black/35 hover:bg-black/55 backdrop-blur-md ring-1 ring-white/15 hover:ring-white/35 shadow-sm transition-all duration-150"
            >
              Убрать фото
            </button>
          )}
          {avatarError && <p className="text-xs text-white/80 mt-1 text-center px-6">{avatarError}</p>}

          {/* Градиенты + Аа + баннер — одна строка */}
          <div className="flex items-center gap-3 mt-5">
            {!bannerPreview && GRADIENTS.map(g => (
              <button
                key={g.id}
                type="button"
                onClick={() => setEditGradientId(g.id)}
                className="focus:outline-none transition-[width,height,box-shadow] duration-200 shrink-0"
                style={{
                  width: editGradientId === g.id ? 28 : 22,
                  height: editGradientId === g.id ? 28 : 22,
                  borderRadius: '50%',
                  background: g.dot,
                  boxShadow: editGradientId === g.id
                    ? `0 0 0 2.5px rgba(255,255,255,0.9), 0 0 0 4px ${g.dot}`
                    : '0 1px 4px rgba(0,0,0,0.25)',
                }}
              />
            ))}
            {/* Аа — цвет текста */}
            <button
              type="button"
              onClick={() => setEditTextDark(v => !v)}
              title={editTextDark ? 'Тёмный текст' : 'Белый текст'}
              className="focus:outline-none transition-[background-color,color] duration-200 flex items-center justify-center shrink-0"
              style={{
                width: 28, height: 28, borderRadius: '50%',
                background: editTextDark ? 'rgba(15,23,42,0.85)' : 'rgba(255,255,255,0.92)',
                boxShadow: '0 0 0 2px rgba(255,255,255,0.5)',
                color: editTextDark ? '#fff' : '#1e293b',
                fontWeight: 800, fontSize: 13, lineHeight: 1,
              }}
            >
              Аа
            </button>
            {/* + баннер: dev — загрузить файл, остальные — перейти в коллекцию */}
            {!user?.is_developer && (
              <button
                type="button"
                title="Открыть коллекцию"
                className="focus:outline-none transition-all duration-200 flex items-center justify-center shrink-0"
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.25)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                  color: 'white', cursor: 'pointer',
                }}
                onClick={() => {
                  setEditView(false);
                  setSkrepView(true);
                  setSkrepSubTab('collection');
                  fetchInventory();
                  fetchShop();
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><line x1="7" y1="2" x2="7" y2="12"/><line x1="2" y1="7" x2="12" y2="7"/></svg>
              </button>
            )}
            {user?.is_developer && (
              <label
                title={bannerPreview ? 'Сменить фон' : 'Загрузить фон'}
                className="focus:outline-none transition-all duration-200 flex items-center justify-center shrink-0"
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: bannerBusy ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.25)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                  color: 'white', cursor: bannerBusy ? 'not-allowed' : 'pointer',
                  pointerEvents: bannerBusy ? 'none' : 'auto',
                }}
              >
                {bannerBusy
                  ? <span className="w-3.5 h-3.5 rounded-full border-2 border-white/50 border-t-white animate-spin block" />
                  : <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><line x1="7" y1="2" x2="7" y2="12"/><line x1="2" y1="7" x2="12" y2="7"/></svg>
                }
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml,image/gif,video/mp4"
                  className="hidden"
                  disabled={bannerBusy}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    setBannerError("");
                    const reader = new FileReader();
                    reader.onload = ev => { setBannerPreview(ev.target.result); setBannerModalOpen(true); };
                    reader.readAsDataURL(file);
                    setBannerBusy(true);
                    try {
                      const fd = new FormData();
                      fd.append("file", file, file.name);
                      const res = await fetch('/auth/banner', { method: 'POST', headers: { ...getAuthHeaders() }, body: fd });
                      const data = await res.json().catch(() => null);
                      if (!res.ok) throw new Error(data?.detail || `Ошибка ${res.status}`);
                      setUser(prev => ({ ...prev, ...data }));
                      setBannerUrl(`${window.location.origin}${data.banner_url.split('?')[0]}?v=${Date.now()}`);
                    } catch (err) {
                      setBannerError(err?.message || "Не удалось загрузить фото");
                      setBannerPreview(null);
                    } finally {
                      setBannerBusy(false);
                    }
                  }}
                />
              </label>
            )}
            {/* × удалить баннер */}
            {user?.is_developer && (bannerPreview || bannerUrl) && (
              <button
                type="button"
                disabled={bannerBusy}
                title="Удалить фон"
                className="focus:outline-none transition-all duration-200 flex items-center justify-center shrink-0"
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'rgba(239,68,68,0.35)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                  color: 'white',
                }}
                onClick={async () => {
                  setBannerPreview(null);
                  setBannerBusy(true);
                  setBannerError("");
                  try {
                    const res = await fetch('/auth/banner', { method: 'DELETE', headers: { ...getAuthHeaders() } });
                    const data = await res.json().catch(() => null);
                    if (!res.ok) throw new Error(data?.detail || `Ошибка ${res.status}`);
                    setUser(prev => ({ ...prev, banner_url: null }));
                    setBannerUrl(null);
                  } catch (err) {
                    setBannerError(err?.message || "Не удалось удалить фото");
                  } finally {
                    setBannerBusy(false);
                  }
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/></svg>
              </button>
            )}
            {/* Dev: frame upload */}
            {user?.is_developer && (
              <>
                <label title={user?.avatar_frame ? 'Сменить рамку' : 'Загрузить рамку'}
                  className="focus:outline-none transition-all duration-200 flex items-center justify-center shrink-0"
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'rgba(168,85,247,0.35)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                    color: 'white', cursor: 'pointer',
                  }}>
                  <span style={{ fontSize: 13, lineHeight: 1 }}>🖼</span>
                  <input type="file"
                    accept="image/jpeg,image/png,image/webp,image/svg+xml,image/gif,video/mp4"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file) return;
                      try {
                        const fd = new FormData();
                        fd.append("file", file, file.name);
                        const res = await fetch('/auth/dev/frame', { method: 'POST', headers: { ...getAuthHeaders() }, body: fd });
                        const data = await res.json().catch(() => null);
                        if (!res.ok) throw new Error(data?.detail || `Ошибка ${res.status}`);
                        setUser(prev => ({ ...prev, ...data }));
                      } catch (err) {
                        alert(err?.message || "Не удалось загрузить рамку");
                      }
                    }}
                  />
                </label>
                {user?.avatar_frame && (
                  <button type="button" title="Удалить рамку"
                    className="focus:outline-none transition-all duration-200 flex items-center justify-center shrink-0"
                    style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: 'rgba(239,68,68,0.25)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                      color: 'white',
                    }}
                    onClick={async () => {
                      try {
                        const res = await fetch('/auth/dev/frame', { method: 'DELETE', headers: { ...getAuthHeaders() } });
                        const data = await res.json().catch(() => null);
                        if (!res.ok) throw new Error(data?.detail || `Ошибка ${res.status}`);
                        setUser(prev => ({ ...prev, ...data }));
                      } catch (err) {
                        alert(err?.message || "Не удалось удалить рамку");
                      }
                    }}>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/></svg>
                  </button>
                )}
                <label title={user?.equipped_badge_asset ? 'Сменить бейджик' : 'Загрузить бейджик'}
                  className="focus:outline-none transition-all duration-200 flex items-center justify-center shrink-0"
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'rgba(16,185,129,0.35)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                    color: 'white', cursor: 'pointer',
                  }}>
                  <span style={{ fontSize: 13, lineHeight: 1 }}>🏅</span>
                  <input type="file"
                    accept="image/jpeg,image/png,image/webp,image/svg+xml,image/gif,video/mp4"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file) return;
                      try {
                        const fd = new FormData();
                        fd.append("file", file, file.name);
                        const res = await fetch('/auth/dev/badge', { method: 'POST', headers: { ...getAuthHeaders() }, body: fd });
                        const data = await res.json().catch(() => null);
                        if (!res.ok) throw new Error(data?.detail || `Ошибка ${res.status}`);
                        setUser(prev => ({ ...prev, ...data }));
                      } catch (err) {
                        alert(err?.message || "Не удалось загрузить бейджик");
                      }
                    }}
                  />
                </label>
                {user?.equipped_badge_asset && (
                  <button type="button" title="Удалить бейджик"
                    className="focus:outline-none transition-all duration-200 flex items-center justify-center shrink-0"
                    style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: 'rgba(239,68,68,0.25)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                      color: 'white',
                    }}
                    onClick={async () => {
                      try {
                        const res = await fetch('/auth/dev/badge', { method: 'DELETE', headers: { ...getAuthHeaders() } });
                        const data = await res.json().catch(() => null);
                        if (!res.ok) throw new Error(data?.detail || `Ошибка ${res.status}`);
                        setUser(prev => ({ ...prev, ...data }));
                      } catch (err) {
                        alert(err?.message || "Не удалось удалить бейджик");
                      }
                    }}>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/></svg>
                  </button>
                )}
              </>
            )}
          </div>
          {bannerError && <p className="text-xs text-white/80 mt-1 text-center px-6">{bannerError}</p>}
        </div>

        {/* Поля формы */}
        <div className="px-4 space-y-5 pb-8 sm:pb-10 pt-5">

          {/* Имя / Фамилия / Отчество */}
          <div className="space-y-3">
            <FloatingInput label="Фамилия" value={editLastName} onChange={e => setEditLastName(e.target.value)} />
            <FloatingInput label="Имя" value={editFirstName} onChange={e => setEditFirstName(e.target.value)} />
            <FloatingInput label="Отчество" value={editPatronymic} onChange={e => setEditPatronymic(e.target.value)} />
          </div>

          {/* Моя группа — кнопка → modal */}
          <div ref={editGroupRef}>
            <span className="block text-[12px] font-medium text-violet-400 mb-1 ml-1">Моя группа</span>
            <button
              type="button"
              onClick={() => { setEditGroupModalSearch(''); setEditGroupModalOpen(true); }}
              className="w-full flex items-center justify-between px-4 py-3.5 bg-white rounded-2xl border-2 border-slate-200 text-[16px] text-left transition-colors hover:border-violet-300 active:border-violet-400"
            >
              <span className={editStudyGroup ? 'text-slate-900' : 'text-slate-400'}>
                {editStudyGroup || 'Не указана'}
              </span>
              <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Modal выбора группы */}
          {editGroupModalOpen && createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
              style={{ paddingBottom: editGroupKbHeight }}
              onClick={() => setEditGroupModalOpen(false)}
            >
              <div
                className="group-picker-modal w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200"
                onClick={e => e.stopPropagation()}
              >
                {/* Шапка */}
                <div className="group-picker-modal__header bg-gradient-to-br from-blue-400 to-blue-500 px-6 py-5 text-white">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-[17px] font-semibold">Выберите группу</h2>
                    <button
                      type="button"
                      onClick={() => setEditGroupModalOpen(false)}
                      className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {/* Поиск */}
                  <div className="flex items-center gap-2 bg-white/15 rounded-2xl px-4 py-2.5">
                    <svg className="w-4 h-4 text-white/70 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      autoFocus
                      type="text"
                      placeholder="Поиск группы..."
                      value={editGroupModalSearch}
                      onChange={e => setEditGroupModalSearch(e.target.value)}
                      className="bg-transparent outline-none text-white placeholder-white/60 text-[15px] w-full"
                    />
                    {editGroupModalSearch && (
                      <button type="button" onClick={() => setEditGroupModalSearch('')} className="text-white/70 hover:text-white shrink-0">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Список */}
                <div className="overflow-y-auto overscroll-contain" style={{ maxHeight: editGroupKbHeight > 50 ? '30vh' : '55vh' }}>
                  {/* Сброс */}
                  <button
                    type="button"
                    onClick={() => { setEditStudyGroup(''); setEditGroupModalOpen(false); }}
                    className={`w-full flex items-center justify-between px-5 py-3.5 text-[15px] border-b border-slate-100 transition-colors hover:bg-slate-50 ${!editStudyGroup ? 'text-violet-600 font-semibold' : 'text-slate-500'}`}
                  >
                    <span>Не указана</span>
                    {!editStudyGroup && <svg className="w-4 h-4 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>}
                  </button>

                  {(editGroupModalSearch
                    ? groups.filter(g => g.toLowerCase().includes(editGroupModalSearch.toLowerCase()))
                    : groups
                  ).map(g => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => { setEditStudyGroup(g); setEditGroupModalOpen(false); }}
                      className={`w-full flex items-center justify-between px-5 py-3.5 text-[15px] border-b border-slate-100 last:border-0 transition-colors hover:bg-violet-50 ${editStudyGroup === g ? 'text-violet-600 font-semibold bg-violet-50/50' : 'text-slate-700'}`}
                    >
                      <span>{g}</span>
                      {editStudyGroup === g && <svg className="w-4 h-4 text-violet-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>}
                    </button>
                  ))}

                  {editGroupModalSearch && groups.filter(g => g.toLowerCase().includes(editGroupModalSearch.toLowerCase())).length === 0 && (
                    <p className="text-center text-[14px] text-slate-400 py-8">Группа не найдена</p>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Email — смена */}
          <div className="space-y-2">
            <div className="relative pt-5">
              <span className="absolute top-0 left-1 text-[12px] font-medium text-violet-400 leading-none pointer-events-none">
                Email
              </span>
              <div className="w-full px-4 py-3.5 bg-white/60 rounded-2xl border-2 border-slate-200 text-[16px] text-slate-400 select-all">
                {email}
              </div>
            </div>

            {emailChangeStep === 'idle' && (
              <button
                type="button"
                onClick={() => { setEmailChangeStep('input'); setEmailChangeErr(''); setNewEmailInput(''); }}
                className="text-[13px] font-semibold text-[#3b82f6] hover:text-[#1e3a8a] transition-colors ml-1"
              >
                Изменить email →
              </button>
            )}

            {emailChangeStep === 'input' && (
              <div className="space-y-2">
                <input
                  type="email"
                  value={newEmailInput}
                  onChange={e => setNewEmailInput(e.target.value.toLowerCase())}
                  placeholder="Новый email"
                  className="w-full px-4 py-3.5 bg-white rounded-2xl border-2 border-slate-200 focus:border-[#3b82f6] outline-none text-[16px] text-slate-900 transition-colors"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleRequestEmailChange}
                    className="flex-1 h-11 rounded-2xl bg-[#3b82f6] hover:bg-[#1e3a8a] text-white text-[14px] font-semibold transition-colors active:scale-[0.97]"
                  >
                    Отправить код
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmailChangeStep('idle')}
                    className="h-11 px-4 rounded-2xl border border-slate-200 text-[14px] text-slate-500 hover:bg-slate-50 transition-colors"
                  >
                    Отмена
                  </button>
                </div>
                {emailChangeErr && <p className="text-[12px] text-red-500 font-medium ml-1">{emailChangeErr}</p>}
              </div>
            )}

            {emailChangeStep === 'sending' && (
              <p className="text-[13px] text-slate-400 ml-1">Отправляем код...</p>
            )}

            {(emailChangeStep === 'code' || emailChangeStep === 'confirming') && (
              <div className="space-y-2">
                <p className="text-[12px] text-slate-500 ml-1">
                  Код отправлен на <span className="font-semibold text-slate-700">{newEmailInput}</span>
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    maxLength={6}
                    value={emailChangeCode}
                    onChange={e => setEmailChangeCode(e.target.value.toUpperCase())}
                    placeholder="000000"
                    className="flex-1 h-11 px-4 rounded-2xl border-2 border-slate-200 focus:border-[#3b82f6] outline-none text-[15px] font-mono tracking-widest text-slate-900 bg-white transition-colors"
                  />
                  <button
                    type="button"
                    onClick={handleConfirmEmailChange}
                    disabled={emailChangeStep === 'confirming'}
                    className="h-11 px-5 rounded-2xl bg-[#3b82f6] hover:bg-[#1e3a8a] text-white text-[14px] font-semibold transition-colors disabled:opacity-50 active:scale-[0.97]"
                  >
                    {emailChangeStep === 'confirming' ? '...' : 'Подтвердить'}
                  </button>
                </div>
                <div className="flex items-center gap-3 ml-1">
                  <button
                    type="button"
                    disabled={emailChangeCooldown > 0 || emailChangeStep === 'confirming'}
                    onClick={() => { setEmailChangeStep('input'); handleRequestEmailChange(); }}
                    className="text-[12px] text-slate-400 hover:text-slate-600 transition-colors disabled:cursor-not-allowed"
                  >
                    {emailChangeCooldown > 0 ? `Повторить через ${emailChangeCooldown}с` : 'Отправить снова'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEmailChangeStep('idle'); setEmailChangeCode(''); setEmailChangeErr(''); }}
                    className="text-[12px] text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    Отмена
                  </button>
                </div>
                {emailChangeErr && <p className="text-[12px] text-red-500 font-medium ml-1">{emailChangeErr}</p>}
              </div>
            )}

            {emailChangeStep === 'done' && (
              <p className="text-[13px] text-emerald-600 font-semibold ml-1">Email успешно изменён</p>
            )}
          </div>

        </div>
      </div>
    </div>{/* /-mt-8 slide container */}

    {/* pushBlockedModal (fixed, вне контейнера) */}
    {editorOpen && editorSrc && (
      <PhotoEditor
        src={editorSrc}
        onConfirm={handleEditorConfirm}
        onClose={() => { setEditorOpen(false); if (editorSrc) { URL.revokeObjectURL(editorSrc); setEditorSrc(null); } }}
      />
    )}

    {pushBlockedModal && (() => {
      const { browser, steps } = getPushBlockedSteps();
      return (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
          onClick={() => setPushBlockedModal(false)}
        >
          <div
            className="push-blocked-sheet bg-white dark:bg-[#1e2128] w-full max-w-sm rounded-[2rem] overflow-hidden shadow-[0_32px_64px_-12px_rgba(15,23,42,0.45)] dark:shadow-[0_32px_64px_-12px_rgba(0,0,0,0.7)]"
            onClick={e => e.stopPropagation()}
          >
            <style>{`
              @keyframes push-blocked-enter {
                from { opacity: 0; transform: translateY(32px); filter: blur(4px); }
                to   { opacity: 1; transform: translateY(0);    filter: blur(0);   }
              }
              .push-blocked-sheet { animation: push-blocked-enter 380ms cubic-bezier(0.16,1,0.3,1) both; }
              @media (prefers-reduced-motion: reduce) {
                .push-blocked-sheet { animation: none; opacity: 1; transform: none; filter: none; }
              }
            `}</style>

            {/* Header */}
            <div className="px-6 pt-6 pb-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#eff6ff] dark:bg-[#1e3a8a]/25 flex items-center justify-center shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-[#3b82f6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 17H20L18.595 15.595A1 1 0 0118 14.878V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.879a1 1 0 01-.293.707L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <div className="min-w-0 pt-0.5">
                <h2 className="text-[16px] font-semibold text-[#0f172a] dark:text-slate-100 leading-snug">
                  Уведомления заблокированы
                </h2>
                <span className="inline-block mt-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-[#94a3b8] dark:text-slate-500">
                  {browser}
                </span>
              </div>
            </div>

            <div className="h-px mx-6 bg-[#e2e8f0] dark:bg-slate-700/50" />

            {/* Steps */}
            <div className="px-6 pt-5 pb-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#94a3b8] dark:text-slate-500 mb-3.5">
                Как разрешить
              </p>
              <div className="space-y-3.5">
                {steps.map((step, i) => (
                  <div key={i} className="flex gap-3.5 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#eff6ff] dark:bg-[#1e3a8a]/25 text-[#3b82f6] text-[12px] font-bold flex items-center justify-center shrink-0 mt-px">
                      {i + 1}
                    </span>
                    <span className="text-[14px] text-[#475569] dark:text-slate-300 leading-snug">{step}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="px-5 pb-5 pt-2">
              <button
                onClick={() => setPushBlockedModal(false)}
                className="w-full h-12 rounded-2xl bg-[#3b82f6] hover:bg-[#1e3a8a] text-white text-[15px] font-semibold transition-colors duration-200 active:scale-[0.97]"
              >
                Понятно
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {/* ── CARD ACTION SHEET ── */}
    {cardMenu && cardMenu.rect && createPortal(
      <div className="fixed inset-0 z-[290]" onClick={() => setCardMenu(null)}>
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: Math.min(cardMenu.rect.top, window.innerHeight - 210),
            left: cardMenu.rect.left,
            width: cardMenu.rect.width,
            zIndex: 291,
            animation: 'card-menu-pop 220ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
            transformOrigin: 'top center',
          }}
        >
          <div style={{
            background: 'oklch(16% 0.01 265)',
            border: '1px solid oklch(100% 0 0 / 0.11)',
            borderRadius: 18,
            overflow: 'hidden',
            boxShadow: '0 28px 56px oklch(5% 0.02 265 / 0.75), 0 4px 12px oklch(5% 0.01 265 / 0.4)',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 9,
              padding: '11px 12px 10px',
              borderBottom: '1px solid oklch(100% 0 0 / 0.07)',
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: 9,
                background: 'oklch(24% 0.01 265)',
                flexShrink: 0, overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <img src={cardMenu.item.asset} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} />
              </div>
              <p style={{
                color: 'oklch(94% 0.004 265)',
                fontWeight: 700, fontSize: 11,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                flex: 1, letterSpacing: '0.01em',
              }}>
                {cardMenu.item.name}
              </p>
            </div>
            {/* Action rows */}
            {[
              {
                icon: <span style={{ fontSize: 12 }}>🎁</span>,
                label: 'Подарить',
                color: 'oklch(79% 0.08 290)',
                action: () => { openGiftModal(cardMenu.item); setCardMenu(null); },
              },
              {
                icon: <img src="/paperclip.svg" alt="" style={{ width: 14, height: 14, opacity: 0.85 }} />,
                label: `Продать · ${getSellPrice(cardMenu.item)}`,
                color: 'oklch(85% 0.12 82)',
                action: () => { setSellConfirm({ item: cardMenu.item, sellPrice: getSellPrice(cardMenu.item), cardEl: document.getElementById(`collection-item-${cardMenu.item.item_id}`) }); setCardMenu(null); },
              },
            ].map((row, i, arr) => (
              <button key={i} type="button" onClick={row.action}
                style={{
                  display: 'flex', alignItems: 'center', gap: 9,
                  width: '100%', padding: '0 13px',
                  height: 44,
                  color: row.color,
                  fontSize: 13, fontWeight: 600,
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: i < arr.length - 1 ? '1px solid oklch(100% 0 0 / 0.06)' : 'none',
                  cursor: 'pointer',
                  transition: 'background 100ms',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'oklch(100% 0 0 / 0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onMouseDown={e => e.currentTarget.style.background = 'oklch(100% 0 0 / 0.08)'}
                onMouseUp={e => e.currentTarget.style.background = 'oklch(100% 0 0 / 0.05)'}
              >
                <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0, width: 16, justifyContent: 'center' }}>{row.icon}</span>
                {row.label}
              </button>
            ))}
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* ── SELL CONFIRM MODAL ── */}
    {sellConfirm && createPortal(
      <div className="fixed inset-0 z-[300] flex items-center justify-center px-4"
        style={{ background: 'oklch(5% 0.01 265 / 0.7)', backdropFilter: 'blur(10px)' }}
        onClick={() => setSellConfirm(null)}>
        <div onClick={e => e.stopPropagation()}
          style={{
            width: '100%', maxWidth: 320,
            background: 'oklch(16% 0.01 265)',
            border: '1px solid oklch(100% 0 0 / 0.1)',
            borderRadius: 24,
            overflow: 'hidden',
            boxShadow: '0 32px 64px oklch(5% 0.02 265 / 0.8)',
            animation: 'card-menu-pop 220ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
          }}>
          {/* Item preview */}
          <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid oklch(100% 0 0 / 0.07)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 14,
                background: 'oklch(24% 0.01 265)',
                flexShrink: 0, overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <img src={sellConfirm.item.asset} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: 'oklch(95% 0.004 265)', fontWeight: 700, fontSize: 14, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {sellConfirm.item.name}
                </p>
                {sellConfirm.item.description && (
                  <p style={{ color: 'oklch(58% 0.006 265)', fontSize: 12, marginTop: 4, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {sellConfirm.item.description}
                  </p>
                )}
              </div>
            </div>
          </div>
          {/* Warning */}
          <p style={{ padding: '12px 20px', color: 'oklch(55% 0.005 265)', fontSize: 12, lineHeight: 1.5, borderBottom: '1px solid oklch(100% 0 0 / 0.06)' }}>
            Продажа необратима. Получишь меньше, чем потратил.
          </p>
          {/* Buttons */}
          <div style={{ display: 'flex', gap: 0 }}>
            <button type="button" onClick={() => setSellConfirm(null)}
              style={{
                flex: 1, height: 50, fontSize: 13, fontWeight: 600,
                color: 'oklch(55% 0.005 265)', background: 'transparent', border: 'none',
                borderRight: '1px solid oklch(100% 0 0 / 0.07)', cursor: 'pointer',
                transition: 'background 100ms',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'oklch(100% 0 0 / 0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              Отмена
            </button>
            <button type="button" onClick={handleSell}
              style={{
                flex: 1, height: 50, fontSize: 13, fontWeight: 700,
                color: 'oklch(85% 0.12 82)', background: 'transparent', border: 'none',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                transition: 'background 100ms',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'oklch(100% 0 0 / 0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              Продать · {sellConfirm.sellPrice} <img src="/paperclip.svg" alt="" style={{ width: 13, height: 13, verticalAlign: 'middle', opacity: 0.85 }} />
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* ── GIFT MODAL ── */}
    {giftModal && createPortal(
      <div className="fixed inset-0 z-[300] flex items-center justify-center px-4"
        style={{ background: 'oklch(5% 0.01 265 / 0.7)', backdropFilter: 'blur(10px)' }}
        onClick={() => setGiftModal(null)}>
        <div onClick={e => e.stopPropagation()}
          style={{
            width: '100%', maxWidth: 320,
            background: 'oklch(16% 0.01 265)',
            border: '1px solid oklch(100% 0 0 / 0.1)',
            borderRadius: 24,
            overflow: 'hidden',
            boxShadow: '0 32px 64px oklch(5% 0.02 265 / 0.8)',
            animation: 'card-menu-pop 220ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
          }}>
          {/* Header */}
          <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid oklch(100% 0 0 / 0.07)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: 'oklch(24% 0.01 265)',
              flexShrink: 0, overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <img src={giftModal.asset} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ color: 'oklch(95% 0.004 265)', fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {giftModal.name}
              </p>
              <p style={{ color: 'oklch(50% 0.005 265)', fontSize: 12, marginTop: 3 }}>🎁 Подарить предмет</p>
            </div>
          </div>

          {!giftRecipient ? (
            /* Step 1: email lookup */
            <>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid oklch(100% 0 0 / 0.06)' }}>
                <p style={{ color: 'oklch(55% 0.005 265)', fontSize: 12, marginBottom: 10 }}>
                  Введи email получателя
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="email"
                    placeholder="example@mail.ru"
                    value={giftEmail}
                    onChange={e => { setGiftEmail(e.target.value); setGiftLookupError(''); }}
                    onKeyDown={e => e.key === 'Enter' && lookupGiftRecipient()}
                    autoFocus
                    style={{
                      flex: 1, padding: '9px 12px', borderRadius: 12, fontSize: 13,
                      background: 'oklch(22% 0.01 265)', border: '1px solid oklch(100% 0 0 / 0.1)',
                      color: 'oklch(95% 0.004 265)', caretColor: 'oklch(85% 0.12 265)', outline: 'none',
                    }}
                  />
                  <button type="button" onClick={lookupGiftRecipient} disabled={giftBusy || !giftEmail.trim()}
                    style={{
                      padding: '9px 14px', borderRadius: 12, fontSize: 13, fontWeight: 600, border: 'none',
                      background: giftBusy || !giftEmail.trim() ? 'oklch(26% 0.01 265)' : 'oklch(32% 0.04 265)',
                      color: giftBusy || !giftEmail.trim() ? 'oklch(42% 0.005 265)' : 'oklch(88% 0.01 265)',
                      cursor: giftBusy || !giftEmail.trim() ? 'not-allowed' : 'pointer',
                      transition: 'background 120ms, color 120ms', flexShrink: 0,
                    }}>
                    {giftBusy ? '…' : 'Найти'}
                  </button>
                </div>
                {giftLookupError && (
                  <p style={{ color: 'oklch(65% 0.18 25)', fontSize: 12, marginTop: 8 }}>{giftLookupError}</p>
                )}
              </div>
              {giftEmail.length > 0 && (() => {
                const q = giftEmail.toLowerCase();
                const matches = giftSuggestions.filter(u =>
                  (u.email || '').toLowerCase().includes(q) || (u.fio || '').toLowerCase().includes(q)
                ).slice(0, 3);
                if (!matches.length) return null;
                return (
                <div style={{ borderBottom: '1px solid oklch(100% 0 0 / 0.06)' }}>
                  {matches.map((u, i) => (
                    <button key={u.id} type="button"
                      onClick={() => setGiftRecipient({ id: u.id, fio: u.fio, email: u.email, avatar_url: u.avatar_url })}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 20px', background: 'transparent', border: 'none', cursor: 'pointer',
                        borderTop: i > 0 ? '1px solid oklch(100% 0 0 / 0.05)' : 'none',
                        transition: 'background 100ms',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'oklch(100% 0 0 / 0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
                        background: 'oklch(30% 0.06 265)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', color: 'oklch(80% 0.06 265)', fontWeight: 700, fontSize: 13,
                      }}>
                        {u.avatar_url
                          ? <img src={u.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : (u.fio?.[0] || '?').toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0, textAlign: 'left' }}>
                        <p style={{ color: 'oklch(90% 0.004 265)', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.fio}</p>
                        <p style={{ color: 'oklch(48% 0.005 265)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
                );
              })()}
              <div style={{ display: 'flex' }}>
                <button type="button" onClick={() => setGiftModal(null)}
                  style={{
                    flex: 1, height: 50, fontSize: 13, fontWeight: 600, border: 'none',
                    color: 'oklch(55% 0.005 265)', background: 'transparent', cursor: 'pointer',
                    transition: 'background 100ms',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'oklch(100% 0 0 / 0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  Отмена
                </button>
              </div>
            </>
          ) : (
            /* Step 2: confirm recipient */
            <>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid oklch(100% 0 0 / 0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
                    background: 'oklch(30% 0.06 265)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'oklch(80% 0.06 265)', fontWeight: 700, fontSize: 15,
                  }}>
                    {giftRecipient.avatar_url
                      ? <img src={giftRecipient.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : (giftRecipient.fio?.[0] || '?').toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ color: 'oklch(95% 0.004 265)', fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {giftRecipient.fio}
                    </p>
                    <p style={{ color: 'oklch(50% 0.005 265)', fontSize: 11, marginTop: 2 }}>{giftRecipient.email}</p>
                  </div>
                </div>
                <p style={{ color: 'oklch(55% 0.005 265)', fontSize: 12, lineHeight: 1.5 }}>
                  Подарить <span style={{ color: 'oklch(88% 0.01 265)', fontWeight: 600 }}>«{giftModal.name}»</span>? Действие необратимо.
                </p>
                <div
                  role="checkbox" aria-checked={giftAnonymous}
                  onClick={() => setGiftAnonymous(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 12, cursor: 'pointer', userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
                >
                  <style>{`
                    @keyframes ga-fill { 0%{transform:scale(0.7);opacity:0} 60%{transform:scale(1.15)} 100%{transform:scale(1);opacity:1} }
                    @keyframes ga-check { 0%{stroke-dashoffset:18} 100%{stroke-dashoffset:0} }
                    @keyframes ga-unfill { 0%{transform:scale(1.05)} 100%{transform:scale(1)} }
                  `}</style>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    border: giftAnonymous ? 'none' : '2px solid oklch(38% 0.01 265)',
                    background: giftAnonymous ? 'oklch(62% 0.14 285)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'border-color 180ms, background 180ms',
                    animation: giftAnonymous ? 'ga-fill 0.28s cubic-bezier(0.34,1.56,0.64,1) both' : 'ga-unfill 0.18s ease both',
                  }}>
                    {giftAnonymous && (
                      <svg width="11" height="9" viewBox="0 0 11 9" fill="none" style={{ display:'block' }}>
                        <polyline
                          points="1.5,4.5 4.2,7.2 9.5,1.5"
                          stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          strokeDasharray="18" strokeDashoffset="0"
                          style={{ animation: 'ga-check 0.22s cubic-bezier(0.4,0,0.2,1) 0.06s both' }}
                        />
                      </svg>
                    )}
                  </div>
                  <span style={{ color: giftAnonymous ? 'oklch(80% 0.01 265)' : 'oklch(55% 0.005 265)', fontSize: 12, transition: 'color 180ms' }}>Анонимно</span>
                </div>
              </div>
              <div style={{ display: 'flex' }}>
                <button type="button" onClick={() => setGiftRecipient(null)}
                  style={{
                    flex: 1, height: 50, fontSize: 13, fontWeight: 600, border: 'none',
                    borderRight: '1px solid oklch(100% 0 0 / 0.07)',
                    color: 'oklch(55% 0.005 265)', background: 'transparent', cursor: 'pointer',
                    transition: 'background 100ms',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'oklch(100% 0 0 / 0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  Назад
                </button>
                <button type="button" onClick={handleGift} disabled={giftBusy}
                  style={{
                    flex: 1, height: 50, fontSize: 13, fontWeight: 700, border: 'none',
                    color: 'oklch(80% 0.12 285)', background: 'transparent', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    transition: 'background 100ms', opacity: giftBusy ? 0.5 : 1,
                  }}
                  onMouseEnter={e => { if (!giftBusy) e.currentTarget.style.background = 'oklch(100% 0 0 / 0.04)'; }}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {giftBusy
                    ? <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid oklch(100% 0 0 / 0.2)', borderTopColor: 'oklch(80% 0.06 265)', animation: 'spin 0.7s linear infinite' }} />
                    : '🎁 Подарить'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>,
      document.body
    )}

    {/* ── AWARD SKREP MODAL ── */}
    {HALLOWEEN_MODE && skrepView && createPortal(
      <button
        type="button"
        onClick={openReaperModal}
        aria-label="Напугать кого-то"
        title="Напугать кого-то"
        style={{
          position: 'fixed',
          right: 'max(16px, env(safe-area-inset-right, 0px))',
          bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
          width: 64, height: 64,
          padding: 0, border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          zIndex: 90,
          filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.5)) drop-shadow(0 0 14px rgba(139,0,139,0.35))',
          animation: 'reaperFloat 3.4s ease-in-out infinite',
        }}
      >
        <style>{`
          @keyframes reaperFloat {
            0%, 100% { transform: translateY(0) rotate(-2deg); }
            50%      { transform: translateY(-6px) rotate(2deg); }
          }
        `}</style>
        <img src="/reaper.svg" alt="" style={{ width: '100%', height: '100%', imageRendering: 'pixelated', pointerEvents: 'none' }} />
      </button>,
      document.body
    )}

    {reaperModalOpen && createPortal(
      <div className="fixed inset-0 z-[300] flex items-center justify-center px-4"
        style={{ background: 'oklch(5% 0.01 265 / 0.72)', backdropFilter: 'blur(12px)' }}
        onClick={() => !reaperSending && setReaperModalOpen(false)}>
        <div onClick={e => e.stopPropagation()}
          style={{
            width: '100%', maxWidth: 380, maxHeight: '80vh',
            background: 'oklch(14% 0.02 300)',
            border: '1px solid oklch(100% 0 0 / 0.1)',
            borderRadius: 24,
            overflow: 'hidden',
            boxShadow: '0 32px 64px oklch(5% 0.02 300 / 0.9)',
            animation: 'card-menu-pop 220ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
            display: 'flex', flexDirection: 'column',
          }}>

          <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid oklch(100% 0 0 / 0.07)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: 'oklch(22% 0.08 300)',
              flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <img src="/reaper.svg" alt="" style={{ width: 32, height: 32, imageRendering: 'pixelated' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ color: 'oklch(95% 0.004 265)', fontWeight: 700, fontSize: 15 }}>Напугать кого-то</p>
              <p style={{ color: 'oklch(55% 0.03 300)', fontSize: 12, marginTop: 2 }}>
                Цена: 3 🍬 · баланс: <span style={{ color: (candyBalance ?? 0) >= 3 ? '#ff9228' : '#ef4444', fontWeight: 600 }}>{candyBalance ?? 0}</span>
              </p>
            </div>
            <button type="button" onClick={() => !reaperSending && setReaperModalOpen(false)}
              style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 8, border: 'none', background: 'oklch(100% 0 0 / 0.07)', color: 'oklch(55% 0.005 265)', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ✕
            </button>
          </div>

          {reaperSentTo ? (
            <div style={{ padding: '32px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 42, marginBottom: 8 }}>👻</div>
              <p style={{ color: 'oklch(90% 0.03 300)', fontSize: 15, fontWeight: 600 }}>Отправлено!</p>
              <p style={{ color: 'oklch(55% 0.03 300)', fontSize: 12, marginTop: 4 }}>{reaperSentTo.fio}</p>
            </div>
          ) : (
            <>
              <div style={{ padding: '12px 20px 4px', display: 'flex', gap: 6 }}>
                {[
                  { id: 'bats', label: 'Летучие мыши', emoji: '🦇' },
                  { id: 'spiders', label: 'Пауки', emoji: '🕷️' },
                  { id: 'clowns', label: 'Клоуны', emoji: '🤡' },
                ].map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setReaperType(t.id)}
                    style={{
                      flex: 1, padding: '8px 6px', borderRadius: 10,
                      border: `1px solid ${reaperType === t.id ? 'oklch(70% 0.2 300)' : 'oklch(100% 0 0 / 0.1)'}`,
                      background: reaperType === t.id ? 'oklch(30% 0.1 300)' : 'oklch(18% 0.02 300)',
                      color: reaperType === t.id ? '#fff' : 'oklch(70% 0.03 300)',
                      cursor: 'pointer', fontSize: 11, fontWeight: 600,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                      transition: 'all 120ms',
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{t.emoji}</span>
                    {t.label}
                  </button>
                ))}
              </div>
              <div style={{ padding: '10px 20px 10px' }}>
                <input
                  type="text"
                  placeholder="Поиск жертвы..."
                  value={reaperSearch}
                  onChange={e => setReaperSearch(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%', padding: '9px 12px', borderRadius: 12, fontSize: 13, boxSizing: 'border-box',
                    background: 'oklch(20% 0.03 300)', border: '1px solid oklch(100% 0 0 / 0.1)',
                    color: 'oklch(95% 0.004 265)', outline: 'none',
                  }}
                />
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '0 10px 10px' }}>
                {reaperUsers
                  .filter(u => {
                    const q = reaperSearch.trim().toLowerCase();
                    if (!q) return true;
                    return (u.fio || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                  })
                  .slice(0, 50)
                  .map(u => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => sendReaper(u)}
                      disabled={reaperSending || (candyBalance ?? 0) < 3}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 10px', borderRadius: 12, border: 'none',
                        background: 'transparent',
                        cursor: (reaperSending || (candyBalance ?? 0) < 3) ? 'not-allowed' : 'pointer',
                        textAlign: 'left', opacity: (candyBalance ?? 0) < 3 ? 0.5 : 1,
                        transition: 'background 120ms',
                      }}
                      onMouseEnter={e => { if ((candyBalance ?? 0) >= 3) e.currentTarget.style.background = 'oklch(22% 0.05 300)'; }}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
                        background: 'oklch(32% 0.06 265)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', color: 'oklch(80% 0.06 265)', fontWeight: 700, fontSize: 12,
                      }}>
                        {u.avatar_url
                          ? <img src={u.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : (u.fio?.[0] || '?').toUpperCase()}
                      </div>
                      <span style={{ color: 'oklch(90% 0.004 265)', fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.fio}</span>
                      <span style={{ color: 'oklch(55% 0.05 300)', fontSize: 11 }}>👻</span>
                    </button>
                  ))}
                {reaperUsers.length === 0 && (
                  <p style={{ color: 'oklch(45% 0.005 265)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>Загрузка...</p>
                )}
              </div>

              {reaperError && (
                <p style={{ color: '#ef4444', fontSize: 12, textAlign: 'center', padding: '0 20px 10px' }}>{reaperError}</p>
              )}
            </>
          )}
        </div>
      </div>,
      document.body
    )}

    {candyExchangeOpen && createPortal(
      (() => {
        const tick = (ms = 12) => { try { navigator.vibrate?.(ms); } catch { /* ignore */ } };
        return (
      <div className="fixed inset-0 z-[300] flex items-center justify-center px-4"
        style={{ background: 'oklch(5% 0.01 265 / 0.72)', backdropFilter: 'blur(12px)' }}
        onClick={() => { if (!candyExchangeBusy) { tick(8); setCandyExchangeOpen(false); } }}>
        <style>{`
          @keyframes candyModalPop {
            0%   { transform: translateY(28px) scale(0.9); opacity: 0; filter: blur(10px); }
            60%  { transform: translateY(-6px) scale(1.015); opacity: 1; filter: blur(0); }
            100% { transform: translateY(0) scale(1); opacity: 1; }
          }
          @keyframes candyIconWiggle {
            0%, 100% { transform: rotate(0deg) scale(1); }
            25%      { transform: rotate(-8deg) scale(1.08); }
            60%      { transform: rotate(6deg) scale(1.04); }
          }
          @keyframes candyRatePulse {
            0%, 100% { opacity: 0.78; transform: scale(1); }
            50%      { opacity: 1;    transform: scale(1.06); }
          }
          @keyframes candyErrShake {
            0%, 100% { transform: translateX(0); }
            20%, 60% { transform: translateX(-6px); }
            40%, 80% { transform: translateX(6px); }
          }
          @keyframes candyMaxFlash {
            0%   { background: oklch(22% 0.03 55); }
            40%  { background: #ff9228; color: #fff; }
            100% { background: oklch(22% 0.03 55); }
          }
          .candy-close-btn { -webkit-tap-highlight-color: transparent; transition: background 150ms, transform 120ms; }
          .candy-close-btn:active { transform: scale(0.86); background: oklch(100% 0 0 / 0.14) !important; }
          .candy-max-btn        { -webkit-tap-highlight-color: transparent; transition: transform 120ms; }
          .candy-max-btn:active { transform: scale(0.92); }
          .candy-step-btn:hover:not(:disabled) { background: oklch(28% 0.04 55); }
        `}</style>
        <div onClick={e => e.stopPropagation()}
          style={{
            width: '100%', maxWidth: 360,
            background: 'oklch(15% 0.01 265)',
            border: '1px solid oklch(100% 0 0 / 0.1)',
            borderRadius: 24,
            overflow: 'hidden',
            boxShadow: '0 32px 64px oklch(5% 0.02 265 / 0.8)',
            animation: 'candyModalPop 320ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
          }}>

          <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid oklch(100% 0 0 / 0.07)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: 'oklch(30% 0.15 55)',
              flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <img src="/candy_pile_3_pixel.svg" alt=""
                style={{ width: 32, height: 32, imageRendering: 'pixelated', animation: 'candyIconWiggle 820ms cubic-bezier(0.36, 1.2, 0.4, 1) 150ms both', transformOrigin: 'center' }} />
            </div>
            <div>
              <p style={{ color: 'oklch(95% 0.004 265)', fontWeight: 700, fontSize: 15 }}>Обмен скрепок на конфеты</p>
              <p style={{ color: 'oklch(50% 0.005 265)', fontSize: 12, marginTop: 2, animation: 'candyRatePulse 2.6s ease-in-out infinite', display: 'inline-block' }}>Курс: 3 📎 → 1 🍬</p>
            </div>
            <button type="button" className="candy-close-btn"
              onClick={() => { if (!candyExchangeBusy) { tick(10); setCandyExchangeOpen(false); } }}
              style={{ marginLeft: 'auto', flexShrink: 0, width: 28, height: 28, borderRadius: 8, border: 'none', background: 'oklch(100% 0 0 / 0.07)', color: 'oklch(55% 0.005 265)', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ✕
            </button>
          </div>

          <div style={{ padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

            <style>{`
              .candy-exchange-input::-webkit-outer-spin-button,
              .candy-exchange-input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
              }
              .candy-exchange-input { -moz-appearance: textfield; appearance: textfield; }
              .candy-step-btn:active:not(:disabled) { transform: scale(0.9); }
              .candy-step-btn { -webkit-tap-highlight-color: transparent; }
            `}</style>
            {(() => {
              const maxSkrep = Math.floor((skrepBalance ?? 0) / 3) * 3;
              const maxCandy = Math.floor((skrepBalance ?? 0) / 3);
              const stepBtnStyle = (disabled) => ({
                width: 38, height: 44, borderRadius: 10, border: '1px solid oklch(100% 0 0 / 0.1)',
                background: 'oklch(22% 0.03 55)', color: '#ff9228', fontSize: 20, fontWeight: 700,
                cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.35 : 1,
                flexShrink: 0, lineHeight: 1, padding: 0, touchAction: 'manipulation',
              });
              const inputStyle = (accent) => ({
                flex: '1 1 auto', width: '100%', padding: '12px 6px', borderRadius: 10, fontSize: 18, fontWeight: 700, boxSizing: 'border-box',
                background: 'oklch(21% 0.01 265)', border: '1px solid oklch(100% 0 0 / 0.1)',
                color: accent, caretColor: '#ff9228', outline: 'none', textAlign: 'center',
                WebkitAppearance: 'none', appearance: 'none', MozAppearance: 'textfield', minWidth: 0,
              });
              const labelStyle = { color: 'oklch(50% 0.005 265)', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 };
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>
                      <img src="/paperclip.svg" alt="" style={{ width: 14, height: 14, filter: 'brightness(1.2)' }} />
                      Скрепки
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button type="button" className="candy-step-btn"
                        onClick={() => { tick(10); setCandyExchangeAmount(a => Math.max(0, a - 3)); }}
                        disabled={candyExchangeBusy || candyExchangeAmount <= 0}
                        style={stepBtnStyle(candyExchangeBusy || candyExchangeAmount <= 0)}>−</button>
                      <input
                        className="candy-exchange-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={candyExchangeAmount}
                        onFocus={e => { tick(8); e.target.select(); }}
                        onChange={e => {
                          const raw = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
                          const capped = Math.min(raw, skrepBalance ?? 0);
                          setCandyExchangeAmount(Math.floor(capped / 3) * 3);
                        }}
                        disabled={candyExchangeBusy}
                        style={inputStyle('oklch(95% 0.004 265)')}
                      />
                      <button type="button" className="candy-step-btn"
                        onClick={() => { tick(10); setCandyExchangeAmount(a => Math.min(maxSkrep, a + 3)); }}
                        disabled={candyExchangeBusy || candyExchangeAmount >= maxSkrep}
                        style={stepBtnStyle(candyExchangeBusy || candyExchangeAmount >= maxSkrep)}>+</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', color: 'oklch(55% 0.005 265)', fontSize: 20, margin: '-4px 0' }}>↓</div>
                  <div>
                    <label style={labelStyle}>
                      <img src="/candy_pile_3_pixel.svg" alt="" style={{ width: 16, height: 16, imageRendering: 'pixelated' }} />
                      Конфеты
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button type="button" className="candy-step-btn"
                        onClick={() => { tick(10); setCandyExchangeAmount(a => Math.max(0, a - 3)); }}
                        disabled={candyExchangeBusy || candyExchangeAmount <= 0}
                        style={stepBtnStyle(candyExchangeBusy || candyExchangeAmount <= 0)}>−</button>
                      <input
                        className="candy-exchange-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={Math.floor(candyExchangeAmount / 3)}
                        onFocus={e => { tick(8); e.target.select(); }}
                        onChange={e => {
                          const raw = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
                          const capped = Math.min(raw, maxCandy);
                          setCandyExchangeAmount(capped * 3);
                        }}
                        disabled={candyExchangeBusy}
                        style={inputStyle('#ff9228')}
                      />
                      <button type="button" className="candy-step-btn"
                        onClick={() => { tick(10); setCandyExchangeAmount(a => Math.min(maxSkrep, a + 3)); }}
                        disabled={candyExchangeBusy || candyExchangeAmount >= maxSkrep}
                        style={stepBtnStyle(candyExchangeBusy || candyExchangeAmount >= maxSkrep)}>+</button>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'oklch(50% 0.005 265)', fontSize: 11 }}>
                Баланс: {skrepBalance ?? 0} 📎 · макс {Math.floor((skrepBalance ?? 0) / 3)} 🍬
              </span>
              <button
                type="button"
                className="candy-max-btn"
                onClick={e => {
                  tick(18);
                  setCandyExchangeAmount(Math.floor((skrepBalance ?? 0) / 3) * 3);
                  const el = e.currentTarget;
                  el.style.animation = 'candyMaxFlash 420ms ease-out';
                  setTimeout(() => { if (el) el.style.animation = ''; }, 440);
                }}
                disabled={candyExchangeBusy || (skrepBalance ?? 0) < 3}
                style={{
                  padding: '4px 10px', borderRadius: 8, border: '1px solid oklch(100% 0 0 / 0.1)',
                  background: 'oklch(22% 0.03 55)', color: '#ff9228', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  opacity: (skrepBalance ?? 0) < 3 ? 0.4 : 1,
                }}
              >Макс</button>
            </div>

            {candyExchangeError && (
              <p key={candyExchangeError}
                style={{ color: '#ef4444', fontSize: 12, textAlign: 'center', animation: 'candyErrShake 380ms cubic-bezier(0.36, 0.07, 0.19, 0.97) both' }}>
                {candyExchangeError}
              </p>
            )}

            <style>{`
              @keyframes candyBtnPulse {
                0%   { transform: scale(1);   box-shadow: 0 0 0 0 rgba(255,146,40,0.6); }
                50%  { transform: scale(1.03); box-shadow: 0 0 0 14px rgba(255,146,40,0); }
                100% { transform: scale(1);   box-shadow: 0 0 0 0 rgba(255,146,40,0); }
              }
              @keyframes candyBurstFly {
                0%   { transform: translate(-50%, -50%) rotate(var(--rot, 0deg)); opacity: 0; }
                18%  { opacity: 1; }
                100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(calc(var(--rot, 0deg) + 420deg)); opacity: 0; }
              }
            `}</style>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={handleCandyExchange}
                disabled={candyExchangeBusy || (skrepBalance ?? 0) < candyExchangeAmount || candyExchangeAmount < 3}
                style={{
                  width: '100%', height: 48, borderRadius: 14, border: 'none',
                  background: candyExchangeBusy || (skrepBalance ?? 0) < candyExchangeAmount ? 'oklch(30% 0.05 55)' : '#ff9228',
                  color: 'white', fontSize: 15, fontWeight: 600,
                  cursor: candyExchangeBusy || (skrepBalance ?? 0) < candyExchangeAmount ? 'not-allowed' : 'pointer',
                  transition: 'transform 100ms',
                  opacity: (skrepBalance ?? 0) < candyExchangeAmount ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  animation: candyBurst.length > 0 ? 'candyBtnPulse 540ms cubic-bezier(0.22,1,0.36,1) both' : undefined,
                }}
              >
                {candyExchangeBusy
                  ? <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid oklch(100% 0 0 / 0.2)', borderTopColor: 'white', animation: 'spin 0.7s linear infinite' }} />
                  : 'Обменять'}
              </button>
              {candyBurst.length > 0 && (
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
                  {candyBurst.map(c => (
                    <span key={c.id}
                      style={{
                        position: 'absolute', left: '50%', top: '50%', fontSize: 22,
                        animation: `candyBurstFly 950ms cubic-bezier(0.2,0.7,0.3,1) both ${c.delay}ms`,
                        willChange: 'transform, opacity',
                        '--dx': `${Math.cos(c.angle) * c.distance}px`,
                        '--dy': `${Math.sin(c.angle) * c.distance - 20}px`,
                        '--rot': `${(Math.random() - 0.5) * 60}deg`,
                        filter: 'drop-shadow(0 2px 6px rgba(255,146,40,0.5))',
                        textShadow: '0 0 10px rgba(255,146,40,0.6)',
                      }}>{c.emoji}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
        );
      })(),
      document.body
    )}

    {awardModal && createPortal(
      <div className="fixed inset-0 z-[300] flex items-center justify-center px-4"
        style={{ background: 'oklch(5% 0.01 265 / 0.72)', backdropFilter: 'blur(12px)' }}
        onClick={() => setAwardModal(false)}>
        <div onClick={e => e.stopPropagation()}
          style={{
            width: '100%', maxWidth: 360,
            background: 'oklch(15% 0.01 265)',
            border: '1px solid oklch(100% 0 0 / 0.1)',
            borderRadius: 24,
            overflow: 'hidden',
            boxShadow: '0 32px 64px oklch(5% 0.02 265 / 0.8)',
            animation: 'card-menu-pop 220ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
          }}>

          {/* Header */}
          <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid oklch(100% 0 0 / 0.07)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: 'oklch(22% 0.03 75)',
              flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22,
            }}>📎</div>
            <div>
              <p style={{ color: 'oklch(95% 0.004 265)', fontWeight: 700, fontSize: 15 }}>Начислить скрепки</p>
              <p style={{ color: 'oklch(50% 0.005 265)', fontSize: 12, marginTop: 2 }}>Награда победителям мероприятия</p>
            </div>
            <button type="button" onClick={() => setAwardModal(false)}
              style={{ marginLeft: 'auto', flexShrink: 0, width: 28, height: 28, borderRadius: 8, border: 'none', background: 'oklch(100% 0 0 / 0.07)', color: 'oklch(55% 0.005 265)', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ✕
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Reason */}
            <div>
              <label style={{ color: 'oklch(50% 0.005 265)', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Мероприятие / повод</label>
              <input
                type="text"
                placeholder="напр. Волейбол 17.09, 1 место"
                value={awardReason}
                onChange={e => setAwardReason(e.target.value)}
                autoFocus
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 12, fontSize: 13, boxSizing: 'border-box',
                  background: 'oklch(21% 0.01 265)', border: '1px solid oklch(100% 0 0 / 0.1)',
                  color: 'oklch(95% 0.004 265)', caretColor: 'oklch(85% 0.15 75)', outline: 'none',
                }}
              />
            </div>

            {/* Amount */}
            <div>
              <label style={{ color: 'oklch(50% 0.005 265)', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Количество 📎 каждому</label>
              <input
                type="number"
                min="1" max="9999"
                placeholder="100"
                value={awardAmount}
                onChange={e => setAwardAmount(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 12, fontSize: 13, boxSizing: 'border-box',
                  background: 'oklch(21% 0.01 265)', border: '1px solid oklch(100% 0 0 / 0.1)',
                  color: 'oklch(95% 0.004 265)', caretColor: 'oklch(85% 0.15 75)', outline: 'none',
                }}
              />
            </div>

            {/* Recipients */}
            <div>
              <label style={{ color: 'oklch(50% 0.005 265)', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Получатели {awardRecipients.length > 0 && <span style={{ color: 'oklch(75% 0.12 75)' }}>· {awardRecipients.length}</span>}
              </label>

              {/* Chips */}
              {awardRecipients.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {awardRecipients.map(u => (
                    <div key={u.id} style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '4px 8px 4px 6px',
                      background: 'oklch(24% 0.03 75)', borderRadius: 20,
                      border: '1px solid oklch(100% 0 0 / 0.08)',
                    }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
                        background: 'oklch(32% 0.06 265)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', color: 'oklch(80% 0.06 265)', fontWeight: 700, fontSize: 10,
                      }}>
                        {u.avatar_url
                          ? <img src={u.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : (u.fio?.[0] || '?').toUpperCase()}
                      </div>
                      <span style={{ color: 'oklch(85% 0.004 265)', fontSize: 12, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.fio}</span>
                      <button type="button"
                        onClick={() => setAwardRecipients(r => r.filter(x => x.id !== u.id))}
                        style={{ background: 'none', border: 'none', color: 'oklch(45% 0.005 265)', cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0, display: 'flex' }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Search */}
              <input
                type="text"
                placeholder="Найти по имени или email..."
                value={awardSearch}
                onChange={e => setAwardSearch(e.target.value)}
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 12, fontSize: 13, boxSizing: 'border-box',
                  background: 'oklch(21% 0.01 265)', border: '1px solid oklch(100% 0 0 / 0.1)',
                  color: 'oklch(95% 0.004 265)', caretColor: 'oklch(85% 0.15 75)', outline: 'none',
                }}
              />

              {/* Dropdown suggestions */}
              {awardSearch.trim().length > 0 && (() => {
                const q = awardSearch.toLowerCase();
                const matches = awardSuggestions.filter(u =>
                  !awardRecipients.find(r => r.id === u.id) &&
                  ((u.fio || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
                ).slice(0, 5);
                if (!matches.length) return (
                  <p style={{ color: 'oklch(40% 0.005 265)', fontSize: 12, marginTop: 6, paddingLeft: 4 }}>Никого не нашли</p>
                );
                return (
                  <div style={{ marginTop: 6, borderRadius: 12, border: '1px solid oklch(100% 0 0 / 0.08)', overflow: 'hidden' }}>
                    {matches.map((u, i) => (
                      <button key={u.id} type="button"
                        onClick={() => { setAwardRecipients(r => [...r, u]); setAwardSearch(''); }}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 12px', background: 'oklch(19% 0.01 265)', border: 'none', cursor: 'pointer',
                          borderTop: i > 0 ? '1px solid oklch(100% 0 0 / 0.05)' : 'none',
                          transition: 'background 100ms', textAlign: 'left',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'oklch(24% 0.01 265)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'oklch(19% 0.01 265)'}>
                        <div style={{
                          width: 30, height: 30, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
                          background: 'oklch(30% 0.06 265)', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', color: 'oklch(80% 0.06 265)', fontWeight: 700, fontSize: 12,
                        }}>
                          {u.avatar_url
                            ? <img src={u.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : (u.fio?.[0] || '?').toUpperCase()}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ color: 'oklch(90% 0.004 265)', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.fio}</p>
                          <p style={{ color: 'oklch(45% 0.005 265)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</p>
                        </div>
                        <span style={{ marginLeft: 'auto', color: 'oklch(60% 0.1 75)', fontSize: 18, flexShrink: 0 }}>+</span>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Error */}
            {awardError && (
              <p style={{ color: 'oklch(65% 0.18 25)', fontSize: 12, marginTop: -4 }}>{awardError}</p>
            )}
          </div>

          {/* Footer */}
          <div style={{ borderTop: '1px solid oklch(100% 0 0 / 0.07)', display: 'flex' }}>
            <button type="button" onClick={() => setAwardModal(false)}
              style={{
                flex: 1, height: 50, fontSize: 13, fontWeight: 600, border: 'none',
                borderRight: '1px solid oklch(100% 0 0 / 0.07)',
                color: 'oklch(50% 0.005 265)', background: 'transparent', cursor: 'pointer',
                transition: 'background 100ms',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'oklch(100% 0 0 / 0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              Отмена
            </button>
            <button type="button" onClick={handleAward} disabled={awardBusy || !awardRecipients.length || !awardAmount || !awardReason.trim()}
              style={{
                flex: 2, height: 50, fontSize: 13, fontWeight: 700, border: 'none',
                color: (!awardBusy && awardRecipients.length && awardAmount && awardReason.trim())
                  ? 'oklch(82% 0.16 75)' : 'oklch(38% 0.005 265)',
                background: 'transparent', cursor: (awardBusy || !awardRecipients.length || !awardAmount || !awardReason.trim()) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'background 100ms, color 120ms',
              }}
              onMouseEnter={e => { if (!awardBusy) e.currentTarget.style.background = 'oklch(100% 0 0 / 0.04)'; }}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {awardBusy
                ? <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid oklch(100% 0 0 / 0.2)', borderTopColor: 'oklch(80% 0.06 265)', animation: 'spin 0.7s linear infinite' }} />
                : <>
                    📎 Начислить {awardAmount ? `${awardAmount}` : ''}
                    {awardRecipients.length > 0 && <span style={{ opacity: 0.65 }}>× {awardRecipients.length}</span>}
                  </>}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* ── GIFT WRAP ANIMATION ── */}
    {giftAnim && createPortal(
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'oklch(6% 0.01 265 / 0.82)',
        backdropFilter: 'blur(24px)',
        animation: 'gwa-bg 2.8s ease-out forwards',
        pointerEvents: 'none',
      }}>
        <style>{`
          @keyframes gwa-bg  { 0%,75%{opacity:1} 100%{opacity:0} }
          @keyframes gwa-txt { 0%{opacity:0;transform:translateY(12px) scale(0.9)} 60%{opacity:1;transform:translateY(-2px) scale(1.02)} 100%{transform:translateY(0) scale(1)} }
          @keyframes gwa-ring {
            0%   { transform:scale(0.5); opacity:0.7; }
            100% { transform:scale(2.6); opacity:0; }
          }
          @keyframes gwa-s1  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-32px,-58px) scale(1.4)}100%{transform:translate(-120px,-145px) scale(0.5);opacity:0}}
          @keyframes gwa-s2  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(34px,-55px) scale(1.5)}100%{transform:translate(130px,-138px) scale(0.4);opacity:0}}
          @keyframes gwa-s3  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-60px,-20px) scale(1.3)}100%{transform:translate(-148px,-36px) scale(0.6);opacity:0}}
          @keyframes gwa-s4  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(62px,-18px) scale(1.4)}100%{transform:translate(152px,-28px) scale(0.5);opacity:0}}
          @keyframes gwa-s5  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(6px,-68px) scale(1.6)}100%{transform:translate(12px,-162px) scale(0.4);opacity:0}}
          @keyframes gwa-s6  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-50px,-44px) scale(1.2)}100%{transform:translate(-112px,-108px) scale(0.7);opacity:0}}
          @keyframes gwa-s7  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(52px,-42px) scale(1.3)}100%{transform:translate(118px,-100px) scale(0.6);opacity:0}}
          @keyframes gwa-s8  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-20px,-62px) scale(1.5)}100%{transform:translate(-36px,-152px) scale(0.5);opacity:0}}
          @keyframes gwa-item-fall {
            0%   { transform:translateX(-50%) translateY(-90px) scale(1.1); opacity:0; }
            18%  { opacity:1; }
            55%  { transform:translateX(-50%) translateY(18px) scale(0.88); opacity:1; }
            80%  { transform:translateX(-50%) translateY(24px) scale(0.6); opacity:0.4; }
            100% { transform:translateX(-50%) translateY(28px) scale(0.3); opacity:0; }
          }
          @keyframes gwa-bob  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
        `}</style>

        {/* Ring pulses */}
        <div style={{ position:'absolute', width:180, height:180, borderRadius:'50%', border:'3px solid rgba(255,255,255,0.15)', animation:'gwa-ring 0.75s cubic-bezier(0.22,1,0.36,1) 0.5s both', pointerEvents:'none' }} />
        <div style={{ position:'absolute', width:180, height:180, borderRadius:'50%', border:'2px solid rgba(100,180,255,0.2)', animation:'gwa-ring 1s cubic-bezier(0.22,1,0.36,1) 0.62s both', pointerEvents:'none' }} />

        {/* Stars */}
        {[
          { a:'gwa-s1', ch:'✦', size:22, color:'#FCD34D', d:'0.48s' },
          { a:'gwa-s2', ch:'★', size:18, color:'#F9A8D4', d:'0.52s' },
          { a:'gwa-s3', ch:'✦', size:16, color:'#6EE7B7', d:'0.56s' },
          { a:'gwa-s4', ch:'★', size:20, color:'#FCD34D', d:'0.50s' },
          { a:'gwa-s5', ch:'✦', size:14, color:'#A5B4FC', d:'0.54s' },
          { a:'gwa-s6', ch:'★', size:17, color:'#F9A8D4', d:'0.58s' },
          { a:'gwa-s7', ch:'✦', size:19, color:'#6EE7B7', d:'0.51s' },
          { a:'gwa-s8', ch:'★', size:15, color:'#FCD34D', d:'0.60s' },
        ].map(({ a, ch, size, color, d }, i) => (
          <div key={i} style={{
            position:'absolute', fontSize:size, color, lineHeight:1,
            textShadow:`0 0 12px ${color}88`,
            animation:`${a} 1.2s cubic-bezier(0.22,1,0.36,1) ${d} both`,
            pointerEvents:'none', userSelect:'none',
          }}>{ch}</div>
        ))}

        {/* Lottie gift box + falling item overlay */}
        <div style={{ position:'relative', width:260, height:260, animation:'gwa-bob 2.4s ease-in-out 2.4s infinite' }}>
          <Lottie
            src={giftAnimData}
            autoplay
            style={{ width:260, height:260 }}
          />
        </div>

        {/* Text */}
        <div style={{ marginTop:12, animation:'gwa-txt 0.42s cubic-bezier(0.34,1.4,0.64,1) 0.68s both', textAlign:'center' }}>
          <p style={{ color:'#fff', fontSize:21, fontWeight:700, letterSpacing:'-0.3px', margin:0 }}>Подарено!</p>
          <p style={{ color:'rgba(255,255,255,0.46)', fontSize:13, fontWeight:500, margin:'5px 0 0' }}>{giftAnim.name}</p>
        </div>
      </div>,
      document.body
    )}

    {/* ── UNBOX ANIMATION ── */}
    {unboxAnim && createPortal(
      <div onClick={() => unboxAnim.onClose()} style={{
        position: 'fixed', inset: 0, zIndex: 9100,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'oklch(6% 0.01 265 / 0.86)',
        backdropFilter: 'blur(24px)',
        cursor: 'pointer',
      }}>
        <style>{`
          @keyframes ub-in     { 0%{opacity:0} 100%{opacity:1} }
          @keyframes ub-txt    { 0%{opacity:0;transform:translateY(14px) scale(0.9)} 60%{opacity:1;transform:translateY(-2px) scale(1.02)} 100%{transform:translateY(0) scale(1)} }
          @keyframes ub-ring   { 0%{transform:scale(0.5);opacity:0.7} 100%{transform:scale(2.8);opacity:0} }
          @keyframes ub-s1  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-34px,-60px) scale(1.4)}100%{transform:translate(-124px,-148px) scale(0.5);opacity:0}}
          @keyframes ub-s2  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(36px,-58px) scale(1.5)}100%{transform:translate(134px,-142px) scale(0.4);opacity:0}}
          @keyframes ub-s3  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-62px,-22px) scale(1.3)}100%{transform:translate(-152px,-38px) scale(0.6);opacity:0}}
          @keyframes ub-s4  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(64px,-20px) scale(1.4)}100%{transform:translate(156px,-30px) scale(0.5);opacity:0}}
          @keyframes ub-s5  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(8px,-70px) scale(1.6)}100%{transform:translate(14px,-166px) scale(0.4);opacity:0}}
          @keyframes ub-s6  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-52px,-46px) scale(1.2)}100%{transform:translate(-116px,-112px) scale(0.7);opacity:0}}
          @keyframes ub-s7  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(54px,-44px) scale(1.3)}100%{transform:translate(122px,-104px) scale(0.6);opacity:0}}
          @keyframes ub-s8  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-22px,-64px) scale(1.5)}100%{transform:translate(-38px,-156px) scale(0.5);opacity:0}}
          @keyframes ub-item-rise {
            0%   { transform:translateX(-50%) translateY(60px) scale(0.5); opacity:0; }
            40%  { transform:translateX(-50%) translateY(-18px) scale(1.18); opacity:1; }
            65%  { transform:translateX(-50%) translateY(4px) scale(0.96); }
            82%  { transform:translateX(-50%) translateY(-6px) scale(1.04); }
            100% { transform:translateX(-50%) translateY(0) scale(1); opacity:1; }
          }
          @keyframes ub-bob    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        `}</style>

        {/* Rings */}
        <div style={{ position:'absolute', width:190, height:190, borderRadius:'50%', border:'3px solid rgba(255,255,255,0.13)', animation:'ub-ring 0.8s cubic-bezier(0.22,1,0.36,1) 1.6s both', pointerEvents:'none' }} />
        <div style={{ position:'absolute', width:190, height:190, borderRadius:'50%', border:'2px solid rgba(255,200,80,0.18)', animation:'ub-ring 1.05s cubic-bezier(0.22,1,0.36,1) 1.72s both', pointerEvents:'none' }} />

        {/* Stars */}
        {[
          { a:'ub-s1', ch:'✦', size:22, color:'#FCD34D', d:'1.52s' },
          { a:'ub-s2', ch:'★', size:18, color:'#F9A8D4', d:'1.56s' },
          { a:'ub-s3', ch:'✦', size:16, color:'#6EE7B7', d:'1.60s' },
          { a:'ub-s4', ch:'★', size:20, color:'#FCD34D', d:'1.54s' },
          { a:'ub-s5', ch:'✦', size:14, color:'#A5B4FC', d:'1.58s' },
          { a:'ub-s6', ch:'★', size:17, color:'#F9A8D4', d:'1.62s' },
          { a:'ub-s7', ch:'✦', size:19, color:'#6EE7B7', d:'1.55s' },
          { a:'ub-s8', ch:'★', size:15, color:'#FCD34D', d:'1.64s' },
        ].map(({ a, ch, size, color, d }, i) => (
          <div key={i} style={{
            position:'absolute', fontSize:size, color, lineHeight:1,
            textShadow:`0 0 12px ${color}88`,
            animation:`${a} 1.3s cubic-bezier(0.22,1,0.36,1) ${d} both`,
            pointerEvents:'none', userSelect:'none',
          }}>{ch}</div>
        ))}

        {/* Box (Lottie reverse) + item rising out */}
        {(() => {
          const UnboxLottie = () => {
            const ref = React.useRef(null);
            React.useEffect(() => {
              const t = setTimeout(() => {
                if (!ref.current) return;
                ref.current.seek(44);
                ref.current.setDirection(-1);
                ref.current.play();
              }, 80);
              return () => clearTimeout(t);
            }, []);
            return (
              <div style={{ position:'relative', width:260, height:260, animation:'ub-bob 2.4s ease-in-out 2.6s infinite' }}>
                <div style={{
                  position:'absolute', left:'50%', top:'22%',
                  zIndex:10,
                  animation:'ub-item-rise 0.55s cubic-bezier(0.34,1.56,0.64,1) 1.5s both',
                  pointerEvents:'none',
                }}>
                  <img src={unboxAnim.asset} alt="" style={{
                    width:64, height:64, borderRadius:14,
                    boxShadow:'0 8px 28px rgba(0,0,0,0.5), 0 0 0 2px rgba(255,255,255,0.1)',
                    objectFit:'cover',
                  }} />
                </div>
                <Lottie
                  src={giftAnimData}
                  lottieRef={ref}
                  style={{ width:260, height:260 }}
                />
              </div>
            );
          };
          return <UnboxLottie />;
        })()}

        {/* Text */}
        <div style={{ marginTop:14, animation:'ub-txt 0.42s cubic-bezier(0.34,1.4,0.64,1) 1.7s both', textAlign:'center' }}>
          <p style={{ color:'#fff', fontSize:21, fontWeight:700, letterSpacing:'-0.3px', margin:0 }}>Вам подарок!</p>
          <p style={{ color:'rgba(255,255,255,0.46)', fontSize:13, fontWeight:500, margin:'5px 0 0' }}>{unboxAnim.name}</p>
          <p style={{ color:'rgba(255,255,255,0.2)', fontSize:11, marginTop:18, letterSpacing:'0.5px', animation:'ub-in 0.5s ease 2.8s both' }}>нажмите чтобы продолжить</p>
        </div>
      </div>,
      document.body
    )}

    {/* ── SKREP AWARD ANIMATION ── */}
    {skrepAwardAnim && createPortal(
      <div onClick={() => skrepAwardAnim.onClose()} style={{
        position: 'fixed', inset: 0, zIndex: 9100,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'oklch(6% 0.015 75 / 0.88)',
        backdropFilter: 'blur(24px)',
        cursor: 'pointer',
      }}>
        <style>{`
          @keyframes saw-in      { 0%{opacity:0} 100%{opacity:1} }
          @keyframes saw-txt     { 0%{opacity:0;transform:translateY(14px) scale(0.9)} 60%{opacity:1;transform:translateY(-2px) scale(1.02)} 100%{transform:translateY(0) scale(1)} }
          @keyframes saw-ring    { 0%{transform:scale(0.5);opacity:0.7} 100%{transform:scale(3.2);opacity:0} }
          @keyframes saw-pop     { 0%{transform:scale(0);opacity:0} 55%{transform:scale(1.22);opacity:1} 75%{transform:scale(0.94)} 100%{transform:scale(1);opacity:1} }
          @keyframes saw-bob     { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
          @keyframes saw-glow    { 0%,100%{filter:drop-shadow(0 0 18px oklch(75% 0.18 75))} 50%{filter:drop-shadow(0 0 38px oklch(85% 0.22 75))} }
          @keyframes saw-s1  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-40px,-68px) scale(1.4)}100%{transform:translate(-130px,-154px) scale(0.4);opacity:0}}
          @keyframes saw-s2  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(42px,-64px) scale(1.5)}100%{transform:translate(138px,-146px) scale(0.4);opacity:0}}
          @keyframes saw-s3  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-68px,-20px) scale(1.3)}100%{transform:translate(-158px,-34px) scale(0.5);opacity:0}}
          @keyframes saw-s4  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(70px,-18px) scale(1.4)}100%{transform:translate(160px,-28px) scale(0.4);opacity:0}}
          @keyframes saw-s5  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(10px,-74px) scale(1.6)}100%{transform:translate(16px,-170px) scale(0.3);opacity:0}}
          @keyframes saw-s6  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-56px,-50px) scale(1.2)}100%{transform:translate(-120px,-118px) scale(0.6);opacity:0}}
          @keyframes saw-s7  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(58px,-48px) scale(1.3)}100%{transform:translate(126px,-108px) scale(0.5);opacity:0}}
          @keyframes saw-s8  {0%{transform:translate(0,0) scale(0);opacity:0}20%{opacity:1;transform:translate(-24px,-68px) scale(1.5)}100%{transform:translate(-40px,-160px) scale(0.4);opacity:0}}
        `}</style>

        {/* Rings */}
        <div style={{ position:'absolute', width:200, height:200, borderRadius:'50%', border:'3px solid oklch(75% 0.18 75 / 0.2)', animation:'saw-ring 0.9s cubic-bezier(0.22,1,0.36,1) 0.7s both', pointerEvents:'none' }} />
        <div style={{ position:'absolute', width:200, height:200, borderRadius:'50%', border:'2px solid oklch(85% 0.12 75 / 0.15)', animation:'saw-ring 1.1s cubic-bezier(0.22,1,0.36,1) 0.82s both', pointerEvents:'none' }} />

        {/* Sparkles */}
        {[
          { a:'saw-s1', ch:'✦', size:22, color:'#FCD34D', d:'0.68s' },
          { a:'saw-s2', ch:'📎', size:16, color:'#FCD34D', d:'0.72s' },
          { a:'saw-s3', ch:'✦', size:16, color:'#FDBA74', d:'0.76s' },
          { a:'saw-s4', ch:'★',  size:20, color:'#FCD34D', d:'0.70s' },
          { a:'saw-s5', ch:'✦', size:14, color:'#FDE68A', d:'0.74s' },
          { a:'saw-s6', ch:'★',  size:17, color:'#FDBA74', d:'0.78s' },
          { a:'saw-s7', ch:'📎', size:14, color:'#FCD34D', d:'0.71s' },
          { a:'saw-s8', ch:'✦', size:19, color:'#FDE68A', d:'0.80s' },
        ].map(({ a, ch, size, color, d }, i) => (
          <div key={i} style={{
            position:'absolute', fontSize:size, color, lineHeight:1,
            textShadow:`0 0 12px ${color}88`,
            animation:`${a} 1.4s cubic-bezier(0.22,1,0.36,1) ${d} both`,
            pointerEvents:'none', userSelect:'none',
          }}>{ch}</div>
        ))}

        {/* Paperclip icon */}
        <div style={{
          animation: 'saw-pop 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.15s both, saw-bob 2.6s ease-in-out 1.2s infinite, saw-glow 2.6s ease-in-out 1.2s infinite',
          fontSize: 96,
          lineHeight: 1,
          userSelect: 'none',
          pointerEvents: 'none',
        }}>📎</div>

        {/* Amount badge */}
        <div style={{
          marginTop: 12,
          animation: 'saw-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.42s both',
          background: 'oklch(22% 0.03 75)',
          border: '1.5px solid oklch(70% 0.18 75 / 0.35)',
          borderRadius: 40,
          padding: '6px 22px',
          color: 'oklch(88% 0.18 75)',
          fontWeight: 800,
          fontSize: 28,
          letterSpacing: '-0.5px',
          pointerEvents: 'none',
        }}>
          +{skrepAwardAnim.amount} 📎
        </div>

        {/* Text */}
        <div style={{ marginTop: 14, animation:'saw-txt 0.42s cubic-bezier(0.34,1.4,0.64,1) 0.6s both', textAlign:'center', maxWidth: 280, padding: '0 20px' }}>
          <p style={{ color:'oklch(96% 0.004 265)', fontSize:20, fontWeight:700, letterSpacing:'-0.3px', margin:0 }}>Вам начислены скрепки!</p>
          {skrepAwardAnim.reason && (
            <p style={{ color:'oklch(60% 0.01 265)', fontSize:13, fontWeight:500, margin:'6px 0 0', lineHeight: 1.4 }}>{skrepAwardAnim.reason}</p>
          )}
          <p style={{ color:'oklch(35% 0.005 265)', fontSize:11, marginTop:22, letterSpacing:'0.5px', animation:'saw-in 0.5s ease 1.6s both' }}>нажмите чтобы продолжить</p>
        </div>
      </div>,
      document.body
    )}

    {/* ── SELL ANIMATION ── */}
    {sellAnim && createPortal(
      <div className="fixed inset-0 pointer-events-none z-[400]">
        {[0,1,2,3,4].map(i => (
          <img key={i} src="/paperclip.svg" alt=""
            style={{
              position: 'fixed',
              left: sellAnim.sx,
              top: sellAnim.sy,
              width: 22,
              height: 22,
              '--fly-x': `${sellAnim.tx - sellAnim.sx}px`,
              '--fly-y': `${sellAnim.ty - sellAnim.sy}px`,
              animation: `sell-clip-fly 750ms ${i * 90}ms cubic-bezier(0.3,0.9,0.3,1) both`,
            }}
          />
        ))}
      </div>,
      document.body
    )}

  </>
  );
}
